import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import API from "../../../api/api";
import { resolveAssetUrl } from "../../../api/config";
import { downloadHtmlAsPdf, downloadDomPagesAsPdf, printHtmlViaIframe, trackServerPdfExport, getUnifiedPrintHtml } from "../../../utils/pdfDownloadHelper";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/footer";
import { exportMonthlyAttendanceExcel, exportToExcel } from "../../../components/common/exportUtils";
import {
  FaCampground,
  FaUsers,
  FaRunning,
  FaUserShield,
  FaCalendarCheck,
  FaClipboardCheck,
  FaFileAlt,
  FaChartLine,
  FaCog,
  FaPlus,
  FaEdit,
  FaTrash,
  FaKey,
  FaEye,
  FaPrint,
  FaDownload,
  FaFileExcel,
  FaCheckCircle,
  FaTimesCircle,
  FaExchangeAlt,
  FaUserPlus,
  FaTimes,
  FaLock,
  FaUnlock,
  FaSave,
  FaSignature,
  FaTags,
  FaUpload,
  FaCalendarDay,
  FaAward,
  FaTrophy,
  FaExternalLinkAlt,
  FaUserTie,
  FaIdCard,
  FaBed,
  FaUserMinus,
  FaUserCheck,
  FaRedoAlt,
  FaSpinner,
} from "react-icons/fa";
import Swal from "sweetalert2";
import CampIdCard, {
  SingleCampIdCard,
  SinglePanCard,
  SingleA4Card,
  generateSingleCardSheetHtml,
  generateSinglePanCardSheetHtml,
  generateSingleA4CardSheetHtml,
} from "./CampIdCard";
import TeamManagement from "./TeamManagement";
import KitMeasurements from "./KitMeasurements";
import FileUploadWithScanner from "../../../components/common/FileUploadWithScanner";
import CampPeopleAttendance from "../../../components/camp/CampPeopleAttendance";
import CampManagerManagement from "../../../components/camp/CampManagerManagement";

const AP_DISTRICTS = [
  "Alluri Sitharama Raju",
  "Anakapalli",
  "Ananthapur",
  "Annamayya",
  "Bapatla",
  "Chittoor",
  "Dr. B.R. Ambedkar Konaseema",
  "East Godavari",
  "Eluru",
  "Guntur",
  "Kakinada",
  "Krishna",
  "Kurnool",
  "Nandyal",
  "NTR",
  "Palnadu",
  "Parvathipuram Manyam",
  "Prakasam",
  "Sri Potti Sriramulu Nellore",
  "Sri Sathya Sai",
  "Srikakulam",
  "Tirupati",
  "Visakhapatnam",
  "Vizianagaram",
  "West Godavari",
  "YSR",
];

const DEFAULT_AVATAR =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M18.685 19.097A9.723 9.723 0 0021.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 003.065 7.097A9.716 9.716 0 0012 21.75a9.716 9.716 0 006.685-2.653zm-12.54-1.285A7.486 7.486 0 0112 15a7.486 7.486 0 015.855 2.812A8.224 8.224 0 0112 20.25a8.224 8.224 0 01-5.855-2.438zM15.75 9a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z' clip-rule='evenodd' /%3E%3C/svg%3E";

const getSafePhotoUrl = (photoPath) => {
  if (!photoPath || typeof photoPath !== "string" || !photoPath.trim()) {
    return DEFAULT_AVATAR;
  }
  return resolveAssetUrl(photoPath, { defaultPath: DEFAULT_AVATAR });
};

const resolvePhoto = (photoPath) => {
  return getSafePhotoUrl(photoPath);
};

const calculateCampDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return null;
  const diffTime = Math.abs(end - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const getTodayISTDate = () => {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
};

const getCurrentISTTime = () => {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
};

const WEEKLY_TRIAL_OPTIONS = [
  { value: "custom_game", label: "This allotted game (custom weekly result)" },
  { value: "test_100m", label: "100m timing" },
  { value: "test_400m", label: "400m timing" },
  { value: "test_shot_put", label: "Shot put distance" },
  { value: "test_long_jump", label: "Long jump distance" },
  { value: "test_skipping", label: "Skipping repetitions" },
  { value: "test_pushups", label: "Push-up repetitions" },
  { value: "test_plank", label: "Plank timing" },
  { value: "test_pullups", label: "Pull-up repetitions" },
  { value: "test_cycling_5km", label: "Cycling 5km timing" },
  { value: "test_swimming_50m", label: "Swimming 50m timing" },
  { value: "test_weight", label: "Body weight" },
  { value: "test_height", label: "Height" },
];

const inferWeeklyTrialField = (gameName = "") => {
  const name = String(gameName).toLowerCase();
  if (/shot\s*put/.test(name)) return "test_shot_put";
  if (/long\s*jump/.test(name)) return "test_long_jump";
  if (/400\s*m/.test(name)) return "test_400m";
  if (/100\s*m|sprint/.test(name)) return "test_100m";
  if (/cycl/.test(name)) return "test_cycling_5km";
  if (/swim/.test(name)) return "test_swimming_50m";
  if (/skip/.test(name)) return "test_skipping";
  if (/push.?up/.test(name)) return "test_pushups";
  if (/pull.?up/.test(name)) return "test_pullups";
  if (/plank/.test(name)) return "test_plank";
  if (/weight|powerlift/.test(name)) return "test_weight";
  return "custom_game";
};

const getAthletePerformanceReports = (athlete, reports = []) => {
  const embedded = athlete?.performance_reports || athlete?.performanceReports;
  const source = Array.isArray(embedded)
    ? embedded
    : reports.filter((report) => String(report.athlete_id) === String(athlete?.id));
  return [...source].sort((a, b) => Number(a.week_number || 0) - Number(b.week_number || 0));
};

const getWeeklyTrialHistory = (athlete, game, reports = []) => {
  const gameName = typeof game === "string" ? game : game?.name || game?.discipline_name || "";
  const field = (typeof game === "object" && game?.weekly_trial_field) || inferWeeklyTrialField(gameName);
  const entries = getAthletePerformanceReports(athlete, reports)
    .map((report) => {
      let value = field === "custom_game"
        ? (Array.isArray(report?.game_trials)
            ? report.game_trials.find((trial) => String(trial?.game_name || "").trim().toLowerCase() === String(gameName).trim().toLowerCase())?.value
            : "")
        : report?.[field];
      value = value === null || value === undefined ? "" : String(value).trim();
      return { week: Number(report.week_number), value };
    })
    .filter((entry) => entry.value !== "");
  return { field, entries, latest: entries.length ? entries[entries.length - 1].value : "" };
};

const getAthleteAllottedGames = (athlete) => {
  const allocated = Array.isArray(athlete?.allocated_disciplines)
    ? athlete.allocated_disciplines
        .map((game) => typeof game === "string" ? game : game?.name || game?.discipline_name || "")
        .map((name) => String(name || "").trim())
        .filter(Boolean)
    : [];
  const fallback = String(athlete?.discipline_name || "").trim();
  return [...new Set(allocated.length ? allocated : (fallback ? [fallback] : []))];
};

const getAthleteProfileCategory = (athlete, sportId = null, sportName = "") => {
  const categories = Array.isArray(athlete?.player_categories) ? athlete.player_categories : [];
  const matched = categories.find((category) =>
    (sportId && String(category?.sport_id) === String(sportId))
      || (sportName && String(category?.sport_name || "").trim().toLowerCase() === String(sportName).trim().toLowerCase())
  );
  return matched || null;
};

const getAthleteGamePerformanceLines = (athlete, reports = []) => {
  if (!Array.isArray(athlete?.allocated_disciplines) || athlete.allocated_disciplines.length === 0) {
    return getAthleteAllottedGames(athlete);
  }

  return athlete.allocated_disciplines.map((game) => {
    const gameData = typeof game === "string" ? {} : game;
    const name = typeof game === "string" ? game : game?.name || game?.discipline_name || "Game";
    const metric = [gameData?.measurement_type, gameData?.measurement_unit].filter(Boolean).join("/");
    const trial = getWeeklyTrialHistory(athlete, game, reports);
    const weeklyTrials = trial.entries.map((entry) => `W${entry.week} ${entry.value}`).join(", ");
    const comparisons = [
      weeklyTrials && `Athlete camp trials ${weeklyTrials}`,
      (gameData?.national_benchmark || gameData?.national_gold_performance)
        && `Previous national benchmark ${gameData.national_benchmark || gameData.national_gold_performance}`,
    ].filter(Boolean);
    return `${name}${metric ? ` (${metric})` : ""}${comparisons.length ? ` — ${comparisons.join(" | ")}` : ""}`;
  }).filter(Boolean);
};

const AttendanceActionButtons = ({ status, inTime, outTime, onAction, disabled = false, allowOverride = false }) => {
  const absent = status === "absent";
  const buttonClass = "px-2 py-1 rounded-lg text-[10px] font-bold border transition disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="flex flex-wrap justify-center gap-1 min-w-[190px]">
      <button type="button" onClick={() => onAction("in")} disabled={disabled || (!allowOverride && (absent || Boolean(inTime)))} className={`${buttonClass} bg-emerald-50 text-emerald-800 border-emerald-300`}>
        In{inTime ? ` ${inTime}` : ""}
      </button>
      <button type="button" onClick={() => onAction("out")} disabled={disabled || !inTime || (!allowOverride && (absent || Boolean(outTime)))} className={`${buttonClass} bg-cyan-50 text-cyan-800 border-cyan-300`}>
        Out{outTime ? ` ${outTime}` : ""}
      </button>
      <button type="button" onClick={() => onAction("absent")} disabled={disabled || (!allowOverride && (absent || Boolean(inTime)))} className={`${buttonClass} bg-rose-50 text-rose-800 border-rose-300`}>
        Absent
      </button>
    </div>
  );
};
const CoachingCampHub = () => {
  // Navigation tabs
  const [activeTab, setActiveTab] = useState("overview");

  // Multi-camp states (cached in sessionStorage for instant frame-1 rendering)
  const [camps, setCamps] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("parasports_cached_camps") || "[]");
    } catch (_e) {
      return [];
    }
  });
  const [selectedCampId, setSelectedCampId] = useState(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem("parasports_cached_camps") || "[]");
      const savedId = sessionStorage.getItem("parasports_selected_camp_id");
      if (savedId && cached.some((c) => String(c.id) === String(savedId))) {
        return savedId;
      }
      const active = cached.find((c) => c.is_active) || cached[0];
      return active ? active.id : "";
    } catch (_e) {
      return "";
    }
  });
  const [tabLoading, setTabLoading] = useState(false);

  // Tab Data States
  const [dashboardStats, setDashboardStats] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [athleteStatusTab, setAthleteStatusTab] = useState("active"); // "active" | "removed"

  const activeAthletes = useMemo(
    () => athletes.filter((a) => a.status !== "removed_from_camp"),
    [athletes]
  );
  const removedAthletes = useMemo(
    () => athletes.filter((a) => a.status === "removed_from_camp"),
    [athletes]
  );
  const [mappings, setMappings] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [athleteAttendanceSheet, setAthleteAttendanceSheet] = useState([]);
  const todayIST = useMemo(() => getTodayISTDate(), []);
  const [attendanceDate, setAttendanceDate] = useState(() => getTodayISTDate());
  const [monthlyAttendanceMonth, setMonthlyAttendanceMonth] = useState(() => getTodayISTDate().slice(0, 7));
  const [exportingMonthlyAttendance, setExportingMonthlyAttendance] = useState(false);
  const [selectedAttendanceAthleteIds, setSelectedAttendanceAthleteIds] = useState([]);
  const [bulkAttendanceSession, setBulkAttendanceSession] = useState("morning");
  const [bulkAttendanceSearch, setBulkAttendanceSearch] = useState("");
  const [bulkAttendanceTeam, setBulkAttendanceTeam] = useState("");
  const [savingBulkAttendance, setSavingBulkAttendance] = useState(false);

  // Eligible players state for appointing coach
  const [eligiblePlayers, setEligiblePlayers] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [playerSearchTerm, setPlayerSearchTerm] = useState("");

  // Coach Profile viewing modal state
  const [selectedCoachProfile, setSelectedCoachProfile] = useState(null);
  const [coachProfileLoading, setCoachProfileLoading] = useState(false);
  const [coachProfileData, setCoachProfileData] = useState(null);

  const fetchEligiblePlayers = async (search = "") => {
    setLoadingEligible(true);
    try {
      const res = await API.camp.getEligiblePlayers({ search: search.trim() });
      setEligiblePlayers(res?.data?.players || res?.players || []);
    } catch (err) {
      console.error("Failed to load eligible players:", err);
    } finally {
      setLoadingEligible(false);
    }
  };

  const openCoachProfileModal = async (coach) => {
    setSelectedCoachProfile(coach);
    setCoachProfileLoading(true);
    setCoachProfileData(null);
    try {
      const res = await API.camp.getPublicCoachProfile(coach.id);
      setCoachProfileData(res?.data?.coach || res?.coach || null);
    } catch (err) {
      console.error("Failed to fetch coach profile:", err);
    } finally {
      setCoachProfileLoading(false);
    }
  };

  // Multi-sport allotment has no fixed sport limit.
  const [sportsAllotmentModalOpen, setSportsAllotmentModalOpen] = useState(false);
  const [selectedAthleteForSportsAllotment, setSelectedAthleteForSportsAllotment] = useState(null);
  const [sportsAllotmentForm, setSportsAllotmentForm] = useState({
    disciplines: [],
    gameDetails: {},
    notes: "",
  });
  const [allotmentSportId, setAllotmentSportId] = useState("");
  const [eligibleAllotmentGames, setEligibleAllotmentGames] = useState([]);
  const [loadingEligibleAllotmentGames, setLoadingEligibleAllotmentGames] = useState(false);
  const [savingSportsAllotment, setSavingSportsAllotment] = useState(false);

  // Date Range Attendance Register & Upload States
  const [attendanceSubTab, setAttendanceSubTab] = useState("range_register"); // "range_register" | "monitor" | "coach_attendance" | "upload"
  const [rangeDateType] = useState("range"); // Daily Register uses a submitted date range
  const [rangeSingleDate, setRangeSingleDate] = useState(() => getTodayISTDate());
  const [rangeDateFrom, setRangeDateFrom] = useState("2026-08-01");
  const [rangeDateTo, setRangeDateTo] = useState(() => getTodayISTDate());
  const [rangeCoachFilter] = useState("");
  const [rangeTeamFilter, setRangeTeamFilter] = useState("");
  const [rangeAthleteFilter, setRangeAthleteFilter] = useState("");
  const [rangeSubmitted, setRangeSubmitted] = useState(false);
  const [rangeFormatStyle, setRangeFormatStyle] = useState("blank"); // "blank" (manual ground copy) | "recorded"
  const [rangeReportData, setRangeReportData] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [isPrintingRegister, setIsPrintingRegister] = useState(false);
  const [emptyFormatDate, setEmptyFormatDate] = useState("2026-08-01");
  const [emptyScope, setEmptyScope] = useState("day"); // "day" (single day) | "entire" (all camp days)
  const [emptyTeamFilter, setEmptyTeamFilter] = useState("");
  const [campTeamsList] = useState([]);

  // Coach Attendance Marking & Edit State (Admin/Secretary)
  const [coachAttendanceDate, setCoachAttendanceDate] = useState(() => getTodayISTDate());
  const [coachAttendanceSheet, setCoachAttendanceSheet] = useState([]);
  const [loadingCoachAttendance, setLoadingCoachAttendance] = useState(false);
  const [savingCoachAttendance, setSavingCoachAttendance] = useState(false);
  const [editingAthleteAttendanceRow, setEditingAthleteAttendanceRow] = useState(null);
  const [savingAthleteAttendance, setSavingAthleteAttendance] = useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);
  const [uploadedReportsLoading, setUploadedReportsLoading] = useState(false);
  const [uploadReportType, setUploadReportType] = useState("attendance"); // "attendance" | "workout_performance"
  const [reportCoachFilter, setReportCoachFilter] = useState("all");
  const [reportTypeFilter, setReportTypeFilter] = useState("all");
  const [reportArchiveSearch, setReportArchiveSearch] = useState("");
  const [reportArchiveDateFilter, setReportArchiveDateFilter] = useState("");
  const [uploadReportForm, setUploadReportForm] = useState({
    date: getTodayISTDate(),
    coach_id: "",
    athlete_id: "",
    training_session: "both",
    activity_details: "",
    performance_rating: "Good",
    remarks: "",
    file: null,
  });
  const [uploadingReport, setUploadingReport] = useState(false);
  const [reportCards, setReportCards] = useState([]);
  const [weeklyReports, setWeeklyReports] = useState([]);
  const [performanceReports, setPerformanceReports] = useState([]);
  const [benchmarkExportWeek, setBenchmarkExportWeek] = useState(1);
  const [benchmarkExportTeam, setBenchmarkExportTeam] = useState("all");
  const [benchmarkExportAthlete, setBenchmarkExportAthlete] = useState("all");
  const [disciplines, setDisciplines] = useState([]);
  const [signatures, setSignatures] = useState({
    assistant_coach_title: "Assistant Coach",
    assistant_coach_name: "",
    camp_incharge_title: "Camp In-Charge",
    camp_incharge_name: "",
    ssa_official_title: "Official",
    ssa_official_name: "Official",
  });

  // Distinct teams available in current camp, sorted in natural order
  const availableTeams = useMemo(() => {
    const names = new Set();
    campTeamsList.forEach((t) => {
      if (t.team_name) names.add(t.team_name.trim());
    });
    coaches.forEach((c) => {
      if (c.team_name) names.add(c.team_name.trim());
    });
    mappings.forEach((m) => {
      if (m.team_name) names.add(m.team_name.trim());
    });
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
  }, [campTeamsList, coaches, mappings]);

  const filteredUploadedReports = useMemo(() => {
    let list = uploadedReports || [];
    if (reportArchiveSearch && reportArchiveSearch.trim()) {
      const q = reportArchiveSearch.trim().toLowerCase();
      list = list.filter((f) =>
        (f.file_name && f.file_name.toLowerCase().includes(q)) ||
        (f.coach_name && f.coach_name.toLowerCase().includes(q)) ||
        (f.athlete_name && f.athlete_name.toLowerCase().includes(q)) ||
        (f.team_name && f.team_name.toLowerCase().includes(q))
      );
    }
    if (reportArchiveDateFilter) {
      list = list.filter((f) => f.uploaded_at && f.uploaded_at.startsWith(reportArchiveDateFilter));
    }
    return list;
  }, [uploadedReports, reportArchiveSearch, reportArchiveDateFilter]);

  // Daily Athlete Performance Report Modal & Filters (Points 12 & 17)
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [individualScope, setIndividualScope] = useState("all"); // "all" | "team" | "single"
  const [selectedIndividualAthleteId, setSelectedIndividualAthleteId] = useState("");
  const [reportSelectedTeam, setReportSelectedTeam] = useState("all");
  const [reportDayMode, setReportDayMode] = useState("single_day"); // "all_days" | "single_day" (defaults to single_day)
  const [reportSingleDayNum, setReportSingleDayNum] = useState(1);
  const [reportSingleDate, setReportSingleDate] = useState(() => getTodayISTDate());
  const [individualDateFrom, setIndividualDateFrom] = useState("");
  const [individualDateTo, setIndividualDateTo] = useState("");
  const [individualHolidayDate, setIndividualHolidayDate] = useState(() => getTodayISTDate());
  const [individualHolidaySession, setIndividualHolidaySession] = useState("full_day");
  const [individualHolidays, setIndividualHolidays] = useState({});

  // Centralized Reports States
  const [centralizedData, setCentralizedData] = useState(null);
  const [selectedCentralCoach, setSelectedCentralCoach] = useState("");
  const [selectedCentralAthlete, setSelectedCentralAthlete] = useState("");
  const [centralTab, setCentralTab] = useState("profile");

  // Modals
  const [campModalOpen, setCampModalOpen] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [coachModalOpen, setCoachModalOpen] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [athleteModalOpen, setAthleteModalOpen] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [disciplineCategoryModalOpen, setDisciplineCategoryModalOpen] = useState(false);
  const [selectedAthleteForDiscipline, setSelectedAthleteForDiscipline] = useState(null);
  const [disciplineCategoryForm, setDisciplineCategoryForm] = useState({
    discipline_id: "",
    discipline_name: "",
    classification_id: "",
    category: "",
  });
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [viewAthleteModal, setViewAthleteModal] = useState(null);
  const [disciplineModalOpen, setDisciplineModalOpen] = useState(false);

  // Get Registered Players to Coaching Camp Modal State
  const [enrollPlayerModalOpen, setEnrollPlayerModalOpen] = useState(false);
  const [availablePlayersList, setAvailablePlayersList] = useState([]);
  const [loadingAvailablePlayers, setLoadingAvailablePlayers] = useState(false);
  const [enrollSearchTerm, setEnrollSearchTerm] = useState("");
  const [enrollDistrictFilter, setEnrollDistrictFilter] = useState("");
  const [enrollGenderFilter, setEnrollGenderFilter] = useState("");
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [enrollForm, setEnrollForm] = useState({
    coach_ids: [],
    team_name: "",
    discipline_id: "",
    discipline_name: "",
    category: "",
  });
  const [submittingEnroll, setSubmittingEnroll] = useState(false);

  // Camp Officials States
  const [officials, setOfficials] = useState([]);
  const [officialsLoading, setOfficialsLoading] = useState(false);
  const [officialSearchTerm, setOfficialSearchTerm] = useState("");
  const [officialDistrictFilter, setOfficialDistrictFilter] = useState("");
  const [officialDutyFilter, setOfficialDutyFilter] = useState("");
  const [enrollOfficialModalOpen, setEnrollOfficialModalOpen] = useState(false);
  const [eligibleOfficialsList, setEligibleOfficialsList] = useState([]);
  const [loadingEligibleOfficials, setLoadingEligibleOfficials] = useState(false);
  const [selectedOfficialUserIds, setSelectedOfficialUserIds] = useState([]);
  const [enrollOfficialForm, setEnrollOfficialForm] = useState({
    designation: "Technical Official",
    duty_role: "Technical Official",
    room_no: "",
    reporting_date: new Date().toISOString().slice(0, 10),
    relieving_date: "",
    notes: "",
  });
  const [submittingEnrollOfficial, setSubmittingEnrollOfficial] = useState(false);
  const [officialModalOpen, setOfficialModalOpen] = useState(false);
  const [editingOfficial, setEditingOfficial] = useState(null);
  const [officialForm, setOfficialForm] = useState({
    name: "",
    designation: "Technical Official",
    duty_role: "Technical Official",
    mobile: "",
    email: "",
    gender: "Male",
    father_name: "",
    district: "",
    room_no: "",
    reporting_date: new Date().toISOString().slice(0, 10),
    relieving_date: "",
    notes: "",
  });
  const [officialPhotoFile, setOfficialPhotoFile] = useState(null);
  const [submittingOfficial, setSubmittingOfficial] = useState(false);
  const [singleIdCardModal, setSingleIdCardModal] = useState(null);
  const [singleIdCardFormat, setSingleIdCardFormat] = useState("badge"); // "badge" | "pan"

  // Room Incharges & Medical Care States
  const [roomInchargesData, setRoomInchargesData] = useState({
    rooms: [],
    assignments: [],
    all_rooms: [],
    available_coaches: [],
  });
  const [roomInchargesLoading, setRoomInchargesLoading] = useState(false);
  const [roomFilterWeek, setRoomFilterWeek] = useState("");
  const [roomSearchTerm, setRoomSearchTerm] = useState("");
  const [roomInchargeModalOpen, setRoomInchargeModalOpen] = useState(false);
  const [roomInchargeForm, setRoomInchargeForm] = useState({
    room_no: "",
    coach_ids: [],
    week_number: 1,
    start_date: "",
    end_date: "",
    role_title: "Room Incharge",
    notes: "",
    replace_active: false,
  });
  const [sendingMedicineEmail, setSendingMedicineEmail] = useState(false);
  const [submittingRoomIncharge, setSubmittingRoomIncharge] = useState(false);

  // Report Cards Filter and Print States
  const [reportCardCoachFilter, setReportCardCoachFilter] = useState("");
  const [reportCardAthleteFilter, setReportCardAthleteFilter] = useState("all");
  const [allAthletesReportCardData, setAllAthletesReportCardData] = useState([]);
  const [printReportScope, setPrintReportScope] = useState("all_weeks");
  const [printMode, setPrintMode] = useState(null); // null | "report_cards" | "performance" | "blank"

  useEffect(() => {
    const handleAfterPrint = () => {
      setPrintMode(null);
    };
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, []);

  // Filtered report cards for screen table
  const displayedReportCards = useMemo(() => {
    return reportCards
      .filter((rc) => {
        if (!reportCardCoachFilter) return true;
        const coachIdStr = String(reportCardCoachFilter);
        if (String(rc.coach_id) === coachIdStr) return true;
        const ath = athletes.find((a) => String(a.id) === String(rc.athlete_id));
        if (ath) {
          if (String(ath.assigned_coach?.id) === coachIdStr) return true;
          if (ath.assigned_coaches && ath.assigned_coaches.some((c) => String(c.id) === coachIdStr)) return true;
          if (ath.mappings && ath.mappings.some((m) => String(m.coach_id) === coachIdStr)) return true;
        }
        if (mappings && mappings.some((m) => String(m.athlete_id) === String(rc.athlete_id) && String(m.coach_id) === coachIdStr && m.status === "active")) return true;
        return false;
      })
      .filter(
        (rc) =>
          !reportCardAthleteFilter ||
          reportCardAthleteFilter === "all" ||
          String(rc.athlete_id) === String(reportCardAthleteFilter)
      );
  }, [reportCards, reportCardCoachFilter, reportCardAthleteFilter, athletes, mappings]);

  // Dependent Classifications for selected discipline
  const availableClassifications = useMemo(() => {
    if (!disciplineCategoryForm.discipline_name && !disciplineCategoryForm.discipline_id) {
      return [];
    }
    const matched = disciplines.find(
      (d) =>
        d.name === disciplineCategoryForm.discipline_name ||
        String(d.id) === String(disciplineCategoryForm.discipline_id)
    );
    return matched?.classifications || [];
  }, [disciplines, disciplineCategoryForm.discipline_name, disciplineCategoryForm.discipline_id]);

  // Dependent Classifications for enroll player form (from para_classifications)
  const selectedEnrollSport = useMemo(() => {
    if (!enrollForm.discipline_id && !enrollForm.discipline_name) return null;
    return (
      disciplines.find(
        (d) =>
          String(d.id) === String(enrollForm.discipline_id) ||
          (enrollForm.discipline_name && d.name?.toLowerCase() === enrollForm.discipline_name?.toLowerCase())
      ) || null
    );
  }, [disciplines, enrollForm.discipline_id, enrollForm.discipline_name]);

  const availableEnrollClassifications = useMemo(() => {
    return selectedEnrollSport?.classifications || [];
  }, [selectedEnrollSport]);

  // Form States
  const [campForm, setCampForm] = useState({
    name: "",
    code: "",
    venue: "",
    district: "",
    start_date: "",
    end_date: "",
    description: "",
    status: "ongoing",
    is_active: true,
  });

  const [coachForm, setCoachForm] = useState({
    name: "",
    father_name: "",
    mobile: "",
    email: "",
    district: "",
    team_name: "",
    disciplines: [],
    password: "",
  });

  const [athleteForm, setAthleteForm] = useState({
    name: "",
    father_name: "",
    aadhaar: "",
    udid: "",
    udise_no: "",
    parent_mobile: "",
    address: "",
    pincode: "",
    district: "",
    state: "Andhra Pradesh",
    bib_no: "",
    id_number: "",
    category: "",
    discipline_name: "",
    coach_id: "",
    health_issues: "",
    medicines: "",
    medicine_timings: "",
    room_no: "",
    photo: null,
    aadhaar_doc: null,
    udid_doc: null,
    sadarem_doc: null,
  });
  const [pincodeLoading, setPincodeLoading] = useState(false);

  const handleAthletePincodeChange = async (e) => {
    const pin = e.target.value.replace(/\D/g, "").slice(0, 6);
    setAthleteForm((prev) => ({ ...prev, pincode: pin }));

    if (pin.length === 6) {
      setPincodeLoading(true);
      try {
        const res = await API.lookupPincode(pin);
        const rows = Array.isArray(res) ? res : res?.data || [];
        if (rows.length > 0 && rows[0].district) {
          const matchedDistrict =
            AP_DISTRICTS.find(
              (d) => d.toLowerCase() === rows[0].district.toLowerCase()
            ) || rows[0].district;

          setAthleteForm((prev) => ({
            ...prev,
            district: matchedDistrict,
            state: rows[0].state || "Andhra Pradesh",
          }));
        }
      } catch (err) {
        console.warn("Pincode lookup error:", err);
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const [mappingForm, setMappingForm] = useState({
    coach_ids: [],
    coach_id: "",
    athlete_ids: [],
    team_name: "",
    mode: "add",
  });
  const [mappingCoachFilter, setMappingCoachFilter] = useState("");
  const [mappingTeamFilter, setMappingTeamFilter] = useState("");
  const [mappingSearchFilter, setMappingSearchFilter] = useState("");
  const [addCoachModal, setAddCoachModal] = useState(null);
  const [addCoachForm, setAddCoachForm] = useState({ coach_id: "" });

  const existingTeams = useMemo(() => {
    const teams = new Set();
    (coaches || []).forEach((c) => {
      if (c.team_name && c.team_name.trim()) teams.add(c.team_name.trim());
    });
    (mappings || []).forEach((m) => {
      if (m.team_name && m.team_name.trim()) teams.add(m.team_name.trim());
    });
    return Array.from(teams).sort();
  }, [coaches, mappings]);

  const [disciplineForm, setDisciplineForm] = useState({
    name: "",
    code: "",
    description: "",
  });

  // 1. Initial Load: Fetch Camps & Settings (Decoupled for instant dropdown rendering)
  const fetchCampsAndSettings = async () => {
    // 1. Fetch camps immediately and independently
    API.camp.getCamps()
      .then((campsRes) => {
        const campList = campsRes?.data?.camps || campsRes?.camps || [];
        setCamps(campList);
        try {
          sessionStorage.setItem("parasports_cached_camps", JSON.stringify(campList));
        } catch (_e) { }

        setSelectedCampId((prev) => {
          if (prev && campList.some((c) => String(c.id) === String(prev))) {
            return prev;
          }
          const active = campList.find((c) => c.is_active) || campList[0];
          if (active) {
            try {
              sessionStorage.setItem("parasports_selected_camp_id", String(active.id));
            } catch (_e) { }
            if (active.from_date || active.start_date) setRangeDateFrom(active.from_date || active.start_date);
            if (active.to_date || active.end_date) setRangeDateTo(active.to_date || active.end_date);
            return active.id;
          }
          return prev;
        });
      })
      .catch((err) => {
        console.error("Failed to load camps:", err);
      });

    // 2. Fetch settings in parallel without blocking camp selection
    API.camp.getSettings()
      .then((settingsRes) => {
        if (settingsRes?.data?.signatures || settingsRes?.signatures) {
          setSignatures(settingsRes?.data?.signatures || settingsRes?.signatures);
        }
        if (settingsRes?.data?.disciplines || settingsRes?.disciplines) {
          setDisciplines(settingsRes?.data?.disciplines || settingsRes?.disciplines);
        }
      })
      .catch((err) => {
        console.error("Failed to load initial camp settings:", err);
      });
  };

  useEffect(() => {
    fetchCampsAndSettings();
  }, []);

  // Coaches are shared by mapping, attendance, reports, ID cards and team
  // assignment. Load them with the camp instead of waiting for the Coaches tab.
  useEffect(() => {
    if (!selectedCampId) {
      setCoaches([]);
      return;
    }

    let cancelled = false;
    API.camp.getCoaches({ camp_id: selectedCampId })
      .then((res) => {
        if (!cancelled) setCoaches(res?.data?.coaches || res?.coaches || []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load camp coaches:", err);
          setCoaches([]);
        }
      });

    return () => { cancelled = true; };
  }, [selectedCampId]);

  // 2. Fetch Tab Specific Data whenever activeTab or selectedCampId changes
  const fetchTabData = async () => {
    if (!selectedCampId) return;

    setTabLoading(true);

    try {
      if (activeTab === "overview") {
        const res = await API.camp.getAdminStats({ camp_id: selectedCampId });
        setDashboardStats(res?.data?.stats || res?.stats || null);
      } else if (activeTab === "coaches") {
        const res = await API.camp.getCoaches({ camp_id: selectedCampId });
        setCoaches(res?.data?.coaches || res?.coaches || []);
      } else if (activeTab === "athletes") {
        const res = await API.camp.getAthletes({ camp_id: selectedCampId, status: "all" });
        setAthletes(res?.data?.athletes || res?.athletes || []);
      } else if (activeTab === "officials") {
        setOfficialsLoading(true);
        try {
          const res = await API.camp.getCampOfficials({ camp_id: selectedCampId });
          setOfficials(res?.data?.officials || res?.officials || []);
        } catch (err) {
          console.error("Failed to load camp officials:", err);
        } finally {
          setOfficialsLoading(false);
        }
      } else if (activeTab === "id_cards") {
        const [athRes, coachRes, offRes] = await Promise.all([
          API.camp.getAthletes({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getCoaches({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getCampOfficials({ camp_id: selectedCampId }).catch(() => null),
        ]);
        if (athRes) setAthletes(athRes?.data?.athletes || athRes?.athletes || []);
        if (coachRes) setCoaches(coachRes?.data?.coaches || coachRes?.coaches || []);
        if (offRes) setOfficials(offRes?.data?.officials || offRes?.officials || []);
      } else if (activeTab === "mappings") {
        const [mapsRes, coachesRes, athletesRes] = await Promise.all([
          API.camp.getMappings({ camp_id: selectedCampId }),
          API.camp.getCoaches({ camp_id: selectedCampId }),
          API.camp.getAthletes({ camp_id: selectedCampId }),
        ]);
        setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
        setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        setAthletes(athletesRes?.data?.athletes || athletesRes?.athletes || []);
      } else if (activeTab === "attendance") {
        const [res, sheetRes, filesRes, athletesRes, coachesRes, mapsRes] = await Promise.all([
          API.camp.getAttendance({ camp_id: selectedCampId, date: attendanceDate }),
          API.camp.getAttendanceSheet({ camp_id: selectedCampId, date: attendanceDate }),
          API.camp.getDailyAttendanceReports({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getAthletes({ camp_id: selectedCampId, status: "all" }).catch(() => null),
          API.camp.getCoaches({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getMappings({ camp_id: selectedCampId }).catch(() => null),
        ]);
        setAttendanceRecords(res?.data?.records || res?.records || []);
        setAthleteAttendanceSheet(sheetRes?.data?.sheet || sheetRes?.sheet || []);
        if (athletesRes) setAthletes(athletesRes?.data?.athletes || athletesRes?.athletes || []);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        if (mapsRes) setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
        const filesList = filesRes?.reports || filesRes?.data?.reports || [];
        setUploadedReports(filesList);
      } else if (activeTab === "report_cards") {
        const [cardsRes, athletesRes, coachesRes, mapsRes, performanceRes] = await Promise.all([
          API.camp.getReportCards({ camp_id: selectedCampId }),
          API.camp.getAthletes({ camp_id: selectedCampId }),
          API.camp.getCoaches({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getMappings({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getPerformanceReports({ camp_id: selectedCampId }).catch(() => null),
        ]);
        setReportCards(cardsRes?.data?.report_cards || cardsRes?.report_cards || []);
        setAthletes(athletesRes?.data?.athletes || athletesRes?.athletes || []);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        if (mapsRes) setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
        if (performanceRes) setPerformanceReports(performanceRes?.data?.reports || performanceRes?.reports || []);
      } else if (activeTab === "weekly_reports") {
        const res = await API.camp.getWeeklyReports({ camp_id: selectedCampId });
        setWeeklyReports(res?.data?.reports || res?.reports || []);
      } else if (activeTab === "camp_reports") {
        const [perfRes, athletesRes, coachesRes, mapsRes] = await Promise.all([
          API.camp.getPerformanceReports({ camp_id: selectedCampId }),
          API.camp.getAthletes({ camp_id: selectedCampId }),
          API.camp.getCoaches({ camp_id: selectedCampId }).catch(() => null),
          API.camp.getMappings({ camp_id: selectedCampId }).catch(() => null),
        ]);
        setPerformanceReports(perfRes?.data?.reports || perfRes?.reports || []);
        setAthletes(athletesRes?.data?.athletes || athletesRes?.athletes || []);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        if (mapsRes) setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
      } else if (activeTab === "centralized_reports") {
        const [res, coachesRes] = await Promise.all([
          API.camp.getCentralizedReports({
            camp_id: selectedCampId,
            coach_id: selectedCentralCoach,
            athlete_id: selectedCentralAthlete,
          }),
          API.camp.getCoaches({ camp_id: selectedCampId }).catch(() => null),
        ]);
        setCentralizedData(res?.data || res);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
      } else if (activeTab === "room_incharges") {
        setRoomInchargesLoading(true);
        try {
          const res = await API.camp.getRoomIncharges({
            camp_id: selectedCampId,
            week_number: roomFilterWeek || undefined,
            status: "all",
          });
          if (res?.data?.data || res?.data) {
            setRoomInchargesData(res.data.data || res.data);
          }
        } finally {
          setRoomInchargesLoading(false);
        }
      }
    } catch (err) {
      console.error("Error loading tab data:", err);
    } finally {
      setTabLoading(false);
    }
  };

  useEffect(() => {
    fetchTabData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedCampId, attendanceDate, selectedCentralCoach, selectedCentralAthlete, roomFilterWeek]);

  const activeCamp = useMemo(() => {
    return camps.find((c) => c.id === parseInt(selectedCampId)) || camps[0];
  }, [camps, selectedCampId]);

  const campStartDate = useMemo(() => {
    return activeCamp?.start_date ? activeCamp.start_date.split("T")[0] : (activeCamp?.from_date || "2026-08-01");
  }, [activeCamp]);

  const campEndDate = useMemo(() => {
    return activeCamp?.end_date ? activeCamp.end_date.split("T")[0] : (activeCamp?.to_date || "2026-10-05");
  }, [activeCamp]);

  const campTotalDays = useMemo(() => {
    if (activeCamp?.total_days) return activeCamp.total_days;
    if (campStartDate && campEndDate) return calculateCampDuration(campStartDate, campEndDate);
    return 66;
  }, [activeCamp, campStartDate, campEndDate]);

  const getCampDayNumber = (targetDate) => {
    if (!campStartDate || !targetDate) return null;
    const start = new Date(campStartDate);
    const curr = new Date(targetDate);
    if (isNaN(start.getTime()) || isNaN(curr.getTime())) return null;
    const diffDays = Math.floor((curr - start) / (1000 * 60 * 60 * 24)) + 1;
    return diffDays > 0 ? diffDays : null;
  };

  const rangeAttendanceStats = useMemo(() => {
    const datesSet = new Set();
    const recordedDatesSet = new Set();
    let totalTeamSheets = 0;
    let recordedTeamSheets = 0;

    rangeReportData.forEach((team) => {
      (team.days || []).forEach((d) => {
        totalTeamSheets++;
        if (d.date) datesSet.add(d.date);

        const isMarked = (d.athletes || []).some(
          (a) =>
            (a.morning_status && String(a.morning_status).trim() !== "") ||
            (a.evening_status && String(a.evening_status).trim() !== "")
        );

        if (isMarked) {
          recordedTeamSheets++;
          if (d.date) recordedDatesSet.add(d.date);
        }
      });
    });

    const uniqueDaysCount = datesSet.size;
    const recordedDaysCount = recordedDatesSet.size;

    let totalCampScopeDays = uniqueDaysCount || 1;
    if (rangeDateType === "single") {
      totalCampScopeDays = 1;
    } else if (rangeDateType === "range") {
      if (campTotalDays && (rangeDateTo >= (campEndDate || "") || !rangeDateTo)) {
        totalCampScopeDays = campTotalDays;
      } else {
        totalCampScopeDays = uniqueDaysCount || campTotalDays || 1;
      }
    }

    return {
      recordedDays: recordedDaysCount,
      totalDays: totalCampScopeDays,
      daysInRange: uniqueDaysCount,
      totalTeamSheets,
      recordedTeamSheets,
    };
  }, [rangeReportData, rangeDateType, campTotalDays, rangeDateTo, campEndDate]);

  const rangeReportByDate = useMemo(() => {
    const grouped = new Map();
    rangeReportData.forEach((team) => {
      (team.days || []).forEach((day) => {
        if (!grouped.has(day.date)) {
          grouped.set(day.date, { date: day.date, teams: [] });
        }
        grouped.get(day.date).teams.push({
          ...team,
          athletes: day.athletes || [],
        });
      });
    });
    return Array.from(grouped.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [rangeReportData]);
  // Synchronize attendance report dates to the active camp's official dates
  useEffect(() => {
    if (campStartDate) {
      setRangeDateFrom(campStartDate);
      const today = new Date().toISOString().slice(0, 10);
      const initialDate = (today >= campStartDate && (!campEndDate || today <= campEndDate)) ? today : campStartDate;
      setRangeSingleDate((prev) => (!prev || prev < campStartDate || (campEndDate && prev > campEndDate) || prev === "2026-09-08" ? initialDate : prev));
      setEmptyFormatDate((prev) => (!prev || prev < campStartDate || (campEndDate && prev > campEndDate) || prev === "2026-09-08" ? initialDate : prev));
    }
    if (campEndDate) {
      setRangeDateTo(campEndDate);
    }
  }, [campStartDate, campEndDate]);

  // ==========================================
  // HANDLERS: CAMPS
  // ==========================================
  const handleSaveCamp = async (e) => {
    e.preventDefault();
    try {
      if (editingCamp) {
        await API.camp.updateCamp(editingCamp.id, campForm);
        Swal.fire("Success", "Camp updated successfully!", "success");
      } else {
        const res = await API.camp.createCamp(campForm);
        Swal.fire("Success", "New camp created successfully!", "success");
        if (res?.data?.camp?.id || res?.camp?.id) {
          setSelectedCampId(res?.data?.camp?.id || res?.camp?.id);
        }
      }
      setCampModalOpen(false);
      setEditingCamp(null);
      fetchCampsAndSettings();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save camp", "error");
    }
  };

  const handleSetActiveCamp = async (id) => {
    try {
      await API.camp.setActiveCamp(id);
      Swal.fire("Active Camp Updated", "Selected camp is now active.", "success");
      fetchCampsAndSettings();
    } catch (err) {
      Swal.fire("Error", "Failed to set active camp", "error");
    }
  };

  // ==========================================
  // HANDLERS: COACHES
  // ==========================================
  const handleSaveCoach = async (e) => {
    e.preventDefault();
    if (!coachForm.disciplines.length) {
      Swal.fire("Warning", "Please select at least one discipline.", "warning");
      return;
    }

    try {
      if (editingCoach) {
        await API.camp.updateCoach(editingCoach.id, coachForm);
        Swal.fire("Success", "Coach profile updated successfully!", "success");
      } else {
        const res = await API.camp.createCoach(coachForm);
        const creds = res?.data?.credentials || res?.credentials;
        Swal.fire({
          icon: "success",
          title: "Coach Created Successfully",
          html: `
            <div class="text-left text-xs bg-slate-100 p-3 rounded mt-2 text-slate-800 space-y-1">
              <p><b>Username / Mobile:</b> ${creds?.username}</p>
              <p><b>Login Portal:</b> <span class="text-cyan-700 font-mono font-semibold">${window.location.origin}/login</span></p>
              <p class="text-emerald-700 font-semibold mt-2">Coach account activated in coach directory.</p>
            </div>
          `,
        });
      }
      setCoachModalOpen(false);
      setEditingCoach(null);
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save coach", "error");
    }
  };

  const handleToggleCoachStatus = async (id) => {
    try {
      const res = await API.camp.toggleCoachStatus(id);
      Swal.fire("Status Updated", res?.data?.message || "Coach status changed", "success");
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", "Failed to change coach status", "error");
    }
  };

  const handleResetCoachPassword = async (coach) => {
    const { value: newPassword } = await Swal.fire({
      title: `Reset Password for ${coach.name}`,
      input: "text",
      inputLabel: "New Password",
      inputValue: `Coach@${coach.mobile.slice(-4)}`,
      showCancelButton: true,
      confirmButtonText: "Update Password",
    });

    if (newPassword) {
      try {
        await API.camp.resetCoachPassword(coach.id, { password: newPassword });
        Swal.fire("Success", `Password updated to: ${newPassword}`, "success");
      } catch (err) {
        Swal.fire("Error", "Failed to reset password", "error");
      }
    }
  };

  const handleDeleteCoach = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Coach?",
      text: "This will remove the coach profile and their login access.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.deleteCoach(id);
        Swal.fire("Deleted", "Coach removed successfully.", "success");
        fetchTabData();
      } catch (err) {
        Swal.fire("Error", "Failed to delete coach", "error");
      }
    }
  };

  // ==========================================
  // HANDLERS: ATHLETES
  // ==========================================
  const openEditAthleteModal = (athlete) => {
    setEditingAthlete(athlete);
    setAthleteForm({
      name: athlete.name,
      father_name: athlete.father_name || "",
      aadhaar: athlete.aadhaar || "",
      udid: athlete.udid || "",
      udise_no: athlete.udise_no || "",
      parent_mobile: athlete.parent_mobile || "",
      address: athlete.address || "",
      pincode: athlete.pincode || "",
      district: athlete.district || "",
      state: athlete.state || "Andhra Pradesh",
      bib_no: athlete.bib_no || "",
      id_number: athlete.id_number || "",
      category: athlete.category || "",
      discipline_name: athlete.discipline_name || "",
      coach_id: athlete.assigned_coach?.id || "",
      health_issues: athlete.health_issues || "",
      medicines: athlete.medicines || "",
      medicine_timings: athlete.medicine_timings || "",
      room_no: athlete.room_no || "",
      photo: null,
      aadhaar_doc: null,
      udid_doc: null,
      sadarem_doc: null,
    });
    setAthleteModalOpen(true);
  };

  const handleOpenDisciplineCategoryModal = (athlete, selectedProfileCategory = null) => {
    setSelectedAthleteForDiscipline(athlete);
    const matched = disciplines.find(
      (d) => selectedProfileCategory
        ? String(d.id) === String(selectedProfileCategory.sport_id)
        : d.name === athlete.discipline_name || String(d.id) === String(athlete.discipline_id)
    );
    const profileCategory = selectedProfileCategory || getAthleteProfileCategory(
      athlete,
      matched?.id || athlete.discipline_id,
      matched?.name || athlete.discipline_name
    );
    setDisciplineCategoryForm({
      discipline_id: matched?.id || profileCategory?.sport_id || athlete.discipline_id || "",
      discipline_name: matched?.name || profileCategory?.sport_name || athlete.discipline_name || "",
      classification_id: profileCategory?.classification_id || athlete.classification_id || "",
      category: profileCategory?.classification || athlete.category || "",
    });
    setDisciplineCategoryModalOpen(true);
  };

  const handleSaveDisciplineCategory = async (e) => {
    e.preventDefault();
    if (!selectedAthleteForDiscipline) return;
    try {
      const response = await API.camp.updateDisciplineCategory(
        selectedAthleteForDiscipline.id,
        disciplineCategoryForm
      );
      Swal.fire({
        icon: "success",
        title: "Updated Successfully!",
        text: response?.data?.message || response?.message || `Sport and profile classification updated for ${selectedAthleteForDiscipline.name}`,
        timer: 2000,
        showConfirmButton: false,
      });
      setDisciplineCategoryModalOpen(false);
      setSelectedAthleteForDiscipline(null);
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to update discipline and category", "error");
    }
  };

  const handleDeleteAthleteClassification = async (athlete, profileCategory) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete classification?",
      html: `Remove <strong>${profileCategory.classification}</strong> for <strong>${profileCategory.sport_name}</strong> from ${athlete.name}'s player profile?`,
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#dc2626",
    });
    if (!result.isConfirmed) return;

    try {
      const response = await API.camp.deleteAthleteClassification(
        athlete.id,
        profileCategory.player_category_id
      );
      await Swal.fire({
        icon: "success",
        title: "Classification Deleted",
        text: response?.data?.message || response?.message || "The profile and Camp Mode were updated.",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to delete classification", "error");
    }
  };

  const handleOpenSportsAllotmentModal = (athlete) => {
    setSelectedAthleteForSportsAllotment(athlete);
    let initialDisciplines = [];
    const initialGameDetails = {};
    if (Array.isArray(athlete.allocated_disciplines) && athlete.allocated_disciplines.length > 0) {
      initialDisciplines = athlete.allocated_disciplines.map((d) =>
        typeof d === "string" ? d : d.discipline_name || d.name || ""
      ).filter(Boolean);
      athlete.allocated_disciplines.forEach((game) => {
        const name = typeof game === "string" ? game : game?.discipline_name || game?.name || "";
        if (!name) return;
        initialGameDetails[name] = typeof game === "string" ? {
          weekly_trial_field: inferWeeklyTrialField(name),
          measurement_type: "time",
        } : {
          game_id: game.game_id || null,
          sport_id: game.sport_id || null,
          sport_name: game.sport_name || "",
          weekly_trial_field: game.weekly_trial_field || inferWeeklyTrialField(name),
          measurement_type: game.measurement_type || "time",
          measurement_unit: game.measurement_unit || "",
          athlete_performance: game.athlete_performance || game.performance_notes || "",
          national_benchmark: game.national_benchmark || game.national_gold_performance || "",
        };
      });
    } else if (athlete.discipline_name) {
      initialDisciplines = [athlete.discipline_name];
      initialGameDetails[athlete.discipline_name] = {
        weekly_trial_field: inferWeeklyTrialField(athlete.discipline_name),
        measurement_type: "time",
      };
    }
    setSportsAllotmentForm({
      disciplines: initialDisciplines,
      gameDetails: initialGameDetails,
      notes: athlete.sports_allotment_notes || athlete.performance_notes || athlete.allocated_disciplines?.find((game) => typeof game === "object")?.performance_notes || "",
    });
    setAllotmentSportId("");
    setEligibleAllotmentGames([]);
    setSportsAllotmentModalOpen(true);
  };

  const handleSaveSportsAllotment = async (e) => {
    e.preventDefault();
    if (!selectedAthleteForSportsAllotment) return;
    if (!sportsAllotmentForm.disciplines || sportsAllotmentForm.disciplines.length === 0) {
      Swal.fire("Validation", "Please allot at least 1 game.", "warning");
      return;
    }
    setSavingSportsAllotment(true);
    try {
      await API.camp.allotSports(selectedAthleteForSportsAllotment.id, {
        camp_id: selectedCampId,
        disciplines: sportsAllotmentForm.disciplines.map((name) => {
          const details = sportsAllotmentForm.gameDetails?.[name] || {};
          const weeklyTrial = getWeeklyTrialHistory(
            selectedAthleteForSportsAllotment,
            { name, weekly_trial_field: details.weekly_trial_field },
            performanceReports
          );
          const profileCategory = getAthleteProfileCategory(
            selectedAthleteForSportsAllotment,
            details.sport_id,
            details.sport_name
          );
          return {
            id: details.sport_id || null,
            game_id: details.game_id || null,
            sport_id: details.sport_id || null,
            sport_name: details.sport_name || null,
            name: String(name),
            category: profileCategory?.classification || selectedAthleteForSportsAllotment.category || null,
            performance_notes: details.performance_notes || sportsAllotmentForm.notes || null,
            weekly_trial_field: details.weekly_trial_field || inferWeeklyTrialField(name) || null,
            measurement_type: details.measurement_type || null,
            measurement_unit: details.measurement_unit || null,
            athlete_performance: weeklyTrial.latest || null,
            national_benchmark: details.national_benchmark || null,
          };
        }),
        performance_notes: sportsAllotmentForm.notes,
      });
      Swal.fire({
        icon: "success",
        title: "Games Allotted Successfully!",
        text: `Allocated ${sportsAllotmentForm.disciplines.length} game(s) to ${selectedAthleteForSportsAllotment.name}.`,
        timer: 2000,
        showConfirmButton: false,
      });
      setSportsAllotmentModalOpen(false);
      setSelectedAthleteForSportsAllotment(null);
      fetchTabData();
    } catch (err) {
      console.error("Failed to allot sports:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to allot sports", "error");
    } finally {
      setSavingSportsAllotment(false);
    }
  };

  const handleAllotmentSportChange = async (sportId) => {
    setAllotmentSportId(sportId);
    setEligibleAllotmentGames([]);
    if (!sportId || !selectedAthleteForSportsAllotment) return;
    setLoadingEligibleAllotmentGames(true);
    try {
      const response = await API.camp.getEligibleAthleteGames(selectedAthleteForSportsAllotment.id, { sport_id: sportId });
      setEligibleAllotmentGames(response?.data?.games || response?.games || []);
    } catch (error) {
      Swal.fire("Could Not Load Games", error?.response?.data?.message || "Failed to load eligible games for this athlete.", "error");
    } finally {
      setLoadingEligibleAllotmentGames(false);
    }
  };

  const handleToggleEligibleAllotmentGame = (game) => {
    const gameName = String(game?.game_name || "").trim();
    if (!gameName) return;
    const selectedSport = disciplines.find((sport) => String(sport.id) === String(allotmentSportId));
    setSportsAllotmentForm((current) => {
      const selected = current.disciplines.includes(gameName);
      const nextDetails = { ...(current.gameDetails || {}) };
      if (selected) {
        delete nextDetails[gameName];
      } else {
        nextDetails[gameName] = {
          game_id: game.id,
          sport_id: Number(allotmentSportId),
          sport_name: selectedSport?.name || "",
          weekly_trial_field: inferWeeklyTrialField(gameName),
          measurement_type: /jump|throw|put/i.test(gameName) ? "distance" : "time",
          measurement_unit: "",
          national_benchmark: game?.eligibility?.mqs || "",
        };
      }
      return {
        ...current,
        disciplines: selected
          ? current.disciplines.filter((name) => name !== gameName)
          : [...current.disciplines, gameName],
        gameDetails: nextDetails,
      };
    });
  };

  const updateAllottedGameDetail = (gameName, field, value) => {
    setSportsAllotmentForm((current) => ({
      ...current,
      gameDetails: {
        ...current.gameDetails,
        [gameName]: {
          ...(current.gameDetails?.[gameName] || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleSaveAthlete = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    Object.keys(athleteForm).forEach((k) => {
      if (athleteForm[k] !== null && athleteForm[k] !== undefined) {
        formData.append(k, athleteForm[k]);
      }
    });
    formData.append("camp_id", selectedCampId);

    try {
      if (editingAthlete) {
        await API.camp.updateAthlete(editingAthlete.id, formData);
        Swal.fire("Success", "Athlete profile updated successfully!", "success");
      } else {
        await API.camp.createAthlete(formData);
        Swal.fire("Success", "Athlete registered successfully!", "success");
      }
      setAthleteModalOpen(false);
      setEditingAthlete(null);
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to save athlete", "error");
    }
  };

  const handleRemoveAthleteFromCamp = async (athlete) => {
    const confirm = await Swal.fire({
      title: `Remove ${athlete.name} from Camp?`,
      html: `<div class="text-left text-xs space-y-1.5">
        <p>The athlete will be removed from active camp sessions and unassigned from their current team.</p>
        <p class="text-emerald-800 bg-emerald-50 p-2 rounded-lg border border-emerald-200">
          <strong>Safe Removal:</strong> All measurements, kit sizes, documents, photos, and ID numbers will be safely preserved. You can re-admit this athlete anytime from the <em>"Removed from Camp"</em> tab.
        </p>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      confirmButtonText: "Yes, Remove from Camp",
      cancelButtonText: "Cancel",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.removeFromCamp(athlete.id);
        Swal.fire({
          icon: "success",
          title: "Removed from Camp",
          text: `${athlete.name} has been removed from camp and team. Data is preserved in "Removed from Camp" tab.`,
          timer: 2200,
          showConfirmButton: false,
        });
        fetchTabData();
      } catch (err) {
        Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to remove athlete", "error");
      }
    }
  };

  const handleReadmitAthleteToCamp = async (athlete) => {
    const confirm = await Swal.fire({
      title: `Re-admit ${athlete.name} to Camp?`,
      text: `This will restore the athlete to the active camp roster with all their existing data, measurements, and records intact.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      confirmButtonText: "Yes, Re-admit Athlete",
      cancelButtonText: "Cancel",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.readmitAthlete(athlete.id);
        Swal.fire({
          icon: "success",
          title: "Athlete Re-admitted!",
          text: `${athlete.name} is now active in the camp. You can now assign them to a team or coach.`,
          timer: 2200,
          showConfirmButton: false,
        });
        fetchTabData();
      } catch (err) {
        Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to re-admit athlete", "error");
      }
    }
  };

  const handleDeleteAthlete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Athlete Record?",
      text: "All associated documents, attendance, and assessment records will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Permanent Delete",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.deleteAthlete(id);
        Swal.fire("Deleted", "Athlete record permanently deleted.", "success");
        fetchTabData();
      } catch (err) {
        Swal.fire("Error", "Failed to delete athlete", "error");
      }
    }
  };

  // ==========================================
  // HANDLERS: GET REGISTERED PLAYERS TO CAMP & ALLOT COACH
  // ==========================================
  const fetchAvailablePlayers = async (search = "", district = "", gender = "") => {
    if (!selectedCampId) return;
    setLoadingAvailablePlayers(true);
    try {
      const res = await API.camp.getAvailablePlayers({
        camp_id: selectedCampId,
        search: (search !== undefined ? search : enrollSearchTerm).trim(),
        district: district !== undefined ? district : enrollDistrictFilter,
        gender: gender !== undefined ? gender : enrollGenderFilter,
      });
      const list = res?.data?.players || res?.players || [];
      setAvailablePlayersList(list);
    } catch (err) {
      console.error("Failed to load available registered players:", err);
    } finally {
      setLoadingAvailablePlayers(false);
    }
  };

  const openEnrollPlayerModal = (preselectedPlayer = null) => {
    setSelectedPlayerIds(preselectedPlayer ? [preselectedPlayer.id] : []);
    setEnrollSearchTerm("");
    setEnrollDistrictFilter("");
    setEnrollGenderFilter("");

    const firstCoach = coaches[0] || null;
    const defaultTeamName = firstCoach?.team_name || (existingTeams[0] || "");

    setEnrollForm({
      coach_ids: firstCoach ? [firstCoach.id] : [],
      team_name: defaultTeamName,
      discipline_id: "",
      discipline_name: "",
      category: "",
      bib_no: "",
      id_number: "",
    });
    setEnrollPlayerModalOpen(true);
    fetchAvailablePlayers("", "", "");
  };

  const handleSaveEnrollPlayer = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (selectedPlayerIds.length === 0) {
      Swal.fire("Select Player", "Please select at least one registered player to add to the coaching camp.", "warning");
      return;
    }
    if (enrollForm.coach_ids.length === 0) {
      Swal.fire("Select Coach", "Please select at least one coach to allot the player(s) to.", "warning");
      return;
    }

    setSubmittingEnroll(true);
    try {
      const payload = {
        camp_id: selectedCampId,
        player_ids: selectedPlayerIds,
        coach_ids: enrollForm.coach_ids,
        team_name: enrollForm.team_name,
        discipline_id: enrollForm.discipline_id || undefined,
        discipline_name: enrollForm.discipline_name || undefined,
        category: enrollForm.category || undefined,
        bib_no: enrollForm.bib_no || undefined,
        id_number: enrollForm.id_number || undefined,
      };
      const res = await API.camp.enrollPlayer(payload);
      Swal.fire({
        icon: "success",
        title: "Player Allotted Successfully!",
        text: res?.message || res?.data?.message || `${selectedPlayerIds.length} player(s) successfully added to coaching camp and allotted to coach!`,
        timer: 2500,
        showConfirmButton: false,
      });
      setEnrollPlayerModalOpen(false);
      setSelectedPlayerIds([]);
      fetchTabData();
    } catch (err) {
      console.error("Failed to enroll player into camp:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to add player to coaching camp.", "error");
    } finally {
      setSubmittingEnroll(false);
    }
  };

  // ==========================================
  // HANDLERS: CAMP OFFICIALS
  // ==========================================
  const fetchEligibleOfficials = async (search = "", district = "") => {
    setLoadingEligibleOfficials(true);
    try {
      const res = await API.camp.getEligibleCampOfficials({
        camp_id: selectedCampId,
        search: search.trim(),
        district: district || undefined,
      });
      setEligibleOfficialsList(res?.data?.officials || res?.officials || []);
    } catch (err) {
      console.error("Failed to load eligible officials:", err);
    } finally {
      setLoadingEligibleOfficials(false);
    }
  };

  const openEnrollOfficialModal = () => {
    setSelectedOfficialUserIds([]);
    setEnrollOfficialForm({
      designation: "Technical Official",
      duty_role: "Technical Official",
      room_no: "",
      reporting_date: new Date().toISOString().slice(0, 10),
      relieving_date: "",
      notes: "",
    });
    setEnrollOfficialModalOpen(true);
    fetchEligibleOfficials("", "");
  };

  const handleSaveEnrollOfficial = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (selectedOfficialUserIds.length === 0) {
      Swal.fire("Select Official", "Please select at least one official from the directory to enroll into the camp.", "warning");
      return;
    }

    setSubmittingEnrollOfficial(true);
    try {
      const payload = {
        camp_id: selectedCampId,
        user_ids: selectedOfficialUserIds,
        designation: enrollOfficialForm.designation,
        duty_role: enrollOfficialForm.duty_role,
        room_no: enrollOfficialForm.room_no,
        reporting_date: enrollOfficialForm.reporting_date,
        relieving_date: enrollOfficialForm.relieving_date || undefined,
        notes: enrollOfficialForm.notes,
      };
      const res = await API.camp.enrollCampOfficial(payload);
      Swal.fire({
        icon: "success",
        title: "Official(s) Enrolled Successfully!",
        text: res?.message || res?.data?.message || `${selectedOfficialUserIds.length} official(s) added to the camp roster.`,
        timer: 2500,
        showConfirmButton: false,
      });
      setEnrollOfficialModalOpen(false);
      setSelectedOfficialUserIds([]);
      fetchTabData();
    } catch (err) {
      console.error("Failed to enroll official into camp:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to add official to camp.", "error");
    } finally {
      setSubmittingEnrollOfficial(false);
    }
  };

  const openAddOfficialModal = () => {
    setEditingOfficial(null);
    setOfficialPhotoFile(null);
    setOfficialForm({
      name: "",
      designation: "Technical Official",
      duty_role: "Technical Official",
      mobile: "",
      email: "",
      gender: "Male",
      father_name: "",
      district: "",
      room_no: "",
      reporting_date: new Date().toISOString().slice(0, 10),
      relieving_date: "",
      notes: "",
    });
    setOfficialModalOpen(true);
  };

  const openEditOfficialModal = (off) => {
    setEditingOfficial(off);
    setOfficialPhotoFile(null);
    setOfficialForm({
      name: off.name || "",
      designation: off.designation || "Technical Official",
      duty_role: off.duty_role || "Technical Official",
      mobile: off.mobile || "",
      email: off.email || "",
      gender: off.gender || "Male",
      father_name: off.father_name || "",
      district: off.district || "",
      room_no: off.room_no || "",
      reporting_date: off.reporting_date ? String(off.reporting_date).slice(0, 10) : "",
      relieving_date: off.relieving_date ? String(off.relieving_date).slice(0, 10) : "",
      notes: off.notes || "",
    });
    setOfficialModalOpen(true);
  };

  const handleSaveOfficial = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!officialForm.name?.trim()) {
      Swal.fire("Name Required", "Please enter the official's name.", "warning");
      return;
    }

    setSubmittingOfficial(true);
    try {
      const fd = new FormData();
      fd.append("camp_id", selectedCampId);
      Object.entries(officialForm).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== "") {
          fd.append(k, v);
        }
      });
      if (officialPhotoFile) {
        fd.append("photo", officialPhotoFile);
      }

      if (editingOfficial) {
        await API.camp.updateCampOfficial(editingOfficial.id, fd);
        Swal.fire({ icon: "success", title: "Official Updated", text: "Official profile updated successfully.", timer: 2000, showConfirmButton: false });
      } else {
        await API.camp.createCampOfficial(fd);
        Swal.fire({ icon: "success", title: "Official Added", text: "Camp official added successfully.", timer: 2000, showConfirmButton: false });
      }

      setOfficialModalOpen(false);
      fetchTabData();
    } catch (err) {
      console.error("Failed to save official:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to save official.", "error");
    } finally {
      setSubmittingOfficial(false);
    }
  };

  const handleDeleteOfficial = async (id) => {
    const result = await Swal.fire({
      title: "Remove Official?",
      text: "Are you sure you want to remove this official from the camp roster?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove",
    });

    if (!result.isConfirmed) return;

    try {
      await API.camp.deleteCampOfficial(id);
      Swal.fire({ icon: "success", title: "Removed", text: "Official removed from camp.", timer: 2000, showConfirmButton: false });
      fetchTabData();
    } catch (err) {
      console.error("Failed to delete official:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to remove official.", "error");
    }
  };

  const handleViewSingleIdCard = (item, type = "athlete") => {
    setSingleIdCardModal({ data: item, type });
  };

  // ==========================================
  // HANDLERS: MAPPINGS
  // ==========================================
  const handleSaveMapping = async (e) => {
    e.preventDefault();
    const coachIds = mappingForm.coach_ids && mappingForm.coach_ids.length > 0
      ? mappingForm.coach_ids
      : (mappingForm.coach_id ? [mappingForm.coach_id] : []);

    if (!coachIds.length || !mappingForm.athlete_ids.length) {
      Swal.fire("Warning", "Please select at least one coach and one athlete for the team.", "warning");
      return;
    }

    try {
      const res = await API.camp.createMapping({
        camp_id: selectedCampId,
        coach_ids: coachIds,
        coach_id: coachIds[0],
        athlete_ids: mappingForm.athlete_ids,
        team_name: mappingForm.team_name,
        mode: mappingForm.mode || "add",
      });
      Swal.fire("Success", res?.message || "Athletes successfully assigned to team and coach(es)!", "success");
      setMappingModalOpen(false);
      setMappingForm({ coach_ids: [], coach_id: "", athlete_ids: [], team_name: "", mode: "add" });
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Mapping failed", "error");
    }
  };

  const handleQuickAddCoach = async (e) => {
    e.preventDefault();
    if (!addCoachForm.coach_id || !addCoachModal) {
      Swal.fire("Warning", "Please select a coach to add.", "warning");
      return;
    }

    try {
      const res = await API.camp.addCoachToTeam({
        camp_id: selectedCampId,
        coach_id: addCoachForm.coach_id,
        team_name: addCoachModal.team_name,
        athlete_ids: addCoachModal.athlete_ids || [],
      });
      Swal.fire("Success", res?.message || "Coach successfully added to team!", "success");
      setAddCoachModal(null);
      setAddCoachForm({ coach_id: "" });
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to add coach", "error");
    }
  };

  const handleRemoveMapping = async (id) => {
    try {
      await API.camp.removeMapping(id);
      Swal.fire("Removed", "Athlete unmapped from coach.", "success");
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", "Failed to unmap athlete", "error");
    }
  };

  // ==========================================
  // HANDLERS: ROOM INCHARGES & MEDICAL CARE
  // ==========================================
  const handleOpenAssignRoomModal = (roomNo = "", existingIncharges = []) => {
    const existingCoachIds = existingIncharges.map((c) => c.coach_id || c.id).filter(Boolean);
    setRoomInchargeForm({
      room_no: roomNo || "",
      coach_ids: existingCoachIds,
      week_number: (existingIncharges[0]?.week_number) || 1,
      start_date: activeCamp?.start_date || activeCamp?.from_date || "",
      end_date: activeCamp?.end_date || activeCamp?.to_date || "",
      role_title: "Room Incharge",
      notes: "",
      replace_active: false,
    });
    setRoomInchargeModalOpen(true);
  };

  const handleSaveRoomIncharge = async (e) => {
    e.preventDefault();
    if (!roomInchargeForm.room_no || !roomInchargeForm.room_no.trim()) {
      Swal.fire("Required", "Please specify a Room Number.", "warning");
      return;
    }
    if (!roomInchargeForm.coach_ids || !roomInchargeForm.coach_ids.length) {
      Swal.fire("Required", "Please select at least one coach to assign as Room In-Charge.", "warning");
      return;
    }

    setSubmittingRoomIncharge(true);
    try {
      const res = await API.camp.saveRoomIncharge({
        camp_id: selectedCampId,
        room_no: roomInchargeForm.room_no.trim(),
        coach_ids: roomInchargeForm.coach_ids,
        week_number: Number(roomInchargeForm.week_number) || 1,
        start_date: roomInchargeForm.start_date || null,
        end_date: roomInchargeForm.end_date || null,
        role_title: roomInchargeForm.role_title || "Room Incharge",
        notes: roomInchargeForm.notes || null,
        replace_active: !!roomInchargeForm.replace_active,
      });

      Swal.fire({
        icon: "success",
        title: "Assigned Successfully!",
        text: res?.data?.message || `Room in-charge(s) assigned for Room ${roomInchargeForm.room_no}.`,
        timer: 2200,
        showConfirmButton: false,
      });
      setRoomInchargeModalOpen(false);
      fetchTabData();
    } catch (err) {
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to assign room incharge", "error");
    } finally {
      setSubmittingRoomIncharge(false);
    }
  };

  const handleDeleteRoomIncharge = async (id, coachName, roomNo) => {
    const result = await Swal.fire({
      title: "Remove In-Charge Coach?",
      text: `Remove ${coachName || "this coach"} from Room ${roomNo}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      confirmButtonText: "Yes, Remove",
    });
    if (result.isConfirmed) {
      try {
        await API.camp.deleteRoomIncharge(id);
        Swal.fire("Removed", "Room in-charge coach assignment removed.", "success");
        fetchTabData();
      } catch (err) {
        Swal.fire("Error", "Failed to remove assignment", "error");
      }
    }
  };

  const handleSendMedicineMail = async (roomNo) => {
    const result = await Swal.fire({
      title: `Email Medicine Schedule?`,
      html: `<div class="text-left text-xs space-y-1.5">
        <p>Send athlete health conditions, prescribed medications, and dosage timings for <strong>Room ${roomNo}</strong> to all active in-charge coaches of this room?</p>
        <p class="text-slate-500 mt-1">Both Samagra Shiksha and Para Sports Association details are formatted automatically.</p>
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0891b2",
      confirmButtonText: "📧 Yes, Dispatch Email",
    });
    if (!result.isConfirmed) return;

    setSendingMedicineEmail(true);
    try {
      const res = await API.camp.sendMedicineMail(roomNo, { camp_id: selectedCampId });
      Swal.fire({
        icon: "success",
        title: "Medicine Schedule Dispatched!",
        text: res?.data?.message || `Alert email successfully sent to in-charge coach(es) of Room ${roomNo}.`,
      });
    } catch (err) {
      Swal.fire("Error Sending Email", err?.response?.data?.message || err?.message || "Failed to dispatch email", "error");
    } finally {
      setSendingMedicineEmail(false);
    }
  };

  const handleSendMedicineMailAll = async () => {
    const result = await Swal.fire({
      title: `Send Medicine Alerts to All Rooms?`,
      html: `<div class="text-left text-xs space-y-1.5">
        <p>This will automatically generate and send individual room medicine schedules to the in-charge coaches of <strong>ALL assigned rooms</strong>.</p>
        <p class="text-amber-700 bg-amber-50 p-2 rounded-lg border border-amber-200">
          Coaches will receive the list of athletes in their room, health issues, medicines, and timings.
        </p>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      confirmButtonText: "📧 Send Alerts to All Incharges",
    });
    if (!result.isConfirmed) return;

    setSendingMedicineEmail(true);
    try {
      const res = await API.camp.sendMedicineMailAll({ camp_id: selectedCampId });
      Swal.fire({
        icon: "success",
        title: "All Alerts Dispatched!",
        text: res?.data?.message || "Medicine schedule emails dispatched to all active in-charge coaches.",
      });
    } catch (err) {
      Swal.fire("Email Error", err?.response?.data?.message || err?.message || "Failed to dispatch alerts", "error");
    } finally {
      setSendingMedicineEmail(false);
    }
  };

  const handleTriggerAutoReminders = async () => {
    setSendingMedicineEmail(true);
    try {
      const res = await API.camp.triggerAutoMedicineReminders(selectedCampId);
      Swal.fire({
        icon: "success",
        title: "Auto-Reminders Processed",
        text: res?.message || `Sent ${res?.sent_count ?? 0} reminders to room in-charges based on schedule.`,
      });
    } catch (err) {
      console.error("Auto reminders error:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to trigger auto reminders", "error");
    } finally {
      setSendingMedicineEmail(false);
    }
  };

  // ==========================================
  // HANDLERS: EXPORTS & PRINT
  // ==========================================
  // Helper to resolve coach name for an athlete
  const getCoachNameForAthlete = (ath) => {
    if (ath?.assigned_coach?.name) return ath.assigned_coach.name;
    if (ath?.assigned_coaches && ath.assigned_coaches.length > 0 && ath.assigned_coaches[0]?.name) {
      return ath.assigned_coaches.map((c) => c.name).join(", ");
    }
    if (ath?.coach?.name) return ath.coach.name;
    if (ath?.mappings && ath.mappings.length > 0) {
      const names = ath.mappings.map((m) => m.coach?.name || coaches.find((c) => String(c.id) === String(m.coach_id))?.name).filter(Boolean);
      if (names.length > 0) return Array.from(new Set(names)).join(", ");
    }
    if (mappings && mappings.length > 0 && ath?.id) {
      const athleteMaps = mappings.filter((m) => String(m.athlete_id) === String(ath.id) && m.status === "active");
      if (athleteMaps.length > 0) {
        const names = athleteMaps
          .map((m) => m.coach?.name || coaches.find((c) => String(c.id) === String(m.coach_id))?.name)
          .filter(Boolean);
        if (names.length > 0) return Array.from(new Set(names)).join(", ");
      }
    }
    return "";
  };

  // Helper to resolve team / squad name for an athlete
  const getTeamNameForAthlete = (ath) => {
    if (ath?.team_name && ath.team_name.trim()) return ath.team_name;
    if (ath?.assigned_coach?.team_name) return ath.assigned_coach.team_name;
    if (ath?.coach?.team_name) return ath.coach.team_name;
    if (ath?.mappings && ath.mappings.length > 0) {
      const activeMap = ath.mappings.find((m) => m.status === "active") || ath.mappings[0];
      if (activeMap?.team_name) return activeMap.team_name;
      if (activeMap?.coach?.team_name) return activeMap.coach.team_name;
    }
    if (mappings && mappings.length > 0 && ath?.id) {
      const activeMap = mappings.find((m) => String(m.athlete_id) === String(ath.id) && m.status === "active");
      if (activeMap) {
        const tName =
          activeMap.team_name ||
          activeMap.coach?.team_name ||
          coaches.find((c) => String(c.id) === String(activeMap.coach_id))?.team_name;
        if (tName) return tName;
      }
      const anyMap = mappings.find((m) => String(m.athlete_id) === String(ath.id));
      if (anyMap) {
        const tName =
          anyMap.team_name ||
          anyMap.coach?.team_name ||
          coaches.find((c) => String(c.id) === String(anyMap.coach_id))?.team_name;
        if (tName) return tName;
      }
    }
    return "General Squad";
  };

  // Helper to chunk an array into groups of max `size`
  const chunkArray = (arr, size = 3) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // Build the array of weeks to display for printing
  const getPrintWeeksForAthlete = (athleteWeeks) => {
    if (printReportScope === "all_weeks") {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9].map((wNum) => {
        const existing = (athleteWeeks || []).find((w) => w.week_number === wNum);
        return (
          existing || {
            week_number: wNum,
            endurance: "-",
            strength: "-",
            agility: "-",
            skill: "-",
            technique: "-",
            discipline: "-",
            performance_rating: "-",
            remarks: "Not Evaluated",
          }
        );
      });
    } else {
      if (!athleteWeeks || athleteWeeks.length === 0) {
        return [1, 2, 3].map((wNum) => ({
          week_number: wNum,
          endurance: "-",
          strength: "-",
          agility: "-",
          skill: "-",
          technique: "-",
          discipline: "-",
          performance_rating: "-",
          remarks: "No Evaluation Recorded",
        }));
      }
      return [...athleteWeeks].sort((a, b) => a.week_number - b.week_number);
    }
  };

  // Athletes list for printing report cards
  const athletesToPrintForReportCards = useMemo(() => {
    // Prefer active athletes in state; fallback to allAthletesReportCardData if athletes array is empty
    let list = [];
    if (Array.isArray(athletes) && athletes.length > 0) {
      list = athletes.filter((a) => a.status !== "removed_from_camp");
    } else if (Array.isArray(allAthletesReportCardData) && allAthletesReportCardData.length > 0) {
      list = allAthletesReportCardData
        .map((item) => item.athlete || item)
        .filter((a) => a.status !== "removed_from_camp");
    }

    if (reportCardCoachFilter) {
      const coachIdStr = String(reportCardCoachFilter);
      list = list.filter(
        (a) =>
          String(a.assigned_coach?.id) === coachIdStr ||
          (a.assigned_coaches && a.assigned_coaches.some((c) => String(c.id) === coachIdStr)) ||
          (a.mappings && a.mappings.some((m) => String(m.coach_id) === coachIdStr)) ||
          (mappings && mappings.some((m) => String(m.athlete_id) === String(a.id) && String(m.coach_id) === coachIdStr && m.status === "active"))
      );
    }
    if (reportCardAthleteFilter && reportCardAthleteFilter !== "all") {
      list = list.filter((a) => String(a.id) === String(reportCardAthleteFilter));
    }

    return list.map((ath) => {
      const foundInBulk = (allAthletesReportCardData || []).find(
        (item) => String(item.athlete?.id || item.id) === String(ath.id)
      );
      const bulkWeeks = foundInBulk?.weeks || [];
      const matchingWeeks =
        bulkWeeks.length > 0
          ? bulkWeeks
          : (reportCards || []).filter((rc) => String(rc.athlete_id) === String(ath.id));

      return {
        athlete: {
          ...ath,
          ...(foundInBulk?.athlete || {}),
        },
        weeks: matchingWeeks,
      };
    });
  }, [athletes, reportCards, allAthletesReportCardData, reportCardCoachFilter, reportCardAthleteFilter, mappings]);

  const handlePrintOfficialReportCards = async (isBlank = false) => {
    const targetCampId = selectedCampId || activeCamp?.id;
    const title = isBlank ? "Weekly_Evaluation_Report_Empty" : "Weekly_Evaluation_Report_Filled";

    setPrintMode(isBlank ? "report_cards_blank" : "report_cards");
    let currentAthletes = athletes;
    let bulkData = allAthletesReportCardData;

    try {
      if (!coaches || coaches.length === 0 || !mappings || mappings.length === 0) {
        const [coachesRes, mapsRes] = await Promise.all([
          API.camp.getCoaches({ camp_id: targetCampId }).catch(() => null),
          API.camp.getMappings({ camp_id: targetCampId }).catch(() => null),
        ]);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        if (mapsRes) setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
      }

      if (!bulkData || bulkData.length === 0) {
        const bulkRes = await API.camp.getAllAthletesReportCards({ camp_id: targetCampId });
        bulkData = bulkRes?.data?.data || bulkRes?.data?.report_cards || bulkRes?.data || [];
        if (Array.isArray(bulkData) && bulkData.length > 0) {
          setAllAthletesReportCardData(bulkData);
        }
      }

      if (!currentAthletes || currentAthletes.length === 0) {
        if (bulkData && bulkData.length > 0) {
          currentAthletes = bulkData.map((d) => d.athlete || d);
          setAthletes(currentAthletes);
        } else {
          const athRes = await API.camp.getAthletes({ camp_id: targetCampId });
          currentAthletes = athRes?.data?.athletes || athRes?.athletes || [];
          if (Array.isArray(currentAthletes) && currentAthletes.length > 0) {
            setAthletes(currentAthletes);
          }
        }
      }
    } catch (e) {
      console.warn("Could not load bulk report cards, using current records:", e);
    }

    // Wait for pages to render, then generate PDF
    setTimeout(async () => {
      await downloadDomPagesAsPdf(
        ".print-only-container",
        title,
        {
          orientation: "portrait",
          pageSelector: ".print-report-page",
          scale: 2,
        }
      );
      setPrintMode(null);
    }, 400);
  };

  const distinctTeams = useMemo(() => {
    const set = new Set();
    athletes.forEach((a) => {
      if (a.status === "removed_from_camp") return;
      const t = a.team_name;
      if (t && typeof t === "string" && t.trim()) set.add(t.trim());
    });
    return Array.from(set).sort();
  }, [athletes]);

  const getCampDaysList = useCallback((athlete) => {
    const camp = athlete?.camp || camps.find((c) => String(c.id) === String(selectedCampId)) || camps[0];
    const startDateStr = camp?.start_date;
    const endDateStr = camp?.end_date;

    if (startDateStr && endDateStr) {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end >= start) {
        const days = [];
        let cur = new Date(start);
        let dayNum = 1;
        while (cur <= end) {
          const yyyy = cur.getFullYear();
          const mm = String(cur.getMonth() + 1).padStart(2, "0");
          const dd = String(cur.getDate()).padStart(2, "0");
          days.push({
            dayNumber: dayNum,
            dateStr: `${dd}-${mm}-${yyyy}`,
            isoDate: `${yyyy}-${mm}-${dd}`,
            label: `Day ${dayNum} (${dd}-${mm}-${yyyy})`,
          });
          cur.setDate(cur.getDate() + 1);
          dayNum++;
        }
        if (days.length > 0) return days;
      }
    }

    return [];
  }, [camps, selectedCampId]);

  const selectedReportCampDays = useMemo(() => {
    return getCampDaysList(null);
  }, [getCampDaysList]);

  const selectedReportDay = useMemo(() => {
    if (reportSingleDate) {
      const byDate = selectedReportCampDays.find((day) => day.isoDate === reportSingleDate);
      if (byDate) return byDate;

      // Dynamically calculate day number if selected date is outside standard camp list (e.g. future date)
      const camp = camps.find((c) => String(c.id) === String(selectedCampId)) || camps[0];
      const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
      const chosen = new Date(reportSingleDate + "T00:00:00");
      let calcDayNum = reportSingleDayNum || 1;
      if (!isNaN(chosen.getTime()) && !isNaN(campStart.getTime())) {
        const diff = Math.round((chosen - campStart) / 86400000) + 1;
        if (diff > 0) calcDayNum = diff;
      }
      const [yyyy, mm, dd] = reportSingleDate.split("-");
      return {
        dayNumber: calcDayNum,
        dateStr: `${dd}-${mm}-${yyyy}`,
        isoDate: reportSingleDate,
        label: `Day ${calcDayNum} (${dd}-${mm}-${yyyy})`,
      };
    }

    const byNum = selectedReportCampDays.find((day) => day.dayNumber === reportSingleDayNum);
    if (byNum) return byNum;

    return {
      dayNumber: reportSingleDayNum || 1,
      dateStr: "22-09-2026",
      isoDate: "2026-09-22",
      label: `Day ${reportSingleDayNum || 1} (22-09-2026)`,
    };
  }, [selectedReportCampDays, reportSingleDate, reportSingleDayNum, camps, selectedCampId]);

  const reportDateInitRef = useRef(false);
  useEffect(() => {
    if (!reportDateInitRef.current && selectedReportCampDays && selectedReportCampDays.length > 0) {
      const todayIso = getTodayISTDate();
      const match = selectedReportCampDays.find((d) => d.isoDate === todayIso);
      if (match) {
        setReportSingleDayNum(match.dayNumber);
        setReportSingleDate(match.isoDate);
      } else {
        const camp = camps.find((c) => String(c.id) === String(selectedCampId)) || camps[0];
        const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
        const todayDate = new Date(todayIso + "T00:00:00");
        const diff = Math.round((todayDate - campStart) / 86400000) + 1;
        setReportSingleDayNum(diff > 0 ? diff : 1);
        setReportSingleDate(todayIso);
      }
      reportDateInitRef.current = true;
    }
  }, [selectedReportCampDays, camps, selectedCampId]);

  const handlePrintIndividualStudentReports = () => {
    const camp = camps.find((c) => String(c.id) === String(selectedCampId)) || activeCamp;
    if (!individualDateFrom) setIndividualDateFrom(String(camp?.start_date || camp?.from_date || "").slice(0, 10));
    if (!individualDateTo) setIndividualDateTo(String(camp?.end_date || camp?.to_date || "").slice(0, 10));
    setIndividualHolidayDate((current) => current || selectedReportDay?.isoDate || reportSingleDate || getTodayISTDate());
    setIndividualModalOpen(true);
  };

  const addIndividualHoliday = () => {
    if (!individualHolidayDate) {
      Swal.fire("Select Date", "Choose the holiday date first.", "warning");
      return;
    }
    setIndividualHolidays((current) => ({
      ...current,
      [individualHolidayDate]: individualHolidaySession,
    }));
  };

  const handleConfirmIndividualAthletePrint = async (isDirectPrint = false) => {
    if (reportDayMode === "date_range" && (!individualDateFrom || !individualDateTo || individualDateFrom > individualDateTo)) {
      Swal.fire("Invalid Date Range", "Select a valid From Date and To Date.", "warning");
      return;
    }
    setIndividualModalOpen(false);

    if (!isDirectPrint) {
      const targetCampId = selectedCampId || activeCamp?.id;
      const campName = activeCamp?.name || "Coaching_Camp";
      const dayLabel = reportDayMode === "all_days" ? "All_Days" : reportDayMode === "date_range" ? `${individualDateFrom}_to_${individualDateTo}` : `Day_${selectedReportDay?.dayNumber || reportSingleDayNum || 1}`;
      const scopeLabel = individualScope === "single" ? "Single_Athlete" : (individualScope === "team" ? (reportSelectedTeam || "Team") : `All_${athletes.length || "Enrolled"}_Athletes`);
      const cleanTitle = `Individual_Athlete_Daily_Evaluation_Reports_${campName}_${dayLabel}_${scopeLabel}`.replace(/[^a-zA-Z0-9_-]/g, "_");

      try {
        const selectedAthleteIds = athletes
          .filter((athlete) => {
            if (individualScope === "single") {
              const selectedId = selectedIndividualAthleteId || athletes[0]?.id;
              return String(athlete.id) === String(selectedId);
            }
            if (individualScope === "team" && reportSelectedTeam && reportSelectedTeam !== "all") {
              return String(getTeamNameForAthlete(athlete) || "").trim().toLowerCase()
                === String(reportSelectedTeam).trim().toLowerCase();
            }
            return true;
          })
          .map((athlete) => Number(athlete.id))
          .filter((id) => Number.isInteger(id) && id > 0);

        const exportRes = await API.camp.createExportJob({
          export_type: "individual_athlete_reports",
          camp_id: targetCampId,
          title: cleanTitle,
          params: {
            scope: individualScope,
            team_name: individualScope === "team" ? reportSelectedTeam : null,
            athlete_id: individualScope === "single" ? (selectedIndividualAthleteId || (athletes[0] ? String(athletes[0].id) : null)) : null,
            athlete_ids: selectedAthleteIds,
            day_mode: reportDayMode,
            single_date: reportDayMode === "single_day" ? (selectedReportDay?.isoDate || reportSingleDate) : null,
            single_day_number: reportDayMode === "single_day" ? (selectedReportDay?.dayNumber || reportSingleDayNum) : null,
            date_from: reportDayMode === "date_range" ? individualDateFrom : null,
            date_to: reportDayMode === "date_range" ? individualDateTo : null,
            holidays: individualHolidays,
          },
        });

        const job = exportRes?.job || exportRes?.data?.job;
        if (job?.id) {
          window.dispatchEvent(new CustomEvent("camp-export-job-started", { detail: job }));
          trackServerPdfExport(job.id, cleanTitle);
          return;
        }
        throw new Error("The server did not return an export job.");
      } catch (err) {
        console.error("Failed to queue background athlete report export:", err);
        Swal.fire({
          icon: "error",
          title: "Background Export Error",
          text: err?.response?.data?.message || err?.message || "Could not queue the ZIP/PDF background export job.",
        });
        return;
      }
    }

    // Direct Print Guard: warn on very large batches (> 30 pages) to prevent crashing Chrome
    if (isDirectPrint && reportDayMode === "all_days" && individualScope !== "single") {
      const res = await Swal.fire({
        icon: "warning",
        title: "Large Direct Print Warning",
        html: `<div style="font-size: 13px; color: #334155; text-align: left; line-height: 1.6;">
          Generating and sending <strong>thousands of pages</strong> directly to Chrome's print dialog will cause your browser tab to freeze or crash.<br><br>
          We strongly recommend using <strong>Generate &amp; Download (PDF)</strong> so the server background worker generates the file and delivers it smoothly to the <strong>PDF Downloads Manager</strong>.
        </div>`,
        showCancelButton: true,
        confirmButtonText: "⬇ Queue PDF Download Instead",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#059669",
        cancelButtonColor: "#475569",
      });
      if (res.isConfirmed) {
        return handleConfirmIndividualAthletePrint(false);
      }
      return;
    }

    // Direct print for single day or single athlete
    setPrintMode("individual_student_reports");

    let currentAthletes = athletes;
    try {
      if (!currentAthletes || currentAthletes.length === 0) {
        const athRes = await API.camp.getAthletes({ camp_id: selectedCampId || activeCamp?.id });
        currentAthletes = athRes?.data?.athletes || athRes?.athletes || [];
        if (Array.isArray(currentAthletes) && currentAthletes.length > 0) {
          setAthletes(currentAthletes);
        }
      }
    } catch (e) {
      console.warn("Could not load athletes for individual reports:", e);
    }

    setTimeout(async () => {
      const container = document.querySelector(".print-only-container");
      if (container) {
        const reportHtml = getUnifiedPrintHtml(
          "Daily Athlete Performance Reports",
          container.innerHTML,
          { orientation: "portrait" }
        );
        if (isDirectPrint) {
          printHtmlViaIframe(reportHtml);
        } else {
          const campName = activeCamp?.name || "Coaching_Camp";
          const dayLabel = reportDayMode === "all_days" ? "All_Days" : `Day_${selectedReportDay?.dayNumber || reportSingleDayNum || 1}`;
          const scopeLabel = individualScope === "single" ? "Single_Athlete" : (individualScope === "team" ? (reportSelectedTeam || "Team") : "All_Athletes");
          const filename = `Daily_Athlete_Performance_${campName}_${dayLabel}_${scopeLabel}`.replace(/[^a-zA-Z0-9_-]/g, "_");
          await downloadHtmlAsPdf(reportHtml, filename, { orientation: "portrait" });
        }
      }
      setTimeout(() => setPrintMode(null), 1000);
    }, 350);
  };

  const handleDirectPrintOfficialReportCards = async (isBlank = false) => {
    setPrintMode(isBlank ? "report_cards_blank" : "report_cards");
    let currentAthletes = athletes;
    let bulkData = allAthletesReportCardData;

    try {
      if (!coaches || coaches.length === 0 || !mappings || mappings.length === 0) {
        const [coachesRes, mapsRes] = await Promise.all([
          API.camp.getCoaches({ camp_id: selectedCampId || activeCamp?.id }).catch(() => null),
          API.camp.getMappings({ camp_id: selectedCampId || activeCamp?.id }).catch(() => null),
        ]);
        if (coachesRes) setCoaches(coachesRes?.data?.coaches || coachesRes?.coaches || []);
        if (mapsRes) setMappings(mapsRes?.data?.mappings || mapsRes?.mappings || []);
      }

      if (!bulkData || bulkData.length === 0) {
        const bulkRes = await API.camp.getAllAthletesReportCards({ camp_id: selectedCampId || activeCamp?.id });
        bulkData = bulkRes?.data?.data || bulkRes?.data?.report_cards || bulkRes?.data || [];
        if (Array.isArray(bulkData) && bulkData.length > 0) {
          setAllAthletesReportCardData(bulkData);
        }
      }

      if (!currentAthletes || currentAthletes.length === 0) {
        if (bulkData && bulkData.length > 0) {
          currentAthletes = bulkData.map((d) => d.athlete || d);
          setAthletes(currentAthletes);
        } else {
          const athRes = await API.camp.getAthletes({ camp_id: selectedCampId || activeCamp?.id });
          currentAthletes = athRes?.data?.athletes || athRes?.athletes || [];
          if (Array.isArray(currentAthletes) && currentAthletes.length > 0) {
            setAthletes(currentAthletes);
          }
        }
      }
    } catch (e) {
      console.warn("Could not load bulk report cards, using current records:", e);
    }

    setTimeout(() => {
      const container = document.querySelector(".print-only-container");
      if (container) {
        const printHtml = getUnifiedPrintHtml(
          isBlank ? "Blank Athlete Report Cards" : "Official Athlete Report Cards",
          container.innerHTML,
          { orientation: "portrait" }
        );
        printHtmlViaIframe(printHtml);
      }
      setTimeout(() => setPrintMode(null), 1000);
    }, 350);
  };

  const handleExportReportCardsExcel = () => {
    const filteredCards = reportCards
      .filter((rc) => !reportCardCoachFilter || String(rc.coach_id) === String(reportCardCoachFilter))
      .filter(
        (rc) =>
          !reportCardAthleteFilter ||
          reportCardAthleteFilter === "all" ||
          String(rc.athlete_id) === String(reportCardAthleteFilter)
      );

    if (!filteredCards.length) {
      Swal.fire("Notice", "No report cards to export for the selected filter.", "info");
      return;
    }

    const data = filteredCards.map((rc, idx) => ({
      "Sl. No": idx + 1,
      "Athlete Name": rc.athlete?.name || "N/A",
      "ID Number": rc.athlete?.id_number || rc.athlete?.bib_no || "N/A",
      "District": rc.athlete?.district || "N/A",
      "Category": rc.athlete?.category || "N/A",
      "Discipline": rc.athlete?.discipline_name || "N/A",
      "Assigned Coach": rc.coach?.name || "N/A",
      "Team / Squad": rc.coach?.team_name || "N/A",
      "Week Number": `Week ${rc.week_number}`,
      "Endurance": rc.endurance || "-",
      "Strength": rc.strength || "-",
      "Agility": rc.agility || "-",
      "Skill": rc.skill || (rc.skill_technique ? rc.skill_technique.split("/")[0]?.trim() : "-"),
      "Technique": rc.technique || (rc.skill_technique ? rc.skill_technique.split("/")[1]?.trim() || "-" : "-"),
      "Discipline Rating": rc.discipline || "-",
      "Overall Rating": rc.performance_rating || "Good",
      "Coach Remarks": rc.remarks || "",
      "Document Attached": rc.file_path ? "Yes" : "No",
    }));

    exportToExcel(data, `Report_Cards_${activeCamp?.name || "Camp"}_${new Date().toISOString().slice(0, 10)}`);
  };

  const teamAttendancePending = useMemo(() => {
    const grouped = new Map();
    const uniqueRows = new Map();
    athleteAttendanceSheet.forEach((row) => {
      const teamName = row.team_name || "General Squad";
      const athleteKey = row.athlete_id || row.id || row.id_number || row.bib_no || row.athlete_name;
      uniqueRows.set(`${teamName}::${athleteKey}`, row);
    });
    uniqueRows.forEach((row) => {
      const teamName = row.team_name || "General Squad";
      if (!grouped.has(teamName)) {
        grouped.set(teamName, {
          team_name: teamName,
          coach_names: new Set(),
          total: 0,
          morning_present: 0,
          morning_absent: 0,
          morning_pending: 0,
          morning_out_complete: 0,
          morning_out_pending: 0,
          evening_present: 0,
          evening_absent: 0,
          evening_pending: 0,
          evening_out_complete: 0,
          evening_out_pending: 0,
        });
      }
      const team = grouped.get(teamName);
      const morningStatus = String(row.morning_status || "").trim().toLowerCase();
      const eveningStatus = String(row.evening_status || "").trim().toLowerCase();
      team.total += 1;
      if (row.coach_name) team.coach_names.add(row.coach_name);
      if (morningStatus === "present") team.morning_present += 1;
      if (morningStatus === "absent") team.morning_absent += 1;
      if (!morningStatus || morningStatus === "pending" || morningStatus === "unmarked" || (morningStatus === "present" && !row.morning_in)) team.morning_pending += 1;
      if (morningStatus === "present" && row.morning_in && row.morning_out) team.morning_out_complete += 1;
      if (morningStatus === "present" && row.morning_in && !row.morning_out) team.morning_out_pending += 1;
      if (eveningStatus === "present") team.evening_present += 1;
      if (eveningStatus === "absent") team.evening_absent += 1;
      if (!eveningStatus || eveningStatus === "pending" || eveningStatus === "unmarked" || (eveningStatus === "present" && !row.evening_in)) team.evening_pending += 1;
      if (eveningStatus === "present" && row.evening_in && row.evening_out) team.evening_out_complete += 1;
      if (eveningStatus === "present" && row.evening_in && !row.evening_out) team.evening_out_pending += 1;
    });
    return Array.from(grouped.values()).map((team) => ({ ...team, coach_names: Array.from(team.coach_names).join(", ") })).sort((a, b) => a.team_name.localeCompare(b.team_name, undefined, { numeric: true }));
  }, [athleteAttendanceSheet]);

  const bulkAttendanceTeams = useMemo(
    () => [...new Set(athleteAttendanceSheet.map((row) => row.team_name || "General Squad"))]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [athleteAttendanceSheet]
  );

  const bulkAttendanceRows = useMemo(() => {
    const search = bulkAttendanceSearch.trim().toLowerCase();
    return athleteAttendanceSheet.filter((row) => {
      const teamName = row.team_name || "General Squad";
      const matchesTeam = !bulkAttendanceTeam || teamName === bulkAttendanceTeam;
      const matchesSearch = !search || [row.athlete_name, row.id_number, row.bib_no, teamName]
        .some((value) => String(value || "").toLowerCase().includes(search));
      return matchesTeam && matchesSearch;
    });
  }, [athleteAttendanceSheet, bulkAttendanceSearch, bulkAttendanceTeam]);
  const handlePrintEmptyBenchmarkReport = (selectedWeek = benchmarkExportWeek, selectedTeam = benchmarkExportTeam, action = "download") => {
    const campName = activeCamp?.name || "State Coaching Camp";
    const venueName = activeCamp?.venue || "State Sports Stadium";
    const generatedDate = new Date().toLocaleDateString("en-GB");

    // Use enrolled athletes, or placeholder if none
    const allSourceAthletes = Array.isArray(athletes) ? athletes.filter((ath) => ath && ath.id) : [];
    if (allSourceAthletes.length === 0) {
      Swal.fire("Notice", "Athlete data is still loading. Please refresh the Camp Reports tab and try again.", "info");
      return;
    }
    const normalizedSelectedTeam = String(selectedTeam || "all").trim().toLowerCase();
    const sourceAthletes = normalizedSelectedTeam === "all"
      ? allSourceAthletes
      : allSourceAthletes.filter((ath) => String(getTeamNameForAthlete(ath) || "General Squad").trim().toLowerCase() === normalizedSelectedTeam);
    const filteredSourceAthletes = benchmarkExportAthlete === "all" ? sourceAthletes : sourceAthletes.filter((ath) => String(ath.id) === String(benchmarkExportAthlete));

    if (filteredSourceAthletes.length === 0) {
      Swal.fire("Notice", `No players are assigned to ${selectedTeam}. Please verify Team Management mappings.`, "info");
      return;
    }
    // Helper to build an empty row for an athlete
    const emptyRow = (ath, idx) => `
      <tr style="height: 36px; border-bottom: 1px solid #0f172a; text-align: center;">
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 36px; color: #64748b;">${idx + 1}</td>
        <td style="border: 1px solid #0f172a; padding: 3px 8px; text-align: left; font-weight: 700; font-size: 11px; color: #0f172a;">${ath.name || ""}</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-family: monospace; font-weight: 700; font-size: 10px; width: 55px;">${ath.id_number || ath.bib_no || ""}</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 60px;">${ath.height_cm ? ath.height_cm + " cm" : ""}</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 55px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 55px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 60px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 60px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 60px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 60px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 55px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 55px;">&nbsp;</td>
        <td style="border: 1px solid #0f172a; padding: 3px 5px; font-size: 10px; width: 65px;">&nbsp;</td>
      </tr>
    `;

    // Official letterhead header block (reused per page)
    const makeHeader = (teamName, week) => `
      <div style="border-bottom: 2px solid #0f172a; padding-bottom: 7px; margin-bottom: 10px;">
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 3px;">
          <img src="${window.location.origin}/images/ssa.png" alt="SSA" style="height: 68px; width: 84px; object-fit: contain; object-position: left center;" />
          <div style="text-align: center; flex: 1; padding: 0 10px;">

            <div style="display: flex; align-items: center; justify-content: center; gap: 5px; margin: 1px 0;">
              <img src="${window.location.origin}/images/parasports.png" alt="Para Sports" style="height: 20px; width: auto; object-fit: contain;" />
              <span style="font-size: 12px; font-weight: 900; text-transform: uppercase; color: #0369a1;">
                PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
              </span>
            </div>
            <div style="font-size: 8.5px; color: #475569; font-weight: 600;">
              Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
            </div>
          </div>
          <img src="${window.location.origin}/images/ap_govt.png" alt="AP Govt" style="height: 68px; width: 84px; object-fit: contain; object-position: left center;" />
        </div>
        <div style="text-align: center; margin-top: 3px;">
          <div style="display: inline-block; background: #0f172a; color: #ffffff; padding: 3px 14px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; border-radius: 4px; letter-spacing: 0.5px;">
            Coaching Camp &mdash; Performance Benchmark Assessment &mdash; WEEK ${week}${week === 1 ? " (INITIAL BENCHMARK)" : week === 9 ? " (FINAL EVALUATION)" : ""}
          </div>
          <div style="font-size: 10px; color: #334155; font-weight: 700; margin-top: 2px;">
            Team / Squad: <strong style="color: #0369a1;">${teamName}</strong>
            &bull; Camp: ${campName} &bull; Venue: ${venueName} &bull; Generated: ${generatedDate}
          </div>
        </div>
      </div>
    `;

    const tableHeader = () => `
      <table style="width: 100%; border-collapse: collapse; border: 1.5px solid #0f172a; font-size: 10px;">
        <thead>
          <tr style="background: #e2e8f0; border-bottom: 1.5px solid #0f172a; text-align: center; font-weight: 800; color: #0f172a;">
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 36px;">Sl.</th>
            <th style="border: 1px solid #0f172a; padding: 5px 8px; text-align: left;">Athlete Name</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 55px;">ID Number</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 60px;">Height</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 55px;">100m</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 55px;">400m</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 60px;">Shot Put</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 60px;">Long Jump</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 60px;">Skipping</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 60px;">Push-ups</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 55px;">Plank</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 55px;">Pull-ups</th>
            <th style="border: 1px solid #0f172a; padding: 5px 3px; width: 65px;">Weight</th>
          </tr>
        </thead>
        <tbody>
    `;

    const signaturesHtml = () => `
      <table style="width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 30px; font-size: 11px; font-weight: 700;">
        <tr>
          <td style="width: 33.33%; text-align: center; vertical-align: bottom; padding: 0 18px;">
            <div style="height: 28px;"></div><div style="border-top: 1.5px solid #0f172a; padding-top: 4px;">Assistant Coach</div><div style="font-size: 8.5px; font-weight: normal; color: #64748b;">Date &amp; Signature</div>
          </td>
          <td style="width: 33.33%; text-align: center; vertical-align: bottom; padding: 0 18px;">
            <div style="height: 28px;"></div><div style="border-top: 1.5px solid #0f172a; padding-top: 4px;">${signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}</div><div style="font-size: 8.5px; font-weight: normal; color: #64748b;">${signatures?.camp_incharge_title || "Camp In-Charge"}</div>
          </td>
          <td style="width: 33.33%; text-align: center; vertical-align: bottom; padding: 0 18px;">
            <div style="height: 28px;"></div><div style="border-top: 1.5px solid #0f172a; padding-top: 4px;">Camp Official</div><div style="font-size: 8.5px; font-weight: normal; color: #64748b;">Date &amp; Signature</div>
          </td>
        </tr>
      </table>
    `;
    // Group athletes by team using getTeamNameForAthlete
    const teamMap = new Map();
    filteredSourceAthletes.forEach((ath) => {
      const tName = getTeamNameForAthlete(ath);
      if (!teamMap.has(tName)) teamMap.set(tName, []);
      teamMap.get(tName).push(ath);
    });
    const sortedTeams = Array.from(teamMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));

    // Build one page section per team per week (page-break between each)
    const pageSections = [];
    sortedTeams.forEach(([teamName, teamAthletes]) => {
      [Number(selectedWeek)].forEach((week) => {
        const rowsHtml = teamAthletes.map((ath, idx) => emptyRow(ath, idx)).join("");
        pageSections.push(`
          <div class="team-sheet" style="page-break-after: always; box-sizing: border-box; padding: 5mm; background: #ffffff; color: #000000;">
            ${makeHeader(teamName, week)}
            ${tableHeader()}
              ${rowsHtml}
            </tbody>
          </table>
          ${signaturesHtml()}
          </div>
        `);
      });
    });

    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Coaching Camp - Performance Benchmark Assessment (Empty Format by Team)</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 7mm;
            }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #000000;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          ${pageSections.join("")}
        </body>
      </html>
    `;

    if (action === "print") {
      printHtmlViaIframe(fullHtml);
    } else {
      downloadHtmlAsPdf(fullHtml, `Weekly_Assessment_Empty_Week_${selectedWeek}_${selectedTeam === "all" ? "All_Teams" : selectedTeam.replace(/\s+/g, "_")}`, {
        orientation: "landscape", pageSelector: ".team-sheet", scale: 2,
      });
    }
  };

  const handlePrintPerformanceReport = (action = "download") => {

    if (!performanceReports || performanceReports.length === 0) {
      Swal.fire("Notice", "No performance assessments to print.", "info");
      return;
    }

    const campName = activeCamp?.name || "State Coaching Camp";
    const venueName = activeCamp?.venue || "State Sports Stadium";
    const generatedDate = new Date().toLocaleDateString("en-GB");
    const filteredPerformanceReports = performanceReports.filter((report) => {
      if (Number(report.week_number) !== Number(benchmarkExportWeek)) return false;
      if (benchmarkExportAthlete !== "all" && String(report.athlete_id) !== String(benchmarkExportAthlete)) return false;
      if (benchmarkExportTeam === "all") return true;
      const athlete = report.athlete || athletes.find((item) => String(item.id) === String(report.athlete_id));
      return getTeamNameForAthlete(athlete) === benchmarkExportTeam;
    });
    if (filteredPerformanceReports.length === 0) {
      Swal.fire("Notice", "No recorded assessments found for the selected week and team.", "info");
      return;
    }

    const rowsHtml = filteredPerformanceReports.map((p) => {
      const heightStr = p.test_height || p.athlete?.height_cm
        ? `${p.test_height || p.athlete?.height_cm} cm`
        : (athletes.find((a) => String(a.id) === String(p.athlete_id))?.height_cm
          ? `${athletes.find((a) => String(a.id) === String(p.athlete_id)).height_cm} cm`
          : "-");
      const weightStr = p.test_weight || p.athlete?.weight_kg
        ? `${p.test_weight || p.athlete?.weight_kg} kg`
        : (athletes.find((a) => String(a.id) === String(p.athlete_id))?.weight_kg
          ? `${athletes.find((a) => String(a.id) === String(p.athlete_id)).weight_kg} kg`
          : (p.test_weight || "-"));

      return `
        <tr style="height: 32px; border-bottom: 1px solid #0f172a; text-align: center;">
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-weight: bold; font-size: 10px; width: 70px;">
            Week ${p.week_number}
          </td>
          <td style="border: 1px solid #0f172a; padding: 4px 8px; text-align: left; font-weight: 700; font-size: 11px; color: #0f172a;">
            ${p.athlete?.name || "N/A"}
          </td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-family: monospace; font-weight: 700; font-size: 10px; width: 65px;">
            ${p.athlete?.id_number || p.athlete?.bib_no || "—"}
          </td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-weight: 600; font-size: 10px; width: 65px;">
            ${heightStr}
          </td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 60px;">${p.test_100m || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 60px;">${p.test_400m || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 65px;">${p.test_shot_put || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 65px;">${p.test_long_jump || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 65px;">${p.test_skipping || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 65px;">${p.test_pushups || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 60px;">${p.test_plank || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-size: 10px; width: 60px;">${p.test_pullups || "-"}</td>
          <td style="border: 1px solid #0f172a; padding: 4px 6px; font-weight: 700; font-size: 10px; width: 70px;">${weightStr}</td>
        </tr>
      `;
    }).join("");

    const fullHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Coaching Camp - Performance Benchmark Assessment - Week ${benchmarkExportWeek}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 7mm;
            }
            * {
              box-sizing: border-box;
            }
            body {
              margin: 0;
              padding: 0;
              background: #ffffff;
              color: #000000;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          </style>
        </head>
        <body>
          <div class="team-sheet" style="box-sizing: border-box; padding: 6mm; background: #ffffff; color: #000000;">
            <!-- Official Header -->
            <div style="border-bottom: 2px solid #0f172a; padding-bottom: 8px; margin-bottom: 12px;">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                <img src="${window.location.origin}/images/ssa.png" alt="SSA" style="height: 48px; width: auto; object-fit: contain;" />
                <div style="text-align: center; flex: 1; padding: 0 12px;">

                  <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin: 2px 0;">
                    <img src="${window.location.origin}/images/parasports.png" alt="Para Sports" style="height: 22px; width: auto; object-fit: contain;" />
                    <span style="font-size: 13px; font-weight: 900; text-transform: uppercase; color: #0369a1;">
                      PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                    </span>
                  </div>
                  <div style="font-size: 9px; color: #475569; font-weight: 600;">
                    Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                  </div>
                </div>
                <img src="${window.location.origin}/images/ap_govt.png" alt="AP Govt" style="height: 48px; width: auto; object-fit: contain;" />
              </div>
              <div style="text-align: center; margin-top: 4px;">
                <div style="display: inline-block; background: #0f172a; color: #ffffff; padding: 3px 14px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-radius: 4px; letter-spacing: 0.5px;">
                  Coaching Camp &mdash; Performance Benchmark Assessment &mdash; Week ${benchmarkExportWeek}${benchmarkExportWeek === 1 ? " (Initial Benchmark)" : benchmarkExportWeek === 9 ? " (Final Evaluation)" : ""}
                </div>
                <div style="font-size: 10.5px; color: #334155; font-weight: 700; margin-top: 3px;">
                  Camp: ${campName} &bull; Venue: ${venueName} &bull; Generated: ${generatedDate}
                </div>
              </div>
            </div>

            <!-- Table -->
            <table style="width: 100%; border-collapse: collapse; border: 1.5px solid #0f172a; font-size: 10px;">
              <thead>
                <tr style="background: #e2e8f0; border-bottom: 1.5px solid #0f172a; text-align: center; font-weight: 800; color: #0f172a;">
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 70px;">Phase</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 8px; text-align: left;">Athlete Name</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">ID Number</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Height</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 60px;">100m</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 60px;">400m</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Shot Put</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Long Jump</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Skipping</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 65px;">Push-ups</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 60px;">Plank</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 60px;">Pull-ups</th>
                  <th style="border: 1px solid #0f172a; padding: 6px 4px; width: 70px;">Weight</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>

            <!-- Signatures -->
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); align-items: end; gap: 28px; margin-top: 36px; padding: 0 20px; font-size: 11px; font-weight: 700;">
              <div style="text-align: center; min-width: 0;">
                <div style="width: 100%; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
                <div>Assistant Coach</div>
                <div style="font-size: 9px; font-weight: normal; color: #64748b;">Date &amp; Signature</div>
              </div>
              <div style="text-align: center; min-width: 0;">
                <div style="width: 100%; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
                <div>${signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}</div>
                <div style="font-size: 9px; font-weight: normal; color: #64748b;">${signatures?.camp_incharge_title || "Camp In-Charge"}</div>
              </div>
              <div style="text-align: center; min-width: 0;">
                <div style="width: 100%; border-bottom: 1.5px solid #0f172a; margin-bottom: 4px;"></div>
                <div>Camp Official</div>
                <div style="font-size: 9px; font-weight: normal; color: #64748b;">Date &amp; Signature</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    if (action === "print") {
      printHtmlViaIframe(fullHtml);
    } else {
      downloadHtmlAsPdf(fullHtml, `Weekly_Assessment_Filled_Week_${benchmarkExportWeek}_${benchmarkExportTeam === "all" ? "All_Teams" : benchmarkExportTeam.replace(/\s+/g, "_")}`, {
        orientation: "landscape", pageSelector: ".team-sheet", scale: 2,
      });
    }
  };

  const handleExportPerformanceExcel = () => {
    if (!performanceReports.length) {
      Swal.fire("Notice", "No performance assessments to export.", "info");
      return;
    }

    const data = performanceReports.map((p, idx) => ({
      "Sl. No": idx + 1,
      "Phase": `Week ${p.week_number}${p.week_number === 1 ? " (Initial Benchmark)" : p.week_number === 9 ? " (Final Evaluation)" : ""}`,
      "Athlete Name": p.athlete?.name || "N/A",
      "ID Number": p.athlete?.id_number || p.athlete?.bib_no || "N/A",
      "District": p.athlete?.district || "N/A",
      "Height": p.test_height || p.athlete?.height_cm ? `${p.test_height || p.athlete?.height_cm} cm` : "-",
      "100m Run": p.test_100m || "-",
      "400m Run": p.test_400m || "-",
      "Shot Put": p.test_shot_put || "-",
      "Long Jump": p.test_long_jump || "-",
      "Skipping": p.test_skipping || "-",
      "Push-ups": p.test_pushups || "-",
      "Plank": p.test_plank || "-",
      "Pull-ups": p.test_pullups || "-",
      "Cycling 5km": p.test_cycling_5km || "-",
      "Swimming 50m": p.test_swimming_50m || "-",
      "Weight": p.test_weight || "-",
    }));

    exportToExcel(data, `Performance_Assessments_${activeCamp?.name || "Camp"}_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportWeeklyReportsExcel = () => {
    if (!weeklyReports.length) {
      Swal.fire("Notice", "No weekly reports to export.", "info");
      return;
    }

    const data = weeklyReports.map((w, idx) => ({
      "Sl. No": idx + 1,
      "Week Number": `Week ${w.week_number}`,
      "Athlete Name": w.athlete?.name || "N/A",
      "ID Number": w.athlete?.id_number || w.athlete?.bib_no || "N/A",
      "Coach Name": w.coach?.name || "N/A",
      "Team Name": w.coach?.team_name || "N/A",
      "Report Date": w.report_date || "N/A",
      "Summary": w.description || "",
      "Document Attached": w.file_path ? "Yes" : "No",
    }));

    exportToExcel(data, `Weekly_Reports_${activeCamp?.name || "Camp"}_${new Date().toISOString().slice(0, 10)}`);
  };

  const handleExportAttendanceExcel = () => {
    if (!attendanceRecords.length) {
      Swal.fire("Notice", "No attendance records to export for this date", "info");
      return;
    }

    const data = attendanceRecords.map((r) => ({
      athlete: r.athlete?.name || "N/A",
      "ID Number": r.athlete?.id_number || r.athlete?.bib_no || "N/A",
      category: r.athlete?.category || "N/A",
      discipline: r.athlete?.discipline_name || "N/A",
      coach: r.coach?.name || "N/A",
      morning_in: r.morning_in || "-",
      morning_out: r.morning_out || "-",
      morning_status: r.morning_status?.toUpperCase(),
      evening_in: r.evening_in || "-",
      evening_out: r.evening_out || "-",
      evening_status: r.evening_status?.toUpperCase(),
      remarks: r.remarks || "",
    }));

    exportToExcel(data, `Attendance_${activeCamp?.name}_${attendanceDate}`);
  };

  const handleExportMonthlyAttendanceExcel = async () => {
    if (!monthlyAttendanceMonth) {
      Swal.fire("Select Month", "Please select the month to export.", "info");
      return;
    }

    setExportingMonthlyAttendance(true);
    try {
      const [year, monthNumber] = monthlyAttendanceMonth.split("-").map(Number);
      const dateFrom = `${monthlyAttendanceMonth}-01`;
      const dateTo = `${monthlyAttendanceMonth}-${String(new Date(year, monthNumber, 0).getDate()).padStart(2, "0")}`;
      const [res, peopleResponse] = await Promise.all([
        API.camp.getAttendanceRangeReport({
          camp_id: selectedCampId,
          date_from: dateFrom,
          date_to: dateTo,
          for_print: 1,
          monthly_export: 1,
        }),
        API.camp.getPeopleAttendanceMonthlyReport({
          camp_id: selectedCampId,
          month: monthlyAttendanceMonth,
        }),
      ]);
      const teams = res?.teams || res?.coaches || res?.data?.teams || res?.data?.coaches || [];
      const peopleAttendance = peopleResponse?.data || peopleResponse || {};
      if (!teams.length) {
        Swal.fire("No Athletes", "No camp athletes were found for the selected month.", "info");
        return;
      }

      await exportMonthlyAttendanceExcel({
        teams,
        peopleAttendance,
        month: monthlyAttendanceMonth,
        fileName: `Monthly_Attendance_${activeCamp?.name || "Camp"}_${monthlyAttendanceMonth}`.replace(/[^a-z0-9_-]+/gi, "_"),
      });
    } catch (err) {
      console.error("Failed to export monthly attendance:", err);
      Swal.fire("Export Failed", err?.response?.data?.message || err?.message || "Could not create the monthly attendance workbook.", "error");
    } finally {
      setExportingMonthlyAttendance(false);
    }
  };

  const handleBulkAttendanceSave = async (status) => {
    if (!selectedAttendanceAthleteIds.length) {
      Swal.fire("Select Students", "Select at least one student before marking attendance.", "info");
      return;
    }

    const selectedIdSet = new Set(selectedAttendanceAthleteIds.map(String));
    const currentTime = getCurrentISTTime();
    const statusKey = `${bulkAttendanceSession}_status`;
    const inKey = `${bulkAttendanceSession}_in`;
    const outKey = `${bulkAttendanceSession}_out`;
    const records = athleteAttendanceSheet
      .filter((row) => selectedIdSet.has(String(row.athlete_id)))
      .map((row) => ({
        athlete_id: row.athlete_id,
        coach_id: row.coach_id || undefined,
        [statusKey]: status,
        [inKey]: status === "absent" ? null : currentTime,
        [outKey]: null,
      }));

    setSavingBulkAttendance(true);
    try {
      const res = await API.camp.saveAttendance({
        camp_id: selectedCampId,
        date: attendanceDate,
        records,
      });
      const [updated, updatedSheet] = await Promise.all([
        API.camp.getAttendance({ camp_id: selectedCampId, date: attendanceDate }),
        API.camp.getAttendanceSheet({ camp_id: selectedCampId, date: attendanceDate }),
      ]);
      setAttendanceRecords(updated?.data?.records || updated?.records || []);
      setAthleteAttendanceSheet(updatedSheet?.data?.sheet || updatedSheet?.sheet || []);
      setSelectedAttendanceAthleteIds([]);
      Swal.fire({
        icon: "success",
        title: "Attendance Marked",
        text: res?.data?.message || res?.message || `${records.length} students updated at ${currentTime}.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Failed to bulk mark attendance:", err);
      Swal.fire("Save Failed", err?.response?.data?.message || "Could not update the selected students.", "error");
    } finally {
      setSavingBulkAttendance(false);
    }
  };

  const fetchCoachAttendanceSheet = async (campId = selectedCampId, date = coachAttendanceDate) => {
    setLoadingCoachAttendance(true);
    try {
      const res = await API.camp.getCoachAttendanceSheet({
        camp_id: campId || selectedCampId,
        date: date || coachAttendanceDate,
      });
      const list = res?.data?.sheet || res?.sheet || [];
      setCoachAttendanceSheet(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load coach attendance:", err);
    } finally {
      setLoadingCoachAttendance(false);
    }
  };

  const updateCoachAttendanceSession = (coachId, session, action) => {
    const time = getCurrentISTTime();
    setCoachAttendanceSheet((prev) => prev.map((item) => {
      if (item.coach_id !== coachId) return item;
      const inKey = `${session}_in`;
      const outKey = `${session}_out`;
      const statusKey = `${session}_status`;
      if (action === "in") return { ...item, [statusKey]: "present", [inKey]: time, [outKey]: null };
      if (action === "out" && item[inKey]) return { ...item, [outKey]: time };
      if (action === "absent") return { ...item, [statusKey]: "absent", [inKey]: null, [outKey]: null };
      return item;
    }));
  };
  const handleSaveCoachAttendance = async (records) => {
    setSavingCoachAttendance(true);
    try {
      const res = await API.camp.saveCoachAttendance({
        camp_id: selectedCampId,
        date: coachAttendanceDate,
        records,
      });
      Swal.fire({
        icon: "success",
        title: "Coach Attendance Saved!",
        text: res?.data?.message || "Coach attendance recorded successfully.",
        timer: 1800,
        showConfirmButton: false,
      });
      fetchCoachAttendanceSheet(selectedCampId, coachAttendanceDate);
    } catch (err) {
      console.error("Save coach attendance failed:", err);
      Swal.fire("Error", err?.response?.data?.message || "Failed to save coach attendance.", "error");
    } finally {
      setSavingCoachAttendance(false);
    }
  };

  const handleSaveEditedAthleteAttendance = async (e) => {
    e.preventDefault();
    if (!editingAthleteAttendanceRow) return;

    setSavingAthleteAttendance(true);
    try {
      const res = await API.camp.saveAttendance({
        camp_id: selectedCampId,
        date: attendanceDate,
        records: [
          {
            athlete_id: editingAthleteAttendanceRow.athlete_id || editingAthleteAttendanceRow.athlete?.id,
            coach_id: editingAthleteAttendanceRow.coach_id,
            morning_in: editingAthleteAttendanceRow.morning_in || null,
            morning_out: editingAthleteAttendanceRow.morning_out || null,
            morning_status: editingAthleteAttendanceRow.morning_status || null,
            evening_in: editingAthleteAttendanceRow.evening_in || null,
            evening_out: editingAthleteAttendanceRow.evening_out || null,
            evening_status: editingAthleteAttendanceRow.evening_status || null,
            remarks: editingAthleteAttendanceRow.remarks || "",
          },
        ],
      });
      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: res?.data?.message || "Athlete attendance record updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
      setEditingAthleteAttendanceRow(null);
      // Refresh live records
      const [updated, updatedSheet] = await Promise.all([
        API.camp.getAttendance({ camp_id: selectedCampId, date: attendanceDate }),
        API.camp.getAttendanceSheet({ camp_id: selectedCampId, date: attendanceDate }),
      ]);
      setAttendanceRecords(updated?.data?.records || updated?.records || []);
      setAthleteAttendanceSheet(updatedSheet?.data?.sheet || updatedSheet?.sheet || []);
    } catch (err) {
      console.error("Failed to update athlete attendance:", err);
      Swal.fire("Error", err?.response?.data?.message || "Failed to update attendance.", "error");
    } finally {
      setSavingAthleteAttendance(false);
    }
  };

  const handleDeleteAttendanceRecord = async (recordId, athleteName) => {
    if (!recordId) return;
    const result = await Swal.fire({
      title: "Delete Attendance Entry?",
      html: `<div class="text-left text-xs space-y-1.5">
        <p>Are you sure you want to permanently delete the attendance record for <strong>${athleteName || "this athlete"}</strong> on <strong>${attendanceDate}</strong>?</p>
        <p class="text-rose-600 bg-rose-50 p-2 rounded-lg border border-rose-200">
          This action will remove the marked session entry from the database.
        </p>
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete Record",
    });

    if (result.isConfirmed) {
      try {
        const res = await API.camp.deleteAttendance(recordId);
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: res?.data?.message || res?.message || "Attendance record deleted successfully.",
          timer: 1600,
          showConfirmButton: false,
        });
        if (editingAthleteAttendanceRow?.id === recordId) {
          setEditingAthleteAttendanceRow(null);
        }
        // Refresh live records
        const [updated, updatedSheet] = await Promise.all([
          API.camp.getAttendance({ camp_id: selectedCampId, date: attendanceDate }),
          API.camp.getAttendanceSheet({ camp_id: selectedCampId, date: attendanceDate }),
        ]);
        setAttendanceRecords(updated?.data?.records || updated?.records || []);
        setAthleteAttendanceSheet(updatedSheet?.data?.sheet || updatedSheet?.sheet || []);
      } catch (err) {
        console.error("Failed to delete attendance:", err);
        Swal.fire("Delete Failed", err?.response?.data?.message || err?.message || "Could not delete attendance record.", "error");
      }
    }
  };

  const fetchAttendanceRangeReport = async () => {
    if (!selectedCampId) return;
    if (!rangeDateFrom || !rangeDateTo) {
      Swal.fire("Dates Required", "Please select both From and To dates.", "warning");
      return;
    }
    if (rangeDateFrom > rangeDateTo) {
      Swal.fire("Invalid Date Range", "The From date cannot be later than the To date.", "warning");
      return;
    }

    setRangeLoading(true);
    setRangeSubmitted(true);
    try {
      const res = await API.camp.getAttendanceRangeReport({
        camp_id: selectedCampId,
        date_from: rangeDateFrom,
        date_to: rangeDateTo,
        team_name: rangeTeamFilter || undefined,
        athlete_id: rangeAthleteFilter || undefined,
        coach_id: rangeCoachFilter || undefined,
        allow_future: true,
      });
      setRangeReportData(res?.teams || res?.coaches || res?.data?.teams || res?.data?.coaches || []);
    } catch (err) {
      console.error("Error fetching attendance range report:", err);
      setRangeReportData([]);
      Swal.fire("Unable to Load", err?.response?.data?.message || "Could not load the attendance register.", "error");
    } finally {
      setRangeLoading(false);
    }
  };


  const printDailyRegisterDocuments = ({ coachesList, isBlank = true, singleDate = null, action = "download" }) => {
    if (!coachesList || coachesList.length === 0) {
      Swal.fire("Notice", "No team or athlete records found to print.", "warning");
      return;
    }

    const pages = [];
    coachesList.forEach((team) => {
      let days = team.days || [];
      if (singleDate) {
        const filtered = days.filter((d) => d.date === singleDate);
        if (filtered.length > 0) {
          days = filtered;
        } else {
          const fallbackAthletes = team.athletes || team.days?.[0]?.athletes || [];
          days = [{
            date: singleDate,
            athletes: fallbackAthletes,
          }];
        }
      }

      days.forEach((day) => {
        const dDate = day.date || singleDate;
        const dDayNum = getCampDayNumber(dDate);
        const dayNumText = dDayNum ? ` (Day ${dDayNum}${campTotalDays ? ` of ${campTotalDays}` : ""})` : "";
        pages.push({
          team,
          coach: team,
          day,
          dayNumText,
          dateFormatted: dDate ? dDate.split("-").reverse().join("-") : "—",
        });
      });
    });

    // Official register order: Day 1 all teams, Day 2 all teams, etc.
    pages.sort((a, b) => {
      const dateCompare = String(a.day?.date || "").localeCompare(String(b.day?.date || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(a.team?.team_name || a.team?.coach_name || "General Squad")
        .localeCompare(String(b.team?.team_name || b.team?.coach_name || "General Squad"), undefined, { numeric: true, sensitivity: "base" });
    });

    if (pages.length === 0) {
      Swal.fire("Notice", "No attendance sheets found matching the chosen date.", "warning");
      return;
    }

    const campName = activeCamp?.name || "State Para Sports Coaching Camp 2026";
    const campVenue = activeCamp?.venue || "State Sports Complex, Andhra Pradesh";

    const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Daily Attendance Register - ${campName}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 5mm 6mm 5mm 6mm;
    }
    * {
      box-sizing: border-box !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a;
      background: #ffffff;
      font-size: 9.5px;
    }
    .print-sheet {
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
      break-inside: avoid;
      box-sizing: border-box;
      padding: 2mm 3mm;
      margin: 0;
      background: #ffffff;
      height: 270mm;
      max-height: 270mm;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      overflow: hidden;
    }
    .print-sheet:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    table.no-border, table.no-border td, table.no-border th {
      border: none;
    }
    table.data-table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: 1.2px solid #0f172a;
      font-size: 9px;
      line-height: 1.15;
    }
    table.data-table th, table.data-table td {
      border: 1px solid #0f172a;
    }
    table.data-table th {
      background-color: #f1f5f9;
      color: #0f172a;
      font-weight: 800;
    }
    .text-center { text-align: center; }
    .text-left { text-align: left; }
    .text-right { text-align: right; }
    .font-mono { font-family: monospace, Courier, monospace; }
    .font-bold { font-weight: 700; }
    .box-cell {
      height: 19px;
      vertical-align: middle;
    }
    .write-line {
      display: inline-block;
      min-width: 38px;
      border-bottom: 1px dotted #94a3b8;
      height: 10px;
    }
    .blank-register {
      font-size: 11px;
    }
    .blank-register table.data-table {
      font-size: 10.5px;
      line-height: 1.25;
    }
    .blank-register table.data-table th {
      font-size: 10.5px !important;
      line-height: 1.25 !important;
      padding-top: 3px !important;
      padding-bottom: 3px !important;
    }
    .blank-register table.data-table td {
      font-size: 10.5px !important;
      line-height: 1.25 !important;
    }
    .blank-register table.data-table td div {
      font-size: 9px !important;
      line-height: 1.2 !important;
    }
    .blank-register .box-cell {
      height: 22px;
    }
    .blank-register .checkbox-square {
      width: 11px;
      height: 11px;
    }    .checkbox-square {
      display: inline-block;
      width: 10px;
      height: 10px;
      border: 1.2px solid #0f172a;
      border-radius: 2px;
      margin-right: 2px;
      vertical-align: middle;
    }
  </style>
</head>
<body>
  ${pages.map(({ team, coach, day, dateFormatted, dayNumText }, pageIdx) => {
      const athletesList = day.athletes || [];
      let coachesOnTeam = team.coaches || [];
      if (!coachesOnTeam || coachesOnTeam.length === 0) {
        if (team.coach_name) {
          if (team.coach_name.includes('&')) {
            coachesOnTeam = team.coach_name.split('&').map((n, idx) => ({
              name: n.trim(),
              role: `Assistant Coach ${idx + 1}`
            }));
          } else {
            coachesOnTeam = [{ name: team.coach_name.trim(), role: 'Assistant Coach 1' }];
          }
        }
      }
      let coach1Name = coachesOnTeam[0]?.name;
      let coach2Name = coachesOnTeam[1]?.name;
      if (!coach2Name && team.coach_name && team.coach_name.includes('&')) {
        const parts = team.coach_name.split('&').map(s => s.trim());
        coach1Name = coach1Name || parts[0];
        coach2Name = parts[1];
      }
      coach1Name = coach1Name || "Assistant Coach 1";
      coach2Name = coach2Name || "Assistant Coach 2";
      const allCoachNames = coachesOnTeam.length > 0
        ? coachesOnTeam.map(c => c.name).join(" & ")
        : (team.coach_name || `${coach1Name} & ${coach2Name}`);

      return `
    <div class="print-sheet${isBlank ? " blank-register" : ""}">
      <div>
        <!-- Header Table (Cross-engine compatible: SSA Logo | Title | AP Govt Logo) -->
        <table class="no-border" style="width: 100%; border: none; margin: 0 0 3px 0; padding: 0 0 3px 0; border-bottom: 2px solid #0f172a;">
          <tr style="border: none;">
            <td style="border: none; width: 78px; text-align: left; vertical-align: middle; padding: 0;">
              <img src="/images/ssa.png" style="height: 68px; width: 84px; object-fit: contain; object-position: left center;" alt="SSA" onerror="this.style.display='none'" />
            </td>
            <td style="border: none; text-align: center; vertical-align: middle; padding: 0 4px;">
              <div style="margin-bottom: 1px;">
                <img src="/images/parasports.png" style="height: 60px; width: auto; max-width: 112px; display: block; object-fit: contain; margin: 0 auto 2px auto;" alt="Para Sports" onerror="this.style.display='none'" />
                <span style="font-size: 13.5px; font-weight: 900; text-transform: uppercase; color: #0f172a; letter-spacing: 0.4px;">
                  Para Sports Association of Andhra Pradesh
                </span>
              </div>
              <div style="font-size: 8.5px; font-weight: 600; color: #334155; margin-top: 1px;">
                Recognized by Paralympic Committee of India (PCI) &amp; Sports Authority of Andhra Pradesh (SAAP)
              </div>
              <div style="font-size: 8.5px; font-weight: 600; color: #475569; margin-top: 1px;">
                In association with: <strong style="color: #0369a1;">Samagra Shiksha</strong> &bull; <strong style="color: #0284c7;">Government of Andhra Pradesh</strong>
              </div>
              <div style="margin-top: 2px;">
                <span style="display: inline-block; background: #0f172a; color: #ffffff; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.8px; padding: 2px 10px; border-radius: 2px;">
                  ${isBlank ? "Daily Athlete Attendance Register (Manual Ground Register)" : "Daily Athlete Attendance Register"}
                </span>
              </div>
            </td>
            <td style="border: none; width: 78px; text-align: right; vertical-align: middle; padding: 0;">
              <img src="/images/ap_govt.png" style="height: 68px; width: 84px; object-fit: contain; object-position: right center;" alt="AP Govt" onerror="this.style.display='none'" />
            </td>
          </tr>
        </table>

        <!-- Details Bar Table -->
        <table class="no-border" style="width: 100%; border: 1.2px solid #64748b; background: #f8fafc; border-radius: 3px; padding: 3px 6px; font-size: 9.5px; margin: 0 0 4px 0;">
          <tr style="border: none;">
            <td style="border: none; padding: 1px 4px; width: 28%;">
              <span style="color: #475569; font-weight: bold; font-size: 9px;">Team / Squad:</span>
              <strong style="color: #0369a1; font-size: 10.5px; margin-left: 2px;">${team.team_name || "General Squad"}</strong>
            </td>
            <td style="border: none; padding: 1px 4px; width: 34%;">
              <span style="color: #475569; font-weight: bold; font-size: 9px;">Coach(es):</span>
              <strong style="color: #0f172a; font-size: 10px; margin-left: 2px;">${allCoachNames}</strong>
            </td>
            <td style="border: none; padding: 1px 4px; width: 26%;">
              <span style="color: #475569; font-weight: bold; font-size: 9px;">Date:</span>
              <strong style="color: #0f172a; font-family: monospace; font-size: 10.5px; margin-left: 2px;">${dateFormatted}${dayNumText}</strong>
            </td>
            <td style="border: none; padding: 1px 4px; width: 12%; text-align: right;">
              <span style="color: #475569; font-weight: bold; font-size: 9px;">Players:</span>
              <strong style="color: #0369a1; font-size: 10.5px; margin-left: 2px;">${athletesList.length}</strong>
            </td>
          </tr>
          <tr style="border: none;">
            <td colspan="4" style="border: none; border-top: 1px dashed #cbd5e1; padding: 2px 4px 0 4px; font-size: 8px; color: #475569;">
              <table class="no-border" style="width: 100%; border: none; padding: 0; margin: 0;">
                <tr style="border: none;">
                  <td style="border: none; padding: 0;"><strong>Camp:</strong> ${campName}</td>
                  <td style="border: none; padding: 0;"><strong>Venue:</strong> ${campVenue}</td>
                  <td style="border: none; padding: 0;"><strong>Duration:</strong> ${campStartDate ? campStartDate.split("-").reverse().join("-") : "Start"} to ${campEndDate ? campEndDate.split("-").reverse().join("-") : "End"}${campTotalDays ? ` (${campTotalDays} Days)` : ""}</td>
                  <td style="border: none; padding: 0; text-align: right;"><strong>Discipline:</strong> ${team.discipline || "All Disciplines"}</td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <!-- Attendance Register Table (Strict 100% Fixed Columns) -->
        <table class="data-table">
          <colgroup>
            <col style="width: 4%;">
            <col style="width: 24%;">
            <col style="width: 7%;">
            <col style="width: 10%;">
            <col style="width: 7%;">
            <col style="width: 7%;">
            <col style="width: 10%;">
            <col style="width: 7%;">
            <col style="width: 7%;">
            <col style="width: 7%;">
            <col style="width: 10%;">
          </colgroup>
          <thead>
            <tr style="background: #e2e8f0; text-align: center;">
              <th style="font-size: 9px; padding: 2px 1px;" rowspan="2">S.No</th>
              <th style="text-align: left; font-size: 9px; padding: 2px 4px;" rowspan="2">Player Name</th>
              <th style="font-size: 9px; padding: 2px 1px;" rowspan="2">ID Number</th>
              <th colspan="4" style="text-align: center; background: #e0f2fe; color: #0369a1; font-size: 9px; padding: 2px 1px;">Morning Session</th>
              <th colspan="4" style="text-align: center; background: #ede9fe; color: #5b21b6; font-size: 9px; padding: 2px 1px;">Evening Session</th>
            </tr>
            <tr style="background: #f8fafc; font-size: 8px; text-align: center;">
              <th style="padding: 2px 1px;">In Time</th>
              <th style="padding: 2px 1px;">Out Time</th>
              <th style="padding: 2px 1px;">Status</th>
              <th style="padding: 2px 1px;">Signature</th>
              <th style="padding: 2px 1px;">In Time</th>
              <th style="padding: 2px 1px;">Out Time</th>
              <th style="padding: 2px 1px;">Status</th>
              <th style="padding: 2px 1px;">Signature</th>
            </tr>
          </thead>
          <tbody>
            ${athletesList.map((ath, idx) => `
              <tr class="box-cell" style="page-break-inside: avoid; break-inside: avoid;">
                <td class="text-center font-bold" style="font-size: 9px; padding: 1px 2px; line-height: 1.15;">${idx + 1}</td>
                <td class="text-left font-bold" style="color: #0f172a; font-size: 9px; padding: 1px 4px; line-height: 1.2; white-space: normal; overflow-wrap: anywhere; word-break: break-word; vertical-align: middle;">
                  ${ath.athlete_name || ath.name || "—"}
                  <div style="font-size: 7.5px; color: #475569; font-weight: normal; margin-top: 0.5px; white-space: normal; overflow-wrap: anywhere; word-break: break-word; line-height: 1.15;">
                    District: ${ath.district || "—"}
                  </div>
                </td>
                <td class="text-center font-mono font-bold" style="font-size: 8.5px; color: #0369a1; padding: 1px 1px; line-height: 1.15;">${ath.id_number || ath.player_registration_id || ath.player?.registration_id || ath.bib_no || "—"}</td>
                
                <!-- Morning Session -->
                <td class="text-center font-mono" style="font-size: 8.5px; padding: 1px 1px; line-height: 1.15;">${isBlank ? `<span class="write-line"></span>` : (ath.morning_in || "—")}</td>
                <td class="text-center font-mono" style="font-size: 8.5px; padding: 1px 1px; line-height: 1.15;">${isBlank ? `<span class="write-line"></span>` : (ath.morning_out || "—")}</td>
                <td class="text-center" style="font-size: 8.5px; font-weight: bold; white-space: nowrap; padding: 1px 1px; line-height: 1.15;">
                  ${isBlank
          ? `<span class="checkbox-square"></span>P &nbsp; <span class="checkbox-square"></span>A`
          : `<strong style="font-size: 9px; color: ${ath.morning_status === 'present' ? '#166534' : '#991b1b'};">${ath.morning_status?.toUpperCase() || '—'}</strong>`
        }
                </td>

                <td class="text-center" style="padding: 1px 2px;"><span class="write-line"></span></td>

                <!-- Evening Session -->
                <td class="text-center font-mono" style="font-size: 8.5px; padding: 1px 1px; line-height: 1.15;">${isBlank ? `<span class="write-line"></span>` : (ath.evening_in || "—")}</td>
                <td class="text-center font-mono" style="font-size: 8.5px; padding: 1px 1px; line-height: 1.15;">${isBlank ? `<span class="write-line"></span>` : (ath.evening_out || "—")}</td>
                <td class="text-center" style="font-size: 8.5px; font-weight: bold; white-space: nowrap; padding: 1px 1px; line-height: 1.15;">
                  ${isBlank
          ? `<span class="checkbox-square"></span>P &nbsp; <span class="checkbox-square"></span>A`
          : `<strong style="font-size: 9px; color: ${ath.evening_status === 'present' ? '#166534' : '#991b1b'};">${ath.evening_status?.toUpperCase() || '—'}</strong>`
        }
                </td>

                <td class="text-center" style="padding: 1px 2px;"><span class="write-line"></span></td>
                <!-- Remarks removed
                <td style="font-size: 7.5px; color: #334155; padding: 1px 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.15;">
                  ${isBlank ? `<span style="display: block; border-bottom: 1px dotted #cbd5e1; height: 10px;"></span>` : (ath.remarks || "—")}
                </td> -->
              </tr>
            `).join("")}
          </tbody>
        </table>
      </div>

      <!-- Footer Signatures (4 columns horizontally, strictly docked at bottom) -->
      <div style="margin-top: auto; padding-top: 18px; border-top: 1.5px solid #0f172a; page-break-inside: avoid; break-inside: avoid; flex-shrink: 0;">
        <table class="no-border" style="width: 100%; border: none; margin-bottom: 2px; font-size: 8px; color: #475569;">
          <tr style="border: none;">
            <td style="border: none; padding: 0;">* Instructions: Take physical attendance on ground twice daily. Maintain signed register for official records and upload daily report.</td>
            <td style="border: none; padding: 0; text-align: right;">Page ${pageIdx + 1} of ${pages.length} &bull; ${team.team_name || team.coach_name}</td>
          </tr>
        </table>
        <table class="no-border" style="width: 100%; border: none; text-align: center;">
          <tr style="border: none;">
            <!-- 1. Assistant Coach 1 -->
            <td style="border: none; width: 25%; vertical-align: bottom; padding: 0 4px;">
              <div style="height: 42px; font-size: 7.5px; color: #94a3b8; text-transform: uppercase;">(Signature)</div>
              <div style="border-top: 1.2px solid #0f172a; padding-top: 2px;">
                <div style="font-weight: 800; color: #0f172a; font-size: 10px; line-height: 1.1;">
                  ${coach1Name}
                </div>
                <div style="font-size: 8px; color: #475569; font-weight: 600; margin-top: 1px;">Assistant Coach 1 (Sign)</div>
              </div>
            </td>

            <!-- 2. Assistant Coach 2 -->
            <td style="border: none; width: 25%; vertical-align: bottom; padding: 0 4px;">
              <div style="height: 42px; font-size: 7.5px; color: #94a3b8; text-transform: uppercase;">(Signature)</div>
              <div style="border-top: 1.2px solid #0f172a; padding-top: 2px;">
                <div style="font-weight: 800; color: #0f172a; font-size: 10px; line-height: 1.1;">
                  ${coach2Name}
                </div>
                <div style="font-size: 8px; color: #475569; font-weight: 600; margin-top: 1px;">Assistant Coach 2 (Sign)</div>
              </div>
            </td>

            <!-- 3. Camp In-Charge -->
            <td style="border: none; width: 25%; vertical-align: bottom; padding: 0 4px;">
              <div style="height: 42px; font-size: 7.5px; color: #94a3b8; text-transform: uppercase;">(Signature)</div>
              <div style="border-top: 1.2px solid #0f172a; padding-top: 2px;">
                <div style="font-weight: 800; color: #0f172a; font-size: 10px; line-height: 1.1;">
                  ${signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                </div>
                <div style="font-size: 8px; color: #475569; font-weight: 600; margin-top: 1px;">
                  ${signatures?.camp_incharge_title || "Camp In-Charge"}
                </div>
              </div>
            </td>

            <!-- 4. Official -->
            <td style="border: none; width: 25%; vertical-align: bottom; padding: 0 4px;">
              <div style="height: 42px; font-size: 7.5px; color: #94a3b8; text-transform: uppercase;">(Signature)</div>
              <div style="border-top: 1.2px solid #0f172a; padding-top: 2px;">
                <div style="font-weight: 800; color: #0f172a; font-size: 10px; line-height: 1.1;">
                  Official
                </div>
                <div style="font-size: 8px; color: #475569; font-weight: 600; margin-top: 1px;">
                  Samagra Shiksha / SAAP Official
                </div>
              </div>
            </td>
          </tr>
        </table>
      </div>
    </div>
    `;
    }).join("")}
</body>
</html>`;

    if (action === "print") {
      printHtmlViaIframe(fullHtml);
    } else {
      const campStartFmt = campStartDate || "2026-08-01";
      const campEndFmt = campEndDate || "2026-10-05";
      const scopeSuffix = singleDate ? `_${singleDate}` : `_Entire_Camp_${campStartFmt}_to_${campEndFmt}`;
      downloadHtmlAsPdf(fullHtml, isBlank ? `Attendance_Register_Blank${scopeSuffix}` : `Attendance_Register_Recorded${scopeSuffix}`, {
        orientation: "portrait",
        pageSelector: ".print-sheet",
        scale: 2,
      });
    }
  };

  const handlePrintRangeAttendance = async (
    isBlankOrOptions = true,
    overrideDate = null,
    actionType = "download",
    overrideScope = null,
    overrideTeam = null
  ) => {
    let isBlank = true;
    let action = "download";
    let scope = null;
    let singleDate = null;
    let teamName = null;

    if (typeof isBlankOrOptions === "object" && isBlankOrOptions !== null) {
      isBlank = isBlankOrOptions.isBlank ?? true;
      action = isBlankOrOptions.action ?? "download";
      scope = isBlankOrOptions.scope ?? null;
      singleDate = isBlankOrOptions.singleDate ?? isBlankOrOptions.overrideDate ?? null;
      teamName = isBlankOrOptions.teamName ?? isBlankOrOptions.overrideTeam ?? null;
    } else {
      isBlank = Boolean(isBlankOrOptions);
      singleDate = overrideDate;
      action = actionType || "download";
      scope = overrideScope;
      teamName = overrideTeam;
    }

    setIsPrintingRegister(true);

    const activeScope = scope || (singleDate ? "single" : rangeDateType);
    const campStart = campStartDate || (activeCamp?.start_date ? activeCamp.start_date.split("T")[0] : "2026-08-01");
    const campEnd = campEndDate || (activeCamp?.end_date ? activeCamp.end_date.split("T")[0] : "2026-10-05");

    let dateFrom, dateTo;
    if (activeScope === "single") {
      const candidateDate = singleDate || (rangeDateType === "single" ? rangeSingleDate : emptyFormatDate) || campStart;
      let validDate = candidateDate;
      dateFrom = validDate;
      dateTo = validDate;
    } else if (activeScope === "entire") {
      dateFrom = campStart;
      dateTo = campEnd;
    } else {
      dateFrom = (rangeDateType === "range" && rangeDateFrom) ? rangeDateFrom : campStart;
      dateTo = (rangeDateType === "range" && rangeDateTo) ? rangeDateTo : campEnd;
    }

    const teamFilter = teamName !== null ? teamName : (rangeTeamFilter || emptyTeamFilter || "");

    try {
      const res = await API.camp.getAttendanceRangeReport({
        camp_id: selectedCampId,
        date_from: dateFrom,
        date_to: dateTo,
        scope: activeScope === "entire" ? "entire" : undefined,
        single_date: activeScope === "single" ? dateFrom : undefined,
        team_name: teamFilter || undefined,
        coach_id: rangeCoachFilter || undefined,
        all_camp_dates: activeScope === "entire" || isBlank ? 1 : undefined,
        for_print: 1,
      });

      const teamsData = res?.teams || res?.coaches || res?.data?.teams || res?.data?.coaches || [];
      if (!teamsData || teamsData.length === 0) {
        Swal.fire("Notice", "No team attendance records found for the selected options.", "warning");
        return;
      }

      setRangeReportData(teamsData);
      setRangeFormatStyle(isBlank ? "blank" : "recorded");
      setPrintMode("daily_attendance");

      printDailyRegisterDocuments({
        coachesList: teamsData,
        isBlank,
        singleDate: activeScope === "single" ? dateFrom : null,
        action,
      });
      setTimeout(() => setPrintMode(null), 1500);
    } catch (err) {
      console.error("Failed to prepare daily register:", err);
      Swal.fire("Error", "Could not load daily attendance register for printing.", "error");
    } finally {
      setIsPrintingRegister(false);
    }
  };

  const handlePrintBlankAssessmentForm = async () => {
    try {
      const exportRes = await API.camp.createExportJob({
        export_type: "assessment_cards",
        camp_id: selectedCampId,
        title: "Athlete_Assessment_Form_Blank",
        params: {
          is_blank: true,
        },
      });
      const job = exportRes?.job || exportRes?.data?.job;
      if (job && job.id) {
        window.dispatchEvent(new CustomEvent("camp-export-job-started", { detail: job }));
        trackServerPdfExport(job.id, "Athlete_Assessment_Form_Blank");
        return;
      }
    } catch (err) {
      console.warn("Fallback to client assessment form PDF:", err);
    }
    setPrintMode("blank");
    setTimeout(async () => {
      await downloadDomPagesAsPdf(
        ".print-only-container",
        "Athlete_Assessment_Form_Blank",
        {
          orientation: "portrait",
          pageSelector: ".print-report-page",
          scale: 2,
        }
      );
      setPrintMode(null);
    }, 400);
  };

  const handleExportRangeAttendanceExcel = () => {
    if (!rangeReportData.length) {
      Swal.fire("Notice", "No attendance records loaded for this date range.", "info");
      return;
    }

    const rows = [];
    rangeReportData.forEach((coach) => {
      (coach.days || []).forEach((day) => {
        const dayNum = getCampDayNumber(day.date);
        (day.athletes || []).forEach((ath, idx) => {
          rows.push({
            "Sl. No": idx + 1,
            "Camp Day": dayNum ? `Day ${dayNum}` : "—",
            "Team / Squad": coach.team_name || "General Squad",
            "Coach(es)": coach.coach_name,
            "Discipline": coach.discipline || "N/A",
            "Date": day.date ? day.date.split("-").reverse().join("-") : day.date,
            "Athlete Name": ath.athlete_name,
            "ID Number": ath.id_number || ath.bib_no || "—",
            "Category": ath.category || "N/A",
            "District": ath.district || "—",
            "Morning In Time": ath.morning_in || "—",
            "Morning Out Time": ath.morning_out || "—",
            "Morning Status": ath.morning_status?.toUpperCase() || "—",
            "Evening In Time": ath.evening_in || "—",
            "Evening Out Time": ath.evening_out || "—",
            "Evening Status": ath.evening_status?.toUpperCase() || "—",
            "Remarks": ath.remarks || "",
          });
        });
      });
    });

    if (!rows.length) {
      Swal.fire("Notice", "No athlete rows found in the selected range.", "info");
      return;
    }

    const campStartFmt = campStartDate || "2026-08-01";
    const campEndFmt = campEndDate || "2026-10-05";
    const fileSuffix = rangeDateType === "single" ? rangeSingleDate : `${campStartFmt}_to_${campEndFmt}`;
    exportToExcel(rows, `Daily_Attendance_Register_${fileSuffix}`);
  };

  const handleExportEmptyAttendanceExcel = async () => {
    setIsPrintingRegister(true);
    try {
      const campStart = campStartDate || "2026-08-01";
      const campEnd = campEndDate || "2026-10-05";
      const isSingle = emptyScope === "day";
      const candidateDate = emptyFormatDate || campStart;
      let chosenDate = candidateDate;
      if (campStart && chosenDate < campStart) chosenDate = campStart;
      if (campEnd && chosenDate > campEnd) chosenDate = campEnd;

      const dateFrom = isSingle ? chosenDate : campStart;
      const dateTo = isSingle ? chosenDate : campEnd;

      const res = await API.camp.getAttendanceRangeReport({
        camp_id: selectedCampId,
        date_from: dateFrom,
        date_to: dateTo,
        scope: isSingle ? undefined : "entire",
        single_date: isSingle ? chosenDate : undefined,
        team_name: emptyTeamFilter || undefined,
      });

      const teamsData = res?.teams || res?.coaches || [];
      if (!teamsData.length) {
        Swal.fire("Notice", "No team attendance records found to export.", "warning");
        return;
      }

      const rows = [];
      let slNo = 1;
      teamsData.forEach((team) => {
        const teamName = team.team_name || team.coach_name || "General Squad";
        const coachNames = team.coaches?.map((c) => c.name).join(" & ") || team.coach_name || "Assistant Coach";
        (team.days || []).forEach((day) => {
          const dayNum = getCampDayNumber(day.date);
          (day.athletes || []).forEach((ath) => {
            rows.push({
              "Sl. No": slNo++,
              "Camp Day": dayNum ? `Day ${dayNum}` : "—",
              "Date": day.date ? day.date.split("-").reverse().join("-") : "—",
              "Team / Squad": teamName,
              "Coach(es)": coachNames,
              "Discipline": team.discipline || ath.discipline || "—",
              "ID Number": ath.id_number || ath.bib_no || "—",
              "Athlete Name": ath.athlete_name || "",
              "Category": ath.category || "—",
              "District": ath.district || "—",
              "Morning In Time": "",
              "Morning Out Time": "",
              "Morning Status (P/A)": "",
              "Evening In Time": "",
              "Evening Out Time": "",
              "Evening Status (P/A)": "",
              "Remarks / Sign": "",
            });
          });
        });
      });

      if (!rows.length) {
        Swal.fire("Notice", "No athlete rows generated.", "info");
        return;
      }

      const fileName = isSingle
        ? `Empty_Attendance_Register_${chosenDate}`
        : `Empty_Attendance_Register_Camp_${campStart}_to_${campEnd}`;
      exportToExcel(rows, fileName);
    } catch (err) {
      console.error("Failed to export empty attendance excel:", err);
      Swal.fire("Error", "Could not export empty attendance register to Excel.", "error");
    } finally {
      setIsPrintingRegister(false);
    }
  };

  const handleUploadDailyReport = async (e) => {
    e.preventDefault();
    if (!uploadReportForm.file) {
      Swal.fire("Validation Error", "Please select a report file to upload.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("camp_id", selectedCampId || activeCamp?.id);
    formData.append("date", uploadReportForm.date);
    formData.append("report_type", uploadReportType);
    if (uploadReportForm.coach_id) {
      formData.append("coach_id", uploadReportForm.coach_id);
    }
    if (uploadReportType === "workout_performance") {
      if (uploadReportForm.athlete_id) formData.append("athlete_id", uploadReportForm.athlete_id);
      if (uploadReportForm.training_session) formData.append("training_session", uploadReportForm.training_session);
      if (uploadReportForm.activity_details) formData.append("activity_details", uploadReportForm.activity_details);
      if (uploadReportForm.performance_rating) formData.append("performance_rating", uploadReportForm.performance_rating);
      if (uploadReportForm.remarks) formData.append("remarks", uploadReportForm.remarks);
    }
    formData.append("file", uploadReportForm.file);

    setUploadingReport(true);
    try {
      const res = await API.camp.uploadDailyAttendanceReport(formData);
      Swal.fire("Uploaded!", res?.data?.message || (uploadReportType === "workout_performance" ? "Daily workout & performance report uploaded successfully." : "Final daily attendance report uploaded successfully."), "success");
      setUploadReportForm({
        date: todayIST,
        coach_id: "",
        athlete_id: "",
        training_session: "both",
        activity_details: "",
        performance_rating: "Good",
        remarks: "",
        file: null,
      });
      fetchDailyReportsList(selectedCampId, reportCoachFilter, reportTypeFilter);
    } catch (err) {
      console.error("Upload error:", err);
      Swal.fire("Error", err?.response?.data?.message || "Failed to upload report.", "error");
    } finally {
      setUploadingReport(false);
    }
  };

  const fetchDailyReportsList = async (campId = selectedCampId, coachId = reportCoachFilter, reportType = reportTypeFilter) => {
    setUploadedReportsLoading(true);
    try {
      const res = await API.camp.getDailyAttendanceReports({
        camp_id: campId || selectedCampId,
        coach_id: coachId && coachId !== "all" ? coachId : undefined,
        report_type: reportType && reportType !== "all" ? reportType : undefined,
      });
      const list = res?.data?.reports || res?.reports || [];
      setUploadedReports(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load daily reports list:", err);
    } finally {
      setUploadedReportsLoading(false);
    }
  };

  const handleExportEmptyExcel = () => {
    const list = athletes.length > 0 ? athletes : [
      { name: "Sample Athlete 1", bib_no: "PSCC0001", district: "Guntur", category: "T44", discipline_name: "Athletics" },
      { name: "Sample Athlete 2", bib_no: "PSCC0002", district: "Krishna", category: "SL3", discipline_name: "Para Badminton" },
    ];

    const emptyTemplate = list.map((a, idx) => ({
      "Sl. No": idx + 1,
      "Athlete Name": a.name || "",
      "BIB / Chest No.": a.bib_no || "",
      "District": a.district || "",
      "Category": a.category || "",
      "Discipline": a.discipline_name || "",
      "Endurance (Ex/Gd/Avg/Pr)": "",
      "Strength (Ex/Gd/Avg/Pr)": "",
      "Agility (Ex/Gd/Avg/Pr)": "",
      "Skill (Ex/Gd/Avg/Pr)": "",
      "Technique (Ex/Gd/Avg/Pr)": "",
      "Discipline (Ex/Gd/Avg/Pr)": "",
      "Overall Rating (Ex/Gd/Avg/Pr)": "",
      "Coach Remarks": "",
    }));

    exportToExcel(emptyTemplate, `Empty_Report_Format_${activeCamp?.name || "Camp"}`);
  };

  // Navigation tabs list
  const tabs = [
    { id: "overview", label: "Overview & Stats", icon: FaChartLine },
    { id: "camps", label: "Camps Manager", icon: FaCampground },
    { id: "coaches", label: "Coaches", icon: FaUserShield },
    { id: "coach_profiles", label: "Coach Profiles", icon: FaAward },
    { id: "athletes", label: "Athletes", icon: FaRunning },
    { id: "kits", label: "Kit & Measurements", icon: FaTrophy },
    { id: "teams", label: "Team Management", icon: FaUsers },
    { id: "officials", label: "Officials", icon: FaUserTie },
    { id: "id_cards", label: "ID Badges", icon: FaIdCard },
    { id: "mappings", label: "Coach Mapping", icon: FaExchangeAlt },
    { id: "room_incharges", label: "Room Incharges & Medical", icon: FaUserShield },
    { id: "attendance", label: "Attendance Monitor", icon: FaCalendarCheck },
    { id: "report_cards", label: "Report Cards", icon: FaClipboardCheck },
    { id: "weekly_reports", label: "Weekly Reports", icon: FaFileAlt },
    { id: "camp_reports", label: "Camp Assessments", icon: FaChartLine },
    { id: "centralized_reports", label: "Centralized Reports", icon: FaUsers },
    { id: "empty_formats", label: "Empty Formats", icon: FaPrint },
    { id: "settings", label: "Settings", icon: FaCog },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      {/* =========================================================
          PRINT-ONLY CSS STYLES
      ========================================================= */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media screen {
            .print-only-container {
              display: none !important;
            }
          }
          @media print {
            body, html, #root, .min-h-screen {
              background-color: #ffffff !important;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              display: block !important;
            }
            .print\\:hidden, .print-screen-only {
              display: none !important;
            }
            .print-only-container {
              display: block !important;
              visibility: visible !important;
              width: 100% !important;
            }
            @page {
              size: A4 portrait;
              margin: 8mm 10mm 8mm 10mm;
            }
            .print-day-paper {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-sizing: border-box !important;
              background-color: #ffffff !important;
              color: #000000 !important;
              margin: 0 !important;
              padding: 4mm 6mm !important;
              width: 100% !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
            }
            .print-day-paper:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .print-day-paper table,
            .print-day-paper th,
            .print-day-paper td {
              border-color: #000000 !important;
              color: #000000 !important;
            }
            .print-report-page {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-sizing: border-box !important;
              background-color: #ffffff !important;
              color: #000000 !important;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 3mm 5mm !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .print-report-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .individual-student-page {
              page-break-after: always !important;
              break-after: page !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
              box-sizing: border-box !important;
              margin: 0 !important;
              padding: 3mm 5mm 2mm 5mm !important;
              height: 270mm !important;
              max-height: 270mm !important;
              overflow: hidden !important;
              display: flex !important;
              flex-direction: column !important;
              justify-content: space-between !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              background: #ffffff !important;
              background-color: #ffffff !important;
            }
            .individual-student-page:last-child {
              page-break-after: auto !important;
              break-after: auto !important;
            }
            .individual-student-page > div:last-child {
              page-break-before: avoid !important;
              break-before: avoid !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .print-report-page table {
              width: 100% !important;
              border-collapse: collapse !important;
              border: 1.5px solid #000000 !important;
              margin-top: 4px !important;
              margin-bottom: 4px !important;
            }
            .print-report-page th,
            .print-report-page td {
              border: 1px solid #000000 !important;
              color: #000000 !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .print-report-page th {
              background-color: #f1f5f9 !important;
              font-weight: 800 !important;
            }
            .print-report-page .report-sig-row {
              display: table !important;
              width: 100% !important;
              margin-top: 6px !important;
            }
            .print-report-page .report-sig-cell {
              display: table-cell !important;
              width: 33.333% !important;
              text-align: center !important;
              vertical-align: top !important;
              padding: 0 8px !important;
            }
            .print-report-page .report-info-table {
              display: table !important;
              width: 100% !important;
            }
            .print-report-page .report-info-row {
              display: table-row !important;
            }
            .print-report-page .report-info-col {
              display: table-cell !important;
              width: 50% !important;
              padding: 2px 4px !important;
              vertical-align: top !important;
            }
          }
        `,
      }} />

      {/* =========================================================
          SCREEN UI (Hidden when printing)
      ========================================================= */}
      <div className="print:hidden print-screen-only flex flex-col flex-1">
        <Navbar />

        {/* Standard Admin Header matching ManagePlayers.js & ManageEvents.js */}
        <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad flex-1 w-full flex flex-col">
          <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">
                  Official Administration &bull; Coaching Camp System
                </p>
                <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1 flex items-center gap-3">
                  <FaCampground className="text-cyan-700" /> Coaching Camp Management System
                </h1>
                <p className="text-sm text-slate-600 mt-1">
                  Manage coaching camps, coach assignments, player attendance, progress reporting, and state assessments.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCamp(null);
                      setCampForm({
                        name: "",
                        code: "",
                        venue: "",
                        district: "",
                        start_date: "",
                        end_date: "",
                        description: "",
                        status: "ongoing",
                        is_active: true,
                      });
                      setCampModalOpen(true);
                    }}
                    className="text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-all flex items-center gap-2 text-xs bg-cyan-700 hover:bg-cyan-800"
                  >
                    <FaPlus /> Create New Camp
                  </button>
                </div>
              </div>

              {/* Active / Selected Camp Selector Box */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                <div>
                  <span className="text-[10px] text-slate-500 font-bold block uppercase tracking-wider">
                    Active / Selected Camp
                  </span>
                  <span className="text-xs text-cyan-800 font-bold">
                    {activeCamp?.name || "Select Camp"}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={selectedCampId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      setSelectedCampId(newId);
                      try {
                        sessionStorage.setItem("parasports_selected_camp_id", String(newId));
                      } catch (_e) { }
                      const matched = camps.find((c) => String(c.id) === String(newId));
                      if (matched) {
                        if (matched.from_date || matched.start_date) setRangeDateFrom(matched.from_date || matched.start_date);
                        if (matched.to_date || matched.end_date) setRangeDateTo(matched.to_date || matched.end_date);
                      }
                    }}
                    className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 max-w-xs shadow-sm"
                  >
                    {camps.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.is_active ? "(Active)" : ""}
                      </option>
                    ))}
                  </select>
                  {tabLoading && (
                    <FaSpinner className="animate-spin text-cyan-600 text-sm shrink-0" title="Loading camp data..." />
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Tab Navigation Pill Bar matching standard admin styling */}
          <div className="flex flex-wrap gap-1.5 mb-6 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === tab.id
                  ? "bg-cyan-700 text-white shadow-sm font-bold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
              >
                <tab.icon className="text-xs" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Main Content Area */}
          <main className="flex-1 w-full space-y-6">
            {/* ============================================================== */}
            {/* TAB 1: OVERVIEW & STATS */}
            {/* ============================================================== */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider block">
                          Total Coaches
                        </span>
                        <h3 className="text-3xl font-bold text-slate-900 mt-1">
                          {dashboardStats?.total_coaches ?? 0}
                        </h3>
                        <p className="text-xs text-emerald-400 mt-1">
                          {dashboardStats?.active_coaches ?? 0} Active &bull;{" "}
                          <span className="text-rose-400">{dashboardStats?.disabled_coaches ?? 0} Disabled</span>
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center text-xl">
                        <FaUserShield />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider block">
                          Total Athletes
                        </span>
                        <h3 className="text-3xl font-bold text-slate-900 mt-1">
                          {dashboardStats?.total_athletes ?? 0}
                        </h3>
                        <p className="text-xs text-indigo-300 mt-1">
                          {dashboardStats?.mapped_athletes ?? 0} Assigned to Coaches
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center text-xl">
                        <FaRunning />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider block">
                          Today's Attendance
                        </span>
                        <h3 className="text-3xl font-bold text-emerald-700 mt-1">
                          {dashboardStats?.today_attendance?.morning_present ?? 0}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          Morning: {dashboardStats?.today_attendance?.morning_present ?? 0} &bull; Evening:{" "}
                          {dashboardStats?.today_attendance?.evening_present ?? 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center text-xl">
                        <FaCalendarCheck />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-[11px] text-slate-500 font-bold uppercase tracking-wider block">
                          Assessments Completed
                        </span>
                        <h3 className="text-3xl font-bold text-purple-700 mt-1">
                          {dashboardStats?.week1_assessments ?? 0}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          Week 1: {dashboardStats?.week1_assessments ?? 0} &bull; Week 9:{" "}
                          {dashboardStats?.week9_assessments ?? 0}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center text-xl">
                        <FaChartLine />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Breakdown Grids */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* By Discipline */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <FaRunning className="text-amber-400" /> Athletes by Sports Discipline
                    </h3>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {dashboardStats?.by_discipline?.map((d, i) => (
                        <div
                          key={i}
                          className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs"
                        >
                          <span className="font-semibold text-slate-800">{d.discipline_name}</span>
                          <span className="font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300">
                            {d.count} Athletes
                          </span>
                        </div>
                      ))}
                      {(!dashboardStats?.by_discipline || !dashboardStats.by_discipline.length) && (
                        <p className="text-xs text-slate-500 text-center py-6">No athletes recorded yet.</p>
                      )}
                    </div>
                  </div>

                  {/* By District */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <FaUsers className="text-indigo-400" /> Athletes by District (Top 10)
                    </h3>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {dashboardStats?.by_district?.map((d, i) => (
                        <div
                          key={i}
                          className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs"
                        >
                          <span className="font-semibold text-slate-800">{d.district}</span>
                          <span className="font-bold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300">
                            {d.count}
                          </span>
                        </div>
                      ))}
                      {(!dashboardStats?.by_district || !dashboardStats.by_district.length) && (
                        <p className="text-xs text-slate-500 text-center py-6">No district data available.</p>
                      )}
                    </div>
                  </div>

                  {/* Athletes by Coach */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <FaUserShield className="text-emerald-400" /> Athletes by Coach
                    </h3>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {dashboardStats?.by_coach?.map((c) => (
                        <div
                          key={c.id}
                          className="p-2.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between text-xs"
                        >
                          <div>
                            <span className="font-bold text-slate-900 block">{c.name}</span>
                            <span className="text-[10px] text-slate-500">{c.district}</span>
                          </div>
                          <span className="font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                            {c.athletes_count} Assigned
                          </span>
                        </div>
                      ))}
                      {(!dashboardStats?.by_coach || !dashboardStats.by_coach.length) && (
                        <p className="text-xs text-slate-500 text-center py-6">No coaches assigned yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 2: CAMPS MANAGER */}
            {/* ============================================================== */}
            {activeTab === "camps" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Coaching Camps Master</h2>
                    <p className="text-xs text-slate-500">
                      Create and manage multiple camps; all athletes, attendance, and reports stay organized per camp.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingCamp(null);
                      setCampForm({
                        name: "",
                        code: "",
                        venue: "",
                        district: "",
                        start_date: "",
                        end_date: "",
                        description: "",
                        status: "ongoing",
                        is_active: true,
                      });
                      setCampModalOpen(true);
                    }}
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2"
                  >
                    <FaPlus /> Create New Camp
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {camps.map((camp) => (
                    <div
                      key={camp.id}
                      className={`p-5 rounded-2xl border transition-all ${camp.is_active
                        ? "bg-white border-amber-500/80 shadow-lg shadow-amber-500/10"
                        : "bg-white border-slate-200/60"
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${camp.is_active
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : "bg-slate-700 text-slate-400"
                              }`}
                          >
                            {camp.is_active ? "Current Active Camp" : camp.status}
                          </span>
                          <h3 className="text-base font-bold text-slate-900 mt-1.5">{camp.name}</h3>
                          <p className="text-xs text-slate-500">{camp.venue || "Venue not set"} &bull; {camp.district}</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-200/60 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-500 text-[10px] block">Duration</span>
                          <span className="text-slate-700 font-semibold">
                            {camp.start_date || "TBD"} to {camp.end_date || "TBD"}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500 text-[10px] block">Mapped Athletes</span>
                          <span className="text-slate-700 font-semibold">
                            {camp.mapped_athletes_count ?? 0} Athletes
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-200/60 flex items-center justify-between">
                        {!camp.is_active ? (
                          <button
                            onClick={() => handleSetActiveCamp(camp.id)}
                            className="text-xs font-semibold text-amber-400 hover:text-amber-300"
                          >
                            Set as Active
                          </button>
                        ) : (
                          <span className="text-xs text-emerald-400 font-semibold flex items-center gap-1">
                            <FaCheckCircle className="text-[10px]" /> Default Active
                          </span>
                        )}

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingCamp(camp);
                              setCampForm({
                                name: camp.name,
                                code: camp.code || "",
                                venue: camp.venue || "",
                                district: camp.district || "",
                                start_date: camp.start_date ? camp.start_date.slice(0, 10) : "",
                                end_date: camp.end_date ? camp.end_date.slice(0, 10) : "",
                                description: camp.description || "",
                                status: camp.status,
                                is_active: camp.is_active,
                              });
                              setCampModalOpen(true);
                            }}
                            className="p-1.5 text-slate-400 hover:text-slate-700 rounded"
                            title="Edit Camp"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 3: COACHES MANAGEMENT */}
            {/* ============================================================== */}
            {activeTab === "coaches" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Coaches Management</h2>
                    <p className="text-xs text-slate-500">
                      Register coaches with multi-discipline capabilities and manage portal login access.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setEditingCoach(null);
                      setCoachForm({
                        player_id: null,
                        selected_player: null,
                        name: "",
                        father_name: "",
                        mobile: "",
                        email: "",
                        district: "",
                        team_name: "",
                        disciplines: [],
                        password: "",
                      });
                      setPlayerSearchTerm("");
                      fetchEligiblePlayers("");
                      setCoachModalOpen(true);
                    }}
                    className="px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 self-start sm:self-auto"
                  >
                    <FaUserPlus /> Appoint Player as Coach
                  </button>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Coach Details</th>
                          <th className="p-3.5">Mobile</th>
                          <th className="p-3.5">District</th>
                          <th className="p-3.5">Sports Disciplines</th>
                          <th className="p-3.5 text-center">Assigned Athletes</th>
                          <th className="p-3.5 text-center">Portal Login Status</th>
                          <th className="p-3.5 pr-5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {coaches.map((coach) => (
                          <tr key={coach.id} className="hover:bg-slate-50/80">
                            <td className="p-3.5 pl-5">
                              <div className="flex items-center gap-3">
                                {coach.photo || coach.player_photo ? (
                                  <img
                                    src={resolvePhoto(coach.photo || coach.player_photo)}
                                    alt={coach.name}
                                    className="w-11 h-11 rounded-full object-cover border-2 border-cyan-200 shadow-sm"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/placeholder-avatar.png";
                                    }}
                                  />
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-cyan-800 text-white flex items-center justify-center font-bold text-xs shadow-sm border border-slate-200">
                                    {coach.name?.charAt(0) || "C"}
                                  </div>
                                )}
                                <div>
                                  <div className="font-bold text-slate-900 text-sm flex items-center gap-1.5">
                                    {coach.name}
                                    {coach.player_id && (
                                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-bold">
                                        Player
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    Father: {coach.father_name || "N/A"}
                                  </div>
                                  {coach.registration_id && (
                                    <div className="text-[10px] text-cyan-800 font-mono font-semibold">
                                      Reg ID: {coach.registration_id}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="p-3.5 font-mono text-slate-700 font-semibold">
                              {coach.mobile}
                            </td>

                            <td className="p-3.5 font-medium text-slate-700">
                              {coach.district || "N/A"}
                            </td>

                            <td className="p-3.5">
                              <div className="flex flex-wrap gap-1 max-w-xs">
                                {coach.disciplines?.map((d) => (
                                  <span
                                    key={d.id}
                                    className="px-2 py-0.5 rounded-md bg-indigo-500/20 text-indigo-700 border border-indigo-500/30 text-[10px] font-semibold"
                                  >
                                    {d.discipline_name}
                                  </span>
                                ))}
                              </div>
                            </td>

                            <td className="p-3.5 text-center font-bold text-amber-600 text-sm">
                              {coach.athletes_count ?? 0}
                            </td>

                            <td className="p-3.5 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 ${coach.status === "active"
                                  ? "bg-emerald-500/20 text-emerald-700 border border-emerald-500/40"
                                  : "bg-rose-500/20 text-rose-700 border border-rose-500/40"
                                  }`}
                              >
                                {coach.status === "active" ? (
                                  <>
                                    <FaCheckCircle className="text-[10px]" /> Enabled
                                  </>
                                ) : (
                                  <>
                                    <FaTimesCircle className="text-[10px]" /> Disabled
                                  </>
                                )}
                              </span>
                            </td>

                            <td className="p-3.5 pr-5 text-right space-x-2">
                              <button
                                onClick={() => handleToggleCoachStatus(coach.id)}
                                title={coach.status === "active" ? "Disable Login" : "Enable Login"}
                                className={`p-1.5 rounded ${coach.status === "active"
                                  ? "text-amber-600 hover:bg-amber-500/10"
                                  : "text-emerald-600 hover:bg-emerald-500/10"
                                  }`}
                              >
                                {coach.status === "active" ? <FaLock /> : <FaUnlock />}
                              </button>

                              <button
                                onClick={() => handleResetCoachPassword(coach)}
                                title="Reset Password"
                                className="p-1.5 text-sky-600 hover:bg-sky-500/10 rounded"
                              >
                                <FaKey />
                              </button>

                              <button
                                onClick={() => handleViewSingleIdCard(coach, "coach")}
                                title="View & Print Coach ID Card"
                                className="p-1.5 text-amber-600 hover:bg-amber-500/10 rounded"
                              >
                                <FaIdCard />
                              </button>

                              <button
                                onClick={() => {
                                  setEditingCoach(coach);
                                  setCoachForm({
                                    player_id: coach.player_id || null,
                                    selected_player: coach.player || null,
                                    name: coach.name,
                                    father_name: coach.father_name || "",
                                    mobile: coach.mobile,
                                    email: coach.email || "",
                                    district: coach.district || "",
                                    team_name: coach.team_name || "",
                                    disciplines: coach.disciplines?.map((d) => d.discipline_id || d.discipline_name) || [],
                                    password: "",
                                  });
                                  setCoachModalOpen(true);
                                }}
                                title="Edit Coach"
                                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg"
                              >
                                <FaEdit />
                              </button>

                              <button
                                onClick={() => handleDeleteCoach(coach.id)}
                                title="Delete Coach"
                                className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded"
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 3.5: COACH PROFILES (WITH ACHIEVEMENTS) */}
            {/* ============================================================== */}
            {activeTab === "coach_profiles" && (
              <div className="space-y-4 animate-fadeIn">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <FaUserShield className="text-cyan-700" /> Coach Profiles & Achievements Directory
                    </h2>
                    <p className="text-xs text-slate-500">
                      Comprehensive profiles of appointed coaches with their verified sporting achievements and certificates.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {coaches.map((coach) => (
                    <div
                      key={coach.id}
                      className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden flex flex-col justify-between"
                    >
                      <div className="p-5 flex flex-col items-center text-center bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
                        <div className="relative mb-3">
                          {coach.photo || coach.player_photo ? (
                            <img
                              src={resolvePhoto(coach.photo || coach.player_photo)}
                              alt={coach.name}
                              className="w-20 h-20 rounded-full object-cover border-2 border-indigo-200 shadow-sm"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/placeholder-avatar.png";
                              }}
                            />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-cyan-800 text-white flex items-center justify-center font-bold text-xl shadow-sm border border-slate-200">
                              {coach.name?.charAt(0) || "C"}
                            </div>
                          )}
                          <span
                            className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${coach.status === "active" ? "bg-emerald-500" : "bg-rose-500"
                              }`}
                          />
                        </div>

                        <h3 className="font-bold text-slate-900 text-sm">{coach.name}</h3>
                        <div className="text-xs font-semibold text-cyan-800 mt-0.5">
                          {coach.team_name || "Squad Not Assigned"}
                        </div>
                        {coach.district && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            District: {coach.district}
                          </div>
                        )}
                        {coach.registration_id && (
                          <div className="text-[10px] text-indigo-700 font-mono font-semibold mt-0.5">
                            Reg ID: {coach.registration_id}
                          </div>
                        )}
                      </div>

                      <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
                            Disciplines
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {coach.disciplines?.map((d, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px] font-semibold border border-slate-200"
                              >
                                {d.discipline_name || d.name || d}
                              </span>
                            ))}
                          </div>
                        </div>

                        <button
                          onClick={() => openCoachProfileModal(coach)}
                          className="w-full py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center justify-center gap-1.5"
                        >
                          <FaAward /> View Full Profile & Achievements
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 4: ATHLETES MANAGEMENT */}
            {/* ============================================================== */}
            {activeTab === "athletes" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                      <span>Camp Athletes &amp; Squad Rosters</span>
                      <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200">
                        {activeAthletes.length} Active in Camp
                      </span>
                      {removedAthletes.length > 0 && (
                        <span className="text-xs font-semibold bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full border border-rose-200">
                          {removedAthletes.length} Removed from Camp
                        </span>
                      )}
                    </h2>
                    <p className="text-xs text-slate-500">
                      Directly get registered portal players into this coaching camp and allot their assistant coach &amp; team. No separate registration needed!
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                    <button
                      onClick={() => openEnrollPlayerModal()}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl shadow-md text-xs font-bold flex items-center gap-2 transition active:scale-95"
                    >
                      <FaUserPlus className="text-sm" /> Get Player to Camp &amp; Allot Coach
                    </button>

                    <a
                      href="/admin/manage-players"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition border border-slate-200"
                      title="Manage master player registrations in portal"
                    >
                      <span>Portal Players</span>
                      <FaExternalLinkAlt className="text-[10px] text-slate-400" />
                    </a>
                  </div>
                </div>

                {/* Status Sub-Tabs: Active in Camp vs Removed from Camp */}
                <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl w-fit">
                  <button
                    type="button"
                    onClick={() => setAthleteStatusTab("active")}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${athleteStatusTab === "active"
                      ? "bg-white text-emerald-800 shadow-xs border border-slate-200"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <span>Active in Camp</span>
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0.2 rounded-full font-black">
                      {activeAthletes.length}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAthleteStatusTab("removed")}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${athleteStatusTab === "removed"
                      ? "bg-white text-rose-800 shadow-xs border border-slate-200"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <span>Removed from Camp</span>
                    <span className="bg-rose-100 text-rose-800 text-[10px] px-1.5 py-0.2 rounded-full font-black">
                      {removedAthletes.length}
                    </span>
                  </button>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Photo</th>
                          <th className="p-3.5">Athlete Info</th>
                          <th className="p-3.5">ID Number</th>
                          <th className="p-3.5">District</th>
                          <th className="p-3.5">Category</th>
                          <th className="p-3.5">Sport – Game – Category</th>
                          <th className="p-3.5">Assigned Assistant Coach</th>
                          <th className="p-3.5 pr-5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(athleteStatusTab === "removed" ? removedAthletes : activeAthletes).length === 0 && (
                          <tr>
                            <td colSpan={8} className="p-8 text-center text-slate-400 text-xs font-semibold">
                              {athleteStatusTab === "removed"
                                ? "No athletes have been removed from this camp."
                                : "No active athletes enrolled in this camp yet."}
                            </td>
                          </tr>
                        )}
                        {(athleteStatusTab === "removed" ? removedAthletes : activeAthletes).map((athlete) => {
                          const photoUrl = getSafePhotoUrl(athlete.photo);
                          const profileCategories = Array.isArray(athlete.player_categories) ? athlete.player_categories : [];

                          return (
                            <tr key={athlete.id} className="hover:bg-slate-50/80">
                              <td className="p-3.5 pl-5">
                                <img
                                  src={photoUrl}
                                  alt={athlete.name}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = DEFAULT_AVATAR;
                                  }}
                                  className="w-10 h-10 rounded-xl object-cover bg-white border border-slate-200"
                                />
                              </td>

                              <td className="p-3.5">
                                <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                                  <span>{athlete.name}</span>
                                  {athlete.player_registration_id && (
                                    <span className="font-mono text-[10px] font-semibold bg-slate-100 text-slate-700 px-1.5 py-0.2 rounded border border-slate-200">
                                      {athlete.player_registration_id}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[11px] text-slate-500">
                                  Father: {athlete.father_name || "N/A"} &bull; Phone: {athlete.parent_mobile}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  {athlete.room_no ? (
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200">
                                      Rm {athlete.room_no}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] font-medium text-slate-400 italic">
                                      No Room
                                    </span>
                                  )}
                                  {(athlete.medicines || athlete.health_issues) && (
                                    <span
                                      title={`Health: ${athlete.health_issues || "None"} | Meds: ${athlete.medicines || "None"} (${athlete.medicine_timings || "N/A"})`}
                                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1 cursor-help"
                                    >
                                      💊 Rx Meds
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td className="p-3.5">
                                <span className="font-mono font-bold text-cyan-900 bg-cyan-50 border border-cyan-200 px-2.5 py-1 rounded-lg text-xs shadow-xs">
                                  {athlete.id_number || athlete.bib_no || "N/A"}
                                </span>
                              </td>

                              <td className="p-3.5 font-medium text-slate-700">
                                {athlete.district}
                              </td>

                              <td className="p-3.5">
                                {(profileCategories.length > 0 || athlete.category) ? (
                                  <div className="flex flex-col items-start gap-1">
                                    {profileCategories.length > 0 ? profileCategories.map((category) => (
                                      <div
                                        key={category.player_category_id}
                                        className="inline-flex items-center overflow-hidden rounded-lg border border-indigo-200 bg-indigo-50 text-[10px] font-bold text-indigo-700"
                                      >
                                        <span className="px-2.5 py-1">
                                          {category.sport_name}: {category.classification}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleOpenDisciplineCategoryModal(athlete, category)}
                                          title={`Change ${category.sport_name} classification`}
                                          className="self-stretch border-l border-indigo-200 px-2 text-indigo-600 transition-colors hover:bg-indigo-100 hover:text-indigo-900"
                                        >
                                          <FaEdit />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteAthleteClassification(athlete, category)}
                                          title={`Delete ${category.sport_name} classification`}
                                          className="self-stretch border-l border-rose-200 bg-rose-50 px-2 text-rose-600 transition-colors hover:bg-rose-100 hover:text-rose-800"
                                        >
                                          <FaTrash />
                                        </button>
                                      </div>
                                    )) : (
                                      <button
                                        type="button"
                                        onClick={() => handleOpenDisciplineCategoryModal(athlete)}
                                        title="Change classification category"
                                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-left text-[10px] font-bold text-indigo-700 transition-colors hover:bg-indigo-100"
                                      >
                                        {athlete.category}
                                        <FaEdit className="ml-1 inline text-[9px] opacity-60" />
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => handleOpenDisciplineCategoryModal(athlete)}
                                    title="Click to assign classification category"
                                    className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium text-[11px] border border-dashed border-slate-300 transition-colors inline-flex items-center gap-1"
                                  >
                                    <FaPlus className="text-[9px]" /> Class Pending
                                  </button>
                                )}
                              </td>

                              <td className="p-3.5">
                                <div className="flex flex-col gap-1">
                                  <div className="flex flex-wrap items-center gap-1">
                                    {(Array.isArray(athlete.allocated_disciplines) && athlete.allocated_disciplines.length > 0
                                      ? athlete.allocated_disciplines
                                      : [athlete.discipline_name].filter(Boolean)
                                    ).map((disp, dIdx) => {
                                      const gameName = typeof disp === "string" ? disp : disp.discipline_name || disp.name;
                                      const sportId = typeof disp === "object" ? disp.sport_id : athlete.discipline_id;
                                      const sportName = (typeof disp === "object" ? disp.sport_name : "") || athlete.discipline_name || "Sport pending";
                                      const profileCategory = getAthleteProfileCategory(athlete, sportId, sportName);
                                      const categoryName = profileCategory?.classification || (typeof disp === "object" ? disp.category : "") || athlete.category || "Category pending";
                                      const displayedGame = String(gameName || "").trim().toLowerCase() === String(sportName).trim().toLowerCase()
                                        ? "Game pending"
                                        : gameName || "Game pending";
                                      const rosterDiscipline = `${sportName} – ${displayedGame} – ${categoryName}`;
                                      const performanceSummary = typeof disp === "object" ? [
                                        disp.athlete_performance && `Camp trial: ${disp.athlete_performance}`,
                                        (disp.national_benchmark || disp.national_gold_performance)
                                          && `Previous national benchmark: ${disp.national_benchmark || disp.national_gold_performance}`,
                                      ].filter(Boolean).join(" | ") : "";
                                      return (
                                        <span
                                          key={dIdx}
                                          className={`px-2 py-0.5 rounded-md font-bold text-[10px] border ${dIdx === 0
                                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                            : dIdx === 1
                                              ? "bg-cyan-50 text-cyan-800 border-cyan-200"
                                              : "bg-indigo-50 text-indigo-800 border-indigo-200"
                                            }`}
                                          title={`Allotted Game #${dIdx + 1}${performanceSummary ? ` — ${performanceSummary}` : ""}`}
                                        >
                                          <span className="opacity-70 mr-0.5 text-[9px]">#{dIdx + 1}</span>
                                          {rosterDiscipline}
                                        </span>
                                      );
                                    })}
                                    {(!athlete.allocated_disciplines || athlete.allocated_disciplines.length === 0) && !athlete.discipline_name && (
                                      <span className="px-2 py-0.5 rounded-md text-[10px] bg-amber-50 text-amber-800 border border-dashed border-amber-300 font-medium">
                                        Sport Pending
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenSportsAllotmentModal(athlete)}
                                      className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                                      title="Allot, change, or delete games"
                                    >
                                      <FaRunning className="text-[9px]" /> Allot Games
                                    </button>
                                  </div>
                                </div>
                              </td>

                              <td className="p-3.5 font-medium text-slate-700">
                                {athlete.assigned_coaches && athlete.assigned_coaches.length > 0 ? (
                                  (() => {
                                    const uniqueCoaches = Array.from(
                                      new Map((athlete.assigned_coaches || []).map((c) => [c.id || c.name, c])).values()
                                    );
                                    return (
                                      <div className="flex flex-col gap-0.5">
                                        <span className="text-cyan-800 font-semibold">
                                          {uniqueCoaches.map((c) => c.name).join(", ")}
                                        </span>
                                        <span className="text-[10px] text-emerald-700 font-bold">
                                          {uniqueCoaches.length > 1 ? `(${uniqueCoaches.length} Asst Coaches` : `(1 Asst Coach`}
                                          {athlete.team_name ? ` • Team: ${athlete.team_name})` : ")"}
                                        </span>
                                      </div>
                                    );
                                  })()
                                ) : athlete.assigned_coach?.name ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-cyan-800 font-semibold">{athlete.assigned_coach.name}</span>
                                    {athlete.team_name && (
                                      <span className="text-[10px] text-cyan-700 font-medium">Team: {athlete.team_name}</span>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setMappingForm({
                                        coach_ids: coaches.length > 0 ? [coaches[0].id] : [],
                                        coach_id: coaches[0]?.id || "",
                                        athlete_ids: [athlete.id],
                                        team_name: athlete.team_name || coaches[0]?.team_name || "",
                                        mode: "add",
                                      });
                                      setMappingModalOpen(true);
                                    }}
                                    className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-dashed border-amber-300 rounded-lg text-xs font-semibold inline-flex items-center gap-1 transition"
                                    title="Click to allot an assistant coach to this athlete"
                                  >
                                    <FaUserPlus className="text-[10px]" /> Allot Asst Coach
                                  </button>
                                )}
                              </td>

                              <td className="p-3.5 pr-5 text-right space-x-1.5 whitespace-nowrap">
                                {athleteStatusTab === "removed" ? (
                                  <>
                                    <button
                                      onClick={() => handleReadmitAthleteToCamp(athlete)}
                                      title="Re-admit Athlete to Camp (Restores with all existing data intact)"
                                      className="px-2.5 py-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-300 font-bold text-xs inline-flex items-center gap-1.5 cursor-pointer shadow-xs transition-colors"
                                    >
                                      <FaUserCheck className="text-xs text-emerald-600" /> Re-admit to Camp
                                    </button>

                                    <button
                                      onClick={() => setViewAthleteModal(athlete)}
                                      title="View Full Profile & Documents"
                                      className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200 transition-colors inline-block"
                                    >
                                      <FaEye className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => handleDeleteAthlete(athlete.id)}
                                      title="Permanent Delete"
                                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg border border-rose-200 transition-colors inline-block"
                                    >
                                      <FaTrash className="text-xs" />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleOpenSportsAllotmentModal(athlete)}
                                      title="Allot / Change Games"
                                      className="p-1.5 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 rounded-lg border border-emerald-200 transition-colors inline-block"
                                    >
                                      <FaRunning className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => handleOpenDisciplineCategoryModal(athlete)}
                                      title="Assign / Update Discipline & Category"
                                      className="p-1.5 text-cyan-700 hover:bg-cyan-50 hover:text-cyan-800 rounded-lg border border-cyan-200 transition-colors inline-block"
                                    >
                                      <FaTags className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => handleViewSingleIdCard(athlete, "athlete")}
                                      title="View & Print ID Card"
                                      className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-lg border border-blue-200 transition-colors inline-block"
                                    >
                                      <FaIdCard className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => setViewAthleteModal(athlete)}
                                      title="View Full Profile & Documents"
                                      className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200 transition-colors inline-block"
                                    >
                                      <FaEye className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => openEditAthleteModal(athlete)}
                                      title="Edit Athlete Profile"
                                      className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors inline-block"
                                    >
                                      <FaEdit className="text-xs" />
                                    </button>

                                    <button
                                      onClick={() => handleRemoveAthleteFromCamp(athlete)}
                                      title="Remove Athlete from Camp (Preserves all data & unassigns from team)"
                                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg border border-rose-200 transition-colors inline-block"
                                    >
                                      <FaUserMinus className="text-xs" />
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB: OFFICIALS MANAGEMENT */}
            {/* ============================================================== */}
            {activeTab === "officials" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <span>Camp Officials & Technical Staff</span>
                      <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200">
                        {officials.length} Enrolled
                      </span>
                    </h2>
                    <p className="text-xs text-slate-500">
                      Assign technical officials, referees, camp management, and medical in-charges to <span className="font-bold text-emerald-800">{activeCamp?.name}</span>.
                    </p>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto flex-wrap">
                    <button
                      onClick={() => openEnrollOfficialModal()}
                      className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white rounded-xl shadow-md text-xs font-bold flex items-center gap-2 transition cursor-pointer"
                    >
                      <FaUsers /> Enroll from Officials Directory
                    </button>
                    <button
                      onClick={() => openAddOfficialModal()}
                      className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                    >
                      <FaPlus /> Add New Official
                    </button>
                    <button
                      onClick={() => {
                        const exportData = officials.map((o) => ({
                          "Official ID": o.official_id_card_no || `OFF-${o.id}`,
                          Name: o.name,
                          Designation: o.designation || "Official",
                          "Duty Role": o.duty_role || "Technical Official",
                          Mobile: o.mobile || "-",
                          Email: o.email || "-",
                          Gender: o.gender || "Male",
                          District: o.district || "-",
                          "Room No": o.room_no || "-",
                          "Reporting Date": o.reporting_date || "-",
                          "Relieving Date": o.relieving_date || "-",
                          Status: o.status || "active",
                        }));
                        exportToExcel(exportData, `Camp_Officials_${activeCamp?.name || "Camp"}`);
                      }}
                      disabled={officials.length === 0}
                      className="px-3 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                    >
                      <FaFileExcel /> Export Excel
                    </button>
                  </div>
                </div>

                <CampManagerManagement campId={selectedCampId || activeCamp?.id} />

                {/* Filters */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2.5 flex-1">
                    <input
                      type="text"
                      placeholder="Search officials by name, designation, mobile, room..."
                      value={officialSearchTerm}
                      onChange={(e) => setOfficialSearchTerm(e.target.value)}
                      className="px-3.5 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex-1 min-w-[220px]"
                    />
                    <select
                      value={officialDistrictFilter}
                      onChange={(e) => setOfficialDistrictFilter(e.target.value)}
                      className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">All Districts</option>
                      {AP_DISTRICTS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select
                      value={officialDutyFilter}
                      onChange={(e) => setOfficialDutyFilter(e.target.value)}
                      className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">All Duty Roles</option>
                      <option value="Technical Official">Technical Official</option>
                      <option value="Camp Management">Camp Management</option>
                      <option value="Health & Medical">Health & Medical</option>
                      <option value="Food & Accommodation">Food & Accommodation</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Security & Discipline">Security & Discipline</option>
                      <option value="Observer / Liaison">Observer / Liaison</option>
                    </select>
                  </div>
                </div>

                {/* Officials Table */}
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Official Details</th>
                          <th className="p-3.5">Designation & Duty</th>
                          <th className="p-3.5">Contact</th>
                          <th className="p-3.5">District</th>
                          <th className="p-3.5 text-center">Room No</th>
                          <th className="p-3.5">Tenure / Dates</th>
                          <th className="p-3.5 text-center">Status</th>
                          <th className="p-3.5 pr-5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {officialsLoading ? (
                          <tr>
                            <td colSpan={8} className="p-8 text-center text-slate-500">
                              <div className="inline-block w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mb-2" />
                              <p>Loading camp officials...</p>
                            </td>
                          </tr>
                        ) : officials.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="p-12 text-center text-slate-500">
                              <FaUserTie className="text-4xl text-slate-300 mx-auto mb-2" />
                              <p className="font-semibold text-slate-700">No Officials In Camp Yet</p>
                              <p className="text-xs text-slate-400 mt-1">
                                Click "Enroll from Officials Directory" or "Add New Official" to include officials in this camp.
                              </p>
                            </td>
                          </tr>
                        ) : (
                          officials
                            .filter((o) => {
                              if (officialDistrictFilter && o.district !== officialDistrictFilter) return false;
                              if (officialDutyFilter && o.duty_role !== officialDutyFilter) return false;
                              if (officialSearchTerm.trim()) {
                                const q = officialSearchTerm.toLowerCase();
                                const mName = o.name?.toLowerCase().includes(q);
                                const mDesig = o.designation?.toLowerCase().includes(q);
                                const mDuty = o.duty_role?.toLowerCase().includes(q);
                                const mMob = o.mobile?.includes(q);
                                const mRoom = o.room_no?.toLowerCase().includes(q);
                                if (!mName && !mDesig && !mDuty && !mMob && !mRoom) return false;
                              }
                              return true;
                            })
                            .map((off) => {
                              const offPhoto = getSafePhotoUrl(off.photo);
                              return (
                                <tr key={off.id} className="hover:bg-slate-50/80 transition-colors">
                                  <td className="p-3.5 pl-5">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={offPhoto}
                                        alt={off.name}
                                        className="w-10 h-10 rounded-full object-cover border border-slate-200 shrink-0"
                                        onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                      />
                                      <div>
                                        <p className="font-bold text-slate-900 text-sm">{off.name}</p>
                                        <p className="text-[11px] text-slate-500">{off.gender || "Male"} • ID: {off.official_id_card_no || `OFF-${off.id}`}</p>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="p-3.5">
                                    <span className="font-bold text-slate-800 block">{off.designation || "Official"}</span>
                                    <span className="inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                      {off.duty_role || "Technical Official"}
                                    </span>
                                  </td>

                                  <td className="p-3.5">
                                    <div className="space-y-0.5 text-[11px]">
                                      <p className="text-slate-700 font-semibold">{off.mobile || "—"}</p>
                                      <p className="text-slate-400 truncate max-w-[150px]">{off.email || "—"}</p>
                                    </div>
                                  </td>

                                  <td className="p-3.5">
                                    <span className="font-semibold text-slate-700">{off.district || "Andhra Pradesh"}</span>
                                  </td>

                                  <td className="p-3.5 text-center">
                                    {off.room_no ? (
                                      <span className="inline-flex items-center gap-1 font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs">
                                        <FaBed className="text-[10px]" /> {off.room_no}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400">—</span>
                                    )}
                                  </td>

                                  <td className="p-3.5">
                                    <div className="text-[11px] text-slate-600">
                                      <p>From: <span className="font-semibold text-slate-800">{off.reporting_date ? String(off.reporting_date).slice(0, 10) : "—"}</span></p>
                                      {off.relieving_date && <p>To: <span className="font-semibold text-slate-800">{String(off.relieving_date).slice(0, 10)}</span></p>}
                                    </div>
                                  </td>

                                  <td className="p-3.5 text-center">
                                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${off.status === "active"
                                      ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                      : "bg-slate-100 text-slate-600 border border-slate-200"
                                      }`}>
                                      {off.status || "active"}
                                    </span>
                                  </td>

                                  <td className="p-3.5 pr-5 text-right space-x-1.5 whitespace-nowrap">
                                    <button
                                      onClick={() => handleViewSingleIdCard(off, "official")}
                                      title="View & Print ID Card"
                                      className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-lg border border-emerald-200 transition-colors inline-block cursor-pointer"
                                    >
                                      <FaIdCard className="text-xs" />
                                    </button>
                                    <button
                                      onClick={() => openEditOfficialModal(off)}
                                      title="Edit Official"
                                      className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors inline-block cursor-pointer"
                                    >
                                      <FaEdit className="text-xs" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteOfficial(off.id)}
                                      title="Remove from Camp"
                                      className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg border border-rose-200 transition-colors inline-block cursor-pointer"
                                    >
                                      <FaTrash className="text-xs" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB: ID CARDS & BADGES */}
            {/* ============================================================== */}
            {activeTab === "id_cards" && (
              <CampIdCard
                camp={activeCamp}
                athletes={athletes}
                coaches={coaches}
                officials={officials}
                onNavigateTab={(tab) => setActiveTab(tab)}
                onEditOfficial={(off) => openEditOfficialModal(off)}
                onUpdateAthlete={(updated) => {
                  setAthletes((prev) =>
                    prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
                  );
                }}
              />
            )}

            {/* ============================================================== */}
            {/* TAB 5: COACH MAPPINGS */}
            {/* ============================================================== */}
            {activeTab === "mappings" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Athlete, Team & Coach Mappings</h2>
                    <p className="text-xs text-slate-500">
                      Assign athletes to teams and coaches in <span className="font-bold text-cyan-800">{activeCamp?.name}</span>. Multiple coaches can be assigned to the same team.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setMappingForm({ coach_ids: [], coach_id: "", athlete_ids: [], team_name: "" });
                      setMappingModalOpen(true);
                    }}
                    className="px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                  >
                    <FaUserPlus /> Assign Athletes to Team & Coaches
                  </button>
                </div>

                {/* Filter Bar */}
                <div className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-wrap items-center gap-3 shadow-sm text-xs">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="Search by athlete, coach, team, or BIB..."
                      value={mappingSearchFilter}
                      onChange={(e) => setMappingSearchFilter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-slate-500 font-medium whitespace-nowrap">Team:</label>
                    <select
                      value={mappingTeamFilter}
                      onChange={(e) => setMappingTeamFilter(e.target.value)}
                      className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs text-slate-900 focus:ring-2 focus:ring-cyan-500 font-medium"
                    >
                      <option value="">All Teams ({existingTeams.length})</option>
                      {existingTeams.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-slate-500 font-medium whitespace-nowrap">Coach:</label>
                    <select
                      value={mappingCoachFilter}
                      onChange={(e) => setMappingCoachFilter(e.target.value)}
                      className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs text-slate-900 focus:ring-2 focus:ring-cyan-500 font-medium"
                    >
                      <option value="">All Coaches ({coaches.length})</option>
                      {coaches.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.team_name ? `(${c.team_name})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(mappingSearchFilter || mappingTeamFilter || mappingCoachFilter) && (
                    <button
                      onClick={() => {
                        setMappingSearchFilter("");
                        setMappingTeamFilter("");
                        setMappingCoachFilter("");
                      }}
                      className="text-cyan-700 hover:text-cyan-900 font-semibold px-2 py-1"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Coach Name</th>
                          <th className="p-3.5">Team / Squad Name</th>
                          <th className="p-3.5">Athlete Name</th>
                          <th className="p-3.5">ID Number</th>
                          <th className="p-3.5">Discipline</th>
                          <th className="p-3.5">Category</th>
                          <th className="p-3.5">Date Assigned</th>
                          <th className="p-3.5">Status</th>
                          <th className="p-3.5 pr-5 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {mappings
                          .filter((m) => {
                            if (mappingTeamFilter) {
                              const t = m.team_name || m.coach?.team_name || "";
                              if (t.toLowerCase() !== mappingTeamFilter.toLowerCase()) return false;
                            }
                            if (mappingCoachFilter) {
                              if (String(m.coach_id) !== String(mappingCoachFilter)) return false;
                            }
                            if (mappingSearchFilter) {
                              const q = mappingSearchFilter.toLowerCase();
                              const athName = (m.athlete?.name || "").toLowerCase();
                              const coachName = (m.coach?.name || "").toLowerCase();
                              const teamName = (m.team_name || m.coach?.team_name || "").toLowerCase();
                              const bib = String(m.athlete?.bib_no || "").toLowerCase();
                              if (!athName.includes(q) && !coachName.includes(q) && !teamName.includes(q) && !bib.includes(q)) {
                                return false;
                              }
                            }
                            return true;
                          })
                          .map((m) => (
                            <tr key={m.id} className="hover:bg-slate-50/80">
                              <td className="p-3.5 pl-5 font-bold text-slate-900">
                                <div className="flex items-center gap-2">
                                  <div className="w-7 h-7 rounded-full overflow-hidden bg-slate-200 border border-slate-300 flex items-center justify-center flex-shrink-0">
                                    {m.coach?.photo || m.coach?.player_photo ? (
                                      <img
                                        src={resolvePhoto(m.coach.photo || m.coach.player_photo)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    ) : (
                                      <span className="text-[10px] font-bold text-slate-500">{m.coach?.name?.charAt(0) || "C"}</span>
                                    )}
                                  </div>
                                  <span>{m.coach?.name}</span>
                                </div>
                              </td>
                              <td className="p-3.5 font-bold text-cyan-800">
                                <div className="flex flex-col gap-0.5">
                                  <span>{m.team_name || m.coach?.team_name || (
                                    <span className="text-slate-400 italic font-normal">Not Assigned</span>
                                  )}</span>
                                  {(() => {
                                    const team = m.team_name || m.coach?.team_name;
                                    if (!team) return null;
                                    const peers = coaches.filter((c) => c.team_name && c.team_name.toLowerCase() === team.toLowerCase());
                                    if (peers.length > 1) {
                                      return (
                                        <span className="text-[10px] text-emerald-600 font-medium">
                                          {peers.length} Coaches in this Team
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </td>
                              <td className="p-3.5 font-semibold text-slate-800">
                                {m.athlete?.name}
                              </td>
                              <td className="p-3.5 font-bold text-amber-500 font-mono">
                                {m.athlete?.id_number || m.athlete?.bib_no || "—"}
                              </td>
                              <td className="p-3.5 text-emerald-700 font-medium">
                                {m.athlete?.discipline_name || "—"}
                              </td>
                              <td className="p-3.5 font-medium text-slate-700">
                                {m.category || m.athlete?.category || "—"}
                              </td>
                              <td className="p-3.5 text-slate-500 text-[11px]">
                                {m.assigned_date}
                              </td>
                              <td className="p-3.5">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  {m.status}
                                </span>
                              </td>
                              <td className="p-3.5 pr-5 text-right space-x-1.5 whitespace-nowrap">
                                <button
                                  onClick={() => {
                                    const team = m.team_name || m.coach?.team_name || "";
                                    setAddCoachModal({
                                      team_name: team,
                                      athlete_ids: [m.athlete_id],
                                      athlete_name: m.athlete?.name,
                                    });
                                    setAddCoachForm({ coach_id: "" });
                                  }}
                                  className="px-2.5 py-1 text-cyan-800 bg-cyan-50 hover:bg-cyan-100 rounded-lg border border-cyan-200 text-[11px] font-semibold transition inline-flex items-center gap-1"
                                  title="Add another coach to this team / athlete"
                                >
                                  <FaPlus className="text-[9px]" /> Add Coach
                                </button>
                                <button
                                  onClick={() => handleRemoveMapping(m.id)}
                                  className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-200"
                                  title="Remove Mapping"
                                >
                                  <FaTimes /> Unmap
                                </button>
                              </td>
                            </tr>
                          ))}
                        {!mappings.length && (
                          <tr>
                            <td colSpan={9} className="p-8 text-center text-slate-500 text-xs">
                              No athlete-to-coach mappings created for this camp yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB: TEAM MANAGEMENT */}
            {/* ============================================================== */}
            {activeTab === "teams" && (
              <TeamManagement
                campId={selectedCampId || activeCamp?.id}
                selectedCamp={activeCamp}
                activeCamp={activeCamp}
                onRefreshAll={fetchTabData}
              />
            )}

            {/* ============================================================== */}
            {/* TAB: KIT & MEASUREMENTS */}
            {/* ============================================================== */}
            {activeTab === "kits" && (
              <KitMeasurements
                campId={selectedCampId || activeCamp?.id}
                selectedCamp={activeCamp}
                activeCamp={activeCamp}
                onRefreshAll={fetchTabData}
              />
            )}

            {/* ============================================================== */}
            {/* TAB: ROOM INCHARGES & MEDICAL CARE */}
            {/* ============================================================== */}
            {activeTab === "room_incharges" && (
              <div className="space-y-6">
                {/* Header & Quick Action Banner */}
                <section className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 via-white to-amber-50 p-5 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-700">
                        Residential Camp Care & Administration
                      </p>
                      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mt-0.5 flex items-center gap-2">
                        <FaUserShield className="text-rose-700 text-lg" /> Room Incharges & Athlete Health Care
                      </h2>
                      <p className="text-xs text-slate-600 mt-1 max-w-2xl leading-relaxed">
                        Assign coaches as room in-charges (supports one or more coaches per room), manage weekly rotations, track athlete medical prescriptions, and dispatch email alerts with medicine administration schedules.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenAssignRoomModal()}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold shadow-sm transition flex items-center gap-1.5"
                      >
                        <FaPlus className="text-[10px]" /> Assign / Rotate Incharge
                      </button>

                      <button
                        type="button"
                        disabled={sendingMedicineEmail}
                        onClick={handleSendMedicineMailAll}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm transition flex items-center gap-1.5 disabled:opacity-50"
                        title="Send medicine alerts to in-charge coaches of all rooms"
                      >
                        {sendingMedicineEmail ? "Sending..." : "📧 Email All Room Incharges"}
                      </button>

                      <button
                        type="button"
                        disabled={sendingMedicineEmail}
                        onClick={handleTriggerAutoReminders}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition flex items-center gap-1.5 disabled:opacity-50"
                        title="Trigger automated medicine reminders check according to prescribed times"
                      >
                        {sendingMedicineEmail ? "Processing..." : "⏰ Trigger Auto-Reminders Now"}
                      </button>
                    </div>
                  </div>

                  {/* Stats Overview */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-rose-100 text-xs">
                    <div className="p-3 bg-white/90 rounded-xl border border-rose-100 shadow-xs">
                      <span className="text-slate-500 block font-semibold text-[11px]">Total Rooms</span>
                      <span className="text-lg font-bold text-slate-900">
                        {roomInchargesData.rooms?.length || 0}
                      </span>
                    </div>
                    <div className="p-3 bg-white/90 rounded-xl border border-rose-100 shadow-xs">
                      <span className="text-slate-500 block font-semibold text-[11px]">In-Charges Assigned</span>
                      <span className="text-lg font-bold text-emerald-700">
                        {roomInchargesData.rooms?.filter((r) => r.has_incharge)?.length || 0}
                      </span>
                    </div>
                    <div className="p-3 bg-white/90 rounded-xl border border-rose-100 shadow-xs">
                      <span className="text-slate-500 block font-semibold text-[11px]">Athletes in Rooms</span>
                      <span className="text-lg font-bold text-cyan-800">
                        {roomInchargesData.rooms?.reduce((acc, r) => acc + (r.athlete_count || 0), 0)}
                      </span>
                    </div>
                    <div className="p-3 bg-white/90 rounded-xl border border-rose-100 shadow-xs">
                      <span className="text-slate-500 block font-semibold text-[11px]">On Medication</span>
                      <span className="text-lg font-bold text-rose-600">
                        {roomInchargesData.rooms?.reduce((acc, r) => acc + (r.medical_alert_count || 0), 0)}
                      </span>
                    </div>
                  </div>
                </section>

                {/* Filter & Search Bar */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm text-xs">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <input
                      type="text"
                      placeholder="Search by room or coach..."
                      value={roomSearchTerm}
                      onChange={(e) => setRoomSearchTerm(e.target.value)}
                      className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500 w-full sm:w-64"
                    />
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <span className="text-slate-500 font-semibold">Filter Week:</span>
                    <select
                      value={roomFilterWeek}
                      onChange={(e) => setRoomFilterWeek(e.target.value)}
                      className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500 cursor-pointer"
                    >
                      <option value="">All Weeks</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((w) => (
                        <option key={w} value={w}>
                          Week {w}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Room Cards Grid */}
                {roomInchargesLoading ? (
                  <div className="p-12 text-center text-slate-500 text-xs bg-white rounded-2xl border border-slate-200">
                    Loading room in-charges and medical records...
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {(roomInchargesData.rooms || [])
                      .filter((r) => {
                        if (!roomSearchTerm.trim()) return true;
                        const term = roomSearchTerm.toLowerCase();
                        const matchRoom = String(r.room_no || "").toLowerCase().includes(term);
                        const matchCoach = r.incharges?.some((c) =>
                          String(c.coach_name || "").toLowerCase().includes(term)
                        );
                        const matchAthlete = r.athletes?.some((a) =>
                          String(a.full_name || "").toLowerCase().includes(term)
                        );
                        return matchRoom || matchCoach || matchAthlete;
                      })
                      .map((room) => (
                        <div
                          key={room.room_no}
                          className="bg-white border border-slate-200 hover:border-rose-300 rounded-2xl shadow-sm hover:shadow-md transition-all flex flex-col justify-between overflow-hidden"
                        >
                          {/* Card Top */}
                          <div className="p-4 space-y-3">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                              <div className="flex items-center gap-2">
                                <span className="px-2.5 py-1 bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-xl font-extrabold text-sm font-mono shadow-xs">
                                  Room {room.room_no}
                                </span>
                                <span className="text-[11px] text-slate-500 font-semibold">
                                  {room.athlete_count} Athlete{room.athlete_count === 1 ? "" : "s"}
                                </span>
                              </div>

                              {room.medical_alert_count > 0 ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                                  💊 {room.medical_alert_count} On Meds
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  All Healthy
                                </span>
                              )}
                            </div>

                            {/* In-Charge Coaches Section (One or more coaches supported) */}
                            <div>
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                  Room In-Charge Coaches ({room.incharges?.length || 0})
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleOpenAssignRoomModal(room.room_no, room.incharges || [])}
                                  className="text-[10px] text-rose-600 hover:text-rose-800 font-bold hover:underline"
                                >
                                  + Assign / Rotate
                                </button>
                              </div>

                              {room.incharges && room.incharges.length > 0 ? (
                                <div className="space-y-1.5">
                                  {room.incharges.map((coach) => (
                                    <div
                                      key={coach.assignment_id || coach.coach_id}
                                      className="flex items-center justify-between p-2 bg-slate-50 rounded-xl border border-slate-200/80 text-xs"
                                    >
                                      <div>
                                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                                          <span>{coach.coach_name}</span>
                                          <span className="text-[10px] px-1.5 py-0.2 bg-rose-100 text-rose-800 font-semibold rounded">
                                            Wk {coach.week_number}
                                          </span>
                                        </div>
                                        <div className="text-[10px] text-slate-500">
                                          {coach.coach_mobile || "No Mobile"} &bull; {coach.coach_email || "No Email"}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDeleteRoomIncharge(
                                            coach.assignment_id,
                                            coach.coach_name,
                                            room.room_no
                                          )
                                        }
                                        title="Remove this coach assignment"
                                        className="text-slate-400 hover:text-rose-600 p-1"
                                      >
                                        <FaTrash className="text-[11px]" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="p-2.5 bg-amber-50/70 border border-dashed border-amber-200 rounded-xl text-center text-xs text-amber-800 font-medium">
                                  ⚠️ No in-charge coach assigned yet
                                </div>
                              )}
                            </div>

                            {/* Athletes in Room & Medical Summary */}
                            <div className="pt-2 border-t border-slate-100">
                              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
                                Athletes In Room ({room.athletes?.length || 0})
                              </span>

                              {room.athletes && room.athletes.length > 0 ? (
                                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                                  {room.athletes.map((ath) => {
                                    const hasMeds =
                                      ath.medicines || ath.health_issues || ath.medicine_timings;
                                    return (
                                      <div
                                        key={ath.id}
                                        className={`p-2 rounded-xl border text-xs ${hasMeds
                                          ? "bg-rose-50/50 border-rose-200"
                                          : "bg-slate-50 border-slate-200"
                                          }`}
                                      >
                                        <div className="flex items-center justify-between font-semibold text-slate-800">
                                          <span>{ath.full_name || ath.name}</span>
                                          <span className="font-mono text-[10px] text-cyan-800">
                                            BIB #{ath.bib_no || "N/A"}
                                          </span>
                                        </div>

                                        {hasMeds && (
                                          <div className="mt-1 text-[11px] space-y-0.5">
                                            {ath.health_issues && (
                                              <div className="text-slate-600">
                                                <strong className="text-slate-700">Health:</strong> {ath.health_issues}
                                              </div>
                                            )}
                                            {ath.medicines && (
                                              <div className="text-rose-700 font-medium">
                                                <strong>Rx Meds:</strong> {ath.medicines}
                                              </div>
                                            )}
                                            {ath.medicine_timings && (
                                              <div className="text-emerald-800 font-medium">
                                                <strong>Timing:</strong> {ath.medicine_timings}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400 italic py-1 text-center">
                                  No athletes allotted to this room yet.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Card Footer Actions */}
                          <div className="p-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenAssignRoomModal(room.room_no, room.incharges || [])}
                              className="px-2.5 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold shadow-xs"
                            >
                              + Assign Coach
                            </button>

                            <button
                              type="button"
                              disabled={sendingMedicineEmail || !room.incharges?.length}
                              onClick={() => handleSendMedicineMail(room.room_no)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 shadow-xs transition ${room.incharges?.length
                                ? "bg-cyan-700 hover:bg-cyan-800 text-white"
                                : "bg-slate-200 text-slate-400 cursor-not-allowed"
                                }`}
                              title={
                                room.incharges?.length
                                  ? "Email medicine schedule to this room's in-charge coaches"
                                  : "Assign an in-charge coach first to send email"
                              }
                            >
                              📧 Email Schedule
                            </button>
                          </div>
                        </div>
                      ))}

                    {(!roomInchargesData.rooms || roomInchargesData.rooms.length === 0) && (
                      <div className="col-span-full p-12 text-center text-slate-500 text-xs bg-white rounded-2xl border border-slate-200">
                        No rooms recorded yet. Enter a room number when enrolling or editing athletes, or assign room in-charges above.
                      </div>
                    )}
                  </div>
                )}

                {/* Historical Assignments Table */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        All Room In-Charge Assignments & Weekly History
                      </h3>
                      <p className="text-xs text-slate-500">
                        Complete records of current and past weekly rotations.
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                        <tr>
                          <th className="p-3 pl-4">Room No</th>
                          <th className="p-3">Coach Name</th>
                          <th className="p-3">Contact</th>
                          <th className="p-3">Week #</th>
                          <th className="p-3">Date Range</th>
                          <th className="p-3">Role Title</th>
                          <th className="p-3">Status</th>
                          <th className="p-3 pr-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {(roomInchargesData.assignments || []).map((asg) => (
                          <tr key={asg.id} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 pl-4 font-bold text-indigo-900 font-mono">
                              Room {asg.room_no}
                            </td>
                            <td className="p-3 font-semibold text-slate-900">
                              {asg.coach?.name || "N/A"}
                            </td>
                            <td className="p-3 text-slate-500 text-[11px]">
                              {asg.coach?.mobile || "—"} {asg.coach?.email ? `• ${asg.coach.email}` : ""}
                            </td>
                            <td className="p-3 font-mono font-bold text-slate-700">
                              Week {asg.week_number}
                            </td>
                            <td className="p-3 text-slate-500 text-[11px]">
                              {asg.start_date ? String(asg.start_date).slice(0, 10) : "Start"} to{" "}
                              {asg.end_date ? String(asg.end_date).slice(0, 10) : "End"}
                            </td>
                            <td className="p-3 text-slate-600">
                              {asg.role_title || "Room Incharge"}
                            </td>
                            <td className="p-3">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${asg.status === "active"
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : "bg-slate-100 text-slate-600 border border-slate-200"
                                  }`}
                              >
                                {asg.status}
                              </span>
                            </td>
                            <td className="p-3 pr-4 text-right">
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteRoomIncharge(asg.id, asg.coach?.name, asg.room_no)
                                }
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                title="Delete assignment"
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {(!roomInchargesData.assignments || roomInchargesData.assignments.length === 0) && (
                          <tr>
                            <td colSpan={8} className="p-6 text-center text-slate-400 italic">
                              No room incharge assignments recorded yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 6: ATTENDANCE MONITOR & DAILY REGISTER */}
            {/* ============================================================== */}
            {activeTab === "attendance" && (
              <div className="space-y-5">
                <CampPeopleAttendance campId={selectedCampId} canManageDirectories />
                {/* Sub-Tabs Segmented Bar */}
                <div className="flex items-center gap-2 p-1.5 bg-slate-100/90 border border-slate-200 rounded-2xl w-full sm:w-fit overflow-x-auto shadow-sm">
                  <button
                    type="button"
                    onClick={() => setAttendanceSubTab("range_register")}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${attendanceSubTab === "range_register"
                      ? "bg-white text-cyan-800 shadow-sm border border-slate-200/80"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <FaCalendarDay className="text-cyan-700" /> Daily Register &amp; Range Monitor
                  </button>
                  <button
                    type="button"
                    onClick={() => setAttendanceSubTab("monitor")}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${attendanceSubTab === "monitor"
                      ? "bg-white text-cyan-800 shadow-sm border border-slate-200/80"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <FaCalendarCheck className="text-cyan-700" /> Athlete Live Monitor &amp; Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAttendanceSubTab("coach_attendance");
                      fetchCoachAttendanceSheet(selectedCampId, coachAttendanceDate);
                    }}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${attendanceSubTab === "coach_attendance"
                      ? "bg-white text-emerald-800 shadow-sm border border-slate-200/80"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <FaUserShield className="text-emerald-700" /> Mark &amp; Edit Coach Attendance
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAttendanceSubTab("upload");
                      fetchDailyReportsList();
                    }}
                    className={`px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition whitespace-nowrap ${attendanceSubTab === "upload"
                      ? "bg-white text-cyan-800 shadow-sm border border-slate-200/80"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    <FaUpload className="text-cyan-700" /> Upload Final Attendance Report
                  </button>
                </div>

                {/* SUB-TAB 1: DATE RANGE REGISTER (Camp Dates: campStartDate to campEndDate) */}
                {attendanceSubTab === "range_register" && (
                  <div className="space-y-4">
                    {/* Control Panel Card */}
                    <div className="bg-gradient-to-br from-amber-50 via-white to-cyan-50 border border-amber-200 p-4 rounded-2xl shadow-sm space-y-3">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                        <div>
                          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
                            <FaCalendarDay className="text-cyan-700" /> Daily Athlete Attendance Register (Ground &amp; Print)
                          </h2>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Generate official attendance registers for {rangeDateType === "single" ? (
                              <strong className="text-slate-800 font-mono">
                                {rangeSingleDate ? rangeSingleDate.split("-").reverse().join("-") : "—"}
                                {getCampDayNumber(rangeSingleDate) ? ` (Day ${getCampDayNumber(rangeSingleDate)}${campTotalDays ? ` of ${campTotalDays}` : ""})` : ""}
                              </strong>
                            ) : (
                              <strong className="text-slate-800 font-mono">
                                {campStartDate ? campStartDate.split("-").reverse().join("-") : "Start"} to {campEndDate ? campEndDate.split("-").reverse().join("-") : "End"} ({campTotalDays || "All"} Days)
                              </strong>
                            )}. Prints 1 clean A4 page per team with logos, player lists in team order, and signature fields.
                          </p>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handlePrintRangeAttendance({ isBlank: true, action: "print" })}
                            disabled={isPrintingRegister}
                            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50"
                            title="Open direct browser print dialog for empty attendance register"
                          >
                            <FaPrint /> Print Empty
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintRangeAttendance({ isBlank: true, action: "download" })}
                            disabled={isPrintingRegister}
                            className="px-3.5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50"
                            title="Download empty attendance register as PDF"
                          >
                            <FaDownload /> Download Empty (PDF)
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintRangeAttendance({ isBlank: false, action: "print" })}
                            disabled={isPrintingRegister}
                            className="px-3.5 py-2 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50"
                            title="Open direct browser print dialog for recorded attendance register"
                          >
                            <FaPrint /> Print Recorded
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePrintRangeAttendance({ isBlank: false, action: "download" })}
                            disabled={isPrintingRegister}
                            className="px-3.5 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50"
                            title="Download recorded attendance register as PDF"
                          >
                            <FaDownload /> Download Recorded (PDF)
                          </button>
                          <button
                            type="button"
                            onClick={handleExportRangeAttendanceExcel}
                            className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition"
                          >
                            <FaFileExcel /> Export Excel
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-end gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
                        <label className="text-[11px] font-bold text-emerald-900 uppercase tracking-wider">
                          Monthly Attendance Sheet
                          <input
                            type="month"
                            value={monthlyAttendanceMonth}
                            onChange={(e) => setMonthlyAttendanceMonth(e.target.value)}
                            className="mt-1 block w-full sm:w-48 bg-white border border-emerald-300 text-slate-900 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handleExportMonthlyAttendanceExcel}
                          disabled={exportingMonthlyAttendance || !monthlyAttendanceMonth}
                          className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center justify-center gap-1.5 transition disabled:opacity-50"
                        >
                          {exportingMonthlyAttendance ? <FaSpinner className="animate-spin" /> : <FaFileExcel />}
                          {exportingMonthlyAttendance ? "Creating Sheet..." : "Export Monthly Sheet + Daily Counts"}
                        </button>
                        <p className="text-[10px] text-emerald-800 sm:pb-2">
                          One student per row, one column per date, with the number of present students below each day.
                        </p>
                      </div>

                      {/* Submit-only filters */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          fetchAttendanceRangeReport();
                        }}
                        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 pt-3 border-t border-amber-100/80"
                      >
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                          From Date
                          <input
                            type="date"
                            value={rangeDateFrom}
                            onChange={(e) => setRangeDateFrom(e.target.value)}
                            className="mt-1 w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                            required
                          />
                        </label>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                          To Date
                          <input
                            type="date"
                            value={rangeDateTo}
                            onChange={(e) => setRangeDateTo(e.target.value)}
                            className="mt-1 w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                            required
                          />
                        </label>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                          Player
                          <select
                            value={rangeAthleteFilter}
                            onChange={(e) => setRangeAthleteFilter(e.target.value)}
                            className="mt-1 w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                          >
                            <option value="">All Players</option>
                            {[...athletes]
                              .sort((a, b) => String(a.name || a.player_name || "").localeCompare(String(b.name || b.player_name || "")))
                              .map((athlete) => (
                                <option key={athlete.id} value={athlete.id}>
                                  {athlete.name || athlete.player_name} {athlete.id_number || athlete.bib_no ? "(" + (athlete.id_number || athlete.bib_no) + ")" : ""}
                                </option>
                              ))}
                          </select>
                        </label>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                          Team
                          <select
                            value={rangeTeamFilter}
                            onChange={(e) => setRangeTeamFilter(e.target.value)}
                            className="mt-1 w-full bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                          >
                            <option value="">All Teams</option>
                            {availableTeams.map((teamName) => (
                              <option key={teamName} value={teamName}>{teamName}</option>
                            ))}
                          </select>
                        </label>
                        <div className="flex items-end">
                          <button
                            type="submit"
                            disabled={rangeLoading}
                            className="w-full px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl text-xs font-bold shadow-sm transition disabled:opacity-50"
                          >
                            {rangeLoading ? "Loading..." : "Get Attendance"}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* KPI Strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white border border-slate-200 rounded-2xl p-3.5 text-center shadow-sm">
                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Teams Included</div>
                        <div className="text-2xl font-black text-cyan-800">{rangeReportData.length}</div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl p-3.5 text-center shadow-sm">
                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">
                          Days with Attendance
                        </div>
                        <div className="text-2xl font-black text-slate-900 flex items-center justify-center gap-1">
                          <span className="text-emerald-700">{rangeAttendanceStats.recordedDays}</span>
                          <span className="text-slate-400 text-lg font-bold">/</span>
                          <span className="text-slate-800 text-2xl font-black">{rangeAttendanceStats.totalDays}</span>
                          <span className="text-xs font-bold text-slate-500 ml-0.5">Days</span>
                        </div>
                        <div className="text-[10px] text-slate-500 font-medium mt-1">
                          {rangeAttendanceStats.recordedDays} of {rangeAttendanceStats.totalDays} Days Recorded
                          {rangeAttendanceStats.totalTeamSheets > 1 && (
                            <span className="text-slate-400"> &bull; {rangeAttendanceStats.totalTeamSheets} Team Sheets</span>
                          )}
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl p-3.5 text-center shadow-sm">
                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Total Athletes Enrolled</div>
                        <div className="text-2xl font-black text-amber-600">
                          {(() => {
                            const ids = new Set();
                            rangeReportData.forEach((c) => {
                              c.days?.forEach((d) => {
                                d.athletes?.forEach((a) => {
                                  if (a.id || a.athlete_id) ids.add(a.id || a.athlete_id);
                                });
                              });
                            });
                            return ids.size || athletes.length;
                          })()}
                        </div>
                      </div>
                      <div className="bg-white border border-slate-200 rounded-2xl p-3.5 text-center shadow-sm">
                        <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1">Print Layout &amp; Sheets</div>
                        <div className="text-xs font-black text-emerald-700 mt-2">
                          1 Day Per Paper &bull; {rangeAttendanceStats.totalTeamSheets} Total Page{rangeAttendanceStats.totalTeamSheets !== 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>

                    {/* Date-wise attendance lists */}
                    {!rangeSubmitted ? (
                      <div className="bg-white border border-dashed border-cyan-300 rounded-2xl p-10 text-center text-slate-500 text-sm shadow-sm">
                        Select a date range and optional player or team, then click <strong>Get Attendance</strong>.
                      </div>
                    ) : rangeLoading ? (
                      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500 text-sm shadow-sm">
                        Loading daily attendance register...
                      </div>
                    ) : rangeReportByDate.length > 0 ? (
                      <div className="space-y-6">
                        {rangeReportByDate.map((dateGroup) => (
                          <section key={dateGroup.date} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                            <div className="bg-cyan-800 text-white px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                              <h3 className="font-extrabold text-sm">
                                {dateGroup.date ? dateGroup.date.split("-").reverse().join("-") : "—"}
                                {getCampDayNumber(dateGroup.date) ? " — Day " + getCampDayNumber(dateGroup.date) : ""}
                              </h3>
                              <span className="text-xs font-semibold text-cyan-100">
                                {dateGroup.teams.length} Team{dateGroup.teams.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="p-4 space-y-5">
                              {dateGroup.teams.map((team, teamIndex) => (
                                <div key={(team.team_name || "team") + "-" + teamIndex} className="border border-slate-200 rounded-xl overflow-hidden">
                                  <div className="bg-slate-100 px-3.5 py-2 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs">
                                    <div>
                                      <strong className="text-slate-900">{team.team_name || "General Squad"}</strong>
                                      <span className="text-slate-500"> • Coach: {team.coach_name || "—"}</span>
                                    </div>
                                    <span className="text-slate-500 font-semibold">{team.athletes.length} Player{team.athletes.length !== 1 ? "s" : ""}</span>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-left text-xs text-slate-800 min-w-[760px]">
                                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                                        <tr>
                                          <th className="p-2.5 text-center">S.No</th>
                                          <th className="p-2.5">Player</th>
                                          <th className="p-2.5">ID Number</th>
                                          <th className="p-2.5 text-center">Morning In</th>
                                          <th className="p-2.5 text-center">Morning Out</th>
                                          <th className="p-2.5 text-center">Morning Status</th>
                                          <th className="p-2.5 text-center">Evening In</th>
                                          <th className="p-2.5 text-center">Evening Out</th>
                                          <th className="p-2.5 text-center">Evening Status</th>
                                          <th className="p-2.5">Remarks</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {team.athletes.map((athlete, index) => (
                                          <tr key={athlete.athlete_id || index} className="hover:bg-slate-50">
                                            <td className="p-2.5 text-center text-slate-500 font-bold">{index + 1}</td>
                                            <td className="p-2.5 font-bold text-slate-900">{athlete.athlete_name}</td>
                                            <td className="p-2.5 font-mono text-amber-700">{athlete.id_number || athlete.bib_no || "—"}</td>
                                            <td className="p-2.5 text-center font-mono">{athlete.morning_in || "—"}</td>
                                            <td className="p-2.5 text-center font-mono">{athlete.morning_out || "—"}</td>
                                            <td className="p-2.5 text-center">
                                              <span className={athlete.morning_status === "present" ? "text-emerald-700 font-bold" : athlete.morning_status === "absent" ? "text-rose-700 font-bold" : "text-slate-400"}>
                                                {athlete.morning_status?.toUpperCase() || "—"}
                                              </span>
                                            </td>
                                            <td className="p-2.5 text-center font-mono">{athlete.evening_in || "—"}</td>
                                            <td className="p-2.5 text-center font-mono">{athlete.evening_out || "—"}</td>
                                            <td className="p-2.5 text-center">
                                              <span className={athlete.evening_status === "present" ? "text-emerald-700 font-bold" : athlete.evening_status === "absent" ? "text-rose-700 font-bold" : "text-slate-400"}>
                                                {athlete.evening_status?.toUpperCase() || "—"}
                                              </span>
                                            </td>
                                            <td className="p-2.5 text-slate-600">{athlete.remarks || "—"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500 text-sm shadow-sm">
                        No players or attendance records were found for the selected filters.
                      </div>
                    )}
                  </div>
                )}

                {/* SUB-TAB 2: SINGLE DAY LIVE MONITOR */}
                {attendanceSubTab === "monitor" && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gradient-to-br from-amber-50 via-white to-cyan-50 border border-amber-200 p-4 rounded-2xl shadow-sm">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                          <FaCalendarCheck className="text-cyan-700" /> Single Day Live Attendance Monitor
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Live attendance submitted by coaches via the Present/Absent buttons in their portal.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="date"
                          value={attendanceDate}
                          max={todayIST}
                          onChange={(e) => {
                            setAttendanceDate(e.target.value);
                            setSelectedAttendanceAthleteIds([]);
                          }}
                          className="bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-cyan-500 shadow-sm font-semibold focus:outline-none"
                        />
                        <button
                          onClick={handleExportAttendanceExcel}
                          className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition"
                        >
                          <FaFileExcel /> Export Excel
                        </button>
                      </div>
                    </div>

                    <div className="bg-white border border-cyan-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="px-4 py-3 bg-cyan-50 border-b border-cyan-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-black text-cyan-950 flex items-center gap-2">
                            <FaUsers className="text-cyan-700" /> Bulk Mark Student Attendance
                          </h3>
                          <p className="text-[10px] text-cyan-800 mt-0.5">
                            Select multiple students and apply one attendance status at the current time.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={bulkAttendanceSession}
                            onChange={(e) => setBulkAttendanceSession(e.target.value)}
                            className="bg-white border border-cyan-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="morning">Morning Session</option>
                            <option value="evening">Evening Session</option>
                          </select>
                          <span className="px-3 py-2 rounded-xl bg-cyan-100 text-cyan-900 border border-cyan-200 text-xs font-black">
                            {selectedAttendanceAthleteIds.length} Selected
                          </span>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_220px_auto] gap-2">
                          <input
                            type="search"
                            value={bulkAttendanceSearch}
                            onChange={(e) => setBulkAttendanceSearch(e.target.value)}
                            placeholder="Search student, ID or team..."
                            className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                          />
                          <select
                            value={bulkAttendanceTeam}
                            onChange={(e) => setBulkAttendanceTeam(e.target.value)}
                            className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="">All Teams</option>
                            {bulkAttendanceTeams.map((teamName) => (
                              <option key={teamName} value={teamName}>{teamName}</option>
                            ))}
                          </select>
                          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={bulkAttendanceRows.length > 0 && bulkAttendanceRows.every((row) => selectedAttendanceAthleteIds.includes(row.athlete_id))}
                              onChange={(e) => {
                                const visibleIds = bulkAttendanceRows.map((row) => row.athlete_id);
                                setSelectedAttendanceAthleteIds((current) => e.target.checked
                                  ? [...new Set([...current, ...visibleIds])]
                                  : current.filter((id) => !visibleIds.includes(id)));
                              }}
                              className="rounded border-slate-300 text-cyan-700 focus:ring-cyan-500"
                            />
                            Select all shown ({bulkAttendanceRows.length})
                          </label>
                        </div>

                        <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                          {bulkAttendanceRows.map((row) => {
                            const selected = selectedAttendanceAthleteIds.includes(row.athlete_id);
                            const sessionStatus = row[`${bulkAttendanceSession}_status`];
                            return (
                              <label key={row.athlete_id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition ${selected ? "bg-cyan-50" : "hover:bg-slate-50"}`}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => setSelectedAttendanceAthleteIds((current) => e.target.checked
                                    ? [...current, row.athlete_id]
                                    : current.filter((id) => id !== row.athlete_id))}
                                  className="rounded border-slate-300 text-cyan-700 focus:ring-cyan-500"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-bold text-slate-900 truncate">{row.athlete_name}</div>
                                  <div className="text-[10px] text-slate-500 truncate">
                                    {row.id_number || row.bib_no || "No ID"} &bull; {row.team_name || "General Squad"}
                                  </div>
                                </div>
                                <span className={`text-[10px] font-bold uppercase ${sessionStatus === "present" ? "text-emerald-700" : sessionStatus === "absent" ? "text-rose-700" : sessionStatus ? "text-amber-700" : "text-slate-400"}`}>
                                  {sessionStatus || "Unmarked"}
                                </span>
                              </label>
                            );
                          })}
                          {!bulkAttendanceRows.length && (
                            <div className="p-6 text-center text-xs text-slate-400">No students match the selected filters.</div>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => handleBulkAttendanceSave("present")} disabled={savingBulkAttendance || !selectedAttendanceAthleteIds.length} className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-40">
                            Mark Present Now
                          </button>
                          <button type="button" onClick={() => handleBulkAttendanceSave("absent")} disabled={savingBulkAttendance || !selectedAttendanceAthleteIds.length} className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold disabled:opacity-40">
                            Mark Absent
                          </button>
                          <button type="button" onClick={() => handleBulkAttendanceSave("late")} disabled={savingBulkAttendance || !selectedAttendanceAthleteIds.length} className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold disabled:opacity-40">
                            Mark Late Now
                          </button>
                          <button type="button" onClick={() => handleBulkAttendanceSave("permission")} disabled={savingBulkAttendance || !selectedAttendanceAthleteIds.length} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold disabled:opacity-40">
                            Mark Permission
                          </button>
                          {selectedAttendanceAthleteIds.length > 0 && (
                            <button type="button" onClick={() => setSelectedAttendanceAthleteIds([])} disabled={savingBulkAttendance} className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold">
                              Clear Selection
                            </button>
                          )}
                          {savingBulkAttendance && <span className="text-xs font-bold text-cyan-700 flex items-center gap-1"><FaSpinner className="animate-spin" /> Saving...</span>}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900">Team-wise Attendance Pending</h3>
                          <p className="text-[10px] text-slate-500 mt-0.5">Pending In/Absent decisions and Out submissions for {attendanceDate}.</p>
                        </div>
                        <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          {teamAttendancePending.filter((team) => team.morning_pending + team.morning_out_pending + team.evening_pending + team.evening_out_pending > 0).length} Teams Pending
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px] text-xs text-slate-800">
                          <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-600 border-b border-slate-200">
                            <tr>
                              <th className="p-3 text-left">Team / Coach</th>
                              <th className="p-3 text-center">Players</th>
                              <th className="p-3 text-center">Morning In/Absent</th>
                              <th className="p-3 text-center">Morning Out/Absent</th>
                              <th className="p-3 text-center">Evening In/Absent</th>
                              <th className="p-3 text-center">Evening Out/Absent</th>
                              <th className="p-3 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {teamAttendancePending.map((team) => {
                              const pending = team.morning_pending + team.morning_out_pending + team.evening_pending + team.evening_out_pending;
                              return (
                                <tr key={team.team_name} className={pending > 0 ? "bg-amber-50/40" : "bg-emerald-50/30"}>
                                  <td className="p-3">
                                    <div className="font-bold text-slate-900">{team.team_name}</div>
                                    <div className="text-[10px] text-slate-500">{team.coach_names || "Coach not assigned"}</div>
                                  </td>
                                  <td className="p-3 text-center font-bold">{team.total}</td>
                                  <td className="p-3 text-center">
                                    <div className="font-bold text-emerald-700">Present: {team.morning_present}</div>
                                    <div className="font-bold text-rose-700">Absent: {team.morning_absent}</div>
                                    <div className="font-bold text-amber-700">Pending: {team.morning_pending}</div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <div className="font-bold text-emerald-700">Out: {team.morning_out_complete}</div>
                                    <div className="font-bold text-rose-700">Absent: {team.morning_absent}</div>
                                    <div className="font-bold text-amber-700">Pending: {team.morning_out_pending}</div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <div className="font-bold text-emerald-700">Present: {team.evening_present}</div>
                                    <div className="font-bold text-rose-700">Absent: {team.evening_absent}</div>
                                    <div className="font-bold text-amber-700">Pending: {team.evening_pending}</div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <div className="font-bold text-emerald-700">Out: {team.evening_out_complete}</div>
                                    <div className="font-bold text-rose-700">Absent: {team.evening_absent}</div>
                                    <div className="font-bold text-amber-700">Pending: {team.evening_out_pending}</div>
                                  </td>
                                  <td className="p-3 text-center">
                                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold border ${pending > 0 ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-emerald-100 text-emerald-800 border-emerald-300"}`}>
                                      {pending > 0 ? `${pending} Pending` : "Complete"}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                            {teamAttendancePending.length === 0 && (
                              <tr><td colSpan={7} className="p-8 text-center text-slate-400">No active athletes found for this camp.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    {attendanceRecords.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: "Morning Present", val: attendanceRecords.filter(r => r.morning_status === "present").length, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
                          { label: "Morning Absent", val: attendanceRecords.filter(r => r.morning_status === "absent").length, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
                          { label: "Evening Present", val: attendanceRecords.filter(r => r.evening_status === "present").length, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
                          { label: "Evening Absent", val: attendanceRecords.filter(r => r.evening_status === "absent").length, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
                        ].map(({ label, val, color, bg }) => (
                          <div key={label} className={`${bg} border rounded-2xl p-3.5 text-center shadow-sm`}>
                            <div className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider mb-1">{label}</div>
                            <div className={`text-2xl font-black ${color}`}>{val}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-slate-800 min-w-[600px]">
                          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="p-3.5 pl-5">Athlete</th>
                              <th className="p-3.5">ID Number</th>
                              <th className="p-3.5">Assigned Coach</th>
                              <th className="p-3.5 text-center">Morning</th>
                              <th className="p-3.5 text-center">Evening</th>
                              <th className="p-3.5">Remarks</th>
                              <th className="p-3.5 pr-5 text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {attendanceRecords.map((r) => (
                              <tr key={r.id} className="hover:bg-slate-50/80">
                                <td className="p-3.5 pl-5 font-bold text-slate-900">{r.athlete?.name}</td>
                                <td className="p-3.5 font-bold text-amber-600 font-mono">{r.athlete?.id_number || r.athlete?.bib_no}</td>
                                <td className="p-3.5">
                                  <div className="font-semibold text-slate-900">{r.coach?.name}</div>
                                  {r.coach?.team_name && <div className="text-[10px] text-cyan-700 font-semibold">{r.coach.team_name}</div>}
                                </td>
                                <td className="p-3.5 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${r.morning_status === "present" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : r.morning_status === "absent" ? "bg-rose-100 text-rose-800 border-rose-300" : r.morning_status === "late" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                                      {r.morning_status?.toUpperCase() || "—"}
                                    </span>
                                    {r.morning_in && <span className="text-[9px] text-slate-500 font-mono">{r.morning_in}</span>}
                                  </div>
                                </td>
                                <td className="p-3.5 text-center">
                                  <div className="flex flex-col items-center gap-1">
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${r.evening_status === "present" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : r.evening_status === "absent" ? "bg-rose-100 text-rose-800 border-rose-300" : r.evening_status === "late" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                                      {r.evening_status?.toUpperCase() || "—"}
                                    </span>
                                    {r.evening_in && <span className="text-[9px] text-slate-500 font-mono">{r.evening_in}</span>}
                                  </div>
                                </td>
                                <td className="p-3.5 text-slate-600 text-[11px]">{r.remarks || <span className="text-slate-300">—</span>}</td>
                                <td className="p-3.5 pr-5 text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      onClick={() => setEditingAthleteAttendanceRow({ ...r })}
                                      title="Edit Attendance & Time"
                                      className="px-2.5 py-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 font-bold rounded-lg text-xs transition border border-cyan-200"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteAttendanceRecord(r.id, r.athlete?.name)}
                                      title="Delete Attendance Entry"
                                      className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-lg text-xs transition border border-rose-200"
                                    >
                                      <FaTrash className="w-3 h-3" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {!attendanceRecords.length && (
                              <tr>
                                <td colSpan={7} className="p-10 text-center text-slate-400 text-xs">
                                  No attendance records for {attendanceDate}. Coaches mark attendance live from their portal.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Edit Athlete Attendance Modal */}
                    {editingAthleteAttendanceRow && (
                      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                          <div className="bg-gradient-to-r from-cyan-800 to-teal-800 text-white p-5 flex justify-between items-center">
                            <div>
                              <h3 className="text-base font-bold">Edit Athlete Attendance &amp; Time</h3>
                              <p className="text-xs text-cyan-100">
                                {editingAthleteAttendanceRow.athlete?.name} &bull; {attendanceDate}
                              </p>
                            </div>
                            <button
                              onClick={() => setEditingAthleteAttendanceRow(null)}
                              className="text-white/80 hover:text-white p-1 rounded-lg"
                            >
                              ✕
                            </button>
                          </div>

                          <form onSubmit={handleSaveEditedAthleteAttendance} className="p-6 space-y-4 text-xs">
                            <div className="space-y-2">
                              <label className="block text-xs font-bold text-slate-700">Morning Attendance</label>
                              <AttendanceActionButtons
                                status={editingAthleteAttendanceRow.morning_status}
                                inTime={editingAthleteAttendanceRow.morning_in}
                                outTime={editingAthleteAttendanceRow.morning_out}
                                allowOverride
                                onAction={(action) => setEditingAthleteAttendanceRow((prev) => {
                                  const time = getCurrentISTTime();
                                  if (action === "in") return { ...prev, morning_status: "present", morning_in: time, morning_out: null };
                                  if (action === "out" && prev.morning_in) return { ...prev, morning_out: time };
                                  if (action === "absent") return { ...prev, morning_status: "absent", morning_in: null, morning_out: null };
                                  return prev;
                                })}
                              />
                              {editingAthleteAttendanceRow.morning_status !== "absent" && (
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="text-[10px] font-semibold text-slate-600">
                                    In time
                                    <input type="time" value={editingAthleteAttendanceRow.morning_in || ""} onChange={(e) => setEditingAthleteAttendanceRow((prev) => ({ ...prev, morning_status: e.target.value ? "present" : prev.morning_status, morning_in: e.target.value || null, morning_out: e.target.value ? prev.morning_out : null }))} className="mt-1 w-full border border-slate-300 rounded-lg p-2 text-xs" />
                                  </label>
                                  <label className="text-[10px] font-semibold text-slate-600">
                                    Out time
                                    <input type="time" value={editingAthleteAttendanceRow.morning_out || ""} disabled={!editingAthleteAttendanceRow.morning_in} onChange={(e) => setEditingAthleteAttendanceRow((prev) => ({ ...prev, morning_out: e.target.value || null }))} className="mt-1 w-full border border-slate-300 rounded-lg p-2 text-xs disabled:bg-slate-100" />
                                  </label>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <label className="block text-xs font-bold text-slate-700">Evening Attendance</label>
                              <AttendanceActionButtons
                                status={editingAthleteAttendanceRow.evening_status}
                                inTime={editingAthleteAttendanceRow.evening_in}
                                outTime={editingAthleteAttendanceRow.evening_out}
                                allowOverride
                                onAction={(action) => setEditingAthleteAttendanceRow((prev) => {
                                  const time = getCurrentISTTime();
                                  if (action === "in") return { ...prev, evening_status: "present", evening_in: time, evening_out: null };
                                  if (action === "out" && prev.evening_in) return { ...prev, evening_out: time };
                                  if (action === "absent") return { ...prev, evening_status: "absent", evening_in: null, evening_out: null };
                                  return prev;
                                })}
                              />
                              {editingAthleteAttendanceRow.evening_status !== "absent" && (
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="text-[10px] font-semibold text-slate-600">
                                    In time
                                    <input type="time" value={editingAthleteAttendanceRow.evening_in || ""} onChange={(e) => setEditingAthleteAttendanceRow((prev) => ({ ...prev, evening_status: e.target.value ? "present" : prev.evening_status, evening_in: e.target.value || null, evening_out: e.target.value ? prev.evening_out : null }))} className="mt-1 w-full border border-slate-300 rounded-lg p-2 text-xs" />
                                  </label>
                                  <label className="text-[10px] font-semibold text-slate-600">
                                    Out time
                                    <input type="time" value={editingAthleteAttendanceRow.evening_out || ""} disabled={!editingAthleteAttendanceRow.evening_in} onChange={(e) => setEditingAthleteAttendanceRow((prev) => ({ ...prev, evening_out: e.target.value || null }))} className="mt-1 w-full border border-slate-300 rounded-lg p-2 text-xs disabled:bg-slate-100" />
                                  </label>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-bold text-slate-700 mb-1">
                                Remarks / Excuse Reason
                              </label>
                              <textarea
                                rows={2}
                                value={editingAthleteAttendanceRow.remarks || ""}
                                onChange={(e) =>
                                  setEditingAthleteAttendanceRow((prev) => ({ ...prev, remarks: e.target.value }))
                                }
                                placeholder="e.g. Medical excuse / attended with permission"
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-800 focus:ring-2 focus:ring-cyan-500"
                              />
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                              <button
                                type="button"
                                onClick={() => handleDeleteAttendanceRecord(editingAthleteAttendanceRow.id, editingAthleteAttendanceRow.athlete?.name)}
                                className="px-3 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold rounded-xl border border-rose-200 flex items-center gap-1.5 transition text-xs"
                                title="Permanently delete this attendance entry"
                              >
                                <FaTrash /> Delete Entry
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingAthleteAttendanceRow(null)}
                                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={savingAthleteAttendance}
                                className="flex-1 py-2.5 bg-cyan-700 hover:bg-cyan-800 text-white font-bold rounded-xl shadow-sm disabled:opacity-50 text-xs"
                              >
                                {savingAthleteAttendance ? "Saving..." : "Save Changes"}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* SUB-TAB: COACH ATTENDANCE MARKING & EDITING */}
                {attendanceSubTab === "coach_attendance" && (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 p-4 rounded-2xl shadow-sm">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                          <FaUserShield className="text-emerald-700" /> Coaching Staff Attendance Register &amp; Daily Tracking
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Mark and edit daily attendance for all appointed coaches and assistant coaches.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="date"
                          value={coachAttendanceDate}
                          max={todayIST}
                          onChange={(e) => {
                            const newDate = e.target.value;
                            setCoachAttendanceDate(newDate);
                            fetchCoachAttendanceSheet(selectedCampId, newDate);
                          }}
                          className="bg-white border border-slate-300 text-slate-900 rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-emerald-500 shadow-sm font-semibold focus:outline-none"
                        />
                        <button
                          onClick={() => fetchCoachAttendanceSheet(selectedCampId, coachAttendanceDate)}
                          disabled={loadingCoachAttendance}
                          className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition"
                        >
                          <FaRedoAlt className={loadingCoachAttendance ? "animate-spin" : ""} /> Refresh
                        </button>
                      </div>
                    </div>

                    {/* KPI Strip for Coaches */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: "Total Coaches", val: coachAttendanceSheet.length, color: "text-slate-900", bg: "bg-white border-slate-200" },
                        { label: "Morning Present", val: coachAttendanceSheet.filter((r) => r.morning_status === "present").length, color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
                        { label: "Evening Present", val: coachAttendanceSheet.filter((r) => r.evening_status === "present").length, color: "text-teal-700", bg: "bg-teal-50 border-teal-200" },
                        { label: "Absentees (Any)", val: coachAttendanceSheet.filter((r) => r.morning_status === "absent" || r.evening_status === "absent").length, color: "text-rose-700", bg: "bg-rose-50 border-rose-200" },
                      ].map(({ label, val, color, bg }) => (
                        <div key={label} className={`${bg} border rounded-2xl p-3.5 text-center shadow-sm`}>
                          <div className="text-[10px] text-slate-600 font-semibold uppercase tracking-wider mb-1">{label}</div>
                          <div className={`text-2xl font-black ${color}`}>{val}</div>
                        </div>
                      ))}
                    </div>

                    {/* Coach Attendance Interactive Table */}
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-800">
                          Coaches Attendance Sheet ({coachAttendanceSheet.length})
                        </span>
                        <button
                          onClick={() => {
                            const records = coachAttendanceSheet.map((r) => ({
                              coach_id: r.coach_id,
                              morning_status: r.morning_status,
                              morning_in: r.morning_in,
                              morning_out: r.morning_out,
                              evening_status: r.evening_status,
                              evening_in: r.evening_in,
                              evening_out: r.evening_out,
                              remarks: r.remarks,
                            }));
                            handleSaveCoachAttendance(records);
                          }}
                          disabled={savingCoachAttendance || coachAttendanceSheet.length === 0}
                          className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs font-bold shadow-sm transition disabled:opacity-50 flex items-center gap-1.5"
                        >
                          <FaSave /> {savingCoachAttendance ? "Saving All..." : "Save All Coach Attendance"}
                        </button>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs text-slate-800">
                          <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="p-3 pl-4 w-10 text-center">#</th>
                              <th className="p-3">Coach Name</th>
                              <th className="p-3">Team / Discipline</th>
                              <th className="p-3 text-center min-w-[210px]">Morning Attendance</th>
                              <th className="p-3 text-center min-w-[210px]">Evening Attendance</th>
                              <th className="p-3">Remarks / Notes</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {loadingCoachAttendance ? (
                              <tr>
                                <td colSpan="6" className="p-10 text-center text-slate-400 text-xs">
                                  Loading coach attendance register...
                                </td>
                              </tr>
                            ) : coachAttendanceSheet.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="p-10 text-center text-slate-400 text-xs">
                                  No coaches enrolled in this coaching camp.
                                </td>
                              </tr>
                            ) : (
                              coachAttendanceSheet.map((c, idx) => (
                                <tr key={c.coach_id} className="hover:bg-slate-50/80">
                                  <td className="p-3 text-center text-slate-400 font-mono text-[11px]">
                                    {idx + 1}
                                  </td>
                                  <td className="p-3">
                                    <div className="font-bold text-slate-900">{c.coach_name}</div>
                                    <div className="text-[10px] text-slate-500 font-mono">{c.coach_mobile || "No Mobile"}</div>
                                  </td>
                                  <td className="p-3">
                                    <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-[10px] font-bold">
                                      {c.team_name || "Camp Squad"}
                                    </span>
                                  </td>
                                  <td className="p-3 text-center">
                                    <AttendanceActionButtons
                                      status={c.morning_status}
                                      inTime={c.morning_in}
                                      outTime={c.morning_out}
                                      disabled={savingCoachAttendance}
                                      allowOverride
                                      onAction={(action) => updateCoachAttendanceSession(c.coach_id, "morning", action)}
                                    />
                                  </td>
                                  <td className="p-3 text-center">
                                    <AttendanceActionButtons
                                      status={c.evening_status}
                                      inTime={c.evening_in}
                                      outTime={c.evening_out}
                                      disabled={savingCoachAttendance}
                                      allowOverride
                                      onAction={(action) => updateCoachAttendanceSession(c.coach_id, "evening", action)}
                                    />
                                  </td>
                                  <td className="p-3">
                                    <input
                                      type="text"
                                      value={c.remarks || ""}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setCoachAttendanceSheet((prev) =>
                                          prev.map((item) =>
                                            item.coach_id === c.coach_id ? { ...item, remarks: val } : item
                                          )
                                        );
                                      }}
                                      placeholder="Coach remarks..."
                                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs font-medium focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* SUB-TAB 3: UPLOAD FINAL DAILY ATTENDANCE & WORKOUT/PERFORMANCE REPORTS */}
                {attendanceSubTab === "upload" && (
                  <div className="space-y-6">
                    {/* Upload Form Box */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm max-w-3xl mx-auto">
                      {/* Option Selector Toggle */}
                      <div className="flex border-b border-slate-200 pb-3 mb-4 gap-2">
                        <button
                          type="button"
                          onClick={() => setUploadReportType("attendance")}
                          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${uploadReportType === "attendance"
                            ? "bg-cyan-700 text-white shadow-sm"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                        >
                          <FaClipboardCheck /> Final End-of-Day Attendance Report
                        </button>
                        <button
                          type="button"
                          onClick={() => setUploadReportType("workout_performance")}
                          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${uploadReportType === "workout_performance"
                            ? "bg-emerald-700 text-white shadow-sm"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                        >
                          <FaRunning /> Daily Workouts &amp; Athlete Performance Report
                        </button>
                      </div>

                      <div className="pb-4 mb-5 border-b border-slate-100">
                        <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
                          <FaUpload className={uploadReportType === "workout_performance" ? "text-emerald-700" : "text-cyan-700"} />
                          {uploadReportType === "workout_performance"
                            ? "Upload Daily Workouts & Individual Performance Report"
                            : "Upload Final End-of-Day Attendance Report File"}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {uploadReportType === "workout_performance"
                            ? "Record daily training sessions, drill performance, and upload workout evaluation sheets or videos/scorecards for individual athletes."
                            : "Upload the signed physical register scan, attendance sheet, or official consolidated report at the end of the day."}
                        </p>
                      </div>

                      <form onSubmit={handleUploadDailyReport} className="space-y-4 text-xs">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                              Report Date <span className="text-rose-600">*</span>
                            </label>
                            <input
                              type="date"
                              value={uploadReportForm.date}
                              max={todayIST}
                              onChange={(e) => setUploadReportForm({ ...uploadReportForm, date: e.target.value })}
                              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-cyan-500 shadow-sm"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                              Coach (Optional / Uploading Coach)
                            </label>
                            <select
                              value={uploadReportForm.coach_id}
                              onChange={(e) => setUploadReportForm({ ...uploadReportForm, coach_id: e.target.value })}
                              className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-cyan-500 shadow-sm"
                            >
                              <option value="">All Coaches / Camp Administration</option>
                              {coaches.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name} {c.team_name ? `(${c.team_name})` : ""}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Additional fields for Daily Workouts & Individual Performance */}
                        {uploadReportType === "workout_performance" && (
                          <div className="space-y-4 pt-2 border-t border-slate-100 bg-emerald-50/40 p-3.5 rounded-xl border border-emerald-100">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                                  Individual Athlete (Optional)
                                </label>
                                <select
                                  value={uploadReportForm.athlete_id}
                                  onChange={(e) => setUploadReportForm({ ...uploadReportForm, athlete_id: e.target.value })}
                                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-emerald-500 shadow-sm"
                                >
                                  <option value="">Entire Squad / Discipline Group</option>
                                  {athletes.filter((a) => a.status !== "removed_from_camp").map((a) => (
                                    <option key={a.id} value={a.id}>
                                      {a.name} ({a.bib_no || "No BIB"}) &bull; {a.discipline_name || "Camp"}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                                  Training Session
                                </label>
                                <select
                                  value={uploadReportForm.training_session}
                                  onChange={(e) => setUploadReportForm({ ...uploadReportForm, training_session: e.target.value })}
                                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-emerald-500 shadow-sm"
                                >
                                  <option value="both">Both Sessions (Morning &amp; Evening)</option>
                                  <option value="morning">Morning Session Only</option>
                                  <option value="evening">Evening Session Only</option>
                                </select>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                                  Performance Rating
                                </label>
                                <select
                                  value={uploadReportForm.performance_rating}
                                  onChange={(e) => setUploadReportForm({ ...uploadReportForm, performance_rating: e.target.value })}
                                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-emerald-500 shadow-sm"
                                >
                                  <option value="Outstanding">Outstanding</option>
                                  <option value="Very Good">Very Good</option>
                                  <option value="Good">Good</option>
                                  <option value="Average">Average</option>
                                  <option value="Needs Improvement">Needs Improvement</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                                  Coach Remarks / Feedback
                                </label>
                                <input
                                  type="text"
                                  value={uploadReportForm.remarks}
                                  onChange={(e) => setUploadReportForm({ ...uploadReportForm, remarks: e.target.value })}
                                  placeholder="e.g. Excellent endurance, needs work on sprint start"
                                  className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-emerald-500 shadow-sm"
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-[11px]">
                                Daily Workout Details &amp; Drill Activities
                              </label>
                              <textarea
                                rows={2}
                                value={uploadReportForm.activity_details}
                                onChange={(e) => setUploadReportForm({ ...uploadReportForm, activity_details: e.target.value })}
                                placeholder="Detail the drills, sets, reps, distance covered, technical focus, and load parameters..."
                                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-slate-900 font-semibold focus:ring-2 focus:ring-emerald-500 shadow-sm"
                              />
                            </div>
                          </div>
                        )}

                        <div>
                          <FileUploadWithScanner
                            label="Select File (PDF / Scanned Image / Document)"
                            name="file"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls"
                            required
                            value={uploadReportForm.file}
                            onChange={(file) => setUploadReportForm({ ...uploadReportForm, file })}
                            scannerTitle="Scan Report / Sheet"
                            targetFileName={`camp_report_${Date.now()}.jpg`}
                            helpText="Accepted formats: PDF, JPG, PNG, DOC, DOCX, XLSX (Max 20MB)"
                          />
                        </div>

                        <div className="pt-2 flex justify-end">
                          <button
                            type="submit"
                            disabled={uploadingReport}
                            className={`px-6 py-2.5 rounded-xl font-bold shadow text-xs flex items-center gap-2 transition disabled:opacity-50 text-white ${uploadReportType === "workout_performance"
                              ? "bg-emerald-700 hover:bg-emerald-800"
                              : "bg-cyan-700 hover:bg-cyan-800"
                              }`}
                          >
                            <FaUpload /> {uploadingReport ? "Uploading Report..." : uploadReportType === "workout_performance" ? "Upload Workout & Performance Report" : "Upload Final Report File"}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Uploaded Reports History with Coach & Type Filters */}
                    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-slate-100 pb-3">
                        <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                          <FaClipboardCheck className="text-cyan-700" /> Uploaded Reports Archive
                        </h4>

                        {/* Filters Toolbar */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Live Search */}
                          <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-bold text-slate-600">Search:</label>
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Search file, coach, athlete..."
                                value={reportArchiveSearch}
                                onChange={(e) => setReportArchiveSearch(e.target.value)}
                                className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-sm w-44"
                              />
                              {reportArchiveSearch && (
                                <button
                                  type="button"
                                  onClick={() => setReportArchiveSearch("")}
                                  className="absolute right-2 top-1.5 text-xs text-slate-400 hover:text-slate-700 font-bold"
                                  title="Clear search"
                                >
                                  &times;
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Filter by Date */}
                          <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-bold text-slate-600">Date:</label>
                            <input
                              type="date"
                              value={reportArchiveDateFilter}
                              onChange={(e) => setReportArchiveDateFilter(e.target.value)}
                              className="bg-slate-50 border border-slate-300 rounded-xl px-2 py-1 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-sm cursor-pointer"
                            />
                            {reportArchiveDateFilter && (
                              <button
                                type="button"
                                onClick={() => setReportArchiveDateFilter("")}
                                className="text-xs text-slate-400 hover:text-slate-700 font-bold px-1"
                                title="Clear date filter"
                              >
                                &times;
                              </button>
                            )}
                          </div>

                          {/* Filter by Coach */}
                          <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-bold text-slate-600">Coach:</label>
                            <select
                              value={reportCoachFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                setReportCoachFilter(val);
                                fetchDailyReportsList(selectedCampId, val, reportTypeFilter);
                              }}
                              className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-sm"
                            >
                              <option value="all">All Coaches / All Uploaders</option>
                              {coaches.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name} {c.team_name ? `(${c.team_name})` : ""}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Filter by Type */}
                          <div className="flex items-center gap-1.5">
                            <label className="text-[11px] font-bold text-slate-600">Category:</label>
                            <select
                              value={reportTypeFilter}
                              onChange={(e) => {
                                const val = e.target.value;
                                setReportTypeFilter(val);
                                fetchDailyReportsList(selectedCampId, reportCoachFilter, val);
                              }}
                              className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-cyan-500 shadow-sm"
                            >
                              <option value="all">All Report Categories</option>
                              <option value="attendance">End-of-Day Attendance Reports</option>
                              <option value="workout_performance">Workout &amp; Performance Reports</option>
                            </select>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setReportArchiveSearch("");
                              setReportArchiveDateFilter("");
                              fetchDailyReportsList(selectedCampId, reportCoachFilter, reportTypeFilter);
                            }}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 transition"
                            title="Reset filters & reload"
                          >
                            <FaRedoAlt className={uploadedReportsLoading ? "animate-spin" : ""} /> Refresh
                          </button>
                        </div>
                      </div>

                      {uploadedReportsLoading ? (
                        <div className="text-center py-8 text-xs text-slate-400">Loading uploaded reports...</div>
                      ) : filteredUploadedReports.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs text-slate-800">
                            <thead className="bg-slate-50 text-[10px] uppercase font-bold text-slate-600 border-b border-slate-200">
                              <tr>
                                <th className="p-3 pl-4">File Name</th>
                                <th className="p-3">Report Category</th>
                                <th className="p-3">Uploaded By (Coach / Official)</th>
                                <th className="p-3">Athlete / Target</th>
                                <th className="p-3">Date &amp; Time (IST)</th>
                                <th className="p-3">Size</th>
                                <th className="p-3 pr-4 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {filteredUploadedReports.map((file, idx) => (
                                <tr key={file.id || idx} className="hover:bg-slate-50">
                                  <td className="p-3 pl-4 font-bold text-slate-900 flex items-center gap-2">
                                    <FaFileAlt className="text-cyan-700 shrink-0" />
                                    <span className="truncate max-w-[220px] sm:max-w-xs">{file.file_name}</span>
                                  </td>
                                  <td className="p-3">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${file.report_type === "workout_performance"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                      : "bg-cyan-50 text-cyan-700 border-cyan-300"
                                      }`}>
                                      {file.report_type === "workout_performance" ? "Daily Workout & Performance" : "Attendance Report"}
                                    </span>
                                  </td>
                                  <td className="p-3">
                                    {file.coach_name ? (
                                      <div>
                                        <div className="font-bold text-slate-900">{file.coach_name}</div>
                                        {file.team_name && <div className="text-[10px] text-cyan-700 font-semibold">{file.team_name}</div>}
                                      </div>
                                    ) : (
                                      <span className="text-slate-500 font-semibold">Camp Administration</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-slate-700">
                                    {file.athlete_name ? (
                                      <span className="font-bold text-slate-900">{file.athlete_name}</span>
                                    ) : (
                                      <span className="text-slate-400">All Camp / Squad</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-slate-600 font-mono text-[11px]">{file.uploaded_at}</td>
                                  <td className="p-3 text-slate-500 font-mono">{file.size}</td>
                                  <td className="p-3 pr-4 text-right">
                                    <a
                                      href={file.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-[11px] font-bold transition"
                                    >
                                      <FaEye className="text-cyan-700" /> View / Download
                                    </a>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-xs text-slate-400">
                          {uploadedReports.length > 0
                            ? "No reports match your search or date filters."
                            : "No report files found matching the selected filters."}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* ============================================================== */}
            {/* TAB 7: REPORT CARDS */}
            {/* ============================================================== */}
            {activeTab === "report_cards" && (
              <div className="space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm print:hidden">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <FaClipboardCheck className="text-cyan-700" /> Athlete Report Cards (Weeks 1 to 9)
                    </h2>
                    <p className="text-xs text-slate-500">
                      Performance evaluations on Endurance, Strength, Agility, Skill, Technique, and Discipline with official print formats.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => handleDirectPrintOfficialReportCards(true)}
                      className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition cursor-pointer"
                      title="Print blank Weekly Evaluation Report using selected filters"
                    >
                      <FaPrint className="text-cyan-700" /> Print Weekly Evaluation
                    </button>

                    <button
                      onClick={() => handlePrintOfficialReportCards(true)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition cursor-pointer"
                      title="Download blank Weekly Evaluation Report as PDF"
                    >
                      <FaDownload className="text-cyan-700" /> Weekly Evaluation Report (PDF)
                    </button>

                    <button
                      onClick={() => handleDirectPrintOfficialReportCards(false)}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition cursor-pointer"
                      title="Direct print weekly report cards to printer"
                    >
                      <FaPrint className="text-cyan-300" /> Print Filled Weekly Evaluation
                    </button>

                    <button
                      onClick={() => handlePrintOfficialReportCards(false)}
                      className="px-4 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition shadow cursor-pointer"
                      title="Download populated weekly report cards with entered performance data as PDF"
                    >
                      <FaDownload /> Filled Weekly Evaluation (PDF) {reportCardAthleteFilter && reportCardAthleteFilter !== "all" ? "(Selected Athlete)" : "(All Athletes)"}
                    </button>

                    <button
                      onClick={handlePrintIndividualStudentReports}
                      className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition shadow cursor-pointer"
                      title="Print or download official individual athlete daily evaluation sheet (1 page per day per athlete)"
                    >
                      <FaPrint /> Daily Athlete Performance Report ({reportCardAthleteFilter && reportCardAthleteFilter !== "all" ? "Selected Athlete" : `All ${athletes.length} Athletes`})
                    </button>

                    <button
                      onClick={handleExportReportCardsExcel}
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-1.5 transition shadow cursor-pointer"
                    >
                      <FaFileExcel /> Export Excel
                    </button>

                    <button
                      onClick={handleExportEmptyExcel}
                      className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                    >
                      <FaFileExcel className="text-emerald-600" /> Empty Template
                    </button>
                  </div>
                </div>

                {/* Daily Athlete Performance Report Configuration Modal */}
                {individualModalOpen && (
                  <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto print:hidden">
                    <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
                      <div className="bg-gradient-to-r from-emerald-800 via-teal-800 to-cyan-900 p-5 text-white flex items-center justify-between shrink-0">
                        <div>
                          <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-200">Official Daily Evaluation</span>
                          <h3 className="text-lg sm:text-xl font-black tracking-tight text-white flex items-center gap-2 mt-0.5">
                            <FaFileAlt /> Daily Athlete Performance Report
                          </h3>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIndividualModalOpen(false)}
                          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-lg transition-colors"
                        >
                          &times;
                        </button>
                      </div>

                      <div className="p-5 space-y-4 text-xs text-slate-700 overflow-y-auto flex-1 min-h-0">
                        {/* Generation Scope (Point 17) */}
                        <div>
                          <label className="block font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-1.5">
                            Report Generation Scope *
                          </label>
                          <div className="grid grid-cols-3 gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => setIndividualScope("all")}
                              className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${individualScope === "all"
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                                }`}
                            >
                              All Athletes ({athletes.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => setIndividualScope("team")}
                              className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${individualScope === "team"
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                                }`}
                            >
                              By Team / Squad
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIndividualScope("single");
                                if (!selectedIndividualAthleteId && athletes.length > 0) {
                                  setSelectedIndividualAthleteId(String(athletes[0].id));
                                }
                              }}
                              className={`p-2.5 rounded-xl border text-xs font-bold text-center transition ${individualScope === "single"
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                                : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                                }`}
                            >
                              Single Athlete
                            </button>
                          </div>

                          {individualScope === "team" && (
                            <div className="mb-2">
                              <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10.5px] mb-1">
                                Select Team *
                              </label>
                              <select
                                value={reportSelectedTeam}
                                onChange={(e) => setReportSelectedTeam(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              >
                                <option value="all">All Teams</option>
                                {distinctTeams.map((team) => (
                                  <option key={team} value={team}>{team}</option>
                                ))}
                              </select>
                            </div>
                          )}

                          {individualScope === "single" && (
                            <div className="mb-2">
                              <label className="block font-bold text-slate-700 uppercase tracking-wider text-[10.5px] mb-1">
                                Select Specific Athlete *
                              </label>
                              <select
                                value={selectedIndividualAthleteId}
                                onChange={(e) => setSelectedIndividualAthleteId(e.target.value)}
                                className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              >
                                {athletes.map((ath) => (
                                  <option key={ath.id} value={ath.id}>
                                    {ath.name} ({ath.bib_no || "No Bib"}) &bull; {ath.district || "AP"}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          <p className="text-[10.5px] text-slate-500">
                            Choose scope to optimize rendering time and generate targeted daily evaluation printouts.
                          </p>
                        </div>

                        {/* Day / Date Mode */}
                        <div>
                          <label className="block font-bold text-slate-800 uppercase tracking-wider text-[11px] mb-1.5">
                            Camp Daily Evaluation (Days) *
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition">
                              <input
                                type="radio"
                                name="adminDayMode"
                                value="all_days"
                                checked={reportDayMode === "all_days"}
                                onChange={() => setReportDayMode("all_days")}
                                className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div>
                                <div className="font-bold text-slate-900">Each and Every Day of Camp (All Days)</div>
                                <p className="text-[10.5px] text-slate-500">
                                  Generates a separate daily training evaluation page for every athlete for each day of the camp (Day 1 to Day N, 1 page per day).
                                </p>
                              </div>
                            </label>

                            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition">
                              <input
                                type="radio"
                                name="adminDayMode"
                                value="single_day"
                                checked={reportDayMode === "single_day"}
                                onChange={() => setReportDayMode("single_day")}
                                className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div className="flex-1">
                                <div className="font-bold text-slate-900">Specific Day / Single Date</div>
                                <p className="text-[10.5px] text-slate-500">
                                  Generates 1 page per athlete for the chosen single camp day.
                                </p>
                              </div>
                            </label>

                            <label className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 cursor-pointer transition">
                              <input
                                type="radio"
                                name="adminDayMode"
                                value="date_range"
                                checked={reportDayMode === "date_range"}
                                onChange={() => setReportDayMode("date_range")}
                                className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                              />
                              <div>
                                <div className="font-bold text-slate-900">Between From and To Dates</div>
                                <p className="text-[10.5px] text-slate-500">Generate reports only for the selected inclusive date range.</p>
                              </div>
                            </label>
                          </div>
                        </div>

                        {reportDayMode === "date_range" && (
                          <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                            <div>
                              <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">From Date</label>
                              <input type="date" value={individualDateFrom} onChange={(e) => setIndividualDateFrom(e.target.value)} className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold" />
                            </div>
                            <div>
                              <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">To Date</label>
                              <input type="date" min={individualDateFrom || undefined} value={individualDateTo} onChange={(e) => setIndividualDateTo(e.target.value)} className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold" />
                            </div>
                          </div>
                        )}

                        {/* If Single Day selected, show Day Number & Date Inputs */}
                        {reportDayMode === "single_day" && (
                          <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                            <div>
                              <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">Camp Day Number</label>
                              <select
                                value={reportSingleDayNum}
                                onChange={(e) => {
                                  const dayNumber = Number(e.target.value);
                                  setReportSingleDayNum(dayNumber);
                                  const matchDay = selectedReportCampDays.find((d) => d.dayNumber === dayNumber);
                                  if (matchDay?.isoDate) {
                                    setReportSingleDate(matchDay.isoDate);
                                  }
                                }}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold focus:ring-2 focus:ring-emerald-500"
                              >
                                {selectedReportCampDays.map((day) => (
                                  <option key={day.dayNumber} value={day.dayNumber}>{day.label}</option>
                                ))}
                                {!selectedReportCampDays.some((d) => d.dayNumber === reportSingleDayNum) && (
                                  <option value={reportSingleDayNum}>
                                    Day {reportSingleDayNum} ({selectedReportDay?.dateStr || reportSingleDate})
                                  </option>
                                )}
                              </select>
                            </div>
                            <div>
                              <label className="block font-bold text-slate-700 text-[10px] uppercase mb-1">Date</label>
                              <input
                                type="date"
                                value={reportSingleDate}
                                onChange={(e) => {
                                  const newDate = e.target.value;
                                  if (!newDate) return;
                                  setReportSingleDate(newDate);
                                  const matchDay = selectedReportCampDays.find((d) => d.isoDate === newDate);
                                  if (matchDay) {
                                    setReportSingleDayNum(matchDay.dayNumber);
                                  } else {
                                    const camp = camps.find((c) => String(c.id) === String(selectedCampId)) || camps[0];
                                    const campStart = camp?.start_date ? new Date(camp.start_date + "T00:00:00") : new Date("2026-08-01T00:00:00");
                                    const chosen = new Date(newDate + "T00:00:00");
                                    if (!isNaN(chosen.getTime()) && !isNaN(campStart.getTime())) {
                                      const diff = Math.round((chosen - campStart) / 86400000) + 1;
                                      setReportSingleDayNum(diff > 0 ? diff : 1);
                                    }
                                  }
                                }}
                                className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                              />
                            </div>
                          </div>
                        )}

                        {/* Holiday watermarks */}
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 space-y-2.5">
                          <div>
                            <div className="font-bold text-amber-950 uppercase tracking-wider text-[11px]">
                              Rest Day Watermark (Optional)
                            </div>
                            <p className="text-[10.5px] text-amber-800 mt-0.5">
                              Mark a date as a full-day, morning-session, or evening-session holiday.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                            <div>
                              <label className="block font-bold text-amber-900 text-[10px] uppercase mb-1">Rest Day Date</label>
                              <input
                                type="date"
                                value={individualHolidayDate}
                                onChange={(e) => setIndividualHolidayDate(e.target.value)}
                                className="w-full bg-white border border-amber-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold"
                              />
                            </div>
                            <div>
                              <label className="block font-bold text-amber-900 text-[10px] uppercase mb-1">Rest Day Session</label>
                              <select
                                value={individualHolidaySession}
                                onChange={(e) => setIndividualHolidaySession(e.target.value)}
                                className="w-full bg-white border border-amber-300 rounded-lg px-2.5 py-1.5 text-xs font-semibold"
                              >
                                <option value="full_day">Full Day</option>
                                <option value="morning">Morning Only</option>
                                <option value="evening">Evening Only</option>
                              </select>
                            </div>
                            <button
                              type="button"
                              onClick={addIndividualHoliday}
                              className="px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
                            >
                              Add
                            </button>
                          </div>
                          {Object.keys(individualHolidays).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {Object.entries(individualHolidays)
                                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                                .map(([date, session]) => (
                                  <span key={date} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-[10.5px] font-bold text-amber-900">
                                    {date} &bull; {session === "full_day" ? "Full Day" : session === "morning" ? "Morning" : "Evening"}
                                    <button
                                      type="button"
                                      onClick={() => setIndividualHolidays((current) => {
                                        const next = { ...current };
                                        delete next[date];
                                        return next;
                                      })}
                                      className="text-rose-600 hover:text-rose-800 text-sm leading-none"
                                      aria-label={`Remove holiday on ${date}`}
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                        {/* Dynamic Page Volume Estimator */}
                        {(() => {
                          const estAthletes = individualScope === "single"
                            ? 1
                            : (individualScope === "team" && reportSelectedTeam !== "all"
                              ? Math.max(1, athletes.filter((a) => {
                                const t = a.team_name || a.mappings?.find((m) => m.status === 'active')?.team_name || a.mappings?.find((m) => m.status === 'active')?.coach?.team_name;
                                return t === reportSelectedTeam;
                              }).length)
                              : athletes.length || 1);
                          const rangedDays = (selectedReportCampDays || []).filter((day) =>
                            (!individualDateFrom || day.isoDate >= individualDateFrom) &&
                            (!individualDateTo || day.isoDate <= individualDateTo)
                          );
                          const estDays = reportDayMode === "single_day" ? 1 : reportDayMode === "date_range" ? Math.max(1, rangedDays.length) : Math.max(1, selectedReportCampDays?.length || 60);
                          const estPages = estAthletes * estDays;
                          const isLarge = estPages > 250;

                          return (
                            <div className={`p-3 rounded-xl border text-[11px] font-medium leading-relaxed ${isLarge
                              ? "bg-amber-50 border-amber-300 text-amber-900"
                              : "bg-emerald-50 border-emerald-300 text-emerald-900"
                              }`}>
                              <div className="flex items-center justify-between font-bold text-xs mb-1">
                                <span>Estimated Volume: <strong className={isLarge ? "text-amber-700 font-black" : "text-emerald-700 font-black"}>{estPages.toLocaleString()} Pages</strong></span>
                                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-current">
                                  {estAthletes} {estAthletes === 1 ? "Athlete" : "Athletes"} &bull; {estDays} {estDays === 1 ? "Day" : "Days"}
                                </span>
                              </div>
                              {isLarge ? (
                                <p className="text-[10.5px] text-amber-800">
                                  ⚠️ Large document ({estPages.toLocaleString()} pages). Multi-thousand page PDFs take considerable time in backend. For fast results, select a <strong>Specific Day</strong> or filter <strong>By Team / Squad</strong>.
                                </p>
                              ) : (
                                <p className="text-[10.5px] text-emerald-800">
                                  ⚡ Fast export estimate: ~{Math.max(1, Math.round(estPages * 0.04))}s. Processed smoothly in background and auto-downloaded via Download Manager.
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setIndividualModalOpen(false)}
                          className="px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200 rounded-xl transition cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConfirmIndividualAthletePrint(true)}
                          className="px-4 py-2.5 text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 rounded-xl shadow flex items-center gap-1.5 transition cursor-pointer"
                          title="Direct print to printer"
                        >
                          <FaPrint /> Direct Print
                        </button>
                        <button
                          type="button"
                          onClick={() => handleConfirmIndividualAthletePrint(false)}
                          className="px-5 py-2.5 text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-800 rounded-xl shadow flex items-center gap-1.5 transition cursor-pointer"
                        >
                          <FaDownload /> Generate &amp; Download (PDF)
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filter Bar */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-2xl border border-slate-200 print:hidden">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 uppercase tracking-wider">
                      Filter by Coach
                    </label>
                    <select
                      value={reportCardCoachFilter}
                      onChange={(e) => setReportCardCoachFilter(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-900 focus:ring-2 focus:ring-cyan-500 font-semibold"
                    >
                      <option value="">-- All Coaches --</option>
                      {coaches.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.team_name ? `(${c.team_name})` : ""} - {c.district}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 uppercase tracking-wider">
                      Filter / Select Athlete
                    </label>
                    <select
                      value={reportCardAthleteFilter}
                      onChange={(e) => setReportCardAthleteFilter(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-900 focus:ring-2 focus:ring-cyan-500 font-semibold"
                    >
                      <option value="all">-- All Athletes (Bulk Print Mode) --</option>
                      {athletes
                        .filter((a) => {
                          if (!reportCardCoachFilter) return true;
                          const coachIdStr = String(reportCardCoachFilter);
                          if (String(a.assigned_coach?.id) === coachIdStr) return true;
                          if (a.assigned_coaches && a.assigned_coaches.some((c) => String(c.id) === coachIdStr)) return true;
                          if (a.mappings && a.mappings.some((m) => String(m.coach_id) === coachIdStr)) return true;
                          if (mappings && mappings.some((m) => String(m.athlete_id) === String(a.id) && String(m.coach_id) === coachIdStr && m.status === "active")) return true;
                          return false;
                        })
                        .map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name} (ID: {a.id_number || a.bib_no || "N/A"}) — Coach: {getCoachNameForAthlete(a)}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1 uppercase tracking-wider">
                      Print Scope
                    </label>
                    <select
                      value={printReportScope}
                      onChange={(e) => setPrintReportScope(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-900 focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="all_weeks">Complete 9 Weeks (Single Page per Athlete)</option>
                      <option value="active_weeks">Only Evaluated Weeks</option>
                    </select>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Week</th>
                          <th className="p-3.5">Athlete</th>
                          <th className="p-3.5">ID Number</th>
                          <th className="p-3.5">Coach</th>
                          <th className="p-3.5">Endurance</th>
                          <th className="p-3.5">Strength</th>
                          <th className="p-3.5">Agility</th>
                          <th className="p-3.5">Skill</th>
                          <th className="p-3.5">Technique</th>
                          <th className="p-3.5">Discipline</th>
                          <th className="p-3.5">Rating</th>
                          <th className="p-3.5 pr-5">Document</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {displayedReportCards.map((rc) => (
                          <tr key={rc.id} className="hover:bg-slate-50/80">
                            <td className="p-3.5 pl-5 font-bold text-sky-700">
                              Week {rc.week_number}
                            </td>
                            <td className="p-3.5 font-bold text-slate-900">
                              {rc.athlete?.name}
                            </td>
                            <td className="p-3.5 font-mono text-amber-700 font-semibold">
                              {rc.athlete?.id_number || rc.athlete?.bib_no}
                            </td>
                            <td className="p-3.5 text-slate-700">
                              <div className="font-semibold text-slate-900">
                                {rc.coach?.name || getCoachNameForAthlete(athletes.find((a) => String(a.id) === String(rc.athlete_id)))}
                              </div>
                              {(rc.coach?.team_name || (athletes.find((a) => String(a.id) === String(rc.athlete_id)) && getTeamNameForAthlete(athletes.find((a) => String(a.id) === String(rc.athlete_id))) !== "General Squad")) && (
                                <div className="text-[10px] text-cyan-700 font-semibold">
                                  {rc.coach?.team_name || getTeamNameForAthlete(athletes.find((a) => String(a.id) === String(rc.athlete_id)))}
                                </div>
                              )}
                            </td>
                            <td className="p-3.5">{rc.endurance}</td>
                            <td className="p-3.5">{rc.strength}</td>
                            <td className="p-3.5">{rc.agility}</td>
                            <td className="p-3.5">{rc.skill || (rc.skill_technique ? rc.skill_technique.split('/')[0]?.trim() : '-')}</td>
                            <td className="p-3.5">{rc.technique || (rc.skill_technique ? rc.skill_technique.split('/')[1]?.trim() || '-' : '-')}</td>
                            <td className="p-3.5">{rc.discipline}</td>
                            <td className="p-3.5 font-bold text-emerald-700">
                              {rc.performance_rating || "Good"}
                            </td>
                            <td className="p-3.5 pr-5">
                              {rc.file_path ? (
                                <a
                                  href={resolveAssetUrl(rc.file_path)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-cyan-700 hover:text-cyan-800 font-semibold inline-flex items-center gap-1 hover:underline"
                                >
                                  <FaFileAlt className="text-xs" /> View Doc
                                </a>
                              ) : (
                                <span className="text-slate-400 text-[11px]">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!displayedReportCards.length && (
                          <tr>
                            <td colSpan={12} className="p-8 text-center text-slate-500 text-xs">
                              {reportCards.length
                                ? "No weekly report cards found matching the filter criteria."
                                : "No weekly report cards recorded yet for this camp."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 8: WEEKLY REPORTS */}
            {/* ============================================================== */}
            {activeTab === "weekly_reports" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Weekly PDF Reports Archive</h2>
                    <p className="text-xs text-slate-500">
                      Official coaching documentation submitted weekly by coaches for their assigned athletes.
                    </p>
                  </div>
                  <button
                    onClick={handleExportWeeklyReportsExcel}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 self-start sm:self-auto transition"
                  >
                    <FaFileExcel className="text-sm" /> Export Excel
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {weeklyReports.map((report) => (
                    <div
                      key={report.id}
                      className="p-4 bg-white border border-slate-200 rounded-2xl flex items-center justify-between gap-3 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center font-bold text-xs shrink-0 border border-amber-500/20">
                          W{report.week_number}
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-900">{report.athlete?.name}</h4>
                          <p className="text-xs text-slate-700 line-clamp-2 mt-0.5">
                            {report.description}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-1">
                            Date: {report.report_date} &bull; Coach: {report.coach?.name}
                            {report.coach?.team_name && (
                              <span className="text-cyan-700 font-semibold ml-1">
                                ({report.coach.team_name})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {report.file_path && (
                        <a
                          href={resolveAssetUrl(report.file_path)}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-cyan-50 text-cyan-800 hover:bg-cyan-700 hover:text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 border border-cyan-200 shadow-sm transition"
                        >
                          <FaEye /> PDF
                        </a>
                      )}
                    </div>
                  ))}
                  {!weeklyReports.length && (
                    <div className="col-span-2 text-center py-12 text-slate-500 text-xs">
                      No weekly reports submitted yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 9: CAMP ASSESSMENTS (WEEK 1 VS WEEK 9) */}
            {/* ============================================================== */}
            {activeTab === "camp_reports" && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Performance Assessments (Weeks 1–9)</h2>
                    <p className="text-xs text-slate-500">
                      Weekly benchmark assessments with team-wise PDF downloads from Week 1 through Week 9.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={benchmarkExportWeek}
                      onChange={(e) => setBenchmarkExportWeek(Number(e.target.value))}
                      className="px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800"
                      aria-label="Assessment week"
                    >
                      {Array.from({ length: 9 }, (_, i) => i + 1).map((week) => (
                        <option key={week} value={week}>Week {week}{week === 1 ? " (Initial)" : week === 9 ? " (Final)" : ""}</option>
                      ))}
                    </select>
                    <select
                      value={benchmarkExportTeam}
                      onChange={(e) => setBenchmarkExportTeam(e.target.value)}
                      className="px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 max-w-[220px]"
                      aria-label="Assessment team"
                    >
                      <option value="all">All Teams</option>
                      {availableTeams.map((team) => <option key={team} value={team}>{team}</option>)}
                    </select>
                    <select
                      value={benchmarkExportAthlete}
                      onChange={(e) => setBenchmarkExportAthlete(e.target.value)}
                      className="px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 max-w-[220px]"
                      aria-label="Assessment player"
                    >
                      <option value="all">All Players</option>
                      {athletes.filter((athlete) => benchmarkExportTeam === "all" || getTeamNameForAthlete(athlete) === benchmarkExportTeam).map((athlete) => (
                        <option key={athlete.id} value={athlete.id}>{athlete.name} ({athlete.id_number || athlete.bib_no || "No ID"})</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handlePrintEmptyBenchmarkReport(Number(benchmarkExportWeek), benchmarkExportTeam, "print")}
                      className="px-3.5 py-2 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                      title="Print empty weekly assessment using selected filters"
                    >
                      <FaPrint className="text-sm" /> Print Weekly Assessment Empty
                    </button>
                    <button
                      onClick={() => handlePrintEmptyBenchmarkReport(Number(benchmarkExportWeek), benchmarkExportTeam, "download")}
                      className="px-3.5 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                      title="Download empty weekly assessment using selected filters"
                    >
                      <FaDownload className="text-sm" /> Weekly Assessment Empty (PDF)
                    </button>
                    <button
                      onClick={() => handlePrintPerformanceReport("print")}
                      className="px-3.5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                      title="Print filled weekly assessment using selected filters"
                    >
                      <FaPrint className="text-sm" /> Print Filled Assessment
                    </button>
                    <button
                      onClick={() => handlePrintPerformanceReport("download")}
                      className="px-3.5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                      title="Download filled Benchmark Report with recorded assessment values as PDF"
                    >
                      <FaDownload className="text-sm" /> Filled Weekly Assessment (PDF)
                    </button>
                    <button
                      onClick={handleExportPerformanceExcel}
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2 transition"
                    >
                      <FaFileExcel className="text-sm" /> Export Excel
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-800">
                      <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-700 font-semibold border-b border-slate-200">
                        <tr>
                          <th className="p-3.5 pl-5">Phase</th>
                          <th className="p-3.5">Athlete</th>
                          <th className="p-3.5">ID Number</th>
                          <th className="p-3.5">Height</th>
                          <th className="p-3.5">100m</th>
                          <th className="p-3.5">400m</th>
                          <th className="p-3.5">Shot Put</th>
                          <th className="p-3.5">Long Jump</th>
                          <th className="p-3.5">Skipping</th>
                          <th className="p-3.5">Push-ups</th>
                          <th className="p-3.5">Plank</th>
                          <th className="p-3.5 pr-5">Weight</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {performanceReports.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-50/80">
                            <td className="p-3.5 pl-5 font-bold">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] ${p.week_number === 1
                                  ? "bg-indigo-500/20 text-indigo-300"
                                  : "bg-purple-500/20 text-purple-300"
                                  }`}
                              >
                                Week {p.week_number} ({p.week_number === 1 ? "Initial" : p.week_number === 9 ? "Final" : "Progress"})
                              </span>
                            </td>
                            <td className="p-3.5 font-bold text-slate-900">{p.athlete?.name}</td>
                            <td className="p-3.5 font-mono text-amber-400">{p.athlete?.id_number || p.athlete?.bib_no}</td>
                            <td className="p-3.5 font-semibold text-slate-700">
                              {p.test_height || p.athlete?.height_cm
                                ? `${p.test_height || p.athlete?.height_cm} cm`
                                : (athletes.find((a) => String(a.id) === String(p.athlete_id))?.height_cm
                                  ? `${athletes.find((a) => String(a.id) === String(p.athlete_id)).height_cm} cm`
                                  : "-")}
                            </td>
                            <td className="p-3.5">{p.test_100m || "-"}</td>
                            <td className="p-3.5">{p.test_400m || "-"}</td>
                            <td className="p-3.5">{p.test_shot_put || "-"}</td>
                            <td className="p-3.5">{p.test_long_jump || "-"}</td>
                            <td className="p-3.5">{p.test_skipping || "-"}</td>
                            <td className="p-3.5">{p.test_pushups || "-"}</td>
                            <td className="p-3.5">{p.test_plank || "-"}</td>
                            <td className="p-3.5 pr-5 font-semibold text-emerald-400">{p.test_weight || "-"}</td>
                          </tr>
                        ))}
                        {!performanceReports.length && (
                          <tr>
                            <td colSpan={12} className="p-8 text-center text-slate-500 text-xs">
                              No performance assessments recorded yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 10: CENTRALIZED REPORTS (DEPENDENT DROPDOWNS) */}
            {/* ============================================================== */}
            {activeTab === "centralized_reports" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Centralized Player Dossier</h2>
                  <p className="text-xs text-slate-500">
                    Select Coach &rarr; Select Player to view complete athlete records, attendance, report cards, assessments, and documents.
                  </p>
                </div>

                {/* Dependent Dropdowns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      1. Select Coach
                    </label>
                    <select
                      value={selectedCentralCoach}
                      onChange={(e) => {
                        setSelectedCentralCoach(e.target.value);
                        setSelectedCentralAthlete("");
                      }}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm"
                    >
                      <option value="">-- Choose a Coach --</option>
                      {centralizedData?.coaches?.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.team_name ? `(${c.team_name}) ` : ""}({c.athletes_count} athletes) - {c.district}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      2. Select Player (Assigned to Coach)
                    </label>
                    <select
                      value={selectedCentralAthlete}
                      onChange={(e) => setSelectedCentralAthlete(e.target.value)}
                      disabled={!selectedCentralCoach}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-900 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-40 shadow-sm"
                    >
                      <option value="">-- Choose an Athlete --</option>
                      {centralizedData?.athletes?.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name} (BIB: {a.bib_no}) - {a.discipline_name} {a.team_name ? `[Team: ${a.team_name}]` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Dossier Display */}
                {centralizedData?.athlete_data ? (
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-6">
                    {/* Profile Header */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                      <img
                        src={resolveAssetUrl(centralizedData.athlete_data.photo, {
                          defaultPath: "/images/default-user.png",
                        })}
                        alt="Athlete"
                        className="w-16 h-16 rounded-xl object-cover bg-white border border-slate-200"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-slate-900">
                            {centralizedData.athlete_data.name}
                          </h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-700 font-bold border border-indigo-500/30">
                            BIB: {centralizedData.athlete_data.bib_no}
                          </span>
                        </div>
                        <p className="text-xs text-slate-700 mt-1">
                          Category: <span className="font-semibold text-amber-700">{centralizedData.athlete_data.category || "N/A"}</span> &bull;{" "}
                          Discipline: <span className="font-semibold text-emerald-700">{centralizedData.athlete_data.discipline_name}</span> &bull;{" "}
                          District: {centralizedData.athlete_data.district} &bull;{" "}
                          State: {centralizedData.athlete_data.state || "Andhra Pradesh"}
                          {centralizedData.athlete_data.mappings?.[0]?.coach?.name && (
                            <span className="ml-2 font-medium text-slate-600">
                              &bull; Coach: <span className="font-bold text-slate-800">{centralizedData.athlete_data.mappings[0].coach.name}</span>
                              {(centralizedData.athlete_data.mappings[0].team_name || centralizedData.athlete_data.mappings[0].coach.team_name) && (
                                <span className="text-cyan-700 font-bold ml-1">
                                  [Team: {centralizedData.athlete_data.mappings[0].team_name || centralizedData.athlete_data.mappings[0].coach.team_name}]
                                </span>
                              )}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Sub-tabs for Athlete */}
                    <div className="flex space-x-2 border-b border-slate-200 pb-2 overflow-x-auto">
                      {["profile", "attendance", "report_cards", "weekly_reports", "camp_reports", "documents"].map(
                        (st) => (
                          <button
                            key={st}
                            onClick={() => setCentralTab(st)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors ${centralTab === st
                              ? "bg-indigo-600 text-white"
                              : "text-slate-400 hover:text-slate-700"
                              }`}
                          >
                            {st.replace(/_/g, " ")}
                          </button>
                        )
                      )}
                    </div>

                    {/* Subtab contents */}
                    {centralTab === "profile" && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-slate-50/70 p-4 rounded-xl border border-slate-200">
                        <div><span className="text-slate-500 block">Father's Name</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.father_name || "N/A"}</span></div>
                        <div><span className="text-slate-500 block">Parent Mobile</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.parent_mobile}</span></div>
                        <div><span className="text-slate-500 block">Pincode</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.pincode || "N/A"}</span></div>
                        <div><span className="text-slate-500 block">District & State</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.district}, {centralizedData.athlete_data.state || "Andhra Pradesh"}</span></div>
                        <div><span className="text-slate-500 block">Aadhaar</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.aadhaar || "N/A"}</span></div>
                        <div><span className="text-slate-500 block">UDID</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.udid || "N/A"}</span></div>
                        <div><span className="text-slate-500 block">UDISE</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.udise_no || "N/A"}</span></div>
                        <div className="col-span-2"><span className="text-slate-500 block">Full Address</span><span className="font-semibold text-slate-900">{centralizedData.athlete_data.address || "N/A"}</span></div>
                      </div>
                    )}

                    {centralTab === "attendance" && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-700">Attendance Log</h4>
                        <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                          {centralizedData.athlete_data.attendances?.map((att) => (
                            <div key={att.id} className="p-2.5 bg-slate-50 rounded-xl flex items-center justify-between text-xs">
                              <span className="font-bold text-slate-800">{att.attendance_date}</span>
                              <span className="text-slate-400">Morning: {att.morning_status} &bull; Evening: {att.evening_status}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {centralTab === "report_cards" && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-700">Weekly Performance Cards</h4>
                        <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                          {centralizedData.athlete_data.report_cards?.map((rc) => (
                            <div key={rc.id} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between text-xs">
                              <div>
                                <span className="font-bold text-sky-400">Week {rc.week_number}</span>
                                <p className="text-[11px] text-slate-500">Endurance: {rc.endurance}, Strength: {rc.strength}, Agility: {rc.agility}</p>
                              </div>
                              <span className="font-bold text-emerald-400">{rc.performance_rating}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {centralTab === "camp_reports" && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-700">Week 1 vs Week 9 Performance Assessments</h4>
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          {centralizedData.athlete_data.performance_reports?.map((pr) => (
                            <div key={pr.id} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                              <span className="font-bold text-purple-300 block">Week {pr.week_number} ({pr.week_number === 1 ? "Initial" : "Final"})</span>
                              <p className="text-[11px] text-slate-500">100m: {pr.test_100m || "-"}</p>
                              <p className="text-[11px] text-slate-500">400m: {pr.test_400m || "-"}</p>
                              <p className="text-[11px] text-slate-500">Shot Put: {pr.test_shot_put || "-"}</p>
                              <p className="text-[11px] text-slate-500">Long Jump: {pr.test_long_jump || "-"}</p>
                              <p className="text-[11px] text-slate-500">Weight: {pr.test_weight || "-"}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {centralTab === "documents" && (
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-700">Attached Documents</h4>
                        <div className="space-y-1.5">
                          {centralizedData.athlete_data.documents?.map((doc) => (
                            <div key={doc.id} className="p-3 bg-slate-50 rounded-xl flex items-center justify-between text-xs">
                              <div>
                                <span className="font-semibold text-slate-800 capitalize">{doc.document_type?.replace(/_/g, " ")}</span>
                                <p className="text-[10px] text-slate-500">{doc.description || doc.file_name}</p>
                              </div>
                              <a href={resolveAssetUrl(doc.file_path)} target="_blank" rel="noreferrer" className="text-indigo-400 font-bold hover:underline">
                                View File
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-12 text-center text-slate-500 text-xs bg-slate-50 rounded-2xl border border-slate-200">
                    Please select both a Coach and an Athlete from the dropdowns above to display the dossier.
                  </div>
                )}
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 11: EMPTY REPORT FORMATS */}
            {/* ============================================================== */}
            {activeTab === "empty_formats" && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Official Empty Report Formats</h2>
                    <p className="text-xs text-slate-500">
                      Generate blank report registers for offline ground evaluations, with official association logos, venue details, team &amp; coach names, signature fields, and Excel templates.
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleExportEmptyExcel}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm text-xs font-bold flex items-center gap-2"
                    >
                      <FaFileExcel /> Download Excel Template
                    </button>
                  </div>
                </div>

                {/* FORMAT 1: EMPTY DAILY ATTENDANCE REGISTER (BY TEAM WISE & DAY / ENTIRE) */}
                <div className="bg-gradient-to-br from-cyan-50 via-white to-amber-50 border-2 border-cyan-300/80 p-5 rounded-2xl shadow-sm space-y-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start sm:items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-cyan-700 text-white flex items-center justify-center text-xl font-bold shadow-sm shrink-0">
                        <FaCalendarDay />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-black text-slate-900">
                            Format 1: Empty Daily Attendance Register (Manual Ground Copy)
                          </h3>
                          <span className="text-[11px] bg-cyan-100 text-cyan-800 font-bold px-2.5 py-0.5 rounded-full">
                            Team-Wise Register
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Official blank ground register with Association logos, camp venue, team name, coaches, allotted athletes in team order, and blank morning/evening session boxes for manual handwriting.
                        </p>
                      </div>
                    </div>

                    {/* Print & Download Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap self-start lg:self-auto">
                      <button
                        type="button"
                        onClick={() =>
                          handlePrintRangeAttendance({
                            isBlank: true,
                            action: "print",
                            scope: emptyScope === "entire" ? "entire" : "single",
                            overrideDate: emptyScope === "day" ? emptyFormatDate : null,
                            overrideTeam: emptyTeamFilter,
                          })
                        }
                        disabled={isPrintingRegister}
                        className="px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl shadow text-xs font-bold flex items-center gap-2 transition whitespace-nowrap disabled:opacity-50"
                        title="Directly open browser print preview dialog"
                      >
                        <FaPrint /> Print Register
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          handlePrintRangeAttendance({
                            isBlank: true,
                            action: "download",
                            scope: emptyScope === "entire" ? "entire" : "single",
                            overrideDate: emptyScope === "day" ? emptyFormatDate : null,
                            overrideTeam: emptyTeamFilter,
                          })
                        }
                        disabled={isPrintingRegister}
                        className="px-4 py-2.5 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow text-xs font-bold flex items-center gap-2 transition whitespace-nowrap disabled:opacity-50"
                        title="Download register as PDF file"
                      >
                        <FaDownload /> Download PDF
                      </button>

                      <button
                        type="button"
                        onClick={handleExportEmptyAttendanceExcel}
                        className="px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow text-xs font-bold flex items-center gap-1.5 transition"
                        title="Download blank team-wise attendance register Excel template"
                      >
                        <FaFileExcel /> Excel Register
                      </button>
                    </div>
                  </div>

                  {/* Scope & Filter Toolbar */}
                  <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-cyan-100">
                    {/* Scope selector */}
                    <div className="flex items-center bg-white border border-slate-300 rounded-xl p-0.5 shadow-sm text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => setEmptyScope("day")}
                        className={`px-3 py-1 rounded-lg transition ${emptyScope === "day"
                          ? "bg-cyan-700 text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                          }`}
                      >
                        Day-Wise (Single Day)
                      </button>
                      <button
                        type="button"
                        onClick={() => setEmptyScope("entire")}
                        className={`px-3 py-1 rounded-lg transition ${emptyScope === "entire"
                          ? "bg-cyan-700 text-white shadow-sm"
                          : "text-slate-600 hover:text-slate-900"
                          }`}
                      >
                        Entire Camp (All Days)
                      </button>
                    </div>

                    {/* If day-wise: show date picker bounded to camp dates */}
                    {emptyScope === "day" ? (
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-700 whitespace-nowrap">Select Date:</label>
                        <input
                          type="date"
                          value={emptyFormatDate}
                          onChange={(e) => setEmptyFormatDate(e.target.value)}
                          className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-900 shadow-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                        {getCampDayNumber(emptyFormatDate) && (
                          <span className="text-[11px] font-bold text-cyan-800 bg-cyan-100 px-2.5 py-1 rounded-lg border border-cyan-200 whitespace-nowrap">
                            Day {getCampDayNumber(emptyFormatDate)}{campTotalDays ? ` of ${campTotalDays}` : ""}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs font-semibold text-cyan-900 bg-cyan-100/70 border border-cyan-200 px-3 py-1.5 rounded-xl flex items-center gap-2">
                        <FaCampground className="text-cyan-700" />
                        <span>
                          Camp Duration: <strong className="font-bold">{campStartDate ? campStartDate.split("-").reverse().join("-") : "Start"}</strong> to <strong className="font-bold">{campEndDate ? campEndDate.split("-").reverse().join("-") : "End"}</strong> ({campTotalDays || "All"} Days) &bull; Prints all {campTotalDays || "camp"} days
                        </span>
                      </div>
                    )}

                    {/* Team selector */}
                    <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                      <label className="text-xs font-bold text-slate-700 whitespace-nowrap">Team / Squad:</label>
                      <select
                        value={emptyTeamFilter}
                        onChange={(e) => setEmptyTeamFilter(e.target.value)}
                        className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-900 shadow-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none w-full"
                      >
                        <option value="">All Teams (in Team Order &bull; 1 Page per Team per Day)</option>
                        {availableTeams.map((tName) => (
                          <option key={tName} value={tName}>
                            Team: {tName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="text-[11px] text-slate-600 bg-white/90 px-3 py-1.5 rounded-xl border border-slate-200 font-semibold">
                      📄 Standard A4 Portrait &bull; Organized by Team Order &bull; No Duplicates
                    </div>
                  </div>
                </div>

                {/* FORMAT 2: ATHLETE WEEKLY ASSESSMENT CARD */}
                <div className="flex items-center justify-between pt-2">
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                    Format 2: Athlete Assessment &amp; Weekly Evaluation Card
                  </h3>
                  <button
                    type="button"
                    onClick={handlePrintBlankAssessmentForm}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-2 border border-slate-200 transition shadow-xs"
                  >
                    <FaDownload className="text-cyan-700" /> Download Assessment Form (PDF)
                  </button>
                </div>

                {/* Printable Blank Report Sheet */}
                <div className="bg-white text-slate-900 p-8 rounded-2xl shadow-2xl border border-slate-300 font-sans space-y-6 max-w-4xl mx-auto">
                  {/* Header */}
                  <div className="text-center border-b-2 border-slate-900 pb-4">
                    <h2 className="text-xl font-black uppercase tracking-wide">
                      Para Sports Association of Andhra Pradesh
                    </h2>
                    <h3 className="text-sm font-bold uppercase text-slate-700 tracking-wider mt-0.5">
                      Coaching Camp Assessment & Athlete Performance Card
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Camp: {activeCamp?.name || "Official State Coaching Camp 2026"} &bull; Venue: {activeCamp?.venue || "State Sports Complex"}
                    </p>
                  </div>

                  {/* Athlete Info Placeholders */}
                  <div className="grid grid-cols-3 gap-4 border border-slate-400 p-3 rounded text-xs">
                    <div>Athlete Name: __________________________</div>
                    <div>BIB / Chest No: _______________________</div>
                    <div>District: _____________________________</div>
                    <div>Discipline: __________________________</div>
                    <div>Category: _____________________________</div>
                    <div>Coach: _______________________________</div>
                  </div>

                  {/* Blank Performance Ratings Table */}
                  <table className="w-full text-left text-xs border-collapse border border-slate-400">
                    <thead>
                      <tr className="bg-slate-100 text-slate-900 font-bold border-b border-slate-400">
                        <th className="p-2 border border-slate-400">Sl</th>
                        <th className="p-2 border border-slate-400">Performance Area</th>
                        <th className="p-2 border border-slate-400 text-center">Excellent</th>
                        <th className="p-2 border border-slate-400 text-center">Good</th>
                        <th className="p-2 border border-slate-400 text-center">Average</th>
                        <th className="p-2 border border-slate-400 text-center">Poor</th>
                        <th className="p-2 border border-slate-400">Remarks / Recommendations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["Endurance & Stamina", "Core Strength & Power", "Agility & Coordination", "Sport-Specific Technique", "Camp Discipline & Ethics"].map((area, idx) => (
                        <tr key={idx} className="border-b border-slate-300">
                          <td className="p-2 border border-slate-400 font-bold">{idx + 1}</td>
                          <td className="p-2 border border-slate-400 font-semibold">{area}</td>
                          <td className="p-2 border border-slate-400 text-center">[ &nbsp; ]</td>
                          <td className="p-2 border border-slate-400 text-center">[ &nbsp; ]</td>
                          <td className="p-2 border border-slate-400 text-center">[ &nbsp; ]</td>
                          <td className="p-2 border border-slate-400 text-center">[ &nbsp; ]</td>
                          <td className="p-2 border border-slate-400 h-8"></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Signatures */}
                  <div className="grid grid-cols-3 gap-8 pt-12 text-center text-xs font-bold text-slate-800">
                    <div>
                      <div className="border-t border-slate-200 pt-1">
                        {signatures?.assistant_coach_title || "Assistant Coach"}
                      </div>
                    </div>
                    <div>
                      <div className="border-t border-slate-200 pt-1">
                        {signatures?.camp_incharge_title || signatures?.chief_coach_title || "Camp In-Charge"}
                      </div>
                    </div>
                    <div>
                      <div className="border-t border-slate-200 pt-1">
                        {signatures?.ssa_official_title || signatures?.organiser_title || "SSA Official"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ============================================================== */}
            {/* TAB 12: SETTINGS */}
            {/* ============================================================== */}
            {activeTab === "settings" && (
              <div className="max-w-3xl space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Coaching Camp Settings</h2>
                  <p className="text-xs text-slate-500">
                    Configure report signatures and customize applicable sports disciplines.
                  </p>
                </div>

                {/* Signatures Configuration */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <FaSignature className="text-amber-400" /> Report Signatures Configuration
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 1 Title</label>
                      <input
                        type="text"
                        value={signatures.assistant_coach_title || ""}
                        onChange={(e) => setSignatures({ ...signatures, assistant_coach_title: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 1 Name (Optional)</label>
                      <input
                        type="text"
                        value={signatures.assistant_coach_name || ""}
                        onChange={(e) => setSignatures({ ...signatures, assistant_coach_name: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 2 Title (Camp In-Charge)</label>
                      <input
                        type="text"
                        value={signatures.camp_incharge_title || signatures.chief_coach_title || ""}
                        onChange={(e) => setSignatures({ ...signatures, camp_incharge_title: e.target.value, chief_coach_title: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 2 Name (Optional)</label>
                      <input
                        type="text"
                        value={signatures.camp_incharge_name || signatures.chief_coach_name || ""}
                        onChange={(e) => setSignatures({ ...signatures, camp_incharge_name: e.target.value, chief_coach_name: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 3 Title (SSA Official)</label>
                      <input
                        type="text"
                        value={signatures.ssa_official_title || signatures.organiser_title || ""}
                        onChange={(e) => setSignatures({ ...signatures, ssa_official_title: e.target.value, organiser_title: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Signature 3 Name (Optional)</label>
                      <input
                        type="text"
                        value={signatures.ssa_official_name || signatures.organiser_name || ""}
                        onChange={(e) => setSignatures({ ...signatures, ssa_official_name: e.target.value, organiser_name: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>

                  <button
                    onClick={async () => {
                      try {
                        await API.camp.updateSignatures(signatures);
                        Swal.fire("Saved", "Report signatures updated successfully!", "success");
                      } catch (err) {
                        Swal.fire("Error", "Failed to update signatures", "error");
                      }
                    }}
                    className="px-5 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold flex items-center gap-2"
                  >
                    <FaSave /> Save Signatures
                  </button>
                </div>

                {/* Sports & Disciplines Master */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <FaRunning className="text-emerald-500" /> Sports Disciplines Master ({disciplines.length})
                      </h3>
                      <p className="text-xs text-slate-500">
                        Sourced directly from master table <span className="font-mono">sports</span> with official classification classes from <span className="font-mono">para_classifications</span>.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {disciplines.map((d) => (
                      <div
                        key={d.id}
                        className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-900 text-xs">{d.name}</span>
                          <span className="text-[10px] font-mono font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                            ID: {d.id}
                          </span>
                        </div>
                        {d.classifications && d.classifications.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {d.classifications.slice(0, 10).map((c) => (
                              <span
                                key={c.id || c.classification}
                                className="text-[9px] font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.2 rounded"
                              >
                                {c.classification}
                              </span>
                            ))}
                            {d.classifications.length > 10 && (
                              <span className="text-[9px] text-slate-400 self-center">
                                +{d.classifications.length - 10} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-[10px] text-slate-400 italic">No classifications mapped</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>

        {/* ============================================================== */}
        {/* MODAL: CREATE / EDIT CAMP */}
        {/* ============================================================== */}
        {campModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-lg w-full p-6 shadow-2xl text-slate-900 space-y-4 relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">
                  {editingCamp ? "Edit Coaching Camp" : "Create New Coaching Camp"}
                </h3>
                <button onClick={() => setCampModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveCamp} className="space-y-4 text-xs">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Camp Title / Name</label>
                  <input
                    type="text"
                    placeholder="e.g. State Para Sports Coaching Camp 2026"
                    value={campForm.name}
                    onChange={(e) => setCampForm({ ...campForm, name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Venue / Stadium</label>
                    <input
                      type="text"
                      placeholder="e.g. IGMC Stadium, Vijayawada"
                      value={campForm.venue}
                      onChange={(e) => setCampForm({ ...campForm, venue: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">District</label>
                    <input
                      type="text"
                      placeholder="e.g. NTR / Krishna"
                      value={campForm.district}
                      onChange={(e) => setCampForm({ ...campForm, district: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">From Date *</label>
                    <input
                      type="date"
                      value={campForm.from_date || campForm.start_date || ""}
                      onChange={(e) => setCampForm({ ...campForm, from_date: e.target.value, start_date: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-semibold"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">To Date *</label>
                    <input
                      type="date"
                      value={campForm.to_date || campForm.end_date || ""}
                      onChange={(e) => setCampForm({ ...campForm, to_date: e.target.value, end_date: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-semibold"
                      required
                    />
                  </div>
                  {calculateCampDuration(campForm.from_date || campForm.start_date, campForm.to_date || campForm.end_date) && (
                    <div className="col-span-2 p-2.5 rounded-xl bg-cyan-50 border border-cyan-200 text-cyan-900 flex items-center justify-between text-xs font-bold">
                      <span>Camp Duration:</span>
                      <span className="px-2.5 py-1 bg-cyan-700 text-white rounded-lg text-xs font-black">
                        {calculateCampDuration(campForm.from_date || campForm.start_date, campForm.to_date || campForm.end_date)} Days
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Description</label>
                  <textarea
                    rows={3}
                    placeholder="Camp objectives, disciplines covered, coaching staff notes..."
                    value={campForm.description}
                    onChange={(e) => setCampForm({ ...campForm, description: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="campActiveCheck"
                    checked={campForm.is_active}
                    onChange={(e) => setCampForm({ ...campForm, is_active: e.target.checked })}
                    className="rounded text-amber-500 focus:ring-0"
                  />
                  <label htmlFor="campActiveCheck" className="text-slate-700 font-semibold">
                    Make this the default active camp
                  </label>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setCampModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm font-semibold"
                  >
                    {editingCamp ? "Save Changes" : "Create Camp"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: APPOINT PLAYER AS COACH / EDIT COACH */}
        {/* ============================================================== */}
        {coachModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-xl w-full p-6 shadow-2xl text-slate-900 space-y-4 max-h-[90vh] overflow-y-auto relative z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    {editingCoach ? "Edit Coach Profile" : "Appoint Player as Coach"}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {editingCoach
                      ? "Update team assignment and sporting disciplines."
                      : "Select a player from the players database to appoint as an official coach."}
                  </p>
                </div>
                <button onClick={() => setCoachModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveCoach} className="space-y-4 text-xs">
                {/* If creating new coach, offer Player selection */}
                {!editingCoach && (
                  <div className="space-y-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                    <div className="flex items-center justify-between">
                      <label className="block text-slate-700 font-bold uppercase tracking-wider text-xs">
                        Step 1: Select Player candidate
                      </label>
                      {coachForm.selected_player && (
                        <button
                          type="button"
                          onClick={() => {
                            setCoachForm((prev) => ({
                              ...prev,
                              player_id: null,
                              selected_player: null,
                              name: "",
                              father_name: "",
                              mobile: "",
                              email: "",
                              district: "",
                            }));
                          }}
                          className="text-cyan-700 hover:underline font-bold text-[11px]"
                        >
                          Change Candidate
                        </button>
                      )}
                    </div>

                    {coachForm.selected_player ? (
                      <div className="flex items-center gap-3.5 bg-white p-3.5 rounded-xl border-2 border-cyan-500 shadow-sm">
                        {coachForm.selected_player.photo ? (
                          <img
                            src={resolvePhoto(coachForm.selected_player.photo)}
                            alt={coachForm.selected_player.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-cyan-400 shadow"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholder-avatar.png";
                            }}
                          />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-cyan-700 text-white flex items-center justify-center font-bold text-lg shadow">
                            {coachForm.selected_player.name?.charAt(0) || "P"}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                            {coachForm.selected_player.name}
                            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                              Selected
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-600 mt-0.5">
                            Father: {coachForm.selected_player.father_name || "N/A"} &bull; District:{" "}
                            <strong>{coachForm.selected_player.district || "N/A"}</strong>
                          </div>
                          <div className="text-[11px] font-mono text-cyan-800 font-semibold mt-0.5">
                            Mobile: {coachForm.selected_player.mobile}
                            {coachForm.selected_player.registration_id && (
                              <span className="ml-2 text-slate-500">
                                (Reg ID: {coachForm.selected_player.registration_id})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Search player by name, reg ID, mobile or district..."
                            value={playerSearchTerm}
                            onChange={(e) => setPlayerSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                fetchEligiblePlayers(playerSearchTerm);
                              }
                            }}
                            className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs font-semibold focus:ring-2 focus:ring-cyan-500"
                          />
                          <button
                            type="button"
                            onClick={() => fetchEligiblePlayers(playerSearchTerm)}
                            disabled={loadingEligible}
                            className="px-4 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold transition disabled:opacity-50"
                          >
                            {loadingEligible ? "Searching..." : "Search"}
                          </button>
                        </div>

                        {/* Candidate Players List */}
                        <div className="max-h-48 overflow-y-auto space-y-1.5 p-1 border border-slate-200 rounded-xl bg-white divide-y divide-slate-100">
                          {loadingEligible ? (
                            <div className="p-4 text-center text-slate-400 text-xs">
                              Searching players database...
                            </div>
                          ) : eligiblePlayers.length === 0 ? (
                            <div className="p-4 text-center text-slate-400 text-xs">
                              No players found. Try typing a name, mobile, or registration ID.
                            </div>
                          ) : (
                            eligiblePlayers.map((p) => (
                              <div
                                key={p.id}
                                className="p-2.5 flex items-center justify-between hover:bg-slate-50 transition rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  {p.photo ? (
                                    <img
                                      src={resolvePhoto(p.photo)}
                                      alt={p.name}
                                      className="w-10 h-10 rounded-full object-cover border border-slate-200 shadow-sm"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/placeholder-avatar.png";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
                                      {p.name?.charAt(0) || "P"}
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-bold text-slate-900 text-xs flex items-center gap-1.5">
                                      {p.name}
                                      {p.is_coach && (
                                        <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold">
                                          Already Coach
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono">
                                      {p.mobile} &bull; {p.district} &bull; {p.registration_id || "No Reg ID"}
                                    </div>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  disabled={p.is_coach}
                                  onClick={() => {
                                    setCoachForm((prev) => ({
                                      ...prev,
                                      player_id: p.id,
                                      selected_player: p,
                                      name: p.name,
                                      father_name: p.father_name || "",
                                      mobile: p.mobile,
                                      email: p.email || "",
                                      district: p.district || "",
                                    }));
                                  }}
                                  className="px-3 py-1.5 bg-cyan-700 hover:bg-cyan-800 disabled:opacity-40 text-white rounded-lg text-xs font-bold transition shadow-sm"
                                >
                                  {p.is_coach ? "Appointed" : "Select"}
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Details & Assignment */}
                <div className="space-y-3">
                  {editingCoach && (
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                        Coach Name
                      </label>
                      <input
                        type="text"
                        value={coachForm.name}
                        onChange={(e) => setCoachForm({ ...coachForm, name: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                        required
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                      District
                    </label>
                    <select
                      value={coachForm.district}
                      onChange={(e) => setCoachForm({ ...coachForm, district: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                    >
                      <option value="">-- Select District --</option>
                      {AP_DISTRICTS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Multi-select Disciplines */}
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                      Sports Disciplines Mentored (Multi-select) *
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2.5 bg-white border border-slate-200 rounded-xl">
                      {disciplines.map((d) => {
                        const isChecked =
                          coachForm.disciplines.includes(d.name) || coachForm.disciplines.includes(d.id);
                        return (
                          <label
                            key={d.id}
                            className={`flex items-center gap-2 p-1.5 rounded-lg text-xs cursor-pointer ${isChecked
                              ? "bg-indigo-600/20 text-indigo-900 font-bold border border-indigo-300"
                              : "text-slate-700 hover:bg-slate-100"
                              }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setCoachForm({
                                    ...coachForm,
                                    disciplines: [...coachForm.disciplines, d.name],
                                  });
                                } else {
                                  setCoachForm({
                                    ...coachForm,
                                    disciplines: coachForm.disciplines.filter(
                                      (item) => item !== d.name && item !== d.id
                                    ),
                                  });
                                }
                              }}
                              className="rounded text-indigo-600"
                            />
                            <span>{d.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setCoachModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm font-bold text-xs"
                  >
                    {editingCoach ? "Save Coach Changes" : "Appoint Player as Coach & Grant Login"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: VIEW COACH PROFILE & ACHIEVEMENTS */}
        {/* ============================================================== */}
        {selectedCoachProfile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 overflow-y-auto animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden relative max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-cyan-950 via-slate-900 to-indigo-950 text-white p-6 relative">
                <button
                  onClick={() => setSelectedCoachProfile(null)}
                  className="absolute top-4 right-4 text-slate-300 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
                >
                  <FaTimes size={18} />
                </button>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                  {selectedCoachProfile.photo || selectedCoachProfile.player_photo ? (
                    <img
                      src={resolvePhoto(selectedCoachProfile.photo || selectedCoachProfile.player_photo)}
                      alt={selectedCoachProfile.name}
                      className="w-20 h-20 rounded-2xl object-cover border-2 border-amber-400 shadow-md"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder-avatar.png";
                      }}
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center border-2 border-amber-400 shadow-md text-2xl font-black text-amber-300">
                      {selectedCoachProfile.name?.charAt(0) || "C"}
                    </div>
                  )}

                  <div className="text-center sm:text-left">
                    <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 mb-1">
                      Certified Para Sports Coach
                    </span>
                    <h2 className="text-2xl font-bold text-white leading-tight">
                      {selectedCoachProfile.name}
                    </h2>
                    <p className="text-xs text-slate-300 mt-0.5">
                      {selectedCoachProfile.team_name ? `Squad: ${selectedCoachProfile.team_name} • ` : ""}
                      District: {selectedCoachProfile.district || "Andhra Pradesh"}
                    </p>
                    {coachProfileData?.registration_id && (
                      <p className="text-[11px] text-amber-300 font-mono mt-0.5">
                        Reg ID: {coachProfileData.registration_id}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6 flex-1">
                {/* Disciplines Strip */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <FaRunning className="text-cyan-700" /> Sports Disciplines
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCoachProfile.disciplines && selectedCoachProfile.disciplines.length > 0 ? (
                      selectedCoachProfile.disciplines.map((d, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-cyan-50 text-cyan-900 border border-cyan-200 rounded-lg text-xs font-semibold"
                        >
                          {d.discipline_name || d.name || d}
                        </span>
                      ))
                    ) : (
                      <span className="text-slate-400 text-xs italic">Para Sports Association</span>
                    )}
                  </div>
                </div>

                {/* Achievements Section */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
                    <FaTrophy className="text-amber-500" /> Verified Achievements & Sporting Records
                  </h4>

                  {coachProfileLoading ? (
                    <div className="py-8 text-center text-slate-400 text-xs">
                      <div className="w-6 h-6 border-2 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      Loading achievements...
                    </div>
                  ) : coachProfileData?.achievements && coachProfileData.achievements.length > 0 ? (
                    <div className="space-y-3">
                      {coachProfileData.achievements.map((ach) => (
                        <div
                          key={ach.id}
                          className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-100/80 transition"
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-2.5 rounded-xl text-white font-bold text-sm shadow-sm ${ach.medal?.toLowerCase() === "gold"
                                ? "bg-amber-500"
                                : ach.medal?.toLowerCase() === "silver"
                                  ? "bg-slate-400"
                                  : ach.medal?.toLowerCase() === "bronze"
                                    ? "bg-amber-700"
                                    : "bg-cyan-700"
                                }`}
                            >
                              <FaAward className="text-lg" />
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                                {ach.event_name}
                                {ach.medal && (
                                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-black bg-amber-100 text-amber-800 border border-amber-300">
                                    {ach.medal}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                                {ach.event_level && (
                                  <span>
                                    Level: <strong>{ach.event_level}</strong>
                                  </span>
                                )}
                                {ach.event_location && <span>• Location: {ach.event_location}</span>}
                                {ach.event_date && (
                                  <span>• Date: {new Date(ach.event_date).toLocaleDateString()}</span>
                                )}
                              </div>
                              {ach.notes && (
                                <p className="text-xs text-slate-600 mt-1 italic">"{ach.notes}"</p>
                              )}
                            </div>
                          </div>

                          {ach.document_url && (
                            <a
                              href={ach.document_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg text-xs font-bold transition shadow-sm self-start sm:self-auto"
                            >
                              <FaFileAlt className="text-rose-500" /> Certificate
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-slate-500 text-xs">
                      <p className="font-semibold text-slate-700">Official Appointed Coach</p>
                      <p className="text-slate-400 mt-1">
                        No external tournament achievement records uploaded yet. Appointed by Andhra Pradesh Para Sports Association.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setSelectedCoachProfile(null)}
                  className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition"
                >
                  Close Profile
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ASSIGN / UPDATE DISCIPLINE & CATEGORY */}
        {/* ============================================================== */}
        {disciplineCategoryModalOpen && selectedAthleteForDiscipline && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 text-slate-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 relative z-10 animate-in fade-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-cyan-50 text-cyan-700 border border-cyan-200">
                    <FaTags className="text-base" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Assign Discipline & Category
                    </h3>
                    <p className="text-xs text-slate-500">
                      Update athlete's sport discipline and classification category
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setDisciplineCategoryModalOpen(false);
                    setSelectedAthleteForDiscipline(null);
                  }}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Athlete Summary Card */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={resolveAssetUrl(selectedAthleteForDiscipline.photo, {
                      defaultPath: "/images/default-user.png",
                    })}
                    alt={selectedAthleteForDiscipline.name}
                    className="w-12 h-12 rounded-xl object-cover border border-slate-200 bg-white"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/default-user.png";
                    }}
                  />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">
                      {selectedAthleteForDiscipline.name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      Father: {selectedAthleteForDiscipline.father_name || "N/A"} &bull; {selectedAthleteForDiscipline.district}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">BIB / Chest No</div>
                  <div className="font-mono font-bold text-cyan-900 text-sm bg-cyan-100/70 border border-cyan-200 px-2.5 py-0.5 rounded-lg inline-block">
                    {selectedAthleteForDiscipline.bib_no || "N/A"}
                  </div>
                </div>
              </div>

              <form onSubmit={handleSaveDisciplineCategory} className="space-y-4 text-xs">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                    Sport Discipline *
                  </label>
                  <select
                    value={disciplineCategoryForm.discipline_name}
                    onChange={(e) => {
                      const selectedName = e.target.value;
                      const matched = disciplines.find((d) => d.name === selectedName);
                      setDisciplineCategoryForm({
                        ...disciplineCategoryForm,
                        discipline_name: selectedName,
                        discipline_id: matched ? matched.id : "",
                        classification_id: "",
                        category: "",
                      });
                    }}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-semibold"
                  >
                    <option value="">-- Select Sport Discipline --</option>
                    {disciplines.map((d) => (
                      <option key={d.id} value={d.name}>
                        {d.name} {d.code ? `(${d.code})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-400 mt-1">
                    Select the assigned sport event or discipline for this athlete.
                  </p>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs flex items-center justify-between">
                    <span>Classification Category / Class *</span>
                    {availableClassifications.length > 0 && (
                      <span className="text-[10px] text-cyan-800 font-semibold uppercase bg-cyan-50 border border-cyan-200 px-1.5 py-0.5 rounded">
                        {availableClassifications.length} classes available
                      </span>
                    )}
                  </label>

                  {availableClassifications.length > 0 ? (
                    <div className="space-y-1.5">
                      <select
                        value={disciplineCategoryForm.classification_id}
                        onChange={(e) => {
                          const selectedClassification = availableClassifications.find(
                            (classification) => String(classification.id) === String(e.target.value)
                          );
                          setDisciplineCategoryForm({
                            ...disciplineCategoryForm,
                            classification_id: selectedClassification?.id || "",
                            category: selectedClassification?.classification || "",
                          });
                        }}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-mono font-bold tracking-wider"
                      >
                        <option value="">-- Select Official Classification / Class --</option>
                        {availableClassifications.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.classification}
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-slate-500">
                        Official classifications fetched from the master table for{" "}
                        <span className="font-semibold text-slate-800">
                          {disciplineCategoryForm.discipline_name}
                        </span>
                        .
                      </p>
                    </div>
                  ) : (
                    <div>
                      <select
                        disabled
                        className="w-full bg-slate-100 border border-slate-300 rounded-xl px-3 py-2.5 text-slate-500 text-xs cursor-not-allowed font-medium"
                      >
                        <option value="">
                          {disciplineCategoryForm.discipline_name
                            ? "-- No official classifications recorded for this sport --"
                            : "-- Select a Sport Discipline above first --"}
                        </option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">
                        Please choose a Sport Discipline above to automatically load its official classification classes.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setDisciplineCategoryModalOpen(false);
                      setSelectedAthleteForDiscipline(null);
                    }}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!disciplineCategoryForm.discipline_id || !disciplineCategoryForm.classification_id}
                    className="px-5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl font-bold shadow-md shadow-cyan-700/20 transition-all flex items-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FaSave /> Save Discipline & Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ALLOT / CHANGE ONE OR MORE GAMES */}
        {/* ============================================================== */}
        {sportsAllotmentModalOpen && selectedAthleteForSportsAllotment && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 text-slate-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 relative z-10 animate-in fade-in zoom-in-95 duration-150">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <FaRunning className="text-base" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Allot / Change Games
                    </h3>
                    <p className="text-xs text-slate-500">
                      Enter one or more games; existing allotments can be changed or deleted
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSportsAllotmentModalOpen(false);
                    setSelectedAthleteForSportsAllotment(null);
                  }}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Athlete Summary Card */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={resolveAssetUrl(selectedAthleteForSportsAllotment.photo, {
                      defaultPath: "/images/default-user.png",
                    })}
                    alt={selectedAthleteForSportsAllotment.name}
                    className="w-12 h-12 rounded-xl object-cover border border-slate-200 bg-white"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/default-user.png";
                    }}
                  />
                  <div>
                    <div className="font-bold text-slate-900 text-sm">
                      {selectedAthleteForSportsAllotment.name}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      District: {selectedAthleteForSportsAllotment.district} &bull; Class: <span className="font-bold text-indigo-700">{selectedAthleteForSportsAllotment.category || "General"}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">ID / BIB No</div>
                  <div className="font-mono font-bold text-cyan-900 text-sm bg-cyan-100/70 border border-cyan-200 px-2.5 py-0.5 rounded-lg inline-block">
                    {selectedAthleteForSportsAllotment.id_number || selectedAthleteForSportsAllotment.bib_no || "N/A"}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-900">
                Camp-only allotment: these games and performance benchmarks apply only to <strong>{activeCamp?.name || "the selected coaching camp"}</strong>. The athlete's permanent/global sports profile is not changed.
              </div>

              <form onSubmit={handleSaveSportsAllotment} className="space-y-4 text-xs">
                {/* Sports Selection Slots */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-slate-700 font-bold uppercase tracking-wider text-xs">
                      Allotted Games (Select one or more) *
                    </label>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-300">
                      {sportsAllotmentForm.disciplines.length} Allotted
                    </span>
                  </div>

                  <div className="rounded-xl border border-cyan-200 bg-cyan-50/60 p-3">
                    <label className="block text-cyan-950 font-bold uppercase tracking-wider text-[10px] mb-1.5">
                      1. Select Sport
                    </label>
                    <select
                      value={allotmentSportId}
                      onChange={(event) => handleAllotmentSportChange(event.target.value)}
                      className="w-full rounded-lg border border-cyan-300 bg-white px-3 py-2 text-xs font-bold text-cyan-950 focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="">Choose Para Sport</option>
                      {disciplines.map((sport) => (
                        <option key={sport.id} value={sport.id}>{sport.name}</option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-[10px] text-cyan-800">
                      Sports are loaded from the sports table. Eligibility uses class <strong>{selectedAthleteForSportsAllotment.category || "Not allotted"}</strong>.
                    </p>
                  </div>

                  {allotmentSportId && (
                    <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5">
                      <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                        2. Select Eligible Games
                      </div>
                      {loadingEligibleAllotmentGames ? (
                        <div className="py-5 text-center text-xs font-semibold text-cyan-700">Loading eligible games...</div>
                      ) : eligibleAllotmentGames.length === 0 ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-4 text-center text-xs font-semibold text-amber-800">
                          No eligible games found for this athlete&apos;s category in the selected sport.
                        </div>
                      ) : (
                        <div className="grid max-h-72 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
                          {eligibleAllotmentGames.map((game) => {
                            const gameName = game.game_name;
                            const selected = sportsAllotmentForm.disciplines.includes(gameName);
                            return (
                              <label key={game.id} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2 ${selected ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => handleToggleEligibleAllotmentGame(game)}
                                  className="mt-0.5 rounded border-slate-300 text-emerald-600"
                                />
                                <span>
                                  <span className="block font-bold text-slate-800">{gameName}</span>
                                  <span className="block text-[9px] text-emerald-700">
                                    Eligible: {game.eligibility?.eligible_classes || selectedAthleteForSportsAllotment.category}
                                    {game.eligibility?.mqs ? ` • Previous national benchmark: ${game.eligibility.mqs}` : ""}
                                  </span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {sportsAllotmentForm.disciplines.length > 0 && (
                    <div className="mt-3 space-y-3">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">Currently allotted games</div>
                      <div className="flex flex-wrap gap-1.5">
                        {sportsAllotmentForm.disciplines.map((gameName) => (
                          <span key={gameName} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-900">
                            {gameName}
                            <button
                              type="button"
                              onClick={() => setSportsAllotmentForm((current) => {
                                const nextDetails = { ...(current.gameDetails || {}) };
                                delete nextDetails[gameName];
                                return {
                                  ...current,
                                  disciplines: current.disciplines.filter((name) => name !== gameName),
                                  gameDetails: nextDetails,
                                };
                              })}
                              className="text-rose-600 hover:text-rose-800 text-sm leading-none"
                              aria-label={`Delete ${gameName} from allotted games`}
                              title={`Delete ${gameName}`}
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>

                      <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
                        {sportsAllotmentForm.disciplines.map((gameName, gameIndex) => {
                          const details = sportsAllotmentForm.gameDetails?.[gameName] || {};
                          const trialHistory = getWeeklyTrialHistory(
                            selectedAthleteForSportsAllotment,
                            { name: gameName, weekly_trial_field: details.weekly_trial_field },
                            performanceReports
                          );
                          return (
                            <div key={`performance-${gameName}`} className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2.5">
                              <div className="flex items-center justify-between gap-2">
                                <div className="font-black text-indigo-950 text-xs">
                                  Game {gameIndex + 1}: {details.sport_name ? `${details.sport_name} — ` : ""}{gameName}
                                </div>
                                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-700">Camp-only evaluation</span>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <label className="text-[10px] font-bold text-slate-700 sm:col-span-2">
                                  Weekly Performance Trial Source
                                  <select
                                    value={details.weekly_trial_field || ""}
                                    onChange={(e) => updateAllottedGameDetail(gameName, "weekly_trial_field", e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-cyan-300 bg-cyan-50 px-2.5 py-2 text-xs font-bold text-cyan-950"
                                  >
                                    <option value="">Select the matching weekly trial</option>
                                    {WEEKLY_TRIAL_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                </label>
                                <label className="text-[10px] font-bold text-slate-700">
                                  Measurement Type
                                  <select
                                    value={details.measurement_type || "time"}
                                    onChange={(e) => updateAllottedGameDetail(gameName, "measurement_type", e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs font-semibold"
                                  >
                                    <option value="time">Timing</option>
                                    <option value="distance">Distance</option>
                                    <option value="weight">Weight</option>
                                    <option value="points">Points / Score</option>
                                    <option value="height">Height</option>
                                    <option value="other">Other</option>
                                  </select>
                                </label>
                                <label className="text-[10px] font-bold text-slate-700">
                                  Unit
                                  <input
                                    type="text"
                                    value={details.measurement_unit || ""}
                                    onChange={(e) => updateAllottedGameDetail(gameName, "measurement_unit", e.target.value)}
                                    placeholder="sec, min, m, cm, kg, points..."
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs font-semibold"
                                  />
                                </label>
                              </div>

                              <div className="grid grid-cols-1 gap-2">
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-[10px] text-emerald-950">
                                  <div className="font-black uppercase tracking-wider">Athlete Camp Trial Record</div>
                                  {trialHistory.entries.length > 0 ? (
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                      {trialHistory.entries.map((entry) => (
                                        <span key={`${gameName}-${entry.week}`} className="rounded-md border border-emerald-300 bg-white px-2 py-1 font-bold">
                                          Week {entry.week}: {entry.value}
                                        </span>
                                      ))}
                                      <span className="rounded-md bg-emerald-700 px-2 py-1 font-black text-white">
                                        Current: {trialHistory.latest}
                                      </span>
                                    </div>
                                  ) : (
                                    <p className="mt-1 text-amber-800 font-semibold">
                                      {details.weekly_trial_field ? "No camp trial result has been entered for this game in the weekly performance report." : "Select a weekly trial source to fetch the athlete's camp record."}
                                    </p>
                                  )}
                                </div>
                                <label className="text-[10px] font-bold text-amber-900">
                                  Previous National Benchmark
                                  <input
                                    type="text"
                                    value={details.national_benchmark || ""}
                                    onChange={(e) => updateAllottedGameDetail(gameName, "national_benchmark", e.target.value)}
                                    placeholder="Previous national timing, distance, weight, or score"
                                    className="mt-1 w-full rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs font-bold text-amber-950"
                                  />
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mt-1">
                    Select every eligible game required for this camp. Use the × button to delete an allotment; at least one game is required when saving.
                  </p>
                </div>

                {/* Performance Notes / Rationale */}
                <div>
                  <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-xs">
                    Performance Notes / Allotment Reason
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter observations based on physical tests, trials, stamina, speed, or performance changes..."
                    value={sportsAllotmentForm.notes}
                    onChange={(e) => setSportsAllotmentForm({ ...sportsAllotmentForm, notes: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl p-2.5 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Record trial scores, coach observations, or rationale for altering or adding sports.
                  </p>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => {
                      setSportsAllotmentModalOpen(false);
                      setSelectedAthleteForSportsAllotment(null);
                    }}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingSportsAllotment}
                    className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <FaSave /> {savingSportsAllotment ? "Saving Allotment..." : "Save Game Allotment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: REGISTER / EDIT ATHLETE */}
        {/* ============================================================== */}
        {athleteModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 text-slate-900 rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">
                  {editingAthlete ? "Edit Athlete Information" : "Register Athlete for Coaching Camp"}
                </h3>
                <button onClick={() => setAthleteModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveAthlete} className="space-y-4 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Athlete Name *</label>
                    <input
                      type="text"
                      value={athleteForm.name}
                      onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Father's Name *</label>
                    <input
                      type="text"
                      value={athleteForm.father_name}
                      onChange={(e) => setAthleteForm({ ...athleteForm, father_name: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                      Camp BIB / Chest No
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. PSCC0001"
                      value={athleteForm.bib_no}
                      onChange={(e) => setAthleteForm({ ...athleteForm, bib_no: e.target.value.toUpperCase() })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-cyan-900 text-xs focus:ring-2 focus:ring-cyan-500 font-mono font-bold"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Manual chest / BIB number for camp</p>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                      ID Number (For ID Card)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. ID-001 or Reg No"
                      value={athleteForm.id_number}
                      onChange={(e) => setAthleteForm({ ...athleteForm, id_number: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-mono font-bold"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Printed on Participant ID Card</p>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Parent Mobile *</label>
                    <input
                      type="text"
                      placeholder="10 digit mobile"
                      value={athleteForm.parent_mobile}
                      onChange={(e) => setAthleteForm({ ...athleteForm, parent_mobile: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-mono"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Assign Coach (Optional)</label>
                  <select
                    value={athleteForm.coach_id}
                    onChange={(e) => setAthleteForm({ ...athleteForm, coach_id: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="">-- Unassigned --</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.team_name ? `(${c.team_name})` : ""} - {c.district}
                      </option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Sport discipline & classification category can be entered separately from the athletes table after registration.
                  </p>
                </div>

                {/* Address & Location Section */}
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <div className="font-bold text-slate-800 text-xs flex items-center justify-between">
                    <span>Address & Location Details</span>
                    {pincodeLoading && (
                      <span className="text-cyan-700 text-[11px] font-semibold animate-pulse">
                        Looking up pincode...
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        Pincode (6 Digits) *
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="e.g. 520002"
                        value={athleteForm.pincode}
                        onChange={handleAthletePincodeChange}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-mono"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        District *
                      </label>
                      <select
                        value={athleteForm.district}
                        onChange={(e) => setAthleteForm({ ...athleteForm, district: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-semibold"
                        required
                      >
                        <option value="">-- Select District --</option>
                        {AP_DISTRICTS.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        State *
                      </label>
                      <input
                        type="text"
                        value={athleteForm.state || "Andhra Pradesh"}
                        onChange={(e) => setAthleteForm({ ...athleteForm, state: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                      House / Street / Locality Address *
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Enter full address..."
                      value={athleteForm.address}
                      onChange={(e) => setAthleteForm({ ...athleteForm, address: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Aadhaar No. (Optional)</label>
                    <input
                      type="text"
                      value={athleteForm.aadhaar}
                      onChange={(e) => setAthleteForm({ ...athleteForm, aadhaar: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">UDID No. (Optional)</label>
                    <input
                      type="text"
                      value={athleteForm.udid}
                      onChange={(e) => setAthleteForm({ ...athleteForm, udid: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">UDISE No. (Optional)</label>
                    <input
                      type="text"
                      value={athleteForm.udise_no}
                      onChange={(e) => setAthleteForm({ ...athleteForm, udise_no: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>

                {/* Room Allotment & Health / Medical Care */}
                <div className="p-3.5 bg-gradient-to-br from-rose-50/70 via-white to-amber-50/50 rounded-xl border border-rose-200 space-y-3">
                  <div className="flex items-center justify-between border-b border-rose-100 pb-1.5">
                    <span className="font-bold text-rose-900 text-xs flex items-center gap-1.5 uppercase tracking-wider">
                      🏥 Room Allotment & Medical / Health Information
                    </span>
                    <span className="text-[10px] text-rose-600 font-semibold">Shared with Room In-Charge Coaches</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        Allotted Room No.
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 101, B-204"
                        value={athleteForm.room_no || ""}
                        onChange={(e) => setAthleteForm({ ...athleteForm, room_no: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500 font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        Medicine Timings / Schedule
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Morning 8 AM (after food), Night 9 PM"
                        value={athleteForm.medicine_timings || ""}
                        onChange={(e) => setAthleteForm({ ...athleteForm, medicine_timings: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        Health Issues / Medical Conditions (if any)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="e.g. Mild Asthma, Low Blood Pressure, Allergy to dust/peanuts, etc."
                        value={athleteForm.health_issues || ""}
                        onChange={(e) => setAthleteForm({ ...athleteForm, health_issues: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-[11px]">
                        Prescribed Medicines (if any)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="e.g. Paracetamol 500mg, Asthalin Inhaler, Cetirizine 10mg"
                        value={athleteForm.medicines || ""}
                        onChange={(e) => setAthleteForm({ ...athleteForm, medicines: e.target.value })}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                      />
                    </div>
                  </div>
                </div>

                {/* File Uploads Section (Optional) */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <span className="font-bold text-slate-800 block text-xs">
                    Documents (Optional — Already uploaded during registration)
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FileUploadWithScanner
                      label="Photo (Optional update)"
                      name="photo"
                      accept="image/*"
                      value={athleteForm.photo}
                      onChange={(file) => setAthleteForm({ ...athleteForm, photo: file })}
                      scannerTitle="Scan Athlete Photo"
                      targetFileName={`photo_${athleteForm.name || "athlete"}.jpg`}
                    />
                    <FileUploadWithScanner
                      label="SADAREM Certificate (Optional)"
                      name="sadarem_doc"
                      accept=".pdf,image/*"
                      value={athleteForm.sadarem_doc}
                      onChange={(file) => setAthleteForm({ ...athleteForm, sadarem_doc: file })}
                      scannerTitle="Scan SADAREM Certificate"
                      targetFileName={`sadarem_${athleteForm.name || "athlete"}.jpg`}
                    />
                    <FileUploadWithScanner
                      label="Aadhaar Card (Optional)"
                      name="aadhaar_doc"
                      accept=".pdf,image/*"
                      value={athleteForm.aadhaar_doc}
                      onChange={(file) => setAthleteForm({ ...athleteForm, aadhaar_doc: file })}
                      scannerTitle="Scan Aadhaar Card"
                      targetFileName={`aadhaar_${athleteForm.name || "athlete"}.jpg`}
                    />
                    <FileUploadWithScanner
                      label="UDID Card (Optional)"
                      name="udid_doc"
                      accept=".pdf,image/*"
                      value={athleteForm.udid_doc}
                      onChange={(file) => setAthleteForm({ ...athleteForm, udid_doc: file })}
                      scannerTitle="Scan UDID Card"
                      targetFileName={`udid_${athleteForm.name || "athlete"}.jpg`}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setAthleteModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm font-semibold"
                  >
                    {editingAthlete ? "Save Athlete Changes" : "Register Athlete"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: GET REGISTERED PLAYER TO CAMP & ALLOT COACH */}
        {/* ============================================================== */}
        {enrollPlayerModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-4xl w-full p-5 sm:p-6 shadow-2xl text-slate-900 space-y-4 relative z-10 my-6 max-h-[92vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-start justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-base shadow-xs">
                    <FaUserPlus />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Get Registered Player to Coaching Camp
                    </h3>
                    <p className="text-xs text-slate-500">
                      Select players from the portal registry and directly allot coach, team & discipline for{" "}
                      <span className="font-semibold text-emerald-800">{activeCamp?.name}</span>.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEnrollPlayerModalOpen(false)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSaveEnrollPlayer} className="space-y-4 text-xs">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                  {/* Left Column: Player Search & Selection (7 cols) */}
                  <div className="lg:col-span-7 space-y-3 border-b lg:border-b-0 lg:border-r border-slate-200 lg:pr-5 pb-4 lg:pb-0">
                    <div className="flex items-center justify-between">
                      <label className="block text-slate-800 font-bold uppercase tracking-wider text-xs">
                        Step 1: Select Registered Player(s)
                      </label>
                      <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        {selectedPlayerIds.length} Selected
                      </span>
                    </div>

                    {/* Search and Filters */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          placeholder="Search name, Reg ID (PA26...), mobile, district..."
                          value={enrollSearchTerm}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEnrollSearchTerm(val);
                            fetchAvailablePlayers(val, enrollDistrictFilter, enrollGenderFilter);
                          }}
                          className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-3 pr-8 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-medium"
                        />
                        {enrollSearchTerm && (
                          <button
                            type="button"
                            onClick={() => {
                              setEnrollSearchTerm("");
                              fetchAvailablePlayers("", enrollDistrictFilter, enrollGenderFilter);
                            }}
                            className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                          >
                            <FaTimes className="text-[10px]" />
                          </button>
                        )}
                      </div>

                      <select
                        value={enrollDistrictFilter}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEnrollDistrictFilter(val);
                          fetchAvailablePlayers(enrollSearchTerm, val, enrollGenderFilter);
                        }}
                        className="bg-slate-50 border border-slate-300 rounded-xl px-2.5 py-2 text-slate-800 text-xs font-medium focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">All Districts</option>
                        {AP_DISTRICTS.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>

                      <select
                        value={enrollGenderFilter}
                        onChange={(e) => {
                          const val = e.target.value;
                          setEnrollGenderFilter(val);
                          fetchAvailablePlayers(enrollSearchTerm, enrollDistrictFilter, val);
                        }}
                        className="bg-slate-50 border border-slate-300 rounded-xl px-2 py-2 text-slate-800 text-xs font-medium focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">All Genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Multi-select controls */}
                    <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1">
                      <span>
                        Showing {availablePlayersList.length} registered player{availablePlayersList.length !== 1 ? "s" : ""}
                      </span>
                      <div className="space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            const selectableIds = availablePlayersList.map((p) => p.id);
                            setSelectedPlayerIds(selectableIds);
                          }}
                          className="text-emerald-700 hover:underline font-semibold"
                        >
                          Select All
                        </button>
                        <span>&bull;</span>
                        <button
                          type="button"
                          onClick={() => setSelectedPlayerIds([])}
                          className="text-slate-500 hover:underline"
                        >
                          Clear Selection
                        </button>
                      </div>
                    </div>

                    {/* Player Cards List */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden max-h-[360px] overflow-y-auto divide-y divide-slate-100 bg-slate-50/50">
                      {loadingAvailablePlayers ? (
                        <div className="p-8 text-center text-slate-400">
                          <div className="animate-spin inline-block w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mb-2" />
                          <p className="text-xs font-medium">Searching registered players...</p>
                        </div>
                      ) : availablePlayersList.length === 0 ? (
                        <div className="p-8 text-center text-slate-500 space-y-2">
                          <FaUsers className="mx-auto text-2xl text-slate-300" />
                          <p className="font-semibold text-xs text-slate-700">No players found matching your search.</p>
                          <p className="text-[11px] text-slate-400">
                            Players can be registered directly in the portal under Manage Players or /register.
                          </p>
                        </div>
                      ) : (
                        availablePlayersList.map((player) => {
                          const isSelected = selectedPlayerIds.includes(player.id);
                          const photoUrl = getSafePhotoUrl(player.photo);

                          return (
                            <div
                              key={player.id}
                              onClick={() => {
                                setSelectedPlayerIds((prev) =>
                                  prev.includes(player.id)
                                    ? prev.filter((id) => id !== player.id)
                                    : [...prev, player.id]
                                );
                              }}
                              className={`p-3 flex items-center justify-between gap-3 cursor-pointer transition ${isSelected
                                ? "bg-emerald-50/90 border-l-4 border-emerald-600"
                                : "hover:bg-white bg-slate-50/40"
                                }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => { }} // Handled by container click
                                  className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                />

                                <img
                                  src={photoUrl}
                                  alt={player.name}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = DEFAULT_AVATAR;
                                  }}
                                  className="w-10 h-10 rounded-xl object-cover border border-slate-200 bg-white shrink-0"
                                />

                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-slate-900 text-xs truncate">
                                      {player.name}
                                    </span>
                                    {player.registration_id && (
                                      <span className="font-mono text-[10px] bg-slate-200/80 text-slate-800 px-1.5 py-0.2 rounded font-semibold">
                                        {player.registration_id}
                                      </span>
                                    )}
                                    {player.gender && (
                                      <span className="text-[10px] text-slate-500 font-medium capitalize">
                                        ({player.gender})
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                                    <span>{player.district}</span>
                                    <span>&bull;</span>
                                    <span className="font-mono">{player.mobile}</span>
                                    {player.father_name && (
                                      <>
                                        <span>&bull;</span>
                                        <span>Father: {player.father_name}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="text-right shrink-0">
                                {player.is_enrolled ? (
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                                      In Camp {player.camp_bib ? `(${player.camp_bib})` : ""}
                                    </span>
                                    {player.assigned_coach?.name && (
                                      <span className="text-[10px] text-slate-500">
                                        Coach: {player.assigned_coach.name}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                    Ready to Add
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Right Column: Coach & Team Allotment (5 cols) */}
                  <div className="lg:col-span-5 space-y-4">
                    <label className="block text-slate-800 font-bold uppercase tracking-wider text-xs">
                      Step 2: Allot Coach & Team Directly
                    </label>

                    {/* 1. Coach Selection */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-slate-700 font-semibold uppercase tracking-wider text-xs">
                          Assign Coach(es) *
                        </label>
                        <span className="text-[10px] text-slate-400 font-medium">
                          (Multiple coaches allowed)
                        </span>
                      </div>

                      <div className="space-y-1.5 max-h-[160px] overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50">
                        {coaches.length === 0 ? (
                          <p className="text-slate-400 text-center py-3 text-xs italic">
                            No coaches appointed for this camp yet.
                          </p>
                        ) : (
                          coaches.map((c) => {
                            const isChecked = enrollForm.coach_ids.includes(c.id);
                            return (
                              <label
                                key={c.id}
                                className={`flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition ${isChecked
                                  ? "bg-white border border-emerald-300 shadow-xs"
                                  : "hover:bg-slate-100/80 border border-transparent"
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setEnrollForm((prev) => {
                                      const nextIds = checked
                                        ? [...prev.coach_ids, c.id]
                                        : prev.coach_ids.filter((id) => id !== c.id);
                                      const nextTeam =
                                        !prev.team_name && c.team_name ? c.team_name : prev.team_name;
                                      return {
                                        ...prev,
                                        coach_ids: nextIds,
                                        team_name: nextTeam,
                                      };
                                    });
                                  }}
                                  className="w-3.5 h-3.5 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300"
                                />
                                <div className="min-w-0 flex-1">
                                  <div className="font-bold text-slate-900 text-xs flex items-center justify-between">
                                    <span>{c.name}</span>
                                    {c.team_name && (
                                      <span className="text-[10px] font-bold text-cyan-800 bg-cyan-50 px-1.5 py-0.2 rounded border border-cyan-200">
                                        {c.team_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-500">
                                    {c.district} &bull; {c.mobile}
                                  </div>
                                </div>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* 2. Team / Squad Name */}
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                        Team / Squad Name (Optional)
                      </label>
                      <input
                        type="text"
                        list="enroll-existing-teams-list"
                        placeholder="e.g. RONGALI RAVI or AP Athletics Squad"
                        value={enrollForm.team_name}
                        onChange={(e) => setEnrollForm({ ...enrollForm, team_name: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-medium"
                      />
                      <datalist id="enroll-existing-teams-list">
                        {existingTeams.map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                      {existingTeams.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {existingTeams.slice(0, 4).map((t) => (
                            <button
                              key={t}
                              type="button"
                              onClick={() => setEnrollForm((prev) => ({ ...prev, team_name: t }))}
                              className={`text-[10px] px-2 py-0.5 rounded-lg border transition ${enrollForm.team_name === t
                                ? "bg-emerald-700 text-white border-emerald-800 font-bold"
                                : "bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200"
                                }`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 3. Sport Discipline */}
                    <div>
                      <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                        Sport Discipline (Optional)
                      </label>
                      <select
                        value={enrollForm.discipline_id}
                        onChange={(e) => {
                          const discId = e.target.value;
                          const disc = disciplines.find((d) => String(d.id) === String(discId));
                          setEnrollForm({
                            ...enrollForm,
                            discipline_id: discId,
                            discipline_name: disc?.name || "",
                            category: "", // Reset classification when sport changes
                          });
                        }}
                        className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-medium"
                      >
                        <option value="">-- Select Sport Discipline (Optional) --</option>
                        {disciplines.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name} {d.classifications && d.classifications.length > 0 ? `(${d.classifications.length} classes)` : ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 4. Para Classification Category (Dependent Dropdown from para_classifications) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-slate-700 font-semibold uppercase tracking-wider text-xs">
                          Para Classification Category (Optional)
                        </label>
                        {selectedEnrollSport && (
                          <span className="text-[10px] text-emerald-700 font-semibold">
                            {availableEnrollClassifications.length} class{availableEnrollClassifications.length !== 1 ? "es" : ""} in {selectedEnrollSport.name}
                          </span>
                        )}
                      </div>

                      {selectedEnrollSport ? (
                        availableEnrollClassifications.length > 0 ? (
                          <select
                            value={enrollForm.category}
                            onChange={(e) => setEnrollForm({ ...enrollForm, category: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-mono font-bold"
                          >
                            <option value="">-- Select Classification (e.g. F57, T44, SL3) --</option>
                            {availableEnrollClassifications.map((c) => (
                              <option key={c.id || c.classification} value={c.classification}>
                                {c.classification}
                              </option>
                            ))}
                            {enrollForm.category &&
                              !availableEnrollClassifications.some((c) => c.classification === enrollForm.category) && (
                                <option value={enrollForm.category}>{enrollForm.category} (Custom)</option>
                              )}
                          </select>
                        ) : (
                          <input
                            type="text"
                            placeholder="e.g. Open / General Category"
                            value={enrollForm.category}
                            onChange={(e) => setEnrollForm({ ...enrollForm, category: e.target.value.toUpperCase() })}
                            className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-mono uppercase font-bold"
                          />
                        )
                      ) : (
                        <select
                          disabled
                          className="w-full bg-slate-100 border border-slate-300 rounded-xl px-3 py-2 text-slate-400 text-xs cursor-not-allowed font-medium"
                        >
                          <option value="">-- Select a Sport Discipline above first --</option>
                        </select>
                      )}
                      <p className="text-[10px] text-slate-400 mt-1">
                        Dependent dropdown loaded directly from master table <span className="font-mono">para_classifications</span>.
                      </p>
                    </div>

                    {/* Manual BIB & ID Number Inputs */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
                      <div>
                        <label className="block text-slate-700 font-bold mb-1 text-[11px] uppercase tracking-wider">
                          Camp BIB / Chest No (Manual / Optional)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. PSCC0042 (Auto-generated if empty)"
                          value={enrollForm.bib_no || ""}
                          onChange={(e) => setEnrollForm({ ...enrollForm, bib_no: e.target.value.toUpperCase() })}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-mono font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-700 font-bold mb-1 text-[11px] uppercase tracking-wider">
                          ID Number (Printed on ID Card)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. ID-2026-089"
                          value={enrollForm.id_number || ""}
                          onChange={(e) => setEnrollForm({ ...enrollForm, id_number: e.target.value })}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-emerald-500 font-semibold"
                        />
                      </div>
                    </div>

                    {/* Selected count review box */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-emerald-900">
                      <div className="font-bold text-xs flex items-center justify-between">
                        <span>Ready to Allot</span>
                        <span>{selectedPlayerIds.length} Player{selectedPlayerIds.length !== 1 ? "s" : ""}</span>
                      </div>
                      <p className="text-[11px] text-emerald-700 mt-0.5">
                        Selected players will be added into the coaching camp and immediately allotted to the selected coach(es).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer Buttons */}
                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setEnrollPlayerModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingEnroll || selectedPlayerIds.length === 0}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 text-white font-bold rounded-xl text-xs shadow-md flex items-center gap-2 transition"
                  >
                    {submittingEnroll ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Allotting to Camp...</span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        <span>Allot {selectedPlayerIds.length > 0 ? `${selectedPlayerIds.length} Player(s)` : ""} to Camp & Coach</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ENROLL OFFICIAL FROM DIRECTORY */}
        {/* ============================================================== */}
        {enrollOfficialModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-3xl max-w-5xl w-full p-6 shadow-2xl text-slate-900 space-y-4 relative z-10 my-8 max-h-[92vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                    <FaUserTie className="text-emerald-600" />
                    <span>Enroll Officials into Coaching Camp</span>
                  </h3>
                  <p className="text-xs text-slate-500">
                    Select registered officials from the directory and allot their camp duty roles and accommodation.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEnrollOfficialModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Modal Form */}
              <form onSubmit={handleSaveEnrollOfficial} className="flex-1 flex flex-col justify-between overflow-hidden space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 flex-1 overflow-y-auto pr-1">
                  {/* Left Column: Official Directory Picker (7 cols) */}
                  <div className="lg:col-span-7 space-y-3 flex flex-col">
                    <div className="flex items-center justify-between">
                      <label className="block text-slate-800 font-bold uppercase tracking-wider text-xs">
                        Step 1: Select Official(s) ({selectedOfficialUserIds.length} Selected)
                      </label>
                      {eligibleOfficialsList.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const nonEnrolled = eligibleOfficialsList.filter((u) => !u.is_enrolled).map((u) => u.id);
                            setSelectedOfficialUserIds(selectedOfficialUserIds.length === nonEnrolled.length ? [] : nonEnrolled);
                          }}
                          className="text-[11px] font-bold text-emerald-700 hover:underline cursor-pointer"
                        >
                          {selectedOfficialUserIds.length > 0 ? "Deselect All" : "Select All Available"}
                        </button>
                      )}
                    </div>

                    {/* Search and District Filters */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Search officials by name, designation, district..."
                        onChange={(e) => fetchEligibleOfficials(e.target.value)}
                        className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 flex-1"
                      />
                    </div>

                    {/* Scrollable Officials List */}
                    <div className="flex-1 border border-slate-200 rounded-2xl p-2.5 overflow-y-auto max-h-[360px] space-y-2 bg-slate-50/50">
                      {loadingEligibleOfficials ? (
                        <div className="p-8 text-center text-slate-400 text-xs">
                          <div className="inline-block w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mb-2" />
                          <p>Loading registered officials...</p>
                        </div>
                      ) : eligibleOfficialsList.length === 0 ? (
                        <div className="p-8 text-center text-slate-400 text-xs">
                          <FaUserTie className="text-3xl mx-auto mb-2 text-slate-300" />
                          <p>No registered officials found in directory.</p>
                        </div>
                      ) : (
                        eligibleOfficialsList.map((u) => {
                          const isSelected = selectedOfficialUserIds.includes(u.id);
                          const photoUrl = getSafePhotoUrl(u.photo);

                          return (
                            <div
                              key={u.id}
                              onClick={() => {
                                if (u.is_enrolled) return;
                                setSelectedOfficialUserIds((prev) =>
                                  isSelected ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                );
                              }}
                              className={`p-2.5 rounded-xl border transition flex items-center justify-between gap-3 ${u.is_enrolled
                                ? "bg-slate-100/70 border-slate-200 opacity-60 cursor-not-allowed"
                                : isSelected
                                  ? "bg-emerald-50 border-emerald-300 shadow-xs cursor-pointer ring-1 ring-emerald-400"
                                  : "bg-white border-slate-200 hover:border-emerald-200 cursor-pointer"
                                }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={isSelected || u.is_enrolled}
                                  disabled={u.is_enrolled}
                                  onChange={() => { }}
                                  className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 shrink-0"
                                />
                                <img
                                  src={photoUrl}
                                  alt={u.name}
                                  className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                                  onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
                                />
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-slate-900 truncate">{u.name}</p>
                                  <p className="text-[10px] text-slate-500 truncate">
                                    {u.designation || "Official"} • {u.district || "Andhra Pradesh"} • {u.mobile || "—"}
                                  </p>
                                </div>
                              </div>

                              <div className="shrink-0">
                                {u.is_enrolled ? (
                                  <span className="text-[9.5px] font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                                    Already In Camp
                                  </span>
                                ) : isSelected ? (
                                  <span className="text-[9.5px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                                    Selected
                                  </span>
                                ) : (
                                  <span className="text-[9.5px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200">
                                    Available
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Right Column: Camp Allotment Settings (5 cols) */}
                  <div className="lg:col-span-5 space-y-3.5 bg-slate-50 p-4 rounded-2xl border border-slate-200">
                    <label className="block text-slate-800 font-bold uppercase tracking-wider text-xs">
                      Step 2: Camp Designation & Duties
                    </label>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Official Designation / Title *
                      </label>
                      <input
                        type="text"
                        value={enrollOfficialForm.designation}
                        onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, designation: e.target.value })}
                        placeholder="e.g. Chief Referee, Scorer, Timekeeper, Medical Officer"
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Camp Duty Role *
                      </label>
                      <select
                        value={enrollOfficialForm.duty_role}
                        onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, duty_role: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        required
                      >
                        <option value="Technical Official">Technical Official</option>
                        <option value="Camp Management">Camp Management</option>
                        <option value="Health & Medical">Health & Medical</option>
                        <option value="Food & Accommodation">Food & Accommodation</option>
                        <option value="Transportation">Transportation</option>
                        <option value="Security & Discipline">Security & Discipline</option>
                        <option value="Observer / Liaison">Observer / Liaison</option>
                        <option value="Camp Incharge">Camp Incharge</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Assigned Room No
                        </label>
                        <input
                          type="text"
                          value={enrollOfficialForm.room_no}
                          onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, room_no: e.target.value })}
                          placeholder="e.g. Room 102"
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">
                          Reporting Date
                        </label>
                        <input
                          type="date"
                          value={enrollOfficialForm.reporting_date}
                          onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, reporting_date: e.target.value })}
                          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Relieving Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={enrollOfficialForm.relieving_date}
                        onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, relieving_date: e.target.value })}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Notes / Remarks (Optional)
                      </label>
                      <textarea
                        value={enrollOfficialForm.notes}
                        onChange={(e) => setEnrollOfficialForm({ ...enrollOfficialForm, notes: e.target.value })}
                        placeholder="Special instructions or responsibilities..."
                        rows={2}
                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                      />
                    </div>

                    <div className="bg-emerald-100/70 border border-emerald-200 rounded-xl p-3">
                      <div className="flex items-center justify-between text-xs font-bold text-emerald-900">
                        <span>Ready to Enroll:</span>
                        <span className="text-sm">{selectedOfficialUserIds.length} Official(s)</span>
                      </div>
                      <p className="text-[10px] text-emerald-700 mt-0.5">
                        Officials will be added to the camp roster with automated Official IDs and credential badges.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setEnrollOfficialModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingEnrollOfficial || selectedOfficialUserIds.length === 0}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50 text-white font-bold rounded-xl text-xs shadow-md flex items-center gap-2 transition cursor-pointer"
                  >
                    {submittingEnrollOfficial ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Enrolling Officials...</span>
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        <span>Enroll {selectedOfficialUserIds.length > 0 ? `${selectedOfficialUserIds.length} Official(s)` : ""} to Camp</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ADD / EDIT CAMP OFFICIAL */}
        {/* ============================================================== */}
        {officialModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-3xl max-w-xl w-full p-6 shadow-2xl text-slate-900 space-y-4 relative z-10 my-8 max-h-[92vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
                  <FaUserTie className="text-indigo-600" />
                  <span>{editingOfficial ? "Edit Camp Official" : "Add New Official to Camp"}</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setOfficialModalOpen(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition cursor-pointer"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveOfficial} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={officialForm.name}
                      onChange={(e) => setOfficialForm({ ...officialForm, name: e.target.value })}
                      placeholder="Enter official's full name"
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Designation *</label>
                    <input
                      type="text"
                      value={officialForm.designation}
                      onChange={(e) => setOfficialForm({ ...officialForm, designation: e.target.value })}
                      placeholder="e.g. Chief Referee, Scorer"
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Camp Duty Role</label>
                    <select
                      value={officialForm.duty_role}
                      onChange={(e) => setOfficialForm({ ...officialForm, duty_role: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="Technical Official">Technical Official</option>
                      <option value="Camp Management">Camp Management</option>
                      <option value="Health & Medical">Health & Medical</option>
                      <option value="Food & Accommodation">Food & Accommodation</option>
                      <option value="Transportation">Transportation</option>
                      <option value="Security & Discipline">Security & Discipline</option>
                      <option value="Observer / Liaison">Observer / Liaison</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Mobile Number</label>
                    <input
                      type="text"
                      value={officialForm.mobile}
                      onChange={(e) => setOfficialForm({ ...officialForm, mobile: e.target.value })}
                      placeholder="10 digit mobile"
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={officialForm.email}
                      onChange={(e) => setOfficialForm({ ...officialForm, email: e.target.value })}
                      placeholder="official@domain.com"
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Gender</label>
                    <select
                      value={officialForm.gender}
                      onChange={(e) => setOfficialForm({ ...officialForm, gender: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">District</label>
                    <select
                      value={officialForm.district}
                      onChange={(e) => setOfficialForm({ ...officialForm, district: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="">Select District</option>
                      {AP_DISTRICTS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Room No</label>
                    <input
                      type="text"
                      value={officialForm.room_no}
                      onChange={(e) => setOfficialForm({ ...officialForm, room_no: e.target.value })}
                      placeholder="Hostel / Hotel Room"
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Reporting Date</label>
                    <input
                      type="date"
                      value={officialForm.reporting_date}
                      onChange={(e) => setOfficialForm({ ...officialForm, reporting_date: e.target.value })}
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setOfficialPhotoFile(e.target.files?.[0] || null)}
                      className="w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1">Notes</label>
                    <textarea
                      value={officialForm.notes}
                      onChange={(e) => setOfficialForm({ ...officialForm, notes: e.target.value })}
                      rows={2}
                      placeholder="Additional details..."
                      className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setOfficialModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingOfficial}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-xl text-xs shadow-md flex items-center gap-2 transition cursor-pointer"
                  >
                    {submittingOfficial ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving Official...</span>
                      </>
                    ) : (
                      <>
                        <FaSave />
                        <span>{editingOfficial ? "Update Official" : "Save Official"}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: SINGLE ID CARD PREVIEW & PRINT */}
        {/* ============================================================== */}
        {singleIdCardModal && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-4 print:hidden">
            <div className={`bg-white rounded-3xl p-6 shadow-2xl w-full flex flex-col items-center gap-4 relative animate-in fade-in zoom-in-95 transition-all ${singleIdCardFormat === "a4" ? "max-w-xl" : "max-w-md"
              }`}>
              <button
                onClick={() => setSingleIdCardModal(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 p-2 rounded-full cursor-pointer transition-colors"
              >
                <FaTimes />
              </button>

              <div className="flex flex-wrap items-center justify-between w-full pr-8 gap-2">
                <h3 className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                  <FaIdCard className="text-blue-600" />
                  <span>Identity Card Preview</span>
                </h3>

                {/* Format Switcher: Badge vs PAN Card vs A4 Size */}
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
                  <button
                    type="button"
                    onClick={() => setSingleIdCardFormat("badge")}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${singleIdCardFormat === "badge"
                      ? "bg-white text-indigo-900 shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    Portrait Badge
                  </button>
                  <button
                    type="button"
                    onClick={() => setSingleIdCardFormat("pan")}
                    className={`px-2.5 py-1 rounded-lg text-xs font-black transition-all cursor-pointer ${singleIdCardFormat === "pan"
                      ? "bg-amber-500 text-slate-950 shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    PAN (100x70)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSingleIdCardFormat("a4")}
                    className={`px-2.5 py-1 rounded-lg text-xs font-black transition-all cursor-pointer ${singleIdCardFormat === "a4"
                      ? "bg-blue-600 text-white shadow-xs"
                      : "text-slate-600 hover:text-slate-900"
                      }`}
                  >
                    A4 Size
                  </button>
                </div>
              </div>

              <div className={`camp-single-modal-wrapper w-full flex justify-center py-1 ${singleIdCardFormat === "a4" ? "overflow-y-auto max-h-[65vh]" : ""}`}>
                {(() => {
                  const campSigs = typeof window !== "undefined" ? {
                    president: localStorage.getItem("camp_president_signature") || "",
                    secretary: localStorage.getItem("camp_secretary_signature") || "",
                  } : null;

                  if (singleIdCardFormat === "a4") {
                    return (
                      <SingleA4Card
                        data={singleIdCardModal.data}
                        type={singleIdCardModal.type}
                        camp={activeCamp}
                        signatures={campSigs}
                      />
                    );
                  }
                  if (singleIdCardFormat === "pan") {
                    return (
                      <SinglePanCard
                        data={singleIdCardModal.data}
                        type={singleIdCardModal.type}
                        camp={activeCamp}
                        signatures={campSigs}
                      />
                    );
                  }
                  return (
                    <SingleCampIdCard
                      data={singleIdCardModal.data}
                      type={singleIdCardModal.type}
                      camp={activeCamp}
                      signatures={campSigs}
                    />
                  );
                })()}
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full pt-2 border-t border-slate-100">
                <button
                  onClick={() => setSingleIdCardModal(null)}
                  className="py-2.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold cursor-pointer transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    const origin = typeof window !== "undefined" ? window.location.origin : "";
                    const campSigs = typeof window !== "undefined" ? {
                      president: localStorage.getItem("camp_president_signature") || "",
                      secretary: localStorage.getItem("camp_secretary_signature") || "",
                    } : null;
                    const html =
                      singleIdCardFormat === "a4"
                        ? generateSingleA4CardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin)
                        : singleIdCardFormat === "pan"
                          ? generateSinglePanCardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin)
                          : generateSingleCardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin);
                    printHtmlViaIframe(html);
                  }}
                  className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center justify-center gap-1.5 transition-all"
                >
                  <FaPrint /> Print {singleIdCardFormat === "a4" ? "A4" : singleIdCardFormat === "pan" ? "PAN" : "Badge"}
                </button>
                <button
                  onClick={() => {
                    const origin = typeof window !== "undefined" ? window.location.origin : "";
                    const campSigs = typeof window !== "undefined" ? {
                      president: localStorage.getItem("camp_president_signature") || "",
                      secretary: localStorage.getItem("camp_secretary_signature") || "",
                    } : null;
                    const cleanName = (singleIdCardModal.data?.name || "Participant").replace(/\s+/g, "_");
                    if (singleIdCardFormat === "a4") {
                      const html = generateSingleA4CardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin);
                      downloadHtmlAsPdf(html, `Camp_A4_ID_Card_${cleanName}`, { orientation: "portrait" });
                    } else if (singleIdCardFormat === "pan") {
                      const html = generateSinglePanCardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin);
                      downloadHtmlAsPdf(html, `PAN_Camp_ID_${cleanName}`, { orientation: "portrait" });
                    } else {
                      const html = generateSingleCardSheetHtml(singleIdCardModal.data, activeCamp, campSigs, false, origin);
                      downloadHtmlAsPdf(html, `Camp_ID_Card_${cleanName}`, { orientation: "portrait" });
                    }
                  }}
                  className={`flex-1 py-2.5 text-xs font-black rounded-xl shadow-md cursor-pointer flex items-center justify-center gap-1.5 transition-all ${singleIdCardFormat === "a4"
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : singleIdCardFormat === "pan"
                      ? "bg-amber-500 hover:bg-amber-600 text-slate-950"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                >
                  <FaDownload /> Download {singleIdCardFormat === "a4" ? "A4 (PDF)" : singleIdCardFormat === "pan" ? "PAN (PDF)" : "Badge (PDF)"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ASSIGN ATHLETES TO COACH */}
        {/* ============================================================== */}
        {mappingModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-2xl w-full p-6 shadow-2xl text-slate-900 space-y-4 relative z-10 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Assign Team, Coaches & Athletes</h3>
                  <p className="text-xs text-slate-500">
                    Multiple coaches can be selected and assigned to the same team and squad.
                  </p>
                </div>
                <button
                  onClick={() => setMappingModalOpen(false)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveMapping} className="space-y-4 text-xs">
                {/* 1. Team / Squad Name */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-slate-700 font-semibold uppercase tracking-wider text-xs">
                      Team / Squad Name *
                    </label>
                    {existingTeams.length > 0 && (
                      <span className="text-[10px] text-slate-500 font-medium">
                        {existingTeams.length} existing team{existingTeams.length > 1 ? "s" : ""} in camp
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    list="mapping-existing-teams-list"
                    placeholder="e.g. AP Para Athletics Team A or choose existing below"
                    value={mappingForm.team_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMappingForm((prev) => {
                        const matchingCoaches = coaches.filter(
                          (c) => c.team_name && c.team_name.trim().toLowerCase() === val.trim().toLowerCase()
                        );
                        const matchingIds = matchingCoaches.map((c) => c.id);
                        return {
                          ...prev,
                          team_name: val,
                          coach_ids: matchingIds,
                        };
                      });
                    }}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                    required
                  />
                  <datalist id="mapping-existing-teams-list">
                    {existingTeams.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>

                  {/* Quick-select chips of existing teams */}
                  {existingTeams.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className="text-[10px] text-slate-500 font-medium self-center">Pick Team:</span>
                      {existingTeams.map((t) => {
                        const isSelected = mappingForm.team_name.trim().toLowerCase() === t.toLowerCase();
                        const teamCoaches = coaches.filter(
                          (c) => c.team_name && c.team_name.trim().toLowerCase() === t.toLowerCase()
                        );
                        return (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              const teamCoachIds = teamCoaches.map((c) => c.id);
                              setMappingForm((prev) => ({
                                ...prev,
                                team_name: t,
                                coach_ids: teamCoachIds,
                              }));
                            }}
                            className={`text-[10px] px-2 py-0.5 rounded-lg border transition ${isSelected
                              ? "bg-cyan-700 text-white border-cyan-800 font-bold shadow-sm"
                              : "bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200 font-medium"
                              }`}
                          >
                            {t} {teamCoaches.length > 0 && `(${teamCoaches.length} coach${teamCoaches.length > 1 ? "es" : ""})`}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 2. Select Coaches for this Team (Multi-select allowed) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <label className="block text-slate-700 font-semibold uppercase tracking-wider text-xs">
                        Select Coach(es) for this Team ({mappingForm.coach_ids.length} Selected) *
                      </label>
                      <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        Multi-Coach Allowed
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {mappingForm.team_name && (
                        <button
                          type="button"
                          onClick={() => {
                            const matchingCoaches = coaches.filter(
                              (c) => c.team_name && c.team_name.trim().toLowerCase() === mappingForm.team_name.trim().toLowerCase()
                            );
                            const matchingIds = matchingCoaches.map((c) => c.id);
                            if (matchingIds.length) {
                              setMappingForm((prev) => ({
                                ...prev,
                                coach_ids: matchingIds,
                              }));
                            }
                          }}
                          className="text-[10px] text-cyan-700 hover:underline font-medium"
                        >
                          Select All for this Team
                        </button>
                      )}
                      {mappingForm.coach_ids.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setMappingForm((prev) => ({ ...prev, coach_ids: [] }))}
                          className="text-[10px] text-rose-600 hover:underline font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 mb-2">
                    Select one or more coaches. All selected coaches will be able to view, train, and mark attendance for this team's athletes.
                  </p>

                  <div className="max-h-48 overflow-y-auto space-y-1.5 p-2 bg-slate-50 border border-slate-200 rounded-xl">
                    {coaches.map((c) => {
                      const isChecked = mappingForm.coach_ids.includes(c.id);
                      const isSameTeam = mappingForm.team_name && c.team_name && c.team_name.trim().toLowerCase() === mappingForm.team_name.trim().toLowerCase();
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center justify-between p-2 rounded-xl border cursor-pointer transition ${isChecked
                            ? "bg-cyan-50/90 border-cyan-300 text-slate-900 shadow-sm"
                            : "bg-white border-slate-200 hover:bg-slate-100/70 text-slate-700"
                            }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMappingForm((prev) => ({
                                    ...prev,
                                    coach_ids: [...prev.coach_ids, c.id],
                                    team_name: prev.team_name || c.team_name || "",
                                  }));
                                } else {
                                  setMappingForm((prev) => ({
                                    ...prev,
                                    coach_ids: prev.coach_ids.filter((id) => id !== c.id),
                                  }));
                                }
                              }}
                              className="rounded text-cyan-600 focus:ring-0 w-4 h-4"
                            />
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 border border-slate-300 flex-shrink-0 flex items-center justify-center">
                              {c.photo || c.player_photo ? (
                                <img
                                  src={resolvePhoto(c.photo || c.player_photo)}
                                  alt=""
                                  className="w-full h-full object-cover"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <span className="text-[10px] font-bold text-slate-500">{c.name?.charAt(0) || "C"}</span>
                              )}
                            </div>
                            <div>
                              <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                                {c.name}
                                {isSameTeam && (
                                  <span className="text-[9px] bg-cyan-100 text-cyan-800 font-bold px-1.5 py-0.5 rounded">
                                    Current Team Member
                                  </span>
                                )}
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {c.district || "District N/A"} {c.team_name ? `• Squad: ${c.team_name}` : ""}
                              </div>
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isChecked ? "bg-cyan-700 text-white" : "bg-slate-100 text-slate-600 border border-slate-200"
                            }`}>
                            {isChecked ? "Selected" : "Select"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* 3. Select Athletes */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-slate-700 font-semibold uppercase tracking-wider text-xs">
                      Select Athletes to Map ({mappingForm.athlete_ids.length} selected) *
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const allIds = athletes.map((a) => a.id);
                          setMappingForm((prev) => ({
                            ...prev,
                            athlete_ids: allIds,
                          }));
                        }}
                        className="text-[10px] text-cyan-700 hover:underline font-medium"
                      >
                        Select All ({athletes.length})
                      </button>
                      {mappingForm.athlete_ids.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setMappingForm((prev) => ({ ...prev, athlete_ids: [] }))}
                          className="text-[10px] text-rose-600 hover:underline font-medium"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-1 p-2.5 bg-white border border-slate-200 rounded-xl">
                    {athletes.map((a) => {
                      const isChecked = mappingForm.athlete_ids.includes(a.id);
                      return (
                        <label
                          key={a.id}
                          className={`flex items-center justify-between p-1.5 rounded-lg cursor-pointer transition ${isChecked ? "bg-cyan-50 text-cyan-900 font-bold border border-cyan-200" : "hover:bg-slate-50 text-slate-700"
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setMappingForm((prev) => ({
                                    ...prev,
                                    athlete_ids: [...prev.athlete_ids, a.id],
                                  }));
                                } else {
                                  setMappingForm((prev) => ({
                                    ...prev,
                                    athlete_ids: prev.athlete_ids.filter((id) => id !== a.id),
                                  }));
                                }
                              }}
                              className="rounded text-cyan-600 focus:ring-0"
                            />
                            <span>{a.name}</span>
                            <span className="text-[10px] text-slate-500 font-normal">
                              ({a.discipline_name || "Discipline Pending"}{a.category ? ` • ${a.category}` : ""})
                            </span>
                          </div>
                          <span className="text-[10px] font-mono text-amber-600 font-bold">BIB: {a.bib_no}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Assignment Mode Option */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-slate-800 text-xs">Assignment Mode:</div>
                    <div className="text-[10px] text-slate-500">
                      {mappingForm.mode === "add"
                        ? "Add Coaches: Keeps already assigned coaches for these athletes and adds the selected coaches."
                        : "Replace Coaches: Overwrites previous coach assignments for these athletes."}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="mapping_mode"
                        value="add"
                        checked={mappingForm.mode === "add"}
                        onChange={() => setMappingForm({ ...mappingForm, mode: "add" })}
                        className="text-cyan-700 focus:ring-0"
                      />
                      <span className="font-bold text-xs text-cyan-900">Add to Squad</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="mapping_mode"
                        value="replace"
                        checked={mappingForm.mode === "replace"}
                        onChange={() => setMappingForm({ ...mappingForm, mode: "replace" })}
                        className="text-slate-600 focus:ring-0"
                      />
                      <span className="text-xs text-slate-600">Replace</span>
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setMappingModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm font-semibold transition flex items-center gap-2"
                  >
                    <FaSave /> Save Team & Coach Assignments
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: QUICK ADD COACH TO TEAM / ATHLETE */}
        {/* ============================================================== */}
        {addCoachModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 shadow-2xl text-slate-900 space-y-4 relative z-10">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Add Another Coach</h3>
                  <p className="text-xs text-slate-500">
                    {addCoachModal.team_name ? `Team: ${addCoachModal.team_name}` : `Athlete: ${addCoachModal.athlete_name}`}
                  </p>
                </div>
                <button
                  onClick={() => setAddCoachModal(null)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleQuickAddCoach} className="space-y-4 text-xs">
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">
                    Select Coach to Add *
                  </label>
                  <select
                    value={addCoachForm.coach_id}
                    onChange={(e) => setAddCoachForm({ coach_id: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-semibold"
                    required
                  >
                    <option value="">-- Choose Coach --</option>
                    {coaches.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.team_name ? `(${c.team_name})` : ""} - {c.district}
                      </option>
                    ))}
                  </select>
                </div>

                <p className="text-[11px] text-slate-600 bg-slate-50 p-2.5 rounded-xl border border-slate-200 leading-relaxed">
                  Adding this coach will retain all previously assigned coaches on this squad and give the new coach active access to mark attendance, train, and review these athletes.
                </p>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setAddCoachModal(null)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-cyan-700 hover:bg-cyan-800 text-white rounded-xl shadow-sm font-semibold transition flex items-center gap-1.5"
                  >
                    <FaPlus className="text-xs" /> Add Coach to Squad
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: VIEW ATHLETE PROFILE & DOCUMENTS */}
        {/* ============================================================== */}
        {viewAthleteModal && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-xl w-full p-6 shadow-2xl text-slate-900 space-y-4 max-h-[90vh] overflow-y-auto relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">Athlete Profile & Documents</h3>
                <button onClick={() => setViewAthleteModal(null)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <div className="space-y-4 text-xs">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <img
                    src={getSafePhotoUrl(viewAthleteModal.photo)}
                    alt="Athlete"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_AVATAR;
                    }}
                    className="w-16 h-16 rounded-xl object-cover bg-white border border-slate-200 text-slate-800"
                  />
                  <div>
                    <h4 className="text-base font-bold text-slate-900">{viewAthleteModal.name}</h4>
                    <p className="text-xs text-slate-500">
                      BIB: <span className="text-amber-700 font-bold">{viewAthleteModal.bib_no}</span> &bull;{" "}
                      Category: <span className="text-cyan-800 font-bold">{viewAthleteModal.category || "N/A"}</span>
                    </p>
                    <p className="text-xs text-slate-700 mt-1">
                      Discipline: {viewAthleteModal.discipline_name} &bull; District: {viewAthleteModal.district}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Pincode: {viewAthleteModal.pincode || "N/A"} &bull; State: {viewAthleteModal.state || "Andhra Pradesh"}
                      {viewAthleteModal.assigned_coach?.team_name && (
                        <span className="text-cyan-700 font-bold ml-1">
                          &bull; Team: {viewAthleteModal.assigned_coach.team_name}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Camp-only allotted games and performance comparison */}
                <div className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-emerald-900 flex items-center gap-1.5 text-xs uppercase tracking-wider">
                      <FaRunning className="text-emerald-700" /> Camp Allotted Games &amp; Performance
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const ath = viewAthleteModal;
                        setViewAthleteModal(null);
                        handleOpenSportsAllotmentModal(ath);
                      }}
                      className="px-2.5 py-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-[10px] flex items-center gap-1 shadow-sm transition"
                    >
                      <FaEdit className="text-[9px]" /> Allot / Change Games
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2 pt-1">
                    {(Array.isArray(viewAthleteModal.allocated_disciplines) && viewAthleteModal.allocated_disciplines.length > 0
                      ? viewAthleteModal.allocated_disciplines
                      : [viewAthleteModal.discipline_name].filter(Boolean)
                    ).map((disp, dIdx) => {
                      const dName = typeof disp === "string" ? disp : disp.discipline_name || disp.name;
                      const weeklyTrial = getWeeklyTrialHistory(viewAthleteModal, disp, performanceReports);
                      const metricLabel = typeof disp === "object"
                        ? [disp.measurement_type, disp.measurement_unit].filter(Boolean).join(" / ")
                        : "";
                      return (
                        <div key={dIdx} className="p-2.5 rounded-lg bg-white border border-emerald-300 text-xs text-slate-800 shadow-xs">
                          <div className="flex flex-wrap items-center justify-between gap-1.5 font-bold">
                            <span><span className="text-emerald-700">Game #{dIdx + 1}:</span> {dName}</span>
                            {metricLabel && <span className="text-[9px] uppercase rounded bg-slate-100 px-2 py-0.5 text-slate-600">{metricLabel}</span>}
                          </div>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[10px]">
                            <div className="rounded bg-cyan-50 border border-cyan-100 p-1.5"><strong>Athlete camp trials:</strong> {weeklyTrial.entries.length ? weeklyTrial.entries.map((entry) => `W${entry.week} ${entry.value}`).join(", ") : "Not entered"}</div>
                            <div className="rounded bg-amber-50 border border-amber-200 p-1.5"><strong>Previous national benchmark:</strong> {disp?.national_benchmark || disp?.national_gold_performance || "Not entered"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(viewAthleteModal.sports_allotment_notes || viewAthleteModal.performance_notes) && (
                    <div className="text-[11px] text-slate-600 bg-white/80 p-2 rounded-lg border border-emerald-100 mt-1">
                      <strong className="text-slate-700">Performance Notes / Reason:</strong> {viewAthleteModal.sports_allotment_notes || viewAthleteModal.performance_notes}
                    </div>
                  )}
                </div>

                {/* Room Allotment & Health / Medical Care */}
                <div className="p-3.5 rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-amber-50/40 space-y-2.5">
                  <div className="flex items-center justify-between border-b border-rose-100 pb-1.5">
                    <span className="font-bold text-rose-900 flex items-center gap-1.5 text-xs uppercase tracking-wider">
                      🏥 Room Allotment & Health Profile
                    </span>
                    {viewAthleteModal.room_no ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-800 border border-indigo-200">
                        Allotted: Room {viewAthleteModal.room_no}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                        Room Not Allotted
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div className="p-2 bg-white rounded-lg border border-rose-100">
                      <span className="text-slate-500 block text-[10px] font-semibold uppercase tracking-wider">Health Conditions / Issues</span>
                      <span className="font-medium text-slate-800 mt-0.5 block">
                        {viewAthleteModal.health_issues || "None reported"}
                      </span>
                    </div>

                    <div className="p-2 bg-white rounded-lg border border-rose-100">
                      <span className="text-slate-500 block text-[10px] font-semibold uppercase tracking-wider">Emergency Contact</span>
                      <span className="font-medium text-slate-800 mt-0.5 block">
                        {viewAthleteModal.emergency_contact || viewAthleteModal.parent_mobile || "N/A"}
                      </span>
                    </div>

                    <div className="p-2 bg-white rounded-lg border border-rose-100 sm:col-span-2">
                      <span className="text-slate-500 block text-[10px] font-semibold uppercase tracking-wider">Prescribed Medicines</span>
                      <span className="font-semibold text-rose-700 mt-0.5 block">
                        {viewAthleteModal.medicines ? (
                          <span className="px-2 py-0.5 rounded bg-rose-50 border border-rose-200 inline-block">
                            💊 {viewAthleteModal.medicines}
                          </span>
                        ) : (
                          <span className="text-slate-500 font-normal">No regular medicines prescribed</span>
                        )}
                      </span>
                    </div>

                    <div className="p-2 bg-white rounded-lg border border-rose-100 sm:col-span-2">
                      <span className="text-slate-500 block text-[10px] font-semibold uppercase tracking-wider">Medicine Timings</span>
                      <span className="font-semibold text-emerald-800 mt-0.5 block">
                        {viewAthleteModal.medicine_timings ? (
                          <span className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 inline-block">
                            ⏰ {viewAthleteModal.medicine_timings}
                          </span>
                        ) : (
                          <span className="text-slate-500 font-normal">N/A</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Document Download Links */}
                <div className="space-y-2">
                  <h5 className="font-bold text-slate-700">Attached Documents</h5>
                  {viewAthleteModal.aadhaar_doc && (
                    <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <span className="font-medium text-slate-800">Aadhaar Document</span>
                      <a href={resolveAssetUrl(viewAthleteModal.aadhaar_doc)} target="_blank" rel="noreferrer" className="text-cyan-700 font-bold hover:underline">
                        View
                      </a>
                    </div>
                  )}
                  {viewAthleteModal.udid_doc && (
                    <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <span className="font-medium text-slate-800">UDID Certificate</span>
                      <a href={resolveAssetUrl(viewAthleteModal.udid_doc)} target="_blank" rel="noreferrer" className="text-cyan-700 font-bold hover:underline">
                        View
                      </a>
                    </div>
                  )}
                  {viewAthleteModal.sadarem_doc && (
                    <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <span className="font-medium text-slate-800">SADAREM Certificate</span>
                      <a href={resolveAssetUrl(viewAthleteModal.sadarem_doc)} target="_blank" rel="noreferrer" className="text-cyan-700 font-bold hover:underline">
                        View
                      </a>
                    </div>
                  )}
                  {!viewAthleteModal.aadhaar_doc && !viewAthleteModal.udid_doc && !viewAthleteModal.sadarem_doc && (
                    <p className="text-slate-500 py-2 text-center">No document attachments uploaded.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ADD DISCIPLINE */}
        {/* ============================================================== */}
        {disciplineModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 text-slate-900 rounded-2xl max-w-sm w-full p-6 shadow-2xl space-y-4 relative z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900">Add Sports Discipline</h3>
                <button onClick={() => setDisciplineModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await API.camp.addDiscipline(disciplineForm);
                    Swal.fire("Added", "Discipline added successfully!", "success");
                    setDisciplineModalOpen(false);
                    fetchCampsAndSettings();
                  } catch (err) {
                    Swal.fire("Error", "Failed to add discipline", "error");
                  }
                }}
                className="space-y-4 text-xs"
              >
                <div>
                  <label className="block text-slate-700 font-semibold mb-1 uppercase tracking-wider text-xs">Discipline Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Wheelchair Tennis"
                    value={disciplineForm.name}
                    onChange={(e) => setDisciplineForm({ ...disciplineForm, name: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setDisciplineModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-sm font-semibold"
                  >
                    Save Discipline
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ============================================================== */}
        {/* MODAL: ASSIGN / ROTATE ROOM INCHARGE */}
        {/* ============================================================== */}
        {roomInchargeModalOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-slate-200 text-slate-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 relative z-10 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Assign / Rotate Room In-Charge
                  </h3>
                  <p className="text-xs text-slate-500">
                    One or more coaches may be assigned per room and rotated on a weekly basis.
                  </p>
                </div>
                <button
                  onClick={() => setRoomInchargeModalOpen(false)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSaveRoomIncharge} className="space-y-4 text-xs">
                {/* Room Number & Week Number */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Room Number *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. 101, B-204"
                      list="available-rooms-list"
                      value={roomInchargeForm.room_no}
                      onChange={(e) =>
                        setRoomInchargeForm({ ...roomInchargeForm, room_no: e.target.value })
                      }
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-bold focus:ring-2 focus:ring-rose-500"
                    />
                    <datalist id="available-rooms-list">
                      {(roomInchargesData.all_rooms || []).map((r) => (
                        <option key={r} value={r} />
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">
                      Week Number *
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      required
                      value={roomInchargeForm.week_number}
                      onChange={(e) =>
                        setRoomInchargeForm({
                          ...roomInchargeForm,
                          week_number: parseInt(e.target.value) || 1,
                        })
                      }
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs font-bold focus:ring-2 focus:ring-rose-500"
                    />
                  </div>
                </div>

                {/* Start Date & End Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">Start Date</label>
                    <input
                      type="date"
                      value={roomInchargeForm.start_date || ""}
                      onChange={(e) =>
                        setRoomInchargeForm({ ...roomInchargeForm, start_date: e.target.value })
                      }
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-700 font-semibold mb-1">End Date</label>
                    <input
                      type="date"
                      value={roomInchargeForm.end_date || ""}
                      onChange={(e) =>
                        setRoomInchargeForm({ ...roomInchargeForm, end_date: e.target.value })
                      }
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                    />
                  </div>
                </div>

                {/* Multi-Select Coaches (One or More Coaches) */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-slate-700 font-bold text-xs">
                      Select In-Charge Coach(es) * (One or More)
                    </label>
                    <span className="text-[10px] text-slate-500">
                      {roomInchargeForm.coach_ids.length} coach(es) selected
                    </span>
                  </div>

                  <div className="border border-slate-200 rounded-xl p-2.5 max-h-48 overflow-y-auto space-y-1.5 bg-slate-50/50">
                    {coaches.map((c) => {
                      const isSelected = roomInchargeForm.coach_ids.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center justify-between p-2 rounded-lg border text-xs cursor-pointer transition ${isSelected
                            ? "bg-rose-50 border-rose-200 text-rose-950 font-semibold"
                            : "bg-white border-slate-200 text-slate-800 hover:bg-slate-50"
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setRoomInchargeForm({
                                    ...roomInchargeForm,
                                    coach_ids: [...roomInchargeForm.coach_ids, c.id],
                                  });
                                } else {
                                  setRoomInchargeForm({
                                    ...roomInchargeForm,
                                    coach_ids: roomInchargeForm.coach_ids.filter((id) => id !== c.id),
                                  });
                                }
                              }}
                              className="rounded text-rose-600 focus:ring-0"
                            />
                            <span>{c.name}</span>
                            {c.team_name && (
                              <span className="text-[10px] text-slate-400 font-normal">
                                ({c.team_name})
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-500">{c.district}</span>
                        </label>
                      );
                    })}
                    {!coaches.length && (
                      <p className="text-slate-400 italic text-center py-2">No coaches available.</p>
                    )}
                  </div>
                </div>

                {/* Weekly Rotation Option */}
                <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl flex items-center justify-between">
                  <div>
                    <span className="font-bold text-amber-900 block text-xs">Weekly Rotation:</span>
                    <span className="text-[11px] text-amber-700">
                      Mark currently active in-charges of this room as completed.
                    </span>
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={roomInchargeForm.replace_active}
                      onChange={(e) =>
                        setRoomInchargeForm({ ...roomInchargeForm, replace_active: e.target.checked })
                      }
                      className="rounded text-amber-600 focus:ring-0"
                    />
                    <span className="font-semibold text-xs text-amber-900">Rotate</span>
                  </label>
                </div>

                {/* Role Title & Notes */}
                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Designation / Role Title
                  </label>
                  <input
                    type="text"
                    value={roomInchargeForm.role_title}
                    onChange={(e) =>
                      setRoomInchargeForm({ ...roomInchargeForm, role_title: e.target.value })
                    }
                    placeholder="e.g. Room Incharge, Floor Supervisor"
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                  />
                </div>

                <div>
                  <label className="block text-slate-700 font-semibold mb-1">
                    Special Care Instructions / Notes
                  </label>
                  <textarea
                    rows={2}
                    value={roomInchargeForm.notes}
                    onChange={(e) =>
                      setRoomInchargeForm({ ...roomInchargeForm, notes: e.target.value })
                    }
                    placeholder="e.g. Ensure nighttime inhaler check for Athlete Ravi, coordinate with Camp Medical Officer."
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:ring-2 focus:ring-rose-500"
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setRoomInchargeModalOpen(false)}
                    className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 rounded-xl font-semibold transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingRoomIncharge}
                    className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl shadow-sm font-semibold transition flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <FaSave /> {submittingRoomIncharge ? "Saving..." : "Save Room Assignment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <Footer />
      </div>

      {/* =========================================================
          DEDICATED PRINTABLE CONTAINER (Visible ONLY during print)
      ========================================================= */}
      {Boolean(printMode) && (
        <div className="print-only-container w-full">
          {/* MODE 1: OFFICIAL REPORT CARDS */}
          {(printMode === "report_cards" || printMode === "report_cards_blank") && (
            <>
              {athletesToPrintForReportCards.length === 0 ? (
                <div className="print-report-page p-8 text-center text-slate-600">
                  <h2 className="text-lg font-bold text-slate-800 mb-2">No Report Cards Found</h2>
                  <p className="text-xs">No athletes match the current camp selection or coach filter. Please select "All Coaches" or ensure athletes are enrolled.</p>
                </div>
              ) : (
                athletesToPrintForReportCards.map((item, athIdx) => {
                  const isBlankReport = printMode === "report_cards_blank";
                  const athlete = item.athlete || item;
                  const athleteWeeks = item.weeks || [];
                  const printWeeks = getPrintWeeksForAthlete(athleteWeeks);
                  const weekChunks = chunkArray(printWeeks, 9); // all 9 weeks in one page per athlete
                  const coachName = getCoachNameForAthlete(athlete);
                  const teamName = getTeamNameForAthlete(athlete);
                  const athletePhoto = getSafePhotoUrl(athlete?.photo || athlete?.player?.photo);
                  const regId = athlete.player_registration_id || athlete.player?.registration_id || athlete.bib_no || "N/A";

                  return (
                    <React.Fragment key={athlete.id || athIdx}>
                      {weekChunks.map((chunk, chunkIdx) => {
                        return (
                          <div
                            key={`${athlete.id}-chunk-${chunkIdx}`}
                            className="print-report-page"
                          >
                            {/* TOP SECTION: ASSOCIATION TITLE & ATHLETE HEADER */}
                            <div>
                              {/* Organization Header with Logos: SSA -> PARASPORTS -> HEAL */}
                              <div className="border-b-2 border-slate-950 pb-2 mb-2">
                                <div className="flex items-center justify-between gap-3 mb-1">
                                  {/* 1. SSA Logo (Left) */}
                                  <div className="flex items-center justify-start shrink-0">
                                    <img
                                      src="/images/ssa.png"
                                      alt="Samagra Shiksha"
                                      style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "left center" }}
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                  </div>

                                  {/* 2. PARASPORTS Logo & Details (Center) */}
                                  <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                                    <div className="flex items-center justify-center gap-2 mb-0.5">
                                      <img
                                        src="/images/parasports.png"
                                        alt="Para Sports"
                                        style={{ height: "56px", width: "auto", maxWidth: "110px", objectFit: "contain" }}
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                      />
                                      <div>
                                        <h1 className="text-sm font-black tracking-tight text-slate-950 uppercase leading-tight">
                                          Para Sports Association of Andhra Pradesh
                                        </h1>
                                        <p className="text-[9px] text-slate-800 font-semibold leading-tight">
                                          Recognized by Paralympic Committee of India &amp; Sports Authority of Andhra Pradesh
                                        </p>
                                      </div>
                                    </div>
                                    <p className="text-[8.5px] text-slate-600 font-medium leading-tight">
                                      In association with: <span className="font-bold text-cyan-800">Samagra Shiksha</span> &bull; <span className="font-bold text-sky-700">Government of Andhra Pradesh</span>
                                    </p>
                                  </div>

                                  {/* 3. Government of Andhra Pradesh Logo (Right) */}
                                  <div className="flex items-center justify-end shrink-0">
                                    <img
                                      src="/images/ap_govt.png"
                                      alt="Government of Andhra Pradesh"
                                      style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "right center" }}
                                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    />
                                  </div>
                                </div>
                                <div className="text-center mt-1">
                                  <div className="inline-block px-3 py-0.5 bg-slate-900 text-white text-[10.5px] font-black uppercase tracking-wider rounded">
                                    Coaching Camp — Athlete Weekly Evaluation Report Card
                                  </div>
                                </div>
                              </div>

                              {/* Athlete Information Table & Top Right Photo */}
                              <div className="border border-slate-900 rounded p-2 mb-2 flex items-start justify-between gap-3 bg-white">
                                {/* Athlete Details Table (Left) */}
                                <div className="flex-1 report-info-table text-[10.5px] text-slate-900">
                                  <div className="report-info-row">
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Player Name:</span>{" "}
                                      <span className="font-black uppercase text-slate-950 text-xs">
                                        {athlete.name}
                                      </span>
                                    </div>
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">ID No:</span>{" "}
                                      <span className="font-black font-mono text-cyan-900">
                                        {athlete.id_number || regId || athlete.player?.registration_id || athlete.bib_no || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="report-info-row">
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Father's Name:</span>{" "}
                                      <span className="font-semibold text-slate-900">
                                        {athlete.father_name || "N/A"}
                                      </span>
                                    </div>
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">District:</span>{" "}
                                      <span className="font-semibold text-slate-900">
                                        {athlete.district || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="report-info-row">
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Team / Squad:</span>{" "}
                                      <span className="font-bold text-cyan-900">
                                        {teamName}
                                      </span>
                                    </div>
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Assigned Assistant Coach:</span>{" "}
                                      <span className="font-bold text-slate-900">
                                        {coachName}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="report-info-row">
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Sport Discipline:</span>{" "}
                                      <span className="font-bold text-slate-900">
                                        {(athlete.discipline_name && athlete.discipline_name.trim() ? (/athletics/i.test(athlete.discipline_name) ? "Athletics" : athlete.discipline_name) : "Para Sports")}
                                      </span>
                                    </div>
                                    <div className="report-info-col">
                                      <span className="font-bold text-slate-700">Para Classification:</span>{" "}
                                      <span className="font-black text-slate-950 font-mono">
                                        {athlete.category || "Open / Unclassified"}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Top Right Corner Photo */}
                                <div className="w-20 h-24 border border-slate-900 rounded overflow-hidden shrink-0 bg-slate-50 flex items-center justify-center text-center p-0.5">
                                  <img
                                    src={athletePhoto}
                                    alt={athlete.name}
                                    className="w-full h-full object-cover rounded-sm"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = DEFAULT_AVATAR;
                                    }}
                                  />
                                </div>
                              </div>

                              {/* Weekly Assessment Table (Weeks 1 to 9) */}
                              <div>
                                <div className="text-[9.5px] font-bold uppercase tracking-wider text-slate-900 mb-1 flex justify-between items-center">
                                  <span>
                                    Weekly Performance Assessment &bull; All 9 Weeks
                                  </span>
                                  <span className="text-slate-600 font-semibold">
                                    Evaluation Scale: Excellent | Good | Average | Poor
                                  </span>
                                </div>

                                <table className="w-full border-collapse border border-slate-900 text-[10px]">
                                  <thead>
                                    <tr className="bg-slate-200 text-slate-950 font-black border-b border-slate-900 text-center">
                                      <th className="border border-slate-900 py-1 px-1.5 w-14">Week</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Endurance</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Strength</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Agility</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Skill</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Technique</th>
                                      <th className="border border-slate-900 py-1 px-1 w-16">Discipline</th>
                                      <th className="border border-slate-900 py-1 px-1 w-20">Rating</th>
                                      <th className="border border-slate-900 py-1 px-2 text-left">Remarks &amp; Development Advice</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {chunk.map((wData) => {
                                      const skillVal =
                                        wData.skill ||
                                        (wData.skill_technique
                                          ? wData.skill_technique.split("/")[0]?.trim()
                                          : "-");
                                      const techniqueVal =
                                        wData.technique ||
                                        (wData.skill_technique
                                          ? wData.skill_technique.split("/")[1]?.trim() || "-"
                                          : "-");

                                      return (
                                        <tr key={wData.week_number} className="border-b border-slate-800 text-center">
                                          <td className="border border-slate-800 py-1 px-1 font-black bg-slate-100 text-slate-950">
                                            Week {wData.week_number}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : (wData.endurance || "-")}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : (wData.strength || "-")}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : (wData.agility || "-")}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : skillVal}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : techniqueVal}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-semibold">
                                            {isBlankReport ? "" : (wData.discipline || "-")}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-1 font-extrabold text-slate-950 bg-slate-50">
                                            {isBlankReport ? "" : (wData.performance_rating || "-")}
                                          </td>
                                          <td className="border border-slate-800 py-1 px-2 text-left text-[9.5px] leading-tight font-medium text-slate-900">
                                            {isBlankReport ? "" : (wData.remarks && wData.remarks !== "-"
                                              ? wData.remarks
                                              : "Satisfactory participation and consistent training maintained.")}
                                            {!isBlankReport && wData.file_path && (
                                              <span className="ml-1 text-[8.5px] font-bold text-cyan-800">
                                                [Assessment Doc Attached]
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* BOTTOM SECTION: NOTES & OFFICIAL VERIFICATION SIGNATURES */}
                            <div className="pt-2 border-t border-slate-950 mt-2">
                              <div className="flex items-center justify-between text-[9px] text-slate-700 mb-3 font-medium">
                                <span>
                                  * Certified that the weekly athletic assessments recorded above reflect objective evaluations conducted by qualified coaches during the camp.
                                </span>
                                <span className="font-bold text-slate-900">
                                  Page {chunkIdx + 1} of {weekChunks.length} &bull; Athlete #{athlete.bib_no || athlete.id}
                                </span>
                              </div>

                              {/* Verification Signatures (Points 7, 8, 13) */}
                              {(() => {
                                const hasDual = athlete?.assigned_coaches && athlete.assigned_coaches.length >= 2;
                                return (
                                  <div className="report-sig-row pt-3">
                                    {hasDual ? (
                                      <>
                                        <div className="report-sig-cell">
                                          <div className="border-t border-slate-950 pt-1 font-black text-slate-950 text-xs">
                                            Assistant Coach 1
                                          </div>
                                          <div className="text-[9.5px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
                                            Date &amp; Signature
                                          </div>
                                        </div>
                                        <div className="report-sig-cell">
                                          <div className="border-t border-slate-950 pt-1 font-black text-slate-950 text-xs">
                                            Assistant Coach 2
                                          </div>
                                          <div className="text-[9.5px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
                                            Date &amp; Signature
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <div className="report-sig-cell">
                                        <div className="border-t border-slate-950 pt-1 font-black text-slate-950 text-xs">
                                          Assistant Coach
                                        </div>
                                        <div className="text-[9.5px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
                                          Assigned Coach / Evaluator
                                        </div>
                                      </div>
                                    )}

                                    <div className="report-sig-cell">
                                      <div className="border-t border-slate-950 pt-1 font-black text-slate-950 text-xs">
                                        {signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                                      </div>
                                      <div className="text-[9.5px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
                                        {signatures?.camp_incharge_title || "Camp In-Charge"}
                                      </div>
                                    </div>

                                    <div className="report-sig-cell">
                                      <div className="border-t border-slate-950 pt-1 font-black text-slate-950 text-xs">
                                        Official
                                      </div>
                                      <div className="text-[9.5px] text-slate-700 font-bold uppercase tracking-wider mt-0.5">
                                        Official
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </>
          )}

          {/* MODE: INDIVIDUAL ATHLETE DAILY TRAINING EVALUATION REPORT */}
          {printMode === "individual_student_reports" && (
            <>
              {(() => {
                const baseList = (Array.isArray(athletes) && athletes.length > 0
                  ? athletes
                  : (athletesToPrintForReportCards.length > 0
                    ? athletesToPrintForReportCards.map((item) => item.athlete || item)
                    : (allAthletesReportCardData || []).map((item) => item.athlete || item))
                ).filter((a) => a && a.status !== "removed_from_camp");

                const effectiveSelectedAthleteId =
                  selectedIndividualAthleteId || (baseList[0] ? String(baseList[0].id) : "");

                const filteredList = individualScope === "single"
                  ? baseList.filter((a) => String(a.id) === String(effectiveSelectedAthleteId))
                  : individualScope === "team"
                    ? baseList.filter((a) => {
                      const t = (a.team_name || a.discipline_name || a.category || (a.camp && a.camp.name) || "").trim();
                      return reportSelectedTeam === "all" || t === reportSelectedTeam;
                    })
                    : baseList;

                // Sort athletes team-wise for printing / export
                const sortedAthletes = [...filteredList].sort((a, b) => {
                  const teamA = getTeamNameForAthlete(a) || "General Squad";
                  const teamB = getTeamNameForAthlete(b) || "General Squad";
                  const cmp = teamA.localeCompare(teamB, undefined, { numeric: true, sensitivity: "base" });
                  if (cmp !== 0) return cmp;
                  const bibA = parseInt(a.bib_no || a.id_number || "0", 10) || 0;
                  const bibB = parseInt(b.bib_no || b.id_number || "0", 10) || 0;
                  if (bibA !== bibB && (bibA > 0 || bibB > 0)) return bibA - bibB;
                  return (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });
                });

                const pages = [];
                const reportDays = reportDayMode === "single_day"
                  ? [{
                    dayNumber: selectedReportDay?.dayNumber || reportSingleDayNum,
                    dateStr: selectedReportDay?.dateStr || "Camp date not set",
                    isoDate: selectedReportDay?.isoDate || reportSingleDate,
                  }]
                  : getCampDaysList(sortedAthletes[0]).filter((day) => reportDayMode !== "date_range" || (
                    (!individualDateFrom || day.isoDate >= individualDateFrom) &&
                    (!individualDateTo || day.isoDate <= individualDateTo)
                  ));

                // Official order: each day, then each team, then all players in that team.
                reportDays.forEach((day) => {
                  sortedAthletes.forEach((athlete) => {
                    pages.push({ athlete, day });
                  });
                });

                if (pages.length === 0) {
                  return (
                    <div className="individual-student-page p-8 text-center text-slate-500 bg-white flex items-center justify-center">
                      <p className="text-sm font-bold">No athletes found for the selected filter or camp.</p>
                    </div>
                  );
                }

                return pages.map(({ athlete, day }, pageIdx) => {
                  const teamName = getTeamNameForAthlete(athlete);
                  const allottedGames = getAthleteAllottedGames(athlete);
                  const allottedGamesText = allottedGames.length ? allottedGames.join(", ") : "Not allotted";
                  const gamePerformanceLines = getAthleteGamePerformanceLines(athlete, performanceReports);
                  const holidaySession = individualHolidays[day.isoDate] || "";
                  const morningHoliday = holidaySession === "full_day" || holidaySession === "morning";
                  const eveningHoliday = holidaySession === "full_day" || holidaySession === "evening";
                  return (
                    <div
                      key={`${athlete.id || 'ath'}-${day.dayNumber}-${pageIdx}`}
                      className="individual-student-page flex flex-col w-full"
                      style={{
                        background: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        boxSizing: "border-box",
                        height: "270mm",
                        maxHeight: "270mm",
                        padding: "3mm 5mm 2mm 5mm",
                        overflow: "hidden",
                        pageBreakInside: "avoid",
                        breakInside: "avoid",
                        pageBreakAfter: pageIdx === pages.length - 1 ? "auto" : "always",
                        breakAfter: pageIdx === pages.length - 1 ? "auto" : "page",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* TOP HEADER SECTION WITH 3 OFFICIAL LOGOS (EQUAL HIGHLIGHT) */}
                      <div>
                        <div className="border-b-2 border-slate-900 pb-1 mb-1 bg-white">
                          <div className="flex items-center justify-between gap-3 mb-0.5">
                            {/* 1. SSA Logo (Left) */}
                            <div className="flex items-center justify-start shrink-0">
                              <img
                                src="/images/ssa.png"
                                alt="Samagra Shiksha"
                                style={{ height: "36px", width: "auto", objectFit: "contain" }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </div>

                            {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
                            <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                              <div className="text-[12px] sm:text-[13px] font-black tracking-wide text-slate-950 uppercase leading-snug">
                                SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
                              </div>
                              <div className="flex items-center justify-center gap-1.5 my-0.5">
                                <img
                                  src="/images/parasports.png"
                                  alt="Para Sports"
                                  style={{ height: "18px", width: "auto", objectFit: "contain" }}
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                                <span className="text-[12px] sm:text-[13px] font-black tracking-wide text-slate-900 uppercase leading-snug">
                                  PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                                </span>
                              </div>
                              <p className="text-[7.5px] text-slate-700 font-semibold tracking-normal">
                                Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                              </p>
                            </div>

                            {/* 3. Government of Andhra Pradesh Emblem (Right) */}
                            <div className="flex items-center justify-end shrink-0">
                              <img
                                src="/images/ap_govt.png"
                                alt="Government of Andhra Pradesh"
                                style={{ height: "36px", width: "auto", objectFit: "contain" }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </div>
                          </div>

                          <div className="text-center mt-0.5">
                            <div className="inline-block px-3 py-0.5 bg-slate-950 text-white text-[9px] font-black uppercase tracking-wider rounded">
                              Coaching Camp &mdash; Daily Athlete Performance Report
                            </div>
                          </div>
                        </div>

                        {/* ATHLETE & CAMP DETAILS META BOX WITH PHOTO */}
                        <div className="border border-slate-900 rounded p-1.5 mb-1.5 bg-white flex items-center justify-between gap-2.5">
                          <div className="grid grid-cols-3 gap-x-2.5 gap-y-0.5 text-[10px] text-slate-900 flex-1">
                            <div>
                              <span className="font-bold text-slate-700">Athlete Name:</span>{" "}
                              <span className="font-extrabold uppercase text-slate-950 text-[10.5px]">
                                {athlete.name}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-700">ID No:</span>{" "}
                              <span className="font-bold font-mono text-cyan-900">
                                {athlete.id_number || athlete.player?.registration_id || athlete.bib_no || "N/A"}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-700">Team Name:</span>{" "}
                              <span className="font-bold text-slate-900">
                                {teamName}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-700">District:</span>{" "}
                              <span className="font-bold text-slate-900">
                                {athlete.district || "N/A"}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-700">Allotted Games:</span>{" "}
                              <span className="font-bold text-slate-900">
                                {allottedGamesText}
                              </span>
                            </div>
                            <div>
                              <span className="font-bold text-slate-700">Date:</span>{" "}
                              <span className="font-bold text-slate-950">
                                {day.dateStr} (Day {day.dayNumber})
                              </span>
                            </div>
                          </div>

                          {/* Top Right Photo */}
                          <div className="border-2 border-slate-900 rounded overflow-hidden shrink-0 bg-slate-50 flex items-center justify-center p-0.5" style={{ width: "20mm", height: "23mm" }}>
                            <img
                              src={getSafePhotoUrl(athlete?.photo || athlete?.player?.photo)}
                              alt={athlete.name}
                              className="w-full h-full object-cover rounded-sm"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DEFAULT_AVATAR;
                              }}
                            />
                          </div>
                        </div>

                        {/* TWO SIDE-BY-SIDE COLUMNS: MORNING SESSION & EVENING SESSION */}
                        <div className="grid grid-cols-2 gap-2.5 mb-1 flex-1">
                          {/* MORNING SESSION COLUMN */}
                          <div className="relative overflow-hidden border-2 border-slate-900 rounded p-2 bg-white flex flex-col justify-between flex-1">
                            {morningHoliday && (
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 5,
                                  pointerEvents: "none",
                                  color: "rgba(185, 28, 28, 0.22)",
                                  fontSize: "42px",
                                  fontWeight: 900,
                                  letterSpacing: "5px",
                                  transform: "rotate(-32deg)",
                                }}
                              >
                                REST DAY
                              </div>
                            )}
                            <div>
                              <div className="text-center pb-1 mb-1.5 border-b-2 border-slate-900 bg-slate-100 py-0.5 rounded-t">
                                <h3 className="text-[10.5px] font-black uppercase tracking-wider text-slate-950">
                                  Morning Session
                                </h3>
                              </div>

                              <div className="space-y-1.5 text-[9.5px]">
                                {/* Reporting time */}
                                <div className="flex items-end gap-2 pt-0.5">
                                  <span className="font-bold text-slate-900 shrink-0">Reporting time :</span>
                                  <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                                </div>

                                {/* Warm up Exercise (5 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Warm up Exercise :</div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                  </div>
                                </div>

                                {/* Stretches (4 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Stretches :</div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                  </div>
                                </div>

                                {/* Specific Sport (6 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Specific Sport / Allotted Games :</div>
                                  <div className="space-y-1 pt-0.5">
                                    {Array.from({ length: 6 }, (_, lineIndex) => (
                                      <div key={lineIndex} className="border-b border-slate-400 border-dashed h-5 px-1 text-[7.5px] leading-5 font-bold text-cyan-950 overflow-hidden">
                                        {gamePerformanceLines[lineIndex] || ""}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="pt-1.5 border-t border-slate-300 space-y-1.5 text-[9.5px] mt-2">
                              <div className="flex items-end gap-2">
                                <span className="font-bold text-slate-900 shrink-0">Disperse time :</span>
                                <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                              </div>
                              <div className="flex items-end gap-2">
                                <span className="font-bold text-slate-900 shrink-0">Sign :</span>
                                <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                              </div>
                            </div>
                          </div>

                          {/* EVENING SESSION COLUMN */}
                          <div className="relative overflow-hidden border-2 border-slate-900 rounded p-2 bg-white flex flex-col justify-between flex-1">
                            {eveningHoliday && (
                              <div
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 5,
                                  pointerEvents: "none",
                                  color: "rgba(185, 28, 28, 0.22)",
                                  fontSize: "42px",
                                  fontWeight: 900,
                                  letterSpacing: "5px",
                                  transform: "rotate(-32deg)",
                                }}
                              >
                                REST DAY
                              </div>
                            )}
                            <div>
                              <div className="text-center pb-1 mb-1.5 border-b-2 border-slate-900 bg-slate-100 py-0.5 rounded-t">
                                <h3 className="text-[10.5px] font-black uppercase tracking-wider text-slate-950">
                                  Evening Session
                                </h3>
                              </div>

                              <div className="space-y-1.5 text-[9.5px]">
                                {/* Reporting time */}
                                <div className="flex items-end gap-2 pt-0.5">
                                  <span className="font-bold text-slate-900 shrink-0">Reporting time :</span>
                                  <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                                </div>

                                {/* Warm up Exercise (5 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Warm up Exercise :</div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                  </div>
                                </div>

                                {/* Stretches (4 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Stretches :</div>
                                  <div className="space-y-1 pt-0.5">
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                    <div className="border-b border-slate-400 border-dashed h-5"></div>
                                  </div>
                                </div>

                                {/* Specific Sport (6 lines) */}
                                <div className="pt-0.5">
                                  <div className="font-bold text-slate-900 mb-0.5">Specific Sport / Allotted Games :</div>
                                  <div className="space-y-1 pt-0.5">
                                    {Array.from({ length: 6 }, (_, lineIndex) => (
                                      <div key={lineIndex} className="border-b border-slate-400 border-dashed h-5 px-1 text-[7.5px] leading-5 font-bold text-cyan-950 overflow-hidden">
                                        {gamePerformanceLines[lineIndex] || ""}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="pt-1.5 border-t border-slate-300 space-y-1.5 text-[9.5px] mt-2">
                              <div className="flex items-end gap-2">
                                <span className="font-bold text-slate-900 shrink-0">Disperse time :</span>
                                <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                              </div>
                              <div className="flex items-end gap-2">
                                <span className="font-bold text-slate-900 shrink-0">Sign :</span>
                                <div className="border-b border-slate-400 border-dashed flex-1 h-5"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* BOTTOM: OFFICIAL VERIFICATION SIGNATURES */}
                      <div
                        className="pt-1.5 border-t-2 border-slate-900 bg-white mt-auto shrink-0"
                        style={{
                          pageBreakInside: "avoid",
                          breakInside: "avoid",
                          pageBreakBefore: "avoid",
                          breakBefore: "avoid",
                        }}
                      >
                        {(() => {
                          const athleteMaps = mappings?.filter((m) => String(m.athlete_id) === String(athlete.id) && m.status === "active") || [];
                          const hasDual = (athlete?.assigned_coaches && athlete.assigned_coaches.length >= 2) || (athleteMaps.length >= 2);
                          return (
                            <div className={`grid ${hasDual ? "grid-cols-5" : "grid-cols-4"} gap-3 text-center text-[10px]`}>
                              <div>
                                <div className="h-12 flex items-end justify-center pb-1">
                                  <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                </div>
                                <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                  Athlete Signature
                                </div>
                                <div className="text-[9px] text-slate-600 font-semibold truncate">
                                  {athlete.name}
                                </div>
                              </div>

                              {hasDual ? (
                                <>
                                  <div>
                                    <div className="h-12 flex items-end justify-center pb-1">
                                      <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                    </div>
                                    <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                      Assistant Coach 1
                                    </div>
                                    <div className="text-[9px] text-slate-600 font-semibold">
                                      Date &amp; Signature
                                    </div>
                                  </div>
                                  <div>
                                    <div className="h-12 flex items-end justify-center pb-1">
                                      <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                    </div>
                                    <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                      Assistant Coach 2
                                    </div>
                                    <div className="text-[9px] text-slate-600 font-semibold">
                                      Date &amp; Signature
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div>
                                  <div className="h-12 flex items-end justify-center pb-1">
                                    <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                  </div>
                                  <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                    Assistant Coach
                                  </div>
                                  <div className="text-[9px] text-slate-600 font-semibold">
                                    Date &amp; Signature
                                  </div>
                                </div>
                              )}

                              <div>
                                <div className="h-12 flex items-end justify-center pb-1">
                                  <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                </div>
                                <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                  {signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                                </div>
                                <div className="text-[9px] text-slate-600 font-semibold">
                                  {signatures?.camp_incharge_title || "Camp In-Charge"}
                                </div>
                              </div>

                              <div>
                                <div className="h-12 flex items-end justify-center pb-1">
                                  <span className="text-[8px] text-slate-400 font-medium tracking-wide uppercase">Sign above</span>
                                </div>
                                <div className="border-t border-slate-900 pt-1 font-bold text-slate-950 text-[10px]">
                                  Official
                                </div>
                                <div className="text-[9px] text-slate-600 font-semibold">
                                  Samagra Shiksha / Official
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                });
              })()}
            </>
          )}

          {/* MODE 2: BENCHMARK PERFORMANCE ASSESSMENTS (WEEK 1 VS WEEK 9) */}
          {printMode === "performance" && (
            <div className="print-report-page">
              <div>
                {/* Organization Header with Logos: SSA -> PARASPORTS -> Govt of AP */}
                <div className="border-b-2 border-slate-900 pb-2 mb-3">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    {/* 1. SSA Logo (Left) */}
                    <div className="flex items-center justify-start shrink-0">
                      <img
                        src="/images/ssa.png"
                        alt="Samagra Shiksha"
                        style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "left center" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>

                    {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
                    <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                      <div className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-950 uppercase leading-snug">
                        SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
                      </div>
                      <div className="flex items-center justify-center gap-1.5 my-0.5">
                        <img
                          src="/images/parasports.png"
                          alt="Para Sports"
                          style={{ height: "24px", width: "auto", objectFit: "contain" }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <span className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-900 uppercase leading-snug">
                          PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                        </span>
                      </div>
                      <p className="text-[8.5px] text-slate-700 font-semibold tracking-normal">
                        Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                      </p>
                    </div>

                    {/* 3. Government of Andhra Pradesh Logo (Right) */}
                    <div className="flex items-center justify-end shrink-0">
                      <img
                        src="/images/ap_govt.png"
                        alt="Government of Andhra Pradesh"
                        style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "right center" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  </div>
                  <div className="text-center mt-1">
                    <div className="inline-block px-3 py-0.5 bg-slate-900 text-white text-[11px] font-extrabold uppercase tracking-wider rounded">
                      Coaching Camp — Performance Benchmark Assessment (Week 1 vs Week 9)
                    </div>
                    <p className="text-[11px] text-slate-800 font-semibold mt-1">
                      Camp: {activeCamp?.name || "State Coaching Camp"} &bull; Venue: {activeCamp?.venue || "State Sports Stadium"} &bull; Generated: {new Date().toLocaleDateString("en-GB")}
                    </p>
                  </div>
                </div>

                {/* Performance Assessments Table */}
                <table className="w-full border-collapse border-2 border-slate-900 text-[10px]">
                  <thead>
                    <tr className="bg-slate-200 text-slate-950 font-black border-b-2 border-slate-900 text-center">
                      <th className="border border-slate-900 py-1.5 px-1">Phase</th>
                      <th className="border border-slate-900 py-1.5 px-1 text-left">Athlete Name</th>
                      <th className="border border-slate-900 py-1.5 px-1">BIB</th>
                      <th className="border border-slate-900 py-1.5 px-1">Height</th>
                      <th className="border border-slate-900 py-1.5 px-1">100m</th>
                      <th className="border border-slate-900 py-1.5 px-1">400m</th>
                      <th className="border border-slate-900 py-1.5 px-1">Shot Put</th>
                      <th className="border border-slate-900 py-1.5 px-1">Long Jump</th>
                      <th className="border border-slate-900 py-1.5 px-1">Skipping</th>
                      <th className="border border-slate-900 py-1.5 px-1">Push-ups</th>
                      <th className="border border-slate-900 py-1.5 px-1">Plank</th>
                      <th className="border border-slate-900 py-1.5 px-1">Pull-ups</th>
                      <th className="border border-slate-900 py-1.5 px-1">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performanceReports.map((p) => (
                      <tr key={p.id} className="border-b border-slate-900 text-center">
                        <td className="border border-slate-900 py-1.5 px-1 font-bold">
                          Week {p.week_number}
                        </td>
                        <td className="border border-slate-900 py-1.5 px-1 font-bold text-left">
                          {p.athlete?.name}
                        </td>
                        <td className="border border-slate-900 py-1.5 px-1 font-mono font-bold">
                          {p.athlete?.bib_no}
                        </td>
                        <td className="border border-slate-900 py-1.5 px-1 font-semibold">
                          {p.test_height || p.athlete?.height_cm
                            ? `${p.test_height || p.athlete?.height_cm} cm`
                            : (athletes.find((a) => String(a.id) === String(p.athlete_id))?.height_cm
                              ? `${athletes.find((a) => String(a.id) === String(p.athlete_id)).height_cm} cm`
                              : "-")}
                        </td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_100m || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_400m || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_shot_put || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_long_jump || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_skipping || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_pushups || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_plank || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1">{p.test_pullups || "-"}</td>
                        <td className="border border-slate-900 py-1.5 px-1 font-bold">{p.test_weight || "-"}</td>
                      </tr>
                    ))}
                    {!performanceReports.length && (
                      <tr>
                        <td colSpan={13} className="border border-slate-900 py-4 text-center text-slate-500">
                          No performance assessments recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom Signatures */}
              <div className="pt-2 border-t-2 border-slate-900">
                <div className="grid grid-cols-3 gap-6 text-center text-[11px] pt-4">
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      Assistant Coach
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      Date &amp; Signature
                    </div>
                  </div>
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      {signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      {signatures?.camp_incharge_title || "Camp In-Charge"}
                    </div>
                  </div>
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      Official
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      Official
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODE 3: BLANK EVALUATION SHEET */}
          {printMode === "blank" && (
            <div className="print-report-page">
              <div className="bg-white text-slate-900 p-4 font-sans space-y-4">
                {/* Organization Header with Logos: SSA -> PARASPORTS -> HEAL */}
                <div className="border-b-2 border-slate-900 pb-3">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    {/* 1. SSA Logo (Left) */}
                    <div className="flex items-center justify-start shrink-0">
                      <img
                        src="/images/ssa.png"
                        alt="Samagra Shiksha"
                        style={{ height: "68px", width: "84px", objectFit: "contain", objectPosition: "left center" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>

                    {/* 2. Equal Highlight: SAMAGRA SHIKSHA & PARA SPORTS (Center) */}
                    <div className="text-center flex-1 px-2 flex flex-col items-center justify-center">
                      <div className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-950 uppercase leading-snug">
                        SAMAGRA SHIKSHA &bull; GOVERNMENT OF ANDHRA PRADESH
                      </div>
                      <div className="flex items-center justify-center gap-1.5 my-0.5">
                        <img
                          src="/images/parasports.png"
                          alt="Para Sports"
                          style={{ height: "24px", width: "auto", objectFit: "contain" }}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                        <span className="text-[13px] sm:text-[14px] font-black tracking-wide text-slate-900 uppercase leading-snug">
                          PARA SPORTS ASSOCIATION OF ANDHRA PRADESH
                        </span>
                      </div>
                      <p className="text-[8.5px] text-slate-700 font-semibold tracking-normal">
                        Recognized by Paralympic Committee of India &bull; Sports Authority of Andhra Pradesh
                      </p>
                      <p className="text-[8px] text-slate-500 mt-0.5">
                        Camp: {activeCamp?.name || "Official State Coaching Camp 2026"} &bull; Venue: {activeCamp?.venue || "State Sports Complex"}
                      </p>
                    </div>

                    {/* 3. Government of Andhra Pradesh Logo (Right) */}
                    <div className="flex items-center justify-end shrink-0">
                      <img
                        src="/images/ap_govt.png"
                        alt="Government of Andhra Pradesh"
                        style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "left center" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 border border-slate-400 p-2.5 rounded text-[11px]">
                  <div>Athlete Name: __________________________</div>
                  <div>ID No: _______________________</div>
                  <div>District: _____________________________</div>
                  <div>Discipline: __________________________</div>
                  <div>Category: _____________________________</div>
                  <div>Assistant Coach: _______________________________</div>
                </div>

                <table className="w-full text-left text-[11px] border-collapse border border-slate-400">
                  <thead>
                    <tr className="bg-slate-100 text-slate-900 font-bold border-b border-slate-400">
                      <th className="p-1.5 border border-slate-400">Sl</th>
                      <th className="p-1.5 border border-slate-400">Performance Area</th>
                      <th className="p-1.5 border border-slate-400 text-center">Excellent</th>
                      <th className="p-1.5 border border-slate-400 text-center">Good</th>
                      <th className="p-1.5 border border-slate-400 text-center">Average</th>
                      <th className="p-1.5 border border-slate-400 text-center">Poor</th>
                      <th className="p-1.5 border border-slate-400">Remarks / Recommendations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["Endurance & Stamina", "Core Strength & Power", "Agility & Coordination", "Sport-Specific Skill", "Sport-Specific Technique", "Camp Discipline & Ethics"].map((area, idx) => (
                      <tr key={idx} className="border-b border-slate-300">
                        <td className="p-1.5 border border-slate-400 font-bold">{idx + 1}</td>
                        <td className="p-1.5 border border-slate-400 font-semibold">{area}</td>
                        <td className="p-1.5 border border-slate-400 text-center">[ &nbsp; ]</td>
                        <td className="p-1.5 border border-slate-400 text-center">[ &nbsp; ]</td>
                        <td className="p-1.5 border border-slate-400 text-center">[ &nbsp; ]</td>
                        <td className="p-1.5 border border-slate-400 text-center">[ &nbsp; ]</td>
                        <td className="p-1.5 border border-slate-400 h-8"></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="pt-2 border-t-2 border-slate-900">
                <div className="grid grid-cols-3 gap-6 text-center text-[11px] pt-4">
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      Assistant Coach
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      Date &amp; Signature
                    </div>
                  </div>
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      {signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      {signatures?.camp_incharge_title || "Camp In-Charge"}
                    </div>
                  </div>
                  <div>
                    <div className="border-t border-slate-900 pt-1.5 font-bold text-slate-950">
                      Official
                    </div>
                    <div className="text-[10px] text-slate-600 font-semibold">
                      Official
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* MODE 4: DAILY ATTENDANCE REGISTER (1 DAY PER PAPER) */}
          {printMode === "daily_attendance" && (
            <div className="w-full">
              {rangeReportData.flatMap((coach) =>
                (coach.days || []).map((day) => ({
                  coach,
                  day,
                }))
              ).map(({ coach, day }, pageIdx) => {
                const coachesOnTeam = coach.coaches || [];
                let coach1Name = coachesOnTeam[0]?.name;
                let coach2Name = coachesOnTeam[1]?.name;
                if (!coach2Name && coach.coach_name && coach.coach_name.includes('&')) {
                  const parts = coach.coach_name.split('&').map(s => s.trim());
                  coach1Name = coach1Name || parts[0];
                  coach2Name = parts[1];
                }
                coach1Name = coach1Name || "Assistant Coach 1";
                coach2Name = coach2Name || "Assistant Coach 2";
                const displayCoaches = coachesOnTeam.length > 0
                  ? coachesOnTeam.map(c => c.name).join(" & ")
                  : (coach.coach_name || `${coach1Name} & ${coach2Name}`);

                return (
                  <div
                    key={`${coach.coach_id || 'c'}-${day.date || 'd'}-${pageIdx}`}
                    className="print-sheet bg-white text-slate-950 font-sans flex flex-col justify-between"
                    style={{
                      height: "270mm",
                      maxHeight: "270mm",
                      padding: "2mm 3mm",
                      boxSizing: "border-box",
                      pageBreakInside: "avoid",
                      breakInside: "avoid",
                      pageBreakAfter: pageIdx === rangeReportData.length - 1 ? "auto" : "always",
                      breakAfter: pageIdx === rangeReportData.length - 1 ? "auto" : "page",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      {/* Header Table: SSA Logo | Title | AP Govt Logo */}
                      <table className="no-border w-full" style={{ border: "none", margin: "0 0 3px 0", padding: "0 0 3px 0", borderBottom: "2px solid #0f172a" }}>
                        <tbody>
                          <tr style={{ border: "none" }}>
                            <td style={{ border: "none", width: "78px", textAlign: "left", verticalAlign: "middle", padding: 0 }}>
                              <img src="/images/ssa.png" style={{ height: "68px", width: "84px", objectFit: "contain", objectPosition: "left center" }} alt="SSA" onError={(e) => { e.target.style.display = "none"; }} />
                            </td>
                            <td style={{ border: "none", textAlign: "center", verticalAlign: "middle", padding: "0 4px" }}>
                              <div style={{ marginBottom: "1px" }}>
                                <img src="/images/parasports.png" style={{ height: "60px", width: "auto", maxWidth: "112px", display: "block", objectFit: "contain", margin: "0 auto 2px auto" }} alt="Para Sports" onError={(e) => { e.target.style.display = "none"; }} />
                                <span style={{ fontSize: "13.5px", fontWeight: 900, textTransform: "uppercase", color: "#0f172a", letterSpacing: "0.4px" }}>
                                  Para Sports Association of Andhra Pradesh
                                </span>
                              </div>
                              <div style={{ fontSize: "8.5px", fontWeight: 600, color: "#334155", marginTop: "1px" }}>
                                Recognized by Paralympic Committee of India (PCI) &amp; Sports Authority of Andhra Pradesh (SAAP)
                              </div>
                              <div style={{ fontSize: "8.5px", fontWeight: 600, color: "#475569", marginTop: "1px" }}>
                                In association with: <strong style={{ color: "#0369a1" }}>Samagra Shiksha</strong> &bull; <strong style={{ color: "#0284c7" }}>Government of Andhra Pradesh</strong>
                              </div>
                              <div style={{ marginTop: "2px" }}>
                                <span style={{ display: "inline-block", background: "#0f172a", color: "#ffffff", fontSize: "9px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", padding: "2px 10px", borderRadius: "2px" }}>
                                  {rangeFormatStyle === "blank" ? "Daily Athlete Attendance Register (Manual Ground Register)" : "Daily Athlete Attendance Register"}
                                </span>
                              </div>
                            </td>
                            <td style={{ border: "none", width: "78px", textAlign: "right", verticalAlign: "middle", padding: 0 }}>
                              <img src="/images/ap_govt.png" style={{ height: "72px", width: "88px", objectFit: "contain", objectPosition: "left center" }} alt="AP Govt" onError={(e) => { e.target.style.display = "none"; }} />
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Details Bar Table */}
                      <table className="no-border w-full" style={{ border: "1.2px solid #64748b", background: "#f8fafc", borderRadius: "3px", padding: "3px 6px", fontSize: "9.5px", margin: "0 0 4px 0" }}>
                        <tbody>
                          <tr style={{ border: "none" }}>
                            <td style={{ border: "none", padding: "1px 4px", width: "28%" }}>
                              <span style={{ color: "#475569", fontWeight: "bold", fontSize: "9px" }}>Team / Squad:</span>
                              <strong style={{ color: "#0369a1", fontSize: "10.5px", marginLeft: "2px" }}>{coach.team_name || coach.discipline || "General Squad"}</strong>
                            </td>
                            <td style={{ border: "none", padding: "1px 4px", width: "34%" }}>
                              <span style={{ color: "#475569", fontWeight: "bold", fontSize: "9px" }}>Coach(es):</span>
                              <strong style={{ color: "#0f172a", fontSize: "10px", marginLeft: "2px" }}>{displayCoaches}</strong>
                            </td>
                            <td style={{ border: "none", padding: "1px 4px", width: "26%" }}>
                              <span style={{ color: "#475569", fontWeight: "bold", fontSize: "9px" }}>Date:</span>
                              <strong style={{ color: "#0f172a", fontFamily: "monospace", fontSize: "10.5px", marginLeft: "2px" }}>
                                {day.date ? day.date.split("-").reverse().join("-") : "—"}{getCampDayNumber(day.date) ? ` (Day ${getCampDayNumber(day.date)}${campTotalDays ? ` of ${campTotalDays}` : ""})` : ""}
                              </strong>
                            </td>
                            <td style={{ border: "none", padding: "1px 4px", width: "12%", textAlign: "right" }}>
                              <span style={{ color: "#475569", fontWeight: "bold", fontSize: "9px" }}>Players:</span>
                              <strong style={{ color: "#0369a1", fontSize: "10.5px", marginLeft: "2px" }}>{day.athletes?.length || 0}</strong>
                            </td>
                          </tr>
                          <tr style={{ border: "none" }}>
                            <td colSpan={4} style={{ border: "none", borderTop: "1px dashed #cbd5e1", padding: "2px 4px 0 4px", fontSize: "8px", color: "#475569" }}>
                              <table className="no-border w-full" style={{ border: "none", padding: 0, margin: 0 }}>
                                <tbody>
                                  <tr style={{ border: "none" }}>
                                    <td style={{ border: "none", padding: 0 }}><strong>Camp:</strong> {activeCamp?.name || "State Para Sports Coaching Camp 2026"}</td>
                                    <td style={{ border: "none", padding: 0 }}><strong>Venue:</strong> {activeCamp?.venue || "State Sports Complex, Andhra Pradesh"}</td>
                                    <td style={{ border: "none", padding: 0 }}><strong>Duration:</strong> {campStartDate ? campStartDate.split("-").reverse().join("-") : "Start"} to {campEndDate ? campEndDate.split("-").reverse().join("-") : "End"}{campTotalDays ? ` (${campTotalDays} Days)` : ""}</td>
                                    <td style={{ border: "none", padding: 0, textAlign: "right" }}><strong>Discipline:</strong> {coach.discipline || "All Disciplines"}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {/* Attendance Register Table (Strict 100% Fixed Columns) */}
                      <table className="data-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", border: "1.2px solid #0f172a", fontSize: "9px", lineHeight: "1.15" }}>
                        <colgroup>
                          <col style={{ width: "4%" }} />
                          <col style={{ width: "24%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "10%" }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: "#e2e8f0", textAlign: "center" }}>
                            <th style={{ fontSize: "9px", padding: "2px 1px", border: "1px solid #0f172a" }} rowSpan={2}>S.No</th>
                            <th style={{ textAlign: "left", fontSize: "9px", padding: "2px 4px", border: "1px solid #0f172a" }} rowSpan={2}>Player Name</th>
                            <th style={{ fontSize: "9px", padding: "2px 1px", border: "1px solid #0f172a" }} rowSpan={2}>ID Number</th>
                            <th colSpan={4} style={{ textAlign: "center", background: "#e0f2fe", color: "#0369a1", fontSize: "9px", padding: "2px 1px", border: "1px solid #0f172a" }}>Morning Session</th>
                            <th colSpan={4} style={{ textAlign: "center", background: "#ede9fe", color: "#5b21b6", fontSize: "9px", padding: "2px 1px", border: "1px solid #0f172a" }}>Evening Session</th>
                          </tr>
                          <tr style={{ background: "#f8fafc", fontSize: "8px", textAlign: "center" }}>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>In Time</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Out Time</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Status</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Signature</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>In Time</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Out Time</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Status</th>
                            <th style={{ padding: "2px 1px", border: "1px solid #0f172a" }}>Signature</th>
                          </tr>
                        </thead>
                        <tbody>
                          {day.athletes && day.athletes.length > 0 ? (
                            day.athletes.map((ath, aIdx) => (
                              <tr key={ath.athlete_id || aIdx} className="box-cell" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                                <td className="text-center font-bold" style={{ fontSize: "9px", padding: "1px 2px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>{aIdx + 1}</td>
                                <td className="text-left font-bold" style={{ color: "#0f172a", fontSize: "9px", padding: "1px 4px", lineHeight: "1.15", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", border: "1px solid #0f172a", textAlign: "left" }}>
                                  {ath.athlete_name || ath.name || "—"}
                                  <div style={{ fontSize: "7.5px", color: "#475569", fontWeight: "normal", marginTop: "0.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "1.1" }}>
                                    District: {ath.district || "—"}
                                  </div>
                                </td>
                                <td className="text-center font-mono font-bold" style={{ fontSize: "8.5px", color: "#0369a1", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {ath.id_number || ath.player_registration_id || ath.bib_no || "—"}
                                </td>
                                {/* Morning Session */}
                                <td className="text-center font-mono" style={{ fontSize: "8.5px", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? <span className="write-line" style={{ display: "inline-block", minWidth: "38px", borderBottom: "1px dotted #94a3b8", height: "10px" }}></span> : (ath.morning_in || "—")}
                                </td>
                                <td className="text-center font-mono" style={{ fontSize: "8.5px", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? <span className="write-line" style={{ display: "inline-block", minWidth: "38px", borderBottom: "1px dotted #94a3b8", height: "10px" }}></span> : (ath.morning_out || "—")}
                                </td>
                                <td className="text-center" style={{ fontSize: "8.5px", fontWeight: "bold", whiteSpace: "nowrap", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? (
                                    <>
                                      <span className="checkbox-square" style={{ display: "inline-block", width: "10px", height: "10px", border: "1.2px solid #0f172a", borderRadius: "2px", marginRight: "2px", verticalAlign: "middle" }}></span>P &nbsp;
                                      <span className="checkbox-square" style={{ display: "inline-block", width: "10px", height: "10px", border: "1.2px solid #0f172a", borderRadius: "2px", marginRight: "2px", verticalAlign: "middle" }}></span>A
                                    </>
                                  ) : (
                                    <strong style={{ fontSize: "9px", color: ath.morning_status === "present" ? "#166534" : "#991b1b" }}>
                                      {ath.morning_status ? ath.morning_status.toUpperCase() : "—"}
                                    </strong>
                                  )}
                                </td>
                                <td style={{ padding: "1px 2px", border: "1px solid #0f172a", textAlign: "center" }}><span className="write-line" style={{ display: "inline-block", width: "90%", borderBottom: "1px dotted #64748b", height: "10px" }}></span></td>
                                {/* Evening Session */}
                                <td className="text-center font-mono" style={{ fontSize: "8.5px", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? <span className="write-line" style={{ display: "inline-block", minWidth: "38px", borderBottom: "1px dotted #94a3b8", height: "10px" }}></span> : (ath.evening_in || "—")}
                                </td>
                                <td className="text-center font-mono" style={{ fontSize: "8.5px", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? <span className="write-line" style={{ display: "inline-block", minWidth: "38px", borderBottom: "1px dotted #94a3b8", height: "10px" }}></span> : (ath.evening_out || "—")}
                                </td>
                                <td className="text-center" style={{ fontSize: "8.5px", fontWeight: "bold", whiteSpace: "nowrap", padding: "1px 1px", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "center" }}>
                                  {rangeFormatStyle === "blank" ? (
                                    <>
                                      <span className="checkbox-square" style={{ display: "inline-block", width: "10px", height: "10px", border: "1.2px solid #0f172a", borderRadius: "2px", marginRight: "2px", verticalAlign: "middle" }}></span>P &nbsp;
                                      <span className="checkbox-square" style={{ display: "inline-block", width: "10px", height: "10px", border: "1.2px solid #0f172a", borderRadius: "2px", marginRight: "2px", verticalAlign: "middle" }}></span>A
                                    </>
                                  ) : (
                                    <strong style={{ fontSize: "9px", color: ath.evening_status === "present" ? "#166534" : "#991b1b" }}>
                                      {ath.evening_status ? ath.evening_status.toUpperCase() : "—"}
                                    </strong>
                                  )}
                                </td>
                                <td style={{ padding: "1px 2px", border: "1px solid #0f172a", textAlign: "center" }}><span className="write-line" style={{ display: "inline-block", width: "90%", borderBottom: "1px dotted #64748b", height: "10px" }}></span></td>
                                {/* Remarks removed
                              <td style={{ fontSize: "7.5px", color: "#334155", padding: "1px 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "1.15", border: "1px solid #0f172a", textAlign: "left" }}>
                                {rangeFormatStyle === "blank" ? <span style={{ display: "block", borderBottom: "1px dotted #cbd5e1", height: "10px" }}></span> : (ath.remarks || "—")}
                              </td> */}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={11} style={{ padding: "8px", textAlign: "center", color: "#64748b", border: "1px solid #0f172a" }}>
                                No athletes mapped to this coach on this date.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer Signatures strictly docked at bottom */}
                    <div style={{ marginTop: "auto", paddingTop: "3px", borderTop: "1.5px solid #0f172a", pageBreakInside: "avoid", breakInside: "avoid", flexShrink: 0 }}>
                      <table className="no-border w-full" style={{ border: "none", marginBottom: "2px", fontSize: "8px", color: "#475569" }}>
                        <tbody>
                          <tr style={{ border: "none" }}>
                            <td style={{ border: "none", padding: 0 }}>* Instructions: Take physical attendance on ground twice daily. Maintain signed register for official records and upload daily report.</td>
                            <td style={{ border: "none", padding: 0, textAlign: "right" }}>Page {pageIdx + 1} of {rangeReportData.length} &bull; {coach.team_name || coach.coach_name}</td>
                          </tr>
                        </tbody>
                      </table>
                      <table className="no-border w-full" style={{ border: "none", textAlign: "center" }}>
                        <tbody>
                          <tr style={{ border: "none" }}>
                            <td style={{ border: "none", width: "25%", verticalAlign: "bottom", padding: "0 4px" }}>
                              <div style={{ height: "42px", fontSize: "7.5px", color: "#94a3b8", textTransform: "uppercase" }}>(Signature)</div>
                              <div style={{ borderTop: "1.2px solid #0f172a", paddingTop: "2px" }}>
                                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "10px", lineHeight: "1.1" }}>{coach1Name}</div>
                                <div style={{ fontSize: "8px", color: "#475569", fontWeight: 600, marginTop: "1px" }}>Assistant Coach 1 (Sign)</div>
                              </div>
                            </td>
                            <td style={{ border: "none", width: "25%", verticalAlign: "bottom", padding: "0 4px" }}>
                              <div style={{ height: "42px", fontSize: "7.5px", color: "#94a3b8", textTransform: "uppercase" }}>(Signature)</div>
                              <div style={{ borderTop: "1.2px solid #0f172a", paddingTop: "2px" }}>
                                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "10px", lineHeight: "1.1" }}>{coach2Name}</div>
                                <div style={{ fontSize: "8px", color: "#475569", fontWeight: 600, marginTop: "1px" }}>Assistant Coach 2 (Sign)</div>
                              </div>
                            </td>
                            <td style={{ border: "none", width: "25%", verticalAlign: "bottom", padding: "0 4px" }}>
                              <div style={{ height: "42px", fontSize: "7.5px", color: "#94a3b8", textTransform: "uppercase" }}>(Signature)</div>
                              <div style={{ borderTop: "1.2px solid #0f172a", paddingTop: "2px" }}>
                                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "10px", lineHeight: "1.1" }}>
                                  {signatures?.camp_incharge_name || signatures?.chief_coach_name || "Camp In-Charge"}
                                </div>
                                <div style={{ fontSize: "8px", color: "#475569", fontWeight: 600, marginTop: "1px" }}>
                                  {signatures?.camp_incharge_title || "Camp In-Charge"}
                                </div>
                              </div>
                            </td>
                            <td style={{ border: "none", width: "25%", verticalAlign: "bottom", padding: "0 4px" }}>
                              <div style={{ height: "42px", fontSize: "7.5px", color: "#94a3b8", textTransform: "uppercase" }}>(Signature)</div>
                              <div style={{ borderTop: "1.2px solid #0f172a", paddingTop: "2px" }}>
                                <div style={{ fontWeight: 800, color: "#0f172a", fontSize: "10px", lineHeight: "1.1" }}>Official</div>
                                <div style={{ fontSize: "8px", color: "#475569", fontWeight: 600, marginTop: "1px" }}>Samagra Shiksha / SAAP Official</div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CoachingCampHub;
